import axios from 'axios';
import mapboxgl, { type Marker, type DataDrivenPropertyValueSpecification } from 'mapbox-gl';

import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useState, useRef, useEffect, useCallback, useMemo, useId } from 'react';

import { useTranslation } from 'react-i18next';
import ResizeObserver from 'resize-observer-polyfill';

import '../../styles/RaceInsightsPage.css';
import { OptimizeRoutePixelCard } from './OptimizeRoutePixelCard';
import {
  SHOW_MANUAL_SEGMENT_TOOLS,
  SHOW_MAP_HIGHLIGHT_SEGMENT,
} from '../../config/featureVisibility';
import arrowBoat from '../../react-icons/arrow.svg';
import { ReactComponent as SaveIcon } from '../../react-icons/floppyDisk.svg';
import { ReactComponent as ProfileIcon } from '../../react-icons/profileIcon.svg';
import { ReactComponent as TriangleIcon } from '../../react-icons/roundedTriangle.svg';
import { ReactComponent as SailTrackLogo } from '../../react-icons/SailTrackLogo.svg';
import { ReactComponent as TrashIcon } from '../../react-icons/trashIcon.svg';
import {
  CommentApiError,
  createMapComment,
  deleteMapComment,
  fetchCommentAuthorAvatar,
  fetchCommentAttachment,
  listMapComments,
  updateMapComment,
} from '../../services/comments';
import {
  getAdminToolsConfig,
  subscribeAdminToolsConfig,
  type AdminToolsConfig,
} from '../../state/adminToolsStore';
import {
  setManeuverLabels,
  subscribeManeuverVisibility,
  type ManeuverVisibility,
} from '../../state/maneuverVisibilityStore';
import { getCurrentColorScheme, type ColorScheme } from '../../theme';
import { calculateHeading, calculateDistance, interpolateColor } from '../../utils/geo';
import { createHighlightId } from '../../utils/highlight';
import { setBearingIfMeaningfullyChanged } from '../../utils/mapBearing';
import {
  safeMoveLayer,
  safeSetGeoJSONData,
  subscribeWhenStyleReady,
} from '../../utils/mapBoxHelpers';
import {
  getCurrentMapColorMode,
  normalizeMapboxStyleURLs,
  resolveMapColorScheme,
  selectMapboxStyleURL,
  type MapColorMode,
  type MapboxStyleResponse,
} from '../../utils/mapboxStyle';
import {
  applyMapCommentAnchorPreview,
  buildMapCommentDraftPreview,
  buildMapCommentPinFeatureCollection,
  type MapCommentDraftAnchor,
} from '../../utils/mapCommentPreview';
import {
  getMapCommentRelativeTime,
  snapMapCommentAnchorToTrack,
  type CommentTrackSnapPoint,
} from '../../utils/mapCommentTrackSnap';
import {
  type GridLayerRefreshOptions,
  getGridLayerVisibility,
  getGridMarginCells,
  getGridZoomSourceRefreshPlan,
  shouldRecenterGridAfterDrag,
  shouldRenderGridLine,
  shouldRefreshGridDuringZoom,
} from '../../utils/mapGrid';
import {
  toggleMapInputMode,
  type MapInputMode,
} from '../../utils/mapInteraction';
import {
  canMergeStyledLineFeatures,
  getFiniteMetricKeys,
  getMetricLineWidth,
  getTacticalMetricColor,
  isFiniteMetricValue,
  resolveDefaultSpeedMetric,
} from '../../utils/mapLineStyle';
import { selectRenderableMarks } from '../../utils/marks';
import { calcularEstadisticasTack } from '../../utils/tack';
import {
  getTenMinuteMarkers,
  getTimeMarkerIntervalMinutes,
  getTimeMarkerLabelOffsetMinutes,
  isFiniteTrackNumber,
  type TrackSelectionType,
} from '../../utils/tenMinutePoints';
import { throttle } from '../../utils/throttle';
import { MapControlIcon } from '../shared/MapControlIcon';
import { QuantitySelector } from '../shared/QuantitySelector';

/* eslint-disable sonarjs/no-duplicate-string */

// ——— Type‑only imports (keep after value imports) ———
import type {
  TrackPoint,
  HighlightSegment,
  Tack,
  TackStats,
  EventFile,
  Mark,
  StartPoint,
  Segment,
  RenderizarRacesProperties,
  ColorLegend,
  LineMode,
  TackMapping,
  SidePanelPosition,
  OverlayTrackLayer,
  CommentAttachment,
  CommentPermission,
  MapComment,
} from '../../types';
// Use global GeoJSON namespace to align with mapbox-gl typings
type LineString = GeoJSON.LineString;
type UpdateLaylinesForPointFn = (
  point: TrackPoint,
  mark: (Mark & { distanceMeters: number }) | null,
  fallbackBearing: number | null,
  allowInactive?: boolean,
) => void;
type UpdatePlayerMarkerFn = (
  point: TrackPoint,
  heading: number | null | undefined,
  options?: { force?: boolean; optimalHeading?: number | null | undefined },
) => Promise<void> | void;
type UpdatePlayerGraphicsFn = () => void;
type GridCreateOptions = {
  marginCells?: number;
  skipMajorLinesCoveredByCoarseGrid?: boolean;
  majorLineInterval?: number;
};

type MapCommentMarkerPalette = {
  ring: string;
  editRing: string;
};

const EMPTY_COMMENT_PERMISSION: CommentPermission = {
  canViewComments: false,
  canManageComments: false,
  isOwner: false,
};
const MAP_COMMENTS_CHANGED_EVENT = 'sailtrack:map-comments-changed';
const MAP_COMMENT_PERMISSION_CHANGED_EVENT = 'sailtrack:comment-permission-changed';
// Screen-space capture keeps the magnetic area predictable at every zoom level.
const MAP_COMMENT_TRACK_SNAP_RADIUS_PX = 24;
// The point remains visually compact; this invisible target gives it a 48px grab area.
const MAP_COMMENT_HIT_RADIUS_PX = 24;
// Comments are quiet annotations, not a route or a buoy. Their compact,
// hollow berry ring stays distinct from the route's blue/purple treatment.
const MAP_COMMENT_MARKER_PALETTES: Record<ColorScheme, MapCommentMarkerPalette> = {
  dark: {
    ring: '#E6A3C3',
    editRing: '#86C6FF',
  },
  light: {
    ring: '#9D2E61',
    editRing: '#1765B4',
  },
};
// Preview and persisted comments intentionally share these exact visual tokens.
const MAP_COMMENT_HALO_RADIUS_PX = 8;
const MAP_COMMENT_PIN_RADIUS_PX = 4.5;
const MAP_COMMENT_PIN_STROKE_WIDTH = 1.5;
const MAP_COMMENT_HALO_OPACITY = 0.14;
const MAP_COMMENT_EDIT_RING_RADIUS_PX = 11;
const MAP_COMMENT_EDIT_RING_STROKE_WIDTH = 1.5;
const MAP_COMMENT_CALLOUT_WIDTH_PX = 316;
const MAP_COMMENT_CALLOUT_HEIGHT_PX = 240;
const MAP_COMMENT_CALLOUT_MARGIN_PX = 12;
const MAP_COMMENT_CALLOUT_GAP_PX = 20;
const MAP_COMMENT_CALLOUT_CONNECTOR_HEADER_OFFSET_PX = 38;
const MAP_COMMENT_CALLOUT_MARKER_OFFSET_PX = 78;
const MAP_COMMENT_CALLOUT_CONNECTOR_HORIZONTAL_LEAD_PX = 22;

const hasMapCommentAnchorChanged = (
  comment: MapComment,
  anchor: MapCommentDraftAnchor,
): boolean =>
  comment.anchorType !== anchor.anchorType ||
  comment.anchorUtc !== anchor.anchorUtc ||
  Math.abs(comment.anchorLat - anchor.anchorLat) > 1e-9 ||
  Math.abs(comment.anchorLon - anchor.anchorLon) > 1e-9;

const resolveMapCommentAnchorAtPointer = ({
  map,
  lat,
  lon,
  anchorType,
  track,
}: {
  map: mapboxgl.Map;
  lat: number;
  lon: number;
  anchorType: MapCommentDraftAnchor['anchorType'];
  track: readonly CommentTrackSnapPoint[];
}): MapCommentDraftAnchor => {
  const cursorPixel = map.project([lon, lat]);
  const snapEdge = map.unproject([
    cursorPixel.x + MAP_COMMENT_TRACK_SNAP_RADIUS_PX,
    cursorPixel.y,
  ]);
  const maxDistanceKm = calculateDistance(lat, lon, snapEdge.lat, snapEdge.lng);
  return (
    snapMapCommentAnchorToTrack({
      anchorType,
      lat,
      lon,
      track,
      maxDistanceKm,
    }) ?? {
      anchorType: 'map_point',
      anchorUtc: null,
      anchorLat: lat,
      anchorLon: lon,
    }
  );
};

const formatCommentTimestamp = (value: string | null, locale: string): string => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(locale, {
    year: 'numeric',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type MapCommentTimeMetaProps = {
  trackTime: string | null;
  createdAt: string | null;
  locale: string;
  trackLabel: string;
  createdLabel: string;
  noCreatedLabel: string;
  compact?: boolean;
};

const MapCommentTimeMeta: React.FC<MapCommentTimeMetaProps> = ({
  trackTime,
  createdAt,
  locale,
  trackLabel,
  createdLabel,
  noCreatedLabel,
  compact = false,
}) => {
  const createdValue = formatCommentTimestamp(createdAt, locale) || noCreatedLabel;
  const ariaLabel = trackTime
    ? `${trackLabel}: ${trackTime}. ${createdLabel}: ${createdValue}.`
    : `${createdLabel}: ${createdValue}.`;

  return (
    <span
      className={`map-comment-time-meta${compact ? ' is-compact' : ''}`}
      role="group"
      aria-label={ariaLabel}
    >
      {trackTime && (
        <span className="map-comment-time-meta__row map-comment-time-meta__row--track">
          <span className="map-comment-time-meta__icon" aria-hidden="true">
            <svg viewBox="0 0 16 16" focusable="false">
              <path d="M2.5 11.5 6.2 7.8l2.5 2.2 4.8-5.5" />
              <circle cx="2.5" cy="11.5" r="1.15" />
              <circle cx="13.5" cy="4.5" r="1.15" />
            </svg>
          </span>
          <span className="map-comment-time-meta__label">{trackLabel}</span>
          <strong className="map-comment-time-meta__value">{trackTime}</strong>
        </span>
      )}
      <span className="map-comment-time-meta__row map-comment-time-meta__row--created">
        <span className="map-comment-time-meta__icon" aria-hidden="true">
          <svg viewBox="0 0 16 16" focusable="false">
            <circle cx="8" cy="8" r="5.5" />
            <path d="M8 4.8v3.5l2.4 1.4" />
          </svg>
        </span>
        <span className="map-comment-time-meta__label">{createdLabel}</span>
        <strong className="map-comment-time-meta__value">{createdValue}</strong>
      </span>
    </span>
  );
};

const commentAuthorAvatarBlobCache = new Map<string, Promise<Blob | null>>();

const getCachedCommentAuthorAvatar = (url: string): Promise<Blob | null> => {
  const cached = commentAuthorAvatarBlobCache.get(url);
  if (cached) return cached;
  const request = fetchCommentAuthorAvatar(url).catch(() => null);
  commentAuthorAvatarBlobCache.set(url, request);
  return request;
};

const CommentAuthorAvatar: React.FC<{ author: MapComment['author'] }> = ({ author }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;
    let url: string | null = null;
    if (!author.avatarUrl) {
      setObjectUrl(null);
      return;
    }
    void getCachedCommentAuthorAvatar(author.avatarUrl).then((blob) => {
      if (disposed || !blob) return null;
      url = URL.createObjectURL(blob);
      setObjectUrl(url);
      return blob;
    });
    return () => {
      disposed = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [author.avatarUrl]);

  if (objectUrl) {
    return <img className="map-comment-author__avatar" src={objectUrl} alt="" aria-hidden="true" />;
  }
  return (
    <span className="map-comment-author__avatar map-comment-author__avatar--fallback" aria-hidden="true">
      <ProfileIcon />
    </span>
  );
};

const PrivateCommentImage: React.FC<{
  attachment: CommentAttachment;
  alt: string;
  onOpen: (attachment: CommentAttachment) => void;
}> = ({ attachment, alt, onOpen }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;
    let url: string | null = null;
    void fetchCommentAttachment(attachment.url)
      .then((blob) => {
        if (disposed) return null;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
        return url;
      })
      .catch(() => {
        if (!disposed) setObjectUrl(null);
        return null;
      });
    return () => {
      disposed = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment.url]);

  if (!objectUrl) {
    return <span className="map-comment-image-loading" aria-label={alt} />;
  }
  return (
    <button
      type="button"
      className="map-comment-image"
      onClick={() => onOpen(attachment)}
      aria-label={alt}
    >
      <img src={objectUrl} alt="" />
    </button>
  );
};

// external imports grouped above; local utils and styles below
/*———————————————————  Safe helpers  ———————————————————*/
/**
 * Wrapper around `map.resize()` that checks Mapbox‑GL internals to avoid
 * calling it when the style has been disposed (common during panel mounts/
 * unmounts), which otherwise throws “applyProjectionUpdate is undefined”.
 */

// Reused Mapbox style keys
const LINE_COLOR = 'line-color' as const;
const LINE_WIDTH = 'line-width' as const;
const LINE_CAP = 'line-cap' as const;
const LINE_JOIN = 'line-join' as const;
const TRACK_PLAIN_LIGHT_COLOR = '#0C1A26';
const TRACK_PLAIN_DARK_COLOR = '#D6E4EA';
const MISSING_METRIC_COLOR = '#8EA0AD';
// Use a restrained light halo on dark maps instead of a near-black underlay
// that reduces the contrast of colored overlays.
const DARK_MAP_CASING_COLOR = 'rgba(135, 177, 217, 0.34)';
const DARK_MAP_CASING_OPACITY = 0.72;

// Common Mapbox layer/source IDs used across the component
const CIRCLE_POINTS_SOURCE_ID = 'circle-points' as const;
const CIRCLE_LAYER_ID = 'circle-layer' as const;
const CIRCLE_LABEL_LAYER_ID = 'circle-label-layer' as const;
const TWD_SOURCE_ID = 'twd-lines' as const;
const TWD_LAYER_ID = 'twd-lines-layer' as const;
const DISTANCE_LINE_SOURCE_ID = 'distance-line' as const;
const DISTANCE_LINE_LAYER_ID = 'distance-line-layer' as const;
const LABEL_SOURCE_ID = 'label-source' as const;
const LABEL_LAYER_ID = 'label-layer' as const;
const OPTIMIZED_ROUTE_MAIN = '#4ba6ff';
const OPTIMIZED_ROUTE_GLOW = '#10263c';
const OPTIMIZED_ROUTE_ACCENT = '#9ad8ff';
const OPTIMIZED_ROUTE_DARK = 'rgba(16, 38, 60, 0.55)';
const OPTIMIZED_ROUTE_HIDDEN = 'rgba(75, 166, 255, 0)';
const OPTIMIZED_ROUTE_SOFT = 'rgba(75, 166, 255, 0.28)';
const OPTIMIZED_ROUTE_STRONG = 'rgba(110, 191, 255, 0.92)';
const OPTIMIZED_POINT_START = '#7ae7ff';
const OPTIMIZED_POINT_END = '#ffbf69';
const OPTIMIZED_POINT_HALO_START = 'rgba(122, 231, 255, 0.45)';
const OPTIMIZED_POINT_HALO_END = 'rgba(255, 191, 105, 0.42)';
const OPTIMIZED_POINT_RING_START = '#b5ecff';
const OPTIMIZED_POINT_RING_END = '#ffdcb0';
const OPTIMAL_HEADING_LINE_METERS = 600; // línea amarilla más visible (fallback si falta LOA)

type OverlayLineWidth = number | DataDrivenPropertyValueSpecification<number>;
type MapOverlayPalette = {
  scheme: ColorScheme;
  plainTrack: string;
  plainTrackWidth: number;
  startLine: string;
  startLinePort: string;
  startLineStbd: string;
  lineCasing: string;
  lineCasingOpacity: number;
  trackCasingWidth: number;
  gridFine: string;
  gridFineOpacity: number;
  gridCoarse: string;
  gridCoarseOpacity: number;
  tackVisible: string;
  tackActive: string;
  tackMuted: string;
  editFull: string;
  editRange: string;
  tacticalPoint: string;
  tacticalLabel: string;
  tacticalLabelHalo: string;
  tacticalLabelHaloWidth: number;
  twdLine: string;
  boatMarker: string;
  playerPath: string;
  laylineStarboard: string;
  laylinePort: string;
  laylineFallback: string;
  overlayCasing: string;
  overlayCasingOpacity: number;
  ladderLine: string;
  ladderLabel: string;
  ladderLabelHalo: string;
  buoy: string;
  distancePrimary: string;
  distanceSecondary: string;
  distanceLabel: string;
  distanceLabelHalo: string;
  distanceLabelHaloWidth: number;
  speedRampStart: [number, number, number];
  speedRampEnd: [number, number, number];
  tacticalRampStart: [number, number, number];
  tacticalRampMid: [number, number, number];
  tacticalRampEnd: [number, number, number];
};

const MAP_OVERLAY_PALETTES: Record<ColorScheme, MapOverlayPalette> = {
  light: {
    scheme: 'light',
    plainTrack: TRACK_PLAIN_LIGHT_COLOR,
    plainTrackWidth: 1,
    startLine: '#B45309',
    startLinePort: '#EF4444',
    startLineStbd: '#22C55E',
    lineCasing: 'rgba(255,255,255,0)',
    lineCasingOpacity: 0,
    trackCasingWidth: 0,
    gridFine: '#777',
    gridFineOpacity: 0.5,
    gridCoarse: '#555',
    gridCoarseOpacity: 0.5,
    tackVisible: '#22d3ee',
    tackActive: '#00FFFF',
    tackMuted: '#22c55e',
    editFull: '#6b86a0',
    editRange: '#0f2236',
    tacticalPoint: '#000',
    tacticalLabel: '#000',
    tacticalLabelHalo: 'rgba(255,255,255,0)',
    tacticalLabelHaloWidth: 0,
    twdLine: '#0C1A26',
    boatMarker: '#15394f',
    playerPath: '#ffd166',
    laylineStarboard: '#00b05a',
    laylinePort: '#d62828',
    laylineFallback: '#1fbad6',
    overlayCasing: 'rgba(255,255,255,0)',
    overlayCasingOpacity: 0,
    ladderLine: 'rgba(82, 96, 112, 0.6)',
    ladderLabel: '#374151',
    ladderLabelHalo: '#F8FAFC',
    buoy: '#FA8072',
    distancePrimary: '#000000',
    distanceSecondary: '#FFFFFF',
    distanceLabel: '#000000',
    distanceLabelHalo: 'rgba(255,255,255,0)',
    distanceLabelHaloWidth: 0,
    speedRampStart: [200, 200, 200],
    speedRampEnd: [0, 0, 155],
    tacticalRampStart: [200, 0, 0],
    tacticalRampMid: [200, 200, 200],
    tacticalRampEnd: [0, 200, 0],
  },
  dark: {
    scheme: 'dark',
    plainTrack: TRACK_PLAIN_DARK_COLOR,
    plainTrackWidth: 1,
    startLine: '#FFB84D',
    startLinePort: '#EF4444',
    startLineStbd: '#22C55E',
    lineCasing: DARK_MAP_CASING_COLOR,
    lineCasingOpacity: DARK_MAP_CASING_OPACITY,
    trackCasingWidth: 0,
    gridFine: 'rgba(116, 145, 162, 0.3)',
    gridFineOpacity: 0.32,
    gridCoarse: 'rgba(151, 178, 193, 0.45)',
    gridCoarseOpacity: 0.46,
    tackVisible: '#78B8CC',
    tackActive: '#B8D6E4',
    tackMuted: '#6E9B83',
    editFull: '#7F9CAF',
    editRange: '#B7CDD9',
    tacticalPoint: '#A8C4D2',
    tacticalLabel: '#B7CAD6',
    tacticalLabelHalo: '#05111B',
    tacticalLabelHaloWidth: 1.4,
    twdLine: '#9DB9C8',
    boatMarker: '#B4CBD8',
    playerPath: '#D5B95E',
    laylineStarboard: '#58BD8D',
    laylinePort: '#D86F75',
    laylineFallback: '#75B8D6',
    overlayCasing: DARK_MAP_CASING_COLOR,
    overlayCasingOpacity: DARK_MAP_CASING_OPACITY,
    ladderLine: 'rgba(143, 174, 190, 0.56)',
    ladderLabel: '#AFC7D4',
    ladderLabelHalo: '#06131D',
    buoy: '#D09374',
    distancePrimary: '#BFD4DF',
    distanceSecondary: '#6FA5C3',
    distanceLabel: '#C8D8E0',
    distanceLabelHalo: '#04101A',
    distanceLabelHaloWidth: 1.6,
    speedRampStart: [197, 77, 63],
    speedRampEnd: [122, 244, 255],
    tacticalRampStart: [210, 105, 112],
    tacticalRampMid: [166, 184, 195],
    tacticalRampEnd: [94, 185, 139],
  },
};

const getCurrentMapOverlayScheme = (): ColorScheme =>
  resolveMapColorScheme(getCurrentColorScheme(), getCurrentMapColorMode());

const getCurrentMapOverlayPalette = (): MapOverlayPalette =>
  MAP_OVERLAY_PALETTES[getCurrentMapOverlayScheme()];

const rgbTupleToCss = ([red, green, blue]: [number, number, number]) =>
  `rgb(${red}, ${green}, ${blue})`;

type RaceStartLineFeatureProperties = {
  segmentId: number;
  color: string;
  endpoint?: 'port' | 'stbd';
};

type SegmentWithStartLine = Segment & {
  start_line_port_lat: number;
  start_line_port_lon: number;
  start_line_stbd_lat: number;
  start_line_stbd_lon: number;
};

const isFiniteStartLineCoordinate = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const hasCompleteStartLine = (segment: Segment): segment is SegmentWithStartLine =>
  isFiniteStartLineCoordinate(segment.start_line_port_lat) &&
  isFiniteStartLineCoordinate(segment.start_line_port_lon) &&
  isFiniteStartLineCoordinate(segment.start_line_stbd_lat) &&
  isFiniteStartLineCoordinate(segment.start_line_stbd_lon);

const findStartLineCourseReferencePoint = (
  segment: SegmentWithStartLine,
  trackData: TrackPoint[],
  startLineMidpoint: { lat: number; lon: number },
): { lat: number; lon: number } | null => {
  const validPoints = trackData
    .map((point, localIndex) => ({ point, localIndex }))
    .filter(
      ({ point }) =>
        typeof point.lat === 'number' &&
        Number.isFinite(point.lat) &&
        typeof point.lon === 'number' &&
        Number.isFinite(point.lon),
    );

  if (validPoints.length === 0) {
    return null;
  }

  let gunIndex = -1;
  let closestGunIndex = -1;
  let closestGunDelta = Infinity;
  for (const [index, { point }] of validPoints.entries()) {
    const value = point.tmtogun;
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      continue;
    }
    const absoluteValue = Math.abs(value);
    if (absoluteValue < closestGunDelta) {
      closestGunDelta = absoluteValue;
      closestGunIndex = index;
    }
    const previousValue = validPoints[index - 1]?.point.tmtogun;
    if (
      value <= 0 &&
      (typeof previousValue !== 'number' || !Number.isFinite(previousValue) || previousValue > 0)
    ) {
      gunIndex = index;
      break;
    }
  }

  const segmentStartIndex = validPoints.findIndex(
    ({ point, localIndex }) =>
      point.globalIndex === segment.start_index || localIndex === segment.start_index,
  );
  const anchorIndex = Math.max(
    0,
    gunIndex >= 0
      ? gunIndex
      : closestGunIndex >= 0
        ? closestGunIndex
        : segmentStartIndex >= 0
          ? segmentStartIndex
          : 0,
  );
  const searchEnd = Math.min(validPoints.length - 1, anchorIndex + 60);
  let bestPoint: { lat: number; lon: number } | null = null;
  let bestDistance = -Infinity;

  for (let index = anchorIndex; index <= searchEnd; index += 1) {
    const { point } = validPoints[index];
    const candidate = { lat: point.lat, lon: point.lon };
    const distance = calculateDistance(
      startLineMidpoint.lat,
      startLineMidpoint.lon,
      candidate.lat,
      candidate.lon,
    );
    if (distance > bestDistance) {
      bestDistance = distance;
      bestPoint = candidate;
    }
  }

  return bestPoint ?? null;
};

const calculateStartLineReferenceBearing = (
  segments: Segment[],
  trackData: TrackPoint[],
): number | null => {
  const segment = segments.find((candidate): candidate is SegmentWithStartLine =>
    hasCompleteStartLine(candidate),
  );
  if (!segment) {
    return null;
  }

  const lineBearing = normalizeBearing(
    calculateHeading(
      { lat: segment.start_line_port_lat, lon: segment.start_line_port_lon },
      { lat: segment.start_line_stbd_lat, lon: segment.start_line_stbd_lon },
    ),
  );
  const portToStbdCourseNormal = normalizeBearing(lineBearing - 90);
  const stbdToPortCourseNormal = normalizeBearing(lineBearing + 90);
  const startLineMidpoint = {
    lat: (segment.start_line_port_lat + segment.start_line_stbd_lat) / 2,
    lon: (segment.start_line_port_lon + segment.start_line_stbd_lon) / 2,
  };
  const courseReferencePoint = findStartLineCourseReferencePoint(
    segment,
    trackData,
    startLineMidpoint,
  );
  if (!courseReferencePoint) {
    return portToStbdCourseNormal;
  }

  const courseBearing = calculateHeading(startLineMidpoint, courseReferencePoint);
  return Math.abs(angleDiff(courseBearing, portToStbdCourseNormal)) <=
    Math.abs(angleDiff(courseBearing, stbdToPortCourseNormal))
    ? portToStbdCourseNormal
    : stbdToPortCourseNormal;
};

const buildRaceStartLineFeatureCollection = (
  segments: Segment[],
  color: string,
  portColor: string,
  stbdColor: string,
): GeoJSON.FeatureCollection<LineString | GeoJSON.Point, RaceStartLineFeatureProperties> => ({
  type: 'FeatureCollection',
  features: segments
    .filter((segment): segment is SegmentWithStartLine => hasCompleteStartLine(segment))
    .flatMap((segment) => [
      {
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [segment.start_line_port_lon, segment.start_line_port_lat],
            [segment.start_line_stbd_lon, segment.start_line_stbd_lat],
          ],
        },
        properties: {
          segmentId: segment.id,
          color,
        },
      },
      {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [segment.start_line_port_lon, segment.start_line_port_lat],
        },
        properties: { segmentId: segment.id, endpoint: 'port', color: portColor },
      },
      {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [segment.start_line_stbd_lon, segment.start_line_stbd_lat],
        },
        properties: { segmentId: segment.id, endpoint: 'stbd', color: stbdColor },
      },
    ]),
});

const syncOptionalLineCasingLayer = ({
  map,
  sourceId,
  casingLayerId,
  beforeLayerId,
  width,
  palette,
  dashArray,
  color,
  opacity,
}: {
  map: mapboxgl.Map;
  sourceId: string;
  casingLayerId: string;
  beforeLayerId: string;
  width: OverlayLineWidth;
  palette: MapOverlayPalette;
  dashArray?: number[];
  color?: string;
  opacity?: number;
}) => {
  const shouldRemoveCasing = typeof width === 'number' && width <= 0;
  if (palette.scheme !== 'dark' || shouldRemoveCasing) {
    if (map.getLayer(casingLayerId)) {
      map.removeLayer(casingLayerId);
    }
    return;
  }

  const casingColor = color ?? palette.lineCasing;
  const casingOpacity = opacity ?? palette.lineCasingOpacity;
  const paint: NonNullable<mapboxgl.LineLayer['paint']> = {
    'line-color': casingColor,
    'line-width': width,
    'line-opacity': casingOpacity,
  };
  if (dashArray) {
    paint['line-dasharray'] = dashArray;
  }

  if (map.getLayer(casingLayerId)) {
    map.setPaintProperty(casingLayerId, LINE_COLOR, casingColor);
    map.setPaintProperty(casingLayerId, LINE_WIDTH, width);
    map.setPaintProperty(casingLayerId, 'line-opacity', casingOpacity);
    if (dashArray) {
      map.setPaintProperty(casingLayerId, 'line-dasharray', dashArray);
    }
  } else if (map.getSource(sourceId)) {
    map.addLayer({
      id: casingLayerId,
      type: 'line',
      source: sourceId,
      paint,
      layout: { [LINE_CAP]: 'round', [LINE_JOIN]: 'round' },
    });
  }
  if (map.getLayer(casingLayerId) && map.getLayer(beforeLayerId)) {
    safeMoveLayer(map, casingLayerId, beforeLayerId);
  }
};

const removeOptionalLineCasingLayer = (map: mapboxgl.Map, casingLayerId: string) => {
  if (map.getLayer(casingLayerId)) {
    map.removeLayer(casingLayerId);
  }
};

const GRID_SOURCE_1X_ID = (side: SidePanelPosition) => `grid-source-1x-${side}`;
const GRID_SOURCE_10X_ID = (side: SidePanelPosition) => `grid-source-10x-${side}`;
const GRID_LAYER_1X_ID = (side: SidePanelPosition) => `grid-layer-1x-${side}`;
const GRID_LAYER_10X_ID = (side: SidePanelPosition) => `grid-layer-10x-${side}`;
const GRID_ARTIFACT_POSITIONS: SidePanelPosition[] = ['tl', 'tr', 'bl', 'br', 't', 'b', 'l', 'r'];
const LADDER_LINES_SOURCE_ID = (suffix: string) => `ladder-lines-source-${suffix}`;
const LADDER_LINES_LAYER_ID = (suffix: string) => `ladder-lines-layer-${suffix}`;
const LADDER_LABELS_SOURCE_ID = (suffix: string) => `ladder-labels-source-${suffix}`;
const LADDER_LABELS_LAYER_ID = (suffix: string) => `ladder-labels-layer-${suffix}`;
const FONT_STACK_REGULAR: string[] = ['Open Sans Regular', 'Arial Unicode MS Regular'];
const FONT_STACK_BOLD: string[] = ['Open Sans Bold', 'Arial Unicode MS Regular'];
const ANALYSIS_BASEMAP_TONE_SOURCE_ID = 'sailtrack-analysis-basemap-tone-source' as const;
const ANALYSIS_BASEMAP_TONE_LAYER_ID = 'sailtrack-analysis-basemap-tone-layer' as const;
const ANALYSIS_BASEMAP_TONE_SLOT = 'bottom' as const;
const ANALYSIS_BASEMAP_TONE_BY_SCHEME: Record<
  ColorScheme,
  { fillColor: string; fillOpacity: number }
> = {
  dark: { fillColor: '#0C1A26', fillOpacity: 0.02 },
  light: { fillColor: '#0C1A26', fillOpacity: 0.02 },
};
const ANALYSIS_BASEMAP_TONE_DATA: GeoJSON.FeatureCollection<GeoJSON.Polygon> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [-180, -85],
            [180, -85],
            [180, 85],
            [-180, 85],
            [-180, -85],
          ],
        ],
      },
    },
  ],
};
const DARK_BASEMAP_ROAD_PAINT_BY_CLASS = {
  major: { color: '#7398AC', opacity: 0.74 },
  minor: { color: '#55768A', opacity: 0.56 },
  service: { color: '#426274', opacity: 0.45 },
  casing: { color: '#29475A', opacity: 0.64 },
} as const;
type DarkBasemapRoadClass = keyof typeof DARK_BASEMAP_ROAD_PAINT_BY_CLASS;
const DARK_BASEMAP_LABEL_PAINT = {
  color: '#AFC7D4',
  haloColor: '#06131D',
  haloWidth: 1.2,
  opacity: 0.94,
} as const;
const DARK_BASEMAP_BOUNDARY_PAINT = {
  color: DARK_BASEMAP_LABEL_PAINT.color,
  opacity: 0.44,
} as const;
const DARK_BASEMAP_BOUNDARY_BACKING_PAINT = {
  color: DARK_BASEMAP_BOUNDARY_PAINT.color,
  opacity: 0,
} as const;

const getStyleLayerSourceLayer = (layer: mapboxgl.AnyLayer) => {
  const sourceLayer = (layer as { 'source-layer'?: unknown })['source-layer'];
  return typeof sourceLayer === 'string' ? sourceLayer.toLowerCase() : '';
};

const getStyleLayerLayoutProperty = (layer: mapboxgl.AnyLayer, property: string) => {
  const layout = (layer as { layout?: Record<string, unknown> }).layout;
  return layout?.[property];
};

const isDarkBasemapBoundaryLayer = (layer: mapboxgl.AnyLayer) => {
  if (layer.type !== 'line') {
    return false;
  }

  const layerId = layer.id.toLowerCase();
  const layerText = `${layerId} ${getStyleLayerSourceLayer(layer)}`;
  if (/label|sailtrack|optimized|distance|layline|ladder/.test(layerText)) {
    return false;
  }
  return /admin|boundary|border|country|state|province|disputed/.test(layerText);
};

const isDarkBasemapBoundaryBackingLayer = (layer: mapboxgl.AnyLayer) => {
  if (!isDarkBasemapBoundaryLayer(layer)) {
    return false;
  }

  const layerId = layer.id.toLowerCase();
  const layerText = `${layerId} ${getStyleLayerSourceLayer(layer)}`;
  return /bg|background|case|casing|outline|stroke|halo/.test(layerText);
};

const isDarkBasemapTextLayer = (layer: mapboxgl.AnyLayer) => {
  if (layer.type !== 'symbol' || getStyleLayerLayoutProperty(layer, 'text-field') == null) {
    return false;
  }

  const layerId = layer.id.toLowerCase();
  const layerText = `${layerId} ${getStyleLayerSourceLayer(layer)}`;
  if (/sailtrack|optimized|distance|layline|ladder|circle|highlight|tack|maneuver/.test(layerText)) {
    return false;
  }
  return /label|place|settlement|country|state|province|poi|water|marine|road|street|admin|boundary/.test(
    layerText,
  );
};

const getDarkBasemapRoadClass = (layer: mapboxgl.AnyLayer): DarkBasemapRoadClass | null => {
  if (layer.type !== 'line') {
    return null;
  }

  const layerId = layer.id.toLowerCase();
  const layerText = `${layerId} ${getStyleLayerSourceLayer(layer)}`;
  if (
    /label|shield|ferry|rail|aerialway|sailtrack|optimized|distance|layline|ladder/.test(
      layerText,
    )
  ) {
    return null;
  }
  if (
    !/road|street|motorway|trunk|primary|secondary|tertiary|residential|service|path|pedestrian|transportation/.test(
      layerText,
    )
  ) {
    return null;
  }
  if (/case|casing|outline|stroke/.test(layerText)) {
    return 'casing';
  }
  if (/motorway|trunk|primary|secondary|major/.test(layerText)) {
    return 'major';
  }
  if (/service|path|pedestrian|track/.test(layerText)) {
    return 'service';
  }
  return 'minor';
};

const applyDarkBasemapRoadContrast = (
  map: mapboxgl.Map,
  mapColorScheme: ColorScheme,
) => {
  if (mapColorScheme !== 'dark') {
    return;
  }
  const layers = map.getStyle().layers ?? [];
  for (const layer of layers) {
    if (!map.getLayer(layer.id)) {
      continue;
    }

    if (isDarkBasemapBoundaryBackingLayer(layer)) {
      const boundaryBackingPaint = DARK_BASEMAP_BOUNDARY_BACKING_PAINT;
      if (map.getPaintProperty(layer.id, LINE_COLOR) !== boundaryBackingPaint.color) {
        map.setPaintProperty(layer.id, LINE_COLOR, boundaryBackingPaint.color);
      }
      if (map.getPaintProperty(layer.id, 'line-opacity') !== boundaryBackingPaint.opacity) {
        map.setPaintProperty(layer.id, 'line-opacity', boundaryBackingPaint.opacity);
      }
      continue;
    }

    if (isDarkBasemapBoundaryLayer(layer)) {
      const boundaryPaint = DARK_BASEMAP_BOUNDARY_PAINT;
      if (map.getPaintProperty(layer.id, LINE_COLOR) !== boundaryPaint.color) {
        map.setPaintProperty(layer.id, LINE_COLOR, boundaryPaint.color);
      }
      if (map.getPaintProperty(layer.id, 'line-opacity') !== boundaryPaint.opacity) {
        map.setPaintProperty(layer.id, 'line-opacity', boundaryPaint.opacity);
      }
      continue;
    }

    if (isDarkBasemapTextLayer(layer)) {
      const labelPaint = DARK_BASEMAP_LABEL_PAINT;
      if (map.getPaintProperty(layer.id, 'text-color') !== labelPaint.color) {
        map.setPaintProperty(layer.id, 'text-color', labelPaint.color);
      }
      if (map.getPaintProperty(layer.id, 'text-halo-color') !== labelPaint.haloColor) {
        map.setPaintProperty(layer.id, 'text-halo-color', labelPaint.haloColor);
      }
      if (map.getPaintProperty(layer.id, 'text-halo-width') !== labelPaint.haloWidth) {
        map.setPaintProperty(layer.id, 'text-halo-width', labelPaint.haloWidth);
      }
      if (map.getPaintProperty(layer.id, 'text-opacity') !== labelPaint.opacity) {
        map.setPaintProperty(layer.id, 'text-opacity', labelPaint.opacity);
      }
      continue;
    }

    const roadClass = getDarkBasemapRoadClass(layer);
    if (!roadClass) {
      continue;
    }

    const roadPaint = DARK_BASEMAP_ROAD_PAINT_BY_CLASS[roadClass];
    if (map.getPaintProperty(layer.id, LINE_COLOR) !== roadPaint.color) {
      map.setPaintProperty(layer.id, LINE_COLOR, roadPaint.color);
    }
    if (map.getPaintProperty(layer.id, 'line-opacity') !== roadPaint.opacity) {
      map.setPaintProperty(layer.id, 'line-opacity', roadPaint.opacity);
    }
  }
};

// Pure helpers moved to module scope to avoid re-creation on each render
const resolveCssVariable = (variableName: string, fallback: string) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fallback;
  }

  const rawValue = getComputedStyle(document.documentElement).getPropertyValue(variableName);
  const value = rawValue.trim();
  return value.length > 0 ? value : fallback;
};

const ensureAnalysisBasemapToneLayer = (
  map: mapboxgl.Map,
  mapColorScheme: ColorScheme,
  mapColorMode: MapColorMode,
) => {
  if (mapColorMode === 'default') {
    if (map.getLayer(ANALYSIS_BASEMAP_TONE_LAYER_ID)) {
      map.removeLayer(ANALYSIS_BASEMAP_TONE_LAYER_ID);
    }
    if (map.getSource(ANALYSIS_BASEMAP_TONE_SOURCE_ID)) {
      map.removeSource(ANALYSIS_BASEMAP_TONE_SOURCE_ID);
    }
    return;
  }
  const tone = ANALYSIS_BASEMAP_TONE_BY_SCHEME[mapColorScheme];
  const existingToneLayer = map.getLayer(ANALYSIS_BASEMAP_TONE_LAYER_ID) as
    | (mapboxgl.AnyLayer & { slot?: unknown })
    | undefined;
  if (existingToneLayer && existingToneLayer.slot !== ANALYSIS_BASEMAP_TONE_SLOT) {
    map.removeLayer(ANALYSIS_BASEMAP_TONE_LAYER_ID);
  }
  if (!map.getSource(ANALYSIS_BASEMAP_TONE_SOURCE_ID)) {
    map.addSource(ANALYSIS_BASEMAP_TONE_SOURCE_ID, {
      type: 'geojson',
      data: ANALYSIS_BASEMAP_TONE_DATA,
    });
  }
  if (map.getLayer(ANALYSIS_BASEMAP_TONE_LAYER_ID)) {
    map.setPaintProperty(ANALYSIS_BASEMAP_TONE_LAYER_ID, 'fill-color', tone.fillColor);
    map.setPaintProperty(ANALYSIS_BASEMAP_TONE_LAYER_ID, 'fill-opacity', tone.fillOpacity);
    return;
  }
  map.addLayer({
    id: ANALYSIS_BASEMAP_TONE_LAYER_ID,
    type: 'fill',
    source: ANALYSIS_BASEMAP_TONE_SOURCE_ID,
    slot: ANALYSIS_BASEMAP_TONE_SLOT,
    paint: {
      'fill-color': tone.fillColor,
      'fill-opacity': tone.fillOpacity,
    },
  } as mapboxgl.AnyLayer);
};

const normalizeBearing = (bearing: number) => ((bearing % 360) + 360) % 360;
const LADDER_STEP_MS = 10 * 1000;
const PLAYBACK_INTERVAL_MS = 650;
const PLAYER_STEP_POINTS = 1;
const TRACK_FIT_RETRY_DELAYS_MS = [180, 420, 900] as const;
const TRACK_FIT_SETTLE_DELAY_MS = Math.max(...TRACK_FIT_RETRY_DELAYS_MS) + 50;
const TRACK_FIT_LOADING_TIMEOUT_MS = 6500;
const TWD_UP_BEARING_TOLERANCE_DEG = 0.5;
const PLAYER_BEARING_SYNC_TOLERANCE_DEG = 0.001;
const START_LINE_ORIENTATION_MODE = 3;
const OPTIMIZED_ROUTE_START_THRESHOLD_METERS = 35;
const DEFAULT_LAYLINE_ANGLE = 45;
const MIN_PLAYER_LINE_METERS = 150;
const BUOY_SNAP_THRESHOLD_METERS = 40;
const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
const toDegrees = (radians: number) => (radians * 180) / Math.PI;
// Diferencia mínima en el rango [-180, 180]
const angleDiff = (a: number, b: number) => ((a - b + 540) % 360) - 180;

const windProjection = (
  mark: { lat: number; lon: number },
  boat: { lat: number; lon: number },
  windBearing: number,
) => {
  const headingFromMark = calculateHeading(
    { lat: mark.lat, lon: mark.lon },
    { lat: boat.lat, lon: boat.lon },
  );
  const delta = Math.abs(angleDiff(headingFromMark, windBearing));
  return Math.cos(toRadians(delta)); // >0 delante (downwind respecto al mark), <0 detrás
};

const distanceToBearingLineMeters = (
  mark: { lat: number; lon: number },
  point: { lat: number; lon: number },
  bearingDegrees: number,
) => {
  const earthRadiusMeters = 6_371_000;
  const distanceMeters = calculateDistance(mark.lat, mark.lon, point.lat, point.lon) * 1000;
  const bearingToPoint = toRadians(
    calculateHeading({ lat: mark.lat, lon: mark.lon }, { lat: point.lat, lon: point.lon }),
  );
  const bearingRad = toRadians(bearingDegrees);
  const crossTrack = Math.asin(
    Math.sin(distanceMeters / earthRadiusMeters) * Math.sin(bearingToPoint - bearingRad),
  );
  return Math.abs(crossTrack * earthRadiusMeters);
};

const calculateMetersPerPixel = (zoom: number, lat = 0): number => {
  return (156_543.033_92 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
};

const destinationPoint = (lat: number, lon: number, bearing: number, distanceMeters: number) => {
  const angularDistance = distanceMeters / 6_371_000;
  const bearingRad = toRadians(bearing);
  const latRad = toRadians(lat);
  const lonRad = toRadians(lon);

  const destLat = Math.asin(
    Math.sin(latRad) * Math.cos(angularDistance) +
      Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearingRad),
  );

  const destLon =
    lonRad +
    Math.atan2(
      Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(latRad),
      Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(destLat),
    );

  return {
    lat: toDegrees(destLat),
    lon: toDegrees(destLon),
  };
};

const determineLegType = (
  mark: { lat: number; lon: number },
  point: { lat: number; lon: number },
  baseWind: number,
): 'upwind' | 'downwind' => {
  const headingTowardBoat = calculateHeading(
    { lat: mark.lat, lon: mark.lon },
    { lat: point.lat, lon: point.lon },
  );
  if (!Number.isFinite(headingTowardBoat)) {
    return 'upwind';
  }

  const oppositeWind = normalizeBearing(baseWind + 180);
  const distanceToBase = distanceToBearingLineMeters(
    { lat: mark.lat, lon: mark.lon },
    { lat: point.lat, lon: point.lon },
    baseWind,
  );
  const distanceToOpposite = distanceToBearingLineMeters(
    { lat: mark.lat, lon: mark.lon },
    { lat: point.lat, lon: point.lon },
    oppositeWind,
  );
  const windAxis = distanceToOpposite < distanceToBase ? oppositeWind : baseWind;
  const isUpwindLeg = Math.abs(angleDiff(headingTowardBoat, windAxis)) > 90;
  return isUpwindLeg ? 'upwind' : 'downwind';
};

const getTrackRenderKey = (track: TrackPoint[], fileId: number | null) => {
  if (track.length === 0) {
    return null;
  }
  const first = track[0];
  const last = track.at(-1) ?? first;
  return [
    fileId ?? 'none',
    track.length,
    first.globalIndex ?? '',
    last.globalIndex ?? '',
    first.utc ?? first.date ?? '',
    last.utc ?? last.date ?? '',
    first.lat,
    first.lon,
    last.lat,
    last.lon,
  ].join('|');
};

type PrimitiveOption = string | number;

interface MapSelectOption<T extends PrimitiveOption> {
  value: T;
  label: React.ReactNode;
}

interface MapSelectProperties<T extends PrimitiveOption> {
  value: T;
  options: MapSelectOption<T>[];
  onChange: (value: T) => void;
  wrapperClassName?: string;
  triggerClassName?: string;
  align?: 'top' | 'bottom';
  ariaLabel?: string;
  triggerContent?: React.ReactNode;
}

type SpeedMetricKey = 'vmg' | 'bsppc' | 'vmgpc' | 'bsp' | 'sog' | 'tws';
type ExtendedMetricKey = SpeedMetricKey | 'twa_abs';
type ColorMetricKey = ExtendedMetricKey | 'heel';
type WidthMetricKey = ExtendedMetricKey | 'none';
type OptimizedRoutePoint = {
  t_idx: number;
  lat: number;
  lon: number;
  board: number;
  twa?: number | null;
};
type OptimizedRouteResult = {
  path?: OptimizedRoutePoint[];
  etaSeconds?: number | null;
  eta_seconds?: number | null;
  start_index?: number | null;
  startIndex?: number | null;
  resultStartIndex?: number | null;
};
type RouteOptimizationJobStatus = 'queued' | 'processing' | 'completed' | 'failed';
type RouteOptimizationJob = {
  jobId: string;
  status: RouteOptimizationJobStatus;
  progress?: number;
  message?: string;
  data?: OptimizedRouteResult;
  error?: string | null;
};
type OptimizeRouteApiResponse = {
  success: boolean;
  processing?: boolean;
  jobId?: string;
  status?: RouteOptimizationJobStatus;
  data?: OptimizedRouteResult | RouteOptimizationJob;
  message?: string;
};
const isRouteOptimizationJob = (value: unknown): value is RouteOptimizationJob =>
  Boolean(
    value &&
      typeof value === 'object' &&
      'jobId' in value &&
      'status' in value &&
      typeof (value as RouteOptimizationJob).jobId === 'string',
  );
const getOptimizedRouteResult = (
  value: OptimizeRouteApiResponse['data'],
): OptimizedRouteResult | undefined =>
  isRouteOptimizationJob(value) ? value.data : (value as OptimizedRouteResult | undefined);
const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
type OptimizedRouteSegment = {
  start: { lat: number; lon: number; t_idx?: number };
  end: { lat: number; lon: number; t_idx?: number };
  startDistance: number;
  endDistance: number;
  heading: number;
  startTidx?: number;
  endTidx?: number;
};
type OptimizedRouteTrack = {
  segments: OptimizedRouteSegment[];
  totalDistance: number;
  durationSeconds: number;
  firstPoint: { lat: number; lon: number; t_idx?: number };
  lastPoint: { lat: number; lon: number; t_idx?: number };
  firstTidx?: number;
  lastTidx?: number;
} | null;
type BoardPreference = 'auto' | 0 | 1;
const smoothOptimizedRoute = (
  path: OptimizedRoutePoint[],
  maxSpacingMeters = 45,
  maxPoints = 2400,
): [number, number][] => {
  const result: [number, number][] = [];
  if (!Array.isArray(path) || path.length === 0) {
    return result;
  }
  result.push([path[0].lon, path[0].lat]);
  for (let index = 1; index < path.length; index += 1) {
    const prev = path[index - 1];
    const curr = path[index];
    if (
      !Number.isFinite(prev?.lat) ||
      !Number.isFinite(prev?.lon) ||
      !Number.isFinite(curr?.lat) ||
      !Number.isFinite(curr?.lon)
    ) {
      continue;
    }
    const distanceMeters = calculateDistance(prev.lat, prev.lon, curr.lat, curr.lon) * 1000;
    const segments = Math.max(1, Math.ceil(distanceMeters / Math.max(maxSpacingMeters, 1)));
    for (let step = 1; step <= segments; step += 1) {
      const t = step / segments;
      const lat = prev.lat + (curr.lat - prev.lat) * t;
      const lon = prev.lon + (curr.lon - prev.lon) * t;
      result.push([lon, lat]);
      if (result.length >= maxPoints) {
        return result;
      }
    }
  }
  return result;
};

function MapSelect<T extends PrimitiveOption>({
  value,
  options,
  onChange,
  wrapperClassName,
  triggerClassName,
  align = 'bottom',
  ariaLabel,
  triggerContent,
}: MapSelectProperties<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(() => {
    const selectedIndex = options.findIndex((option) => option.value === value);
    return selectedIndex >= 0 ? selectedIndex : 0;
  });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  useEffect(() => {
    const selectedIndex = options.findIndex((option) => option.value === value);
    if (selectedIndex >= 0) {
      setHighlightedIndex(selectedIndex);
    }
  }, [options, value]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        menuRef.current?.focus();
      });
    }
  }, [isOpen]);

  const openMenu = useCallback(
    (nextIndex?: number) => {
      if (options.length === 0) {
        return;
      }
      const selectedIndex = options.findIndex((option) => option.value === value);
      const indexToUse =
        typeof nextIndex === 'number'
          ? Math.min(Math.max(nextIndex, 0), options.length - 1)
          : selectedIndex >= 0
            ? selectedIndex
            : 0;
      setHighlightedIndex(indexToUse);
      setIsOpen(true);
    },
    [options, value],
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    requestAnimationFrame(() => {
      buttonRef.current?.focus();
    });
  }, []);

  const selectOption = useCallback(
    (optionValue: T) => {
      onChange(optionValue);
      closeMenu();
    },
    [closeMenu, onChange],
  );

  const handleTriggerClick = () => {
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (options.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const currentIndex = options.findIndex((option) => option.value === value);
      const nextIndex = currentIndex >= 0 ? Math.min(currentIndex + 1, options.length - 1) : 0;
      openMenu(nextIndex);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const currentIndex = options.findIndex((option) => option.value === value);
      const nextIndex = currentIndex >= 0 ? Math.max(currentIndex - 1, 0) : options.length - 1;
      openMenu(nextIndex);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isOpen) {
        if (options[highlightedIndex]) {
          selectOption(options[highlightedIndex].value);
        }
      } else {
        openMenu();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (options.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((previous) => (previous + 1) % options.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((previous) => (previous - 1 + options.length) % options.length);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setHighlightedIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setHighlightedIndex(options.length - 1);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (options[highlightedIndex]) {
        selectOption(options[highlightedIndex].value);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
    }
  };

  const selectedOption = options.find((option) => option.value === value);
  const displayedLabel = selectedOption?.label ?? options[0]?.label ?? '';

  const wrapperClasses = ['map-select-wrapper', wrapperClassName, isOpen ? 'is-open' : null]
    .filter(Boolean)
    .join(' ');

  const triggerClasses = ['map-select', 'map-select-trigger', triggerClassName]
    .filter(Boolean)
    .join(' ');

  const listboxActiveId =
    isOpen && highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined;

  const triggerChildren = triggerContent ? (
    <>
      {triggerContent}
      <span className="sr-only">{displayedLabel}</span>
    </>
  ) : (
    <span className="map-select-value">{displayedLabel}</span>
  );

  return (
    <div ref={containerRef} className={wrapperClasses} data-align={align}>
      <button
        ref={buttonRef}
        type="button"
        className={triggerClasses}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={ariaLabel}
      >
        {triggerChildren}
      </button>
      <div
        ref={menuRef}
        className="map-select-menu"
        role="listbox"
        id={listboxId}
        tabIndex={-1}
        aria-activedescendant={listboxActiveId}
        onKeyDown={handleMenuKeyDown}
        aria-hidden={!isOpen}
      >
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isHighlighted = index === highlightedIndex;
          const optionClasses = [
            'map-select-option',
            isSelected ? 'is-selected' : null,
            isHighlighted ? 'is-highlighted' : null,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              type="button"
              role="option"
              key={String(option.value)}
              id={`${listboxId}-option-${index}`}
              aria-selected={isSelected}
              className={optionClasses}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectOption(option.value)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <span className="map-select-option-label">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const PlayIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
    <path d="M3 2l8 5-8 5z" fill="currentColor" />
  </svg>
);

const PauseIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
    <rect x="3" y="2.5" width="3" height="9" rx="0.5" fill="currentColor" />
    <rect x="8" y="2.5" width="3" height="9" rx="0.5" fill="currentColor" />
  </svg>
);

export function createBuoyCenterPoint(mark: {
  id: number;
  lat: number;
  lon: number;
}): GeoJSON.Feature<GeoJSON.Point> {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [mark.lon, mark.lat],
    },
    properties: {
      buoyId: mark.id,
    },
  };
}

export function createGeodesicCircle(
  center: [number, number], // [lon, lat]
  radiusInMeters: number,
  points = 64,
): GeoJSON.Feature<GeoJSON.Polygon> {
  const [lng, lat] = center;
  const coordinates: [number, number][] = [];
  const earthRadius = 6_371_000; // m
  const angularDistance = radiusInMeters / earthRadius;
  const latRadians = (lat * Math.PI) / 180;
  const lngRadians = (lng * Math.PI) / 180;

  for (let index = 0; index <= points; index++) {
    const bearing = (index / points) * 2 * Math.PI;
    const sinLat =
      Math.sin(latRadians) * Math.cos(angularDistance) +
      Math.cos(latRadians) * Math.sin(angularDistance) * Math.cos(bearing);
    const dLat = Math.asin(sinLat);
    const dLng =
      lngRadians +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latRadians),
        Math.cos(angularDistance) - Math.sin(latRadians) * Math.sin(dLat),
      );
    coordinates.push([(dLng * 180) / Math.PI, (dLat * 180) / Math.PI]);
  }

  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [coordinates] },
    properties: {},
  };
}

export const getColorBasedOnMetric = (
  value: number,
  minMetricValue: number,
  maxMetricValue: number,
): string => {
  const diff = maxMetricValue - minMetricValue || 1; // avoid divide-by-zero
  const normalizedValue = (value - minMetricValue) / diff;
  return interpolateColor(normalizedValue);
};

const getColorBasedOnMetricForPalette = (
  value: number,
  minMetricValue: number,
  maxMetricValue: number,
  palette: MapOverlayPalette,
): string => {
  const diff = maxMetricValue - minMetricValue || 1;
  const normalizedValue = (value - minMetricValue) / diff;
  return interpolateColor(normalizedValue, palette.speedRampStart, palette.speedRampEnd);
};

export function combineSegments<T extends GeoJSON.Geometry>(
  features: Array<GeoJSON.Feature<T, GeoJSON.GeoJsonProperties>>,
): Array<GeoJSON.Feature<T, GeoJSON.GeoJsonProperties>> {
  if (features.length === 0) {
    return [];
  }

  const out: Array<GeoJSON.Feature<T, GeoJSON.GeoJsonProperties>> = [];
  let current: GeoJSON.Feature<T, GeoJSON.GeoJsonProperties> = features[0];

  for (let index = 1; index < features.length; index++) {
    const nextFeature = features[index];

    const bothLineStrings =
      current.geometry.type === 'LineString' && nextFeature.geometry.type === 'LineString';

    if (bothLineStrings && canMergeStyledLineFeatures(current.properties, nextFeature.properties)) {
      // Make a new LineString with concatenated coordinates, do not mutate existing objects
      const currentGeom = current.geometry as LineString;
      const nextGeom = nextFeature.geometry as LineString;
      const merged: GeoJSON.Feature<T, GeoJSON.GeoJsonProperties> = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [...currentGeom.coordinates, ...nextGeom.coordinates.slice(1)],
        } as unknown as T,
        properties: { ...current.properties },
      };
      current = merged;
    } else {
      out.push(current);
      current = nextFeature;
    }
  }

  out.push(current);
  return out;
}

export const loadSvgAsHtml = async (url: string): Promise<string> => {
  const response = await fetch(url);
  return await response.text();
};

/* eslint-disable no-console */
const developmentLog = {
  debug: (...arguments_: unknown[]) => {
    if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
      console.debug(...arguments_);
    }
  },
  warn: (...arguments_: unknown[]) => {
    if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
      console.warn(...arguments_);
    }
  },
  error: (...arguments_: unknown[]) => {
    if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
      console.error(...arguments_);
    }
  },
};
/* eslint-enable no-console */

// Importamos todos los tipos desde la ruta correcta

/* 🗝️  Cache global (en este módulo) para las credenciales Mapbox.
   Así evitamos pedir token+style varias veces y reducimos fallos
   por límite de peticiones simultáneas.                          */
let cachedMapboxToken: string | null = null;

export type MapCameraState = {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
  fileId: number | null;
};

export interface MapPanelProperties {
  loaderKey: number;
  highlightSegments: HighlightSegment[];
  setSelectedHighlightSegment: (segment: HighlightSegment) => void;
  setShowSaveTrainingModal: (show: boolean) => void;
  canManageSelectedFile: boolean;

  removeHighlight: (index: number) => void;

  metricBounds: {
    max: {
      [key in keyof Pick<
        TrackPoint,
        'vmg' | 'bsppc' | 'vmgpc' | 'bsp' | 'sog' | 'tws' | 'heel' | 'twa_abs'
      >]: number;
    };
    min: {
      [key in keyof Pick<
        TrackPoint,
        'vmg' | 'bsppc' | 'vmgpc' | 'bsp' | 'sog' | 'tws' | 'heel' | 'twa_abs'
      >]: number;
    };
    iqrMin: {
      [key in keyof Pick<
        TrackPoint,
        'vmg' | 'bsppc' | 'vmgpc' | 'bsp' | 'sog' | 'tws' | 'heel' | 'twa_abs'
      >]: number;
    };
    iqrMax: {
      [key in keyof Pick<
        TrackPoint,
        'vmg' | 'bsppc' | 'vmgpc' | 'bsp' | 'sog' | 'tws' | 'heel' | 'twa_abs'
      >]: number;
    };
  };
  colorSchemes: {
    tactical: { start: string; mid: string; end: string };
    speed: { start: string; end: string };
  };

  highlightTrackSegment: (segKey: string, segment: HighlightSegment, data: TrackPoint[]) => void;
  races: Segment[];
  startLineSegments: Segment[];

  RenderizarRaces: React.FC<RenderizarRacesProperties>;
  cachedTrackData: TrackPoint[];
  selectedFile: EventFile | null;
  primarySelectionType?: TrackSelectionType;
  tacks: Tack[];
  maneuverVisibility?: Record<number, boolean>;
  onManeuverVisibilityChange?: (tackIndex: number, visible: boolean) => void;

  showSidePage: boolean;
  setShowSidePage: React.Dispatch<React.SetStateAction<boolean>>;
  showSidebar: boolean;
  setShowSidebar: React.Dispatch<React.SetStateAction<boolean>>;

  trackData: TrackPoint[];
  relatedTrackLayers: OverlayTrackLayer[];
  globalToRelativeIndexMap: Record<number, number>;
  cachedTacksGeoJSON: Record<number, GeoJSON.FeatureCollection | null>;
  mapContainer: React.RefObject<HTMLDivElement | null>;
  marks: Array<Mark>;
  loa: number;
  setHighlightSegments: React.Dispatch<React.SetStateAction<HighlightSegment[]>>;
  generateRandomColor: () => string;
  editingSegmentId?: number | null;
  editingRange?: { start: number; end: number } | null;
  editingMode?: 'idle' | 'addBuoy' | 'moveBuoy' | 'deleteBuoy';
  onEditingMarksChange?: (marks: Mark[]) => void;
  onEditingRangeChange?: (boundary: 'start' | 'end', value: number) => void;
  onSaveEditing?: () => void;
  onCancelEditing?: () => void;
  editingSegmentType?: 'race' | 'training';
  editingSegmentName?: string | null;
  setEditingMode?: (mode: 'idle' | 'addBuoy' | 'moveBuoy' | 'deleteBuoy') => void;
  setCachedTacksGeoJSON: React.Dispatch<
    React.SetStateAction<Record<number, GeoJSON.FeatureCollection | null>>
  >;
  setDraftHighlight: React.Dispatch<
    React.SetStateAction<(HighlightSegment & { source: 'map' | 'chart' | 'chartPreview' }) | null>
  >;
  combineSegments: (
    features: GeoJSON.Feature<GeoJSON.Geometry>[],
  ) => GeoJSON.Feature<GeoJSON.Geometry>[];
  getTackColor: (startIndex: number, endIndex: number) => string;
  polars?: {
    angles: number[];
    tws: number[];
    speeds: number[][];
    stds: number[][];
    raw?: string | null;
  };

  /** id del fichero actualmente cargado (o null) */
  lastFileId: number | null;

  /** permite regenerar el loader desde el hijo */
  setLoaderKey: React.Dispatch<React.SetStateAction<number>>;
  mapInstance: React.MutableRefObject<mapboxgl.Map | null>;
  side: SidePanelPosition;
  panelId: 'p1' | 'p2' | 'p3' | 'p4';
  sharedCameraRef: React.MutableRefObject<MapCameraState | null>;
  suppressLoadingOverlay?: boolean;
}

const formatPointLabelValue = (point?: TrackPoint) => {
  if (!point) {
    return '—';
  }
  const raw = point.utc ?? point.date;
  if (!raw) {
    return '—';
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

type SegmentEditorTimebarProperties = {
  cachedTrackData: TrackPoint[];
  editingRange: { start: number; end: number };
  onEditingRangeChange?: (boundary: 'start' | 'end', value: number) => void;
  t: (key: string, defaultValue?: string) => string;
  editingMode?: 'idle' | 'addBuoy' | 'moveBuoy' | 'deleteBuoy';
  onEditingModeChange?: (mode: 'idle' | 'addBuoy' | 'moveBuoy' | 'deleteBuoy') => void;
  onSaveEditing?: () => void;
  onCancelEditing?: () => void;
  editingSegmentName?: string | null;
  editingSegmentType?: 'race' | 'training' | null;
  showManualZoneTools?: boolean;
};

const SegmentEditorTimebar: React.FC<SegmentEditorTimebarProperties> = ({
  cachedTrackData,
  editingRange,
  onEditingRangeChange,
  t,
  editingMode,
  onEditingModeChange,
  onSaveEditing,
  onCancelEditing,
  editingSegmentName,
  editingSegmentType,
  showManualZoneTools = false,
}) => {
  const handleModeToggle = useCallback(
    (mode: 'addBuoy' | 'deleteBuoy') => {
      const nextMode = editingMode === mode ? 'idle' : mode;
      onEditingModeChange?.(nextMode);
    },
    [editingMode, onEditingModeChange],
  );

  const clampIndex = (value: number) => {
    if (cachedTrackData.length === 0 || !Number.isFinite(value)) {
      return 0;
    }
    const max = Math.max(0, cachedTrackData.length - 1);
    return Math.min(Math.max(0, Math.floor(value)), max);
  };

  const totalPoints = Math.max(0, cachedTrackData.length - 1);
  const startIdx = clampIndex(editingRange.start);
  const endIdx = clampIndex(editingRange.end);
  const minIdx = Math.min(startIdx, endIdx);
  const maxIdx = Math.max(startIdx, endIdx);

  const durationLabel = (() => {
    const start = cachedTrackData[minIdx];
    const end = cachedTrackData[maxIdx];
    const parse = (point?: TrackPoint) => {
      if (!point) {
        return null;
      }
      const raw = point.utc ?? point.date;
      if (!raw) {
        return null;
      }
      const parsed = new Date(raw);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };
    const startDate = parse(start);
    const endDate = parse(end);
    if (!startDate || !endDate) {
      return '';
    }
    const delta = Math.abs(endDate.getTime() - startDate.getTime());
    const totalSeconds = Math.floor(delta / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
    }
    return `${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
  })();

  const sliderStartPercent = totalPoints > 0 ? (minIdx / totalPoints) * 100 : 0;
  const sliderEndPercent = totalPoints > 0 ? (maxIdx / totalPoints) * 100 : 0;
  const segmentTitle = editingSegmentName || t('race.segmentEditor.active', 'Segment editing');
  const typeLabel =
    editingSegmentType === 'race'
      ? t('race.segmentEditor.race', 'Race')
      : editingSegmentType === 'training'
        ? t('race.segmentEditor.training', 'Training')
        : t('race.segmentEditor.segment', 'Segment');
  const handleRangeInput = useCallback(
    (boundary: 'start' | 'end') =>
      (event: React.FormEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>) => {
        onEditingRangeChange?.(boundary, Number(event.currentTarget.value));
      },
    [onEditingRangeChange],
  );

  return (
    <div
      className="segment-editor-timebar"
      style={
        {
          '--slider-start': `${sliderStartPercent}%`,
          '--slider-end': `${sliderEndPercent}%`,
          '--slider-width': `${Math.max(sliderEndPercent - sliderStartPercent, 0)}%`,
        } as React.CSSProperties
      }
    >
      {/* Row 1: Actions + Title + Save/Cancel */}
      <div className="segment-editor-row">
        {showManualZoneTools && (
          <div className="segment-editor-action-group">
            <span className="segment-editor-action-label">
              {t('race.segmentEditor.buoys', 'Buoys')}
            </span>
            <button
              type="button"
              className={`segment-editor-action compact${editingMode === 'addBuoy' ? ' is-active' : ''}`}
              onClick={() => handleModeToggle('addBuoy')}
              title={t('race.segmentEditor.addBuoy', 'Add buoy')}
            >
              +
            </button>
            <button
              type="button"
              className={`segment-editor-action compact${editingMode === 'deleteBuoy' ? ' is-active' : ''}`}
              onClick={() => handleModeToggle('deleteBuoy')}
              title={t('race.segmentEditor.deleteBuoy', 'Remove buoy')}
            >
              −
            </button>
          </div>
        )}
        <div className="segment-editor-center">
          <span className="segment-editor-chip">{typeLabel}</span>
          <span className="segment-editor-name" title={segmentTitle}>
            {segmentTitle}
          </span>
        </div>
        <div className="segment-editor-action-group">
          <button
            type="button"
            className="segment-editor-action compact ghost"
            onClick={() => onCancelEditing?.()}
            disabled={!onCancelEditing}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="segment-editor-action compact primary"
            onClick={() => onSaveEditing?.()}
            disabled={!onSaveEditing}
          >
            {t('common.save', 'Save')}
          </button>
        </div>
      </div>
      {/* Row 2: Time labels + Slider + Duration */}
      <div className="segment-editor-row slider-row">
        <span className="segment-editor-time-label">
          {formatPointLabelValue(cachedTrackData[startIdx])}
        </span>
        <div className="segment-editor-slider">
          <div className="segment-editor-track" />
          <div
            className="segment-editor-window"
            style={{
              left: `${sliderStartPercent}%`,
              width: `${Math.max(sliderEndPercent - sliderStartPercent, 0)}%`,
            }}
          />
          <input
            type="range"
            min={0}
            max={totalPoints}
            step={1}
            value={startIdx}
            className="segment-editor-handle segment-editor-handle--start"
            onChange={handleRangeInput('start')}
            onInput={handleRangeInput('start')}
            aria-label={t('race.segmentEditor.startLabel', 'Segment start')}
          />
          <input
            type="range"
            min={0}
            max={totalPoints}
            step={1}
            value={endIdx}
            className="segment-editor-handle segment-editor-handle--end"
            onChange={handleRangeInput('end')}
            onInput={handleRangeInput('end')}
            aria-label={t('race.segmentEditor.endLabel', 'Segment end')}
          />
        </div>
        <span className="segment-editor-time-label">
          {formatPointLabelValue(cachedTrackData[endIdx])}
        </span>
        <span className="segment-editor-duration-badge">{durationLabel || '—'}</span>
      </div>
    </div>
  );
};

const MapPanel: React.FC<MapPanelProperties> = ({
  loaderKey: _loaderKey,
  highlightSegments,
  setSelectedHighlightSegment,
  setShowSaveTrainingModal,
  canManageSelectedFile,
  metricBounds,

  races,
  startLineSegments,

  RenderizarRaces,
  cachedTrackData,
  selectedFile,
  primarySelectionType,
  tacks,
  maneuverVisibility,
  onManeuverVisibilityChange,

  showSidePage,
  showSidebar,
  setShowSidebar: _setShowSidebar,
  side,
  globalToRelativeIndexMap,
  cachedTacksGeoJSON,
  marks,
  editingSegmentId = null,
  editingRange = null,
  editingMode = 'idle',
  onEditingMarksChange,
  onEditingRangeChange,
  onSaveEditing,
  onCancelEditing,
  editingSegmentType,
  editingSegmentName,
  setEditingMode,
  loa,
  setHighlightSegments,
  setCachedTacksGeoJSON,
  setDraftHighlight,
  getTackColor,
  lastFileId,
  setLoaderKey: _setLoaderKey,
  trackData,
  relatedTrackLayers,
  generateRandomColor,
  removeHighlight,
  highlightTrackSegment: _highlightTrackSegment,
  mapInstance,
  mapContainer,
  sharedCameraRef,
  panelId: _panelId,
  polars,
  suppressLoadingOverlay = false,
}) => {
  const { t } = useTranslation('common');
  const panelId = _panelId;
  // Keep map-tool contrast tied to the basemap that is actually selected,
  // rather than to the surrounding application's light/dark shell.
  const [mapColorScheme, setMapColorScheme] = useState<ColorScheme>(() =>
    getCurrentMapOverlayScheme(),
  );
  const commentMarkerPalette = MAP_COMMENT_MARKER_PALETTES[mapColorScheme];
  const timeMarkerIntervalMinutes = getTimeMarkerIntervalMinutes(primarySelectionType);
  const timeMarkerLabelOffsetMinutes = getTimeMarkerLabelOffsetMinutes(primarySelectionType);
  const safeTrackData = useMemo(
    () =>
      trackData.filter(
        (p) =>
          typeof p.lat === 'number' &&
          typeof p.lon === 'number' &&
          Number.isFinite(p.lat) &&
          Number.isFinite(p.lon),
      ),
    [trackData],
  );
  const translateEditor = useCallback(
    (key: string, fallback?: string) => t(key, { defaultValue: fallback }),
    [t],
  );
  const isEditingSegment = editingSegmentId !== null && editingSegmentId !== undefined;
  const [adminToolsConfig, setAdminToolsConfig] = useState<AdminToolsConfig>(() =>
    getAdminToolsConfig(),
  );
  const [hoverCoordinates, setHoverCoordinates] = useState<{ lat: number; lon: number } | null>(
    null,
  );
  useEffect(() => {
    return subscribeAdminToolsConfig(setAdminToolsConfig);
  }, []);
  useEffect(() => {
    if (!adminToolsConfig.showHoverCoordinates) {
      lastHoverCoordsRef.current = null;
      setHoverCoordinates(null);
    }
  }, [adminToolsConfig.showHoverCoordinates]);
  const [pendingDraftHighlightId, setPendingDraftHighlightId] = useState<string | null>(null);
  const clearMapDraftHighlight = useCallback(() => {
    setDraftHighlight((current) => (current && current.source === 'map' ? null : current));
  }, [setDraftHighlight]);
  useEffect(() => {
    if (!pendingDraftHighlightId) {
      return;
    }
    const hasRendered = highlightSegments.some((segment) => segment.id === pendingDraftHighlightId);
    if (!hasRendered) {
      return;
    }
    setPendingDraftHighlightId(null);
    clearMapDraftHighlight();
  }, [pendingDraftHighlightId, highlightSegments, clearMapDraftHighlight]);
  const notAvailableLabel = t('map.raceStatsPanel.notAvailable', 'N/A');
  // safe helper
  const safeResizeMap = useCallback((map: mapboxgl.Map | null | undefined) => {
    if (!map) {
      return;
    } // extra guard #1
    type InternalMapStyle = { applyProjectionUpdate?: unknown };
    const internal = (map as unknown as { style?: InternalMapStyle }).style;
    if (!internal || typeof internal.applyProjectionUpdate !== 'function') {
      return;
    } // guard #2
    map.resize();
  }, []);
  // ———————————————————————————
  // 🔍 Debug helper (scoped to panel side)
  // Usage: dbg("message", additional, info, …)
  const dbg = (...arguments_: unknown[]) => {
    developmentLog.debug(`[MapPanel ${side}]`, ...arguments_);
  };
  // ———————————————————————————
  // Al montar y redimensionar
  // MapPanel.tsx  (sustituye los dos useEffect actuales por ESTE bloque)
  const [, setMapboxStyleURL] = useState<string | undefined>();
  const [mapReadyToken, setMapReadyToken] = useState(0);
  const boatMarkerReference = useRef<Marker | null>(null);
  const playerMarkerReference = useRef<mapboxgl.Marker | null>(null);
  const playerMarkerElementRef = useRef<HTMLElement | null>(null);
  const playerMarkerElementPromiseRef = useRef<Promise<HTMLElement | null> | null>(null);
  const playerMarkerPositionRef = useRef<{ lat: number; lon: number } | null>(null);
  const playerMarkerHeadingRef = useRef<number | null>(null);
  const playerMarkerResizeHandlerRef = useRef<(() => void) | null>(null);

  const playerRenderTokenRef = useRef(0);
  const fitTokenRef = useRef(0);
  const cameraLoadTokenRef = useRef(0);
  const lineModeApplyTokenRef = useRef(0);
  const arrowSizeRef = useRef<{ width: number; height: number } | null>(null);
  const loaRef = useRef(loa);
  const markClickHandlerRef = useRef<((event: mapboxgl.MapLayerMouseEvent) => void) | null>(null);
  const suppressNextEditingMapClickRef = useRef(false);
  const pendingInitialCameraRef = useRef(false);
  const initialFitAppliedRef = useRef(false);
  const laylineLockRef = useRef(false);
  const legendContainerReference = useRef<HTMLDivElement>(null);
  // justo con el resto de refs
  const startPointReference = useRef<StartPoint | null>(null); // ⬅️ NUEVO
  const endPointReference = useRef<{ lng: number; lat: number } | null>(null); // ⬅️ NUEVO

  const currentHighlightColorReference = useRef<string | null>(null);
  const marksRef = useRef<Mark[]>(marks);
  // Synchronous update ensures marksRef.current is always current before renderMarks runs
  marksRef.current = marks;
  const editingModeRef = useRef(editingMode);
  // Synchronous update ensures editingModeRef.current is always current
  editingModeRef.current = editingMode;
  const currentPlayerPointRef = useRef<TrackPoint | null>(null);
  const activeLaylineMarkRef = useRef<{
    mark: (Mark & { distanceMeters: number }) | null;
    projection: number | null;
  }>({ mark: null, projection: null });
  const manualLaylineIdRef = useRef(-1);
  const lastPlayerSkipKeyRef = useRef<string | null>(null);

  const suppressCurrentEditingMapClick = () => {
    suppressNextEditingMapClickRef.current = true;
    window.setTimeout(() => {
      suppressNextEditingMapClickRef.current = false;
    }, 0);
  };

  const arrowMarkers = useRef<mapboxgl.Marker[]>([]);
  const gridOriginRef = useRef<{ lng: number; lat: number } | null>(null);
  const renderedGridViewportRef = useRef<{
    center: { lng: number; lat: number };
    spanLng: number;
    spanLat: number;
  } | null>(null);
  const renderedGridVisibilityRef = useRef<ReturnType<typeof getGridLayerVisibility> | null>(null);
  const isGridRotationActiveRef = useRef(false);
  const labelMarkers = useRef<mapboxgl.Marker[]>([]);
  const isPlayerActiveRef = useRef(false);
  const isEditingSegmentRef = useRef(false);
  const polarsEmptyLoggedRef = useRef(false);
  const hoverFrameRef = useRef<number | null>(null);
  const lastHoverCoordsRef = useRef<{ lat: number; lon: number } | null>(null);

  useEffect(() => {
    const container = mapContainer.current;
    if (!container) {
      return;
    }

    let resizeFrame: number | null = null;
    const scheduleMapContainerResize = () => {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        if (container.clientWidth === 0 || container.clientHeight === 0) {
          return;
        }
        safeResizeMap(mapInstance.current);
      });
    };

    const mapContainerResizeObserver = new ResizeObserver(scheduleMapContainerResize);
    mapContainerResizeObserver.observe(container);
    scheduleMapContainerResize();

    return () => {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }
      mapContainerResizeObserver.disconnect();
    };
  }, [mapContainer, mapInstance, mapReadyToken, safeResizeMap]);

  const clearArrowMarkers = useCallback(() => {
    for (const marker of arrowMarkers.current) {
      marker.remove();
    }
    arrowMarkers.current = [];
  }, []);
  const [showLegend, setShowLegend] = useState(false);
  const [isArrowFixed, setIsArrowFixed] = useState(false);
  const [mapInputMode, setMapInputMode] = useState<MapInputMode>('idle');
  const mapInputModeTransitionRef = useRef<((mode: MapInputMode) => void) | null>(null);
  const isMeasuringDistance = mapInputMode === 'measure';
  const isOptimizingRoute = mapInputMode === 'optimize';
  const isHighlighting = mapInputMode === 'highlight';
  const [optimizeStart, setOptimizeStart] = useState<{
    index: number;
    lat: number;
    lon: number;
  } | null>(null);
  const [optimizeGoal, setOptimizeGoal] = useState<{ lat: number; lon: number } | null>(null);
  const [optimizeMaxTacksInput, setOptimizeMaxTacksInput] = useState('');
  const [optimizeMinTacksInput, setOptimizeMinTacksInput] = useState('');
  const [optimizeFirstBoard, setOptimizeFirstBoard] = useState<BoardPreference>('auto');
  const [optimizeLastBoard, setOptimizeLastBoard] = useState<BoardPreference>(1);
  const firstBoardOptions = useMemo<MapSelectOption<BoardPreference>[]>(
    () => [
      { value: 'auto', label: t('map.optimize.boardAuto', 'Auto') },
      { value: 0, label: t('map.optimize.boardPort', 'Port tack') },
      { value: 1, label: t('map.optimize.boardStarboard', 'Starboard tack') },
    ],
    [t],
  );
  const lastBoardOptions = useMemo<MapSelectOption<BoardPreference>[]>(
    () => [
      { value: 1, label: t('map.optimize.boardStarboard', 'Starboard tack') },
      { value: 0, label: t('map.optimize.boardPort', 'Port tack') },
      { value: 'auto', label: t('map.optimize.boardAuto', 'No preference') },
    ],
    [t],
  );
  const [optimizedRoute, setOptimizedRoute] = useState<{
    path: OptimizedRoutePoint[];
    etaSeconds?: number | null;
    startIndex?: number | null;
  } | null>(null);
  const optimizedRouteAnimationFrameRef = useRef<number | null>(null);
  const optimizedRouteAnimationStartRef = useRef<number | null>(null);
  const optimizedRoutePulseFrameRef = useRef<number | null>(null);
  const optimizedRoutePulseStartRef = useRef<number | null>(null);
  const optimizedRouteTrack: OptimizedRouteTrack = useMemo(() => {
    if (!optimizedRoute?.path || optimizedRoute.path.length === 0) {
      return null;
    }
    const waypoints = optimizedRoute.path
      .map((point) => ({
        lat: Number(point.lat),
        lon: Number(point.lon),
        t_idx: point.t_idx, // Guardar índice temporal
      }))
      .filter(
        (point) =>
          Number.isFinite(point.lat) &&
          Number.isFinite(point.lon) &&
          typeof point.lat === 'number' &&
          typeof point.lon === 'number',
      );
    if (waypoints.length === 0) {
      return null;
    }
    let totalDistance = 0;
    const segments: OptimizedRouteSegment[] = [];
    for (let index = 0; index < waypoints.length - 1; index += 1) {
      const start = waypoints[index];
      const end = waypoints[index + 1];
      const distanceMeters = calculateDistance(start.lat, start.lon, end.lat, end.lon) * 1000;
      const startDistance = totalDistance;
      totalDistance += Number.isFinite(distanceMeters) ? distanceMeters : 0;
      const heading = calculateHeading(
        { lat: start.lat, lon: start.lon },
        { lat: end.lat, lon: end.lon },
      );
      segments.push({
        start,
        end,
        startDistance,
        endDistance: totalDistance,
        heading,
        startTidx: start.t_idx, // Índice temporal del punto inicial
        endTidx: end.t_idx, // Índice temporal del punto final
      });
    }
    const etaSeconds =
      typeof optimizedRoute.etaSeconds === 'number' && Number.isFinite(optimizedRoute.etaSeconds)
        ? optimizedRoute.etaSeconds
        : null;
    const firstIdx = optimizedRoute.path[0]?.t_idx;
    const lastIdx = optimizedRoute.path.at(-1)?.t_idx;
    const idxDuration =
      Number.isFinite(firstIdx) &&
      Number.isFinite(lastIdx) &&
      (lastIdx as number) > (firstIdx as number)
        ? (lastIdx as number) - (firstIdx as number)
        : null;
    const durationSeconds =
      (etaSeconds && etaSeconds > 0 ? etaSeconds : null) ??
      (idxDuration && idxDuration > 0 ? idxDuration : null) ??
      Math.max(segments.length, 1) * (PLAYBACK_INTERVAL_MS / 1000);

    return {
      segments,
      totalDistance,
      durationSeconds,
      firstPoint: waypoints[0],
      lastPoint: waypoints.at(-1) ?? waypoints[0],
      firstTidx: firstIdx, // Primer índice temporal
      lastTidx: lastIdx, // Último índice temporal
    };
  }, [optimizedRoute]);
  const stopOptimizedRouteAnimation = useCallback(() => {
    if (optimizedRouteAnimationFrameRef.current !== null) {
      cancelAnimationFrame(optimizedRouteAnimationFrameRef.current);
    }
    optimizedRouteAnimationFrameRef.current = null;
    optimizedRouteAnimationStartRef.current = null;
  }, []);
  const stopOptimizedRoutePulse = useCallback(() => {
    if (optimizedRoutePulseFrameRef.current !== null) {
      cancelAnimationFrame(optimizedRoutePulseFrameRef.current);
    }
    optimizedRoutePulseFrameRef.current = null;
    optimizedRoutePulseStartRef.current = null;
  }, []);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);
  const [optimizeLoading, setOptimizeLoading] = useState(false);
  const [lineMode, setLineMode] = useState<LineMode>('plain');
  const [startPoint, setStartPoint] = useState<StartPoint | null>(null);
  const [showTacks, setShowTacks] = useState(false);
  const [mapComments, setMapComments] = useState<MapComment[]>([]);
  const [commentPermission, setCommentPermission] = useState<CommentPermission>(
    EMPTY_COMMENT_PERMISSION,
  );
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsVisible, setCommentsVisible] = useState(true);
  const [activeCommentId, setActiveCommentId] = useState<number | null>(null);
  const [pinnedCommentId, setPinnedCommentId] = useState<number | null>(null);
  const [commentCalloutPosition, setCommentCalloutPosition] = useState<{
    left: number;
    top: number;
    markerX: number;
    markerY: number;
    connectorX: number;
    connectorY: number;
  } | null>(null);
  const [isCommentListOpen, setIsCommentListOpen] = useState(false);
  const [isCommentComposerOpen, setIsCommentComposerOpen] = useState(false);
  const [commentComposerAnchor, setCommentComposerAnchor] = useState<MapCommentDraftAnchor | null>(
    null,
  );
  const commentPlacementMode = mapInputMode === 'comment-placement' ? 'map_point' : null;
  const [commentDraftBody, setCommentDraftBody] = useState('');
  const [commentDraftFiles, setCommentDraftFiles] = useState<Array<{ file: File; previewUrl: string }>>(
    [],
  );
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentVersion, setEditingCommentVersion] = useState<number | null>(null);
  const [removedCommentAttachmentIds, setRemovedCommentAttachmentIds] = useState<number[]>([]);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentConflict, setCommentConflict] = useState(false);
  const [resumeCommentComposerAfterPlacement, setResumeCommentComposerAfterPlacement] = useState(false);
  const [expandedCommentAttachment, setExpandedCommentAttachment] = useState<CommentAttachment | null>(
    null,
  );
  const commentDismissTimeoutRef = useRef<number | null>(null);
  const commentDraftPreviewUrlsRef = useRef<string[]>([]);
  const commentsRequestTokenRef = useRef(0);
  const commentPositionAnchorRef = useRef<MapCommentDraftAnchor | null>(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [mapBearing, setMapBearing] = useState(0);
  const mapBearingRef = useRef<number>(mapBearing);
  const wasPlayerActiveRef = useRef(false);
  const updateLaylinesForPointRef = useRef<UpdateLaylinesForPointFn | null>(null);
  const updatePlayerMarkerRef = useRef<UpdatePlayerMarkerFn | null>(null);
  const updatePlayerGraphicsRef = useRef<UpdatePlayerGraphicsFn | null>(null);
  const orientationBearingRef = useRef(0);
  const orientationIsWindRef = useRef(false);
  const orientationIsStartLineRef = useRef(false);
  const lastTwdRef = useRef<number | null>(null);
  const lastLaylineKeyRef = useRef<string | null>(null);
  const [gridOrientationBearing, setGridOrientationBearing] = useState(0);
  const lastFileIdRef = useRef<number | null>(null);
  const gridRotationAngle = useMemo(
    () => normalizeBearing(gridOrientationBearing),
    [gridOrientationBearing],
  );
  const gridIdSuffix = useRef<string>(Math.random().toString(36).slice(2, 8));

  const captureCameraState = useCallback(() => {
    if (!mapInstance.current || !sharedCameraRef) {
      return;
    }
    const map = mapInstance.current;
    const center = map.getCenter();
    const normalizedBearing = normalizeBearing(map.getBearing());
    sharedCameraRef.current = {
      center: [center.lng, center.lat],
      zoom: map.getZoom(),
      bearing: normalizedBearing,
      pitch: map.getPitch(),
      fileId: lastFileIdRef.current ?? null,
    };
  }, [mapInstance, sharedCameraRef]);

  const applySharedCameraIfMatchingTrack = useCallback(
    (options?: { ignoreFileCheck?: boolean }) => {
      if (!mapInstance.current || !sharedCameraRef?.current) {
        return false;
      }
      const camera = sharedCameraRef.current;
      if (!options?.ignoreFileCheck && (lastFileIdRef.current ?? null) !== camera.fileId) {
        return false;
      }
      const normalizedBearing = normalizeBearing(camera.bearing ?? 0);
      dbg('[bearing] applySharedCameraIfMatchingTrack', {
        source: options?.ignoreFileCheck ? 'init-ignoreFileCheck' : 'shared-camera',
        bearing: camera.bearing,
        normalizedBearing,
        fileId: camera.fileId,
        lastFileId: lastFileIdRef.current,
      });
      mapInstance.current.jumpTo({
        center: camera.center,
        zoom: camera.zoom,
        bearing: camera.bearing,
        pitch: camera.pitch,
      });
      orientationBearingRef.current = normalizedBearing;
      setMapBearing(normalizedBearing);
      setGridOrientationBearing((previous) =>
        previous === normalizedBearing ? previous : normalizedBearing,
      );
      return true;
    },
    [mapInstance, setGridOrientationBearing, setMapBearing, sharedCameraRef],
  );

  useEffect(() => {
    lastFileIdRef.current = lastFileId;
  }, [lastFileId]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    const clearHover = () => {
      lastHoverCoordsRef.current = null;
      setHoverCoordinates(null);
    };
    map.on('mouseleave', clearHover);
    map.on('mouseout', clearHover);
    return () => {
      map.off('mouseleave', clearHover);
      map.off('mouseout', clearHover);
    };
  }, [mapInstance]);

  useEffect(() => {
    return () => {
      if (hoverFrameRef.current !== null) {
        cancelAnimationFrame(hoverFrameRef.current);
      }
      stopOptimizedRouteAnimation();
    };
  }, [stopOptimizedRouteAnimation]);

  /**
   * Fallback to ensure the loading spinner disappears.
   * Sometimes the Mapbox `"load"` event can fire before we attach the
   * listener (e.g. when the container was 0 × 0 px or the map was
   * re‑parented). We therefore also watch `isStyleLoaded()` plus the
   * `"idle"` and `"styledata"` events which always fire once the style
   * is completely ready.  In addition, we forcibly remove any overlay
   * node that may have been injected imperatively outside React.
   */
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }

    // (El estado se ocultará más adelante; aquí sólo limpiamos overlays imperativos)
    const hideLoader = () => {
      // ① React state → oculta el spinner controlado por JSX

      // ② Imperative overlay → pudo inyectarse en `mapContainer`
      const overlayRoot = mapContainer.current?.parentElement ?? mapContainer.current;
      if (overlayRoot) {
        const overlay =
          overlayRoot.querySelector<HTMLElement>('.map-loader-overlay') ??
          overlayRoot.querySelector<HTMLElement>('.loader-overlay') ??
          overlayRoot.querySelector<HTMLElement>('.map-loader');
        if (overlay) {
          overlay.remove();
        }
      }
    };

    // El estilo ya está cargado antes de tiempo
    if (map.isStyleLoaded()) {
      hideLoader();
      return;
    }

    // Eventos que garantizan la carga completa del estilo
    const handleReady = () => {
      if (map.isStyleLoaded()) {
        hideLoader();
        map.off('idle', handleReady);
        map.off('styledata', handleReady);
      }
    };

    map.on('idle', handleReady);
    map.on('styledata', handleReady);

    // Cleanup
    return () => {
      hideLoader();
      map.off('idle', handleReady);
      map.off('styledata', handleReady);
    };
  }, [mapContainer.current]);
  const [trackModes, setTrackModes] = useState(1);
  const [isRaceStatsVisible, setIsRaceStatsVisible] = useState(false);
  const [forceUpdate, _setForceUpdate] = useState(0);
  const [colorLegend, setColorLegend] = useState<ColorLegend | null>(null);
  const _hasMap = useRef(false);
  const [tackStats, setTackStats] = useState<TackStats[]>([]);
  const idSuffix = gridIdSuffix.current; // add this once at the top

  const tackSourceId = `tack-lines-${idSuffix}`; // ejemplo: tack-lines-left
  const tackLayerId = `tack-lines-layer-${idSuffix}`; // ejemplo: tack-lines-layer-left
  const tackCasingLayerId = `tack-lines-casing-layer-${idSuffix}`;

  const [selectedMetric, setSelectedMetric] = useState<ColorMetricKey>('bsp');
  const [selectedMetricWidth, setSelectedMetricWidth] = useState<WidthMetricKey>('tws');

  // justo con el resto de refs
  const _labelMarkerReference = useRef<mapboxgl.Marker | null>(null);
  const [legendScale, setLegendScale] = useState(1);

  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const [isLegendEditing, setIsLegendEditing] = useState(false);
  const [sharedManeuverVisibility, setSharedManeuverVisibility] = useState<ManeuverVisibility>({});
  const hasTwdData = useMemo(
    () => trackData.some((point) => typeof point.twd === 'number' && Number.isFinite(point.twd)),
    [trackData],
  );

  const [isPlayerActive, setIsPlayerActive] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerIndex, setPlayerIndex] = useState(0);

  useEffect(() => {
    loaRef.current = loa;
  }, [loa]);

  useEffect(() => {
    isPlayerActiveRef.current = isPlayerActive;
  }, [isPlayerActive]);

  useEffect(() => {
    if (!isPlayerActive || !boatMarkerReference.current) {
      return;
    }
    boatMarkerReference.current.remove();
    boatMarkerReference.current = null;
  }, [isPlayerActive]);

  useEffect(() => {
    const unsubscribe = subscribeManeuverVisibility(setSharedManeuverVisibility);
    return () => unsubscribe();
  }, []);

  // marksRef is updated synchronously at the top of the component, no useEffect needed

  const distanceInfoElementReference = useRef<HTMLDivElement | null>(null);
  const [tackMapping, setTackMapping] = useState<TackMapping>({});
  const panelReference = useRef<HTMLDivElement>(null);
  const [panelSize, setPanelSize] = useState({ width: 0, height: 0 });
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [resizing, setResizing] = useState(false);
  const [movingBuoyId, setMovingBuoyId] = useState<number | null>(null);
  const previousTrackOpacityRef = useRef<number | null>(null);
  const tempMarkIdRef = useRef(-1);
  const markDragHandlerRef = useRef<((event: mapboxgl.MapLayerMouseEvent) => void) | null>(null);
  const markDragTouchHandlerRef = useRef<((event: mapboxgl.MapLayerTouchEvent) => void) | null>(
    null,
  );

  const [startPointOld, setStartPointOld] = useState<{
    lng: number;
    lat: number;
  } | null>(null);
  const [_endPoint, _setEndPoint] = useState<{ lng: number; lat: number } | null>(null);
  const trackSourceId = `track-source-${idSuffix}`;
  const trackLayerId = `track-layer-${idSuffix}`; // unique per panel instance
  const trackCasingLayerId = `track-casing-layer-${idSuffix}`;
  const raceStartLineSourceId = `race-start-lines-${idSuffix}`;
  const raceStartLineLayerId = `race-start-lines-layer-${idSuffix}`;
  const raceStartLinePointLayerId = `race-start-lines-points-${idSuffix}`;
  const editFullSourceId = `segment-edit-full-${idSuffix}`;
  const editFullLayerId = `segment-edit-full-layer-${idSuffix}`;
  const editRangeSourceId = `segment-edit-range-${idSuffix}`;
  const editRangeLayerId = `segment-edit-range-layer-${idSuffix}`;
  const overlaySourcePrefix = `overlay-track-source-${idSuffix}-`;
  const overlayLayerPrefix = `overlay-track-layer-${idSuffix}-`;
  const optimizedRouteSourceId = `optimized-route-source-${idSuffix}`;
  const optimizedRouteLayerId = `optimized-route-layer-${idSuffix}`;
  const optimizedRouteGlowLayerId = `optimized-route-glow-layer-${idSuffix}`;
  const optimizedRoutePointsSourceId = `optimized-route-points-source-${idSuffix}`;
  const optimizedRoutePointsLayerId = `optimized-route-points-layer-${idSuffix}`;
  const optimizedRoutePointsHaloLayerId = `optimized-route-points-halo-layer-${idSuffix}`;
  const optimizedRoutePointsRingLayerId = `optimized-route-points-ring-layer-${idSuffix}`;
  const ladderLinesSourceId = LADDER_LINES_SOURCE_ID(idSuffix);
  const ladderLinesLayerId = LADDER_LINES_LAYER_ID(idSuffix);
  const ladderLabelsSourceId = LADDER_LABELS_SOURCE_ID(idSuffix);
  const ladderLabelsLayerId = LADDER_LABELS_LAYER_ID(idSuffix);
  const playerLaylinesSourceId = `player-laylines-${idSuffix}`;
  const playerLaylinesLayerId = `player-laylines-layer-${idSuffix}`;
  const playerLaylinesCasingLayerId = `player-laylines-casing-layer-${idSuffix}`;
  const playerPathSourceId = `player-path-${idSuffix}`;
  const playerPathLayerId = `player-path-layer-${idSuffix}`;
  const playerPathCasingLayerId = `player-path-casing-layer-${idSuffix}`;
  const commentsSourceId = `map-comments-source-${idSuffix}`;
  const commentsHaloLayerId = `map-comments-halo-${idSuffix}`;
  const commentsEditRingLayerId = `map-comments-edit-ring-${idSuffix}`;
  const commentsPinLayerId = `map-comments-pins-${idSuffix}`;
  const commentsHitLayerId = `map-comments-hit-${idSuffix}`;
  const commentDraftPreviewSourceId = `map-comment-draft-preview-source-${idSuffix}`;
  const commentDraftPreviewHaloLayerId = `map-comment-draft-preview-halo-${idSuffix}`;
  const commentDraftPreviewRingLayerId = `map-comment-draft-preview-ring-${idSuffix}`;
  const commentDraftPreviewPinLayerId = `map-comment-draft-preview-pin-${idSuffix}`;
  const commentDraftPreviewHitLayerId = `map-comment-draft-preview-hit-${idSuffix}`;
  // Hot reload does not remove Mapbox layers owned by the previous module.
  // Keep these legacy IDs only to remove artifacts created by earlier builds.
  const obsoleteCommentEditPreviewSourceId = `map-comment-edit-preview-source-${idSuffix}`;
  const obsoleteCommentEditPreviewHaloLayerId = `map-comment-edit-preview-halo-${idSuffix}`;
  const obsoleteCommentEditPreviewRingLayerId = `map-comment-edit-preview-ring-${idSuffix}`;
  const obsoleteCommentEditPreviewPinLayerId = `map-comment-edit-preview-pin-${idSuffix}`;
  const obsoleteCommentEditPreviewHitLayerId = `map-comment-edit-preview-hit-${idSuffix}`;

  const isEditingCommentPreview = Boolean(
    editingCommentId !== null &&
      isCommentComposerOpen &&
      commentComposerAnchor !== null,
  );
  const isCommentPositionDraggable = isCommentComposerOpen && commentComposerAnchor !== null;

  const clearCommentDismissTimer = useCallback(() => {
    if (commentDismissTimeoutRef.current !== null) {
      window.clearTimeout(commentDismissTimeoutRef.current);
      commentDismissTimeoutRef.current = null;
    }
  }, []);

  const clearCommentDraft = useCallback(() => {
    clearCommentDismissTimer();
    for (const previewUrl of commentDraftPreviewUrlsRef.current) {
      URL.revokeObjectURL(previewUrl);
    }
    commentDraftPreviewUrlsRef.current = [];
    setCommentDraftFiles([]);
    setCommentDraftBody('');
    setRemovedCommentAttachmentIds([]);
    setEditingCommentId(null);
    setEditingCommentVersion(null);
    setCommentError(null);
    setCommentConflict(false);
  }, [clearCommentDismissTimer]);

  useEffect(() => {
    commentDraftPreviewUrlsRef.current = commentDraftFiles.map((draftFile) => draftFile.previewUrl);
  }, [commentDraftFiles]);

  useEffect(() => {
    commentPositionAnchorRef.current = commentComposerAnchor;
  }, [commentComposerAnchor]);

  useEffect(
    () => () => {
      clearCommentDismissTimer();
      for (const previewUrl of commentDraftPreviewUrlsRef.current) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [clearCommentDismissTimer],
  );

  const refreshMapComments = useCallback(async () => {
    const fileId = selectedFile?.id;
    const requestToken = ++commentsRequestTokenRef.current;
    if (!fileId) {
      setMapComments([]);
      setCommentPermission(EMPTY_COMMENT_PERMISSION);
      setActiveCommentId(null);
      setPinnedCommentId(null);
      setCommentCalloutPosition(null);
      setCommentsLoading(false);
      return;
    }
    setMapComments([]);
    setCommentPermission(EMPTY_COMMENT_PERMISSION);
    setActiveCommentId(null);
    setPinnedCommentId(null);
    setCommentCalloutPosition(null);
    setCommentsLoading(true);
    try {
      const result = await listMapComments(fileId);
      if (requestToken !== commentsRequestTokenRef.current) return;
      setMapComments(result.comments);
      setCommentPermission(result.permission);
    } catch (error) {
      if (requestToken !== commentsRequestTokenRef.current) return;
      setMapComments([]);
      setActiveCommentId(null);
      setPinnedCommentId(null);
      if (error instanceof CommentApiError && (error.status === 403 || error.status === 404)) {
        setCommentPermission(EMPTY_COMMENT_PERMISSION);
        // Permission may have changed while this panel was open. Remove every
        // comment surface instead of leaving a stale draft or callout visible.
        setIsCommentListOpen(false);
        setIsCommentComposerOpen(false);
        setCommentComposerAnchor(null);
        setExpandedCommentAttachment(null);
        setResumeCommentComposerAfterPlacement(false);
        mapInputModeTransitionRef.current?.('idle');
        clearCommentDraft();
      } else {
        setCommentPermission(EMPTY_COMMENT_PERMISSION);
        developmentLog.warn('[MapPanel] unable to load comments', error);
      }
    } finally {
      if (requestToken === commentsRequestTokenRef.current) {
        setCommentsLoading(false);
      }
    }
  }, [clearCommentDraft, selectedFile?.id]);

  useEffect(() => {
    void refreshMapComments();
  }, [refreshMapComments]);

  useEffect(() => {
    const handleCommentsChanged = (event_: Event) => {
      const detail = (event_ as CustomEvent<{ fileId?: number; sourceId?: string }>).detail;
      if (detail?.fileId === selectedFile?.id && detail.sourceId !== idSuffix) {
        void refreshMapComments();
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'hidden') {
        void refreshMapComments();
      }
    };
    window.addEventListener(MAP_COMMENTS_CHANGED_EVENT, handleCommentsChanged);
    window.addEventListener(MAP_COMMENT_PERMISSION_CHANGED_EVENT, refreshWhenVisible);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.removeEventListener(MAP_COMMENTS_CHANGED_EVENT, handleCommentsChanged);
      window.removeEventListener(MAP_COMMENT_PERMISSION_CHANGED_EVENT, refreshWhenVisible);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [idSuffix, refreshMapComments, selectedFile?.id]);

  const commentRelativeTimes = useMemo(() => {
    const relativeTimes = new Map<number, string>();
    for (const comment of mapComments) {
      if (comment.anchorType !== 'race_time') continue;
      const relativeTime = getMapCommentRelativeTime({
        anchorUtc: comment.anchorUtc,
        track: safeTrackData,
      });
      if (relativeTime !== null) {
        relativeTimes.set(comment.id, relativeTime);
      }
    }
    return relativeTimes;
  }, [mapComments, safeTrackData]);

  // Temporal comments are meaningful only while their recorded sample is
  // part of the active track selection. Spatial comments remain visible.
  const visibleMapComments = useMemo(
    () =>
      mapComments.filter(
        (comment) =>
          comment.anchorType !== 'race_time' || commentRelativeTimes.has(comment.id),
      ),
    [commentRelativeTimes, mapComments],
  );

  const activeComment = useMemo(
    () => visibleMapComments.find((comment) => comment.id === activeCommentId) ?? null,
    [activeCommentId, visibleMapComments],
  );
  const commentCalloutConnectorPath = useMemo(() => {
    if (!commentCalloutPosition) return null;
    const { markerX, markerY, connectorX, connectorY } = commentCalloutPosition;
    const approachX =
      connectorX > markerX
        ? connectorX - MAP_COMMENT_CALLOUT_CONNECTOR_HORIZONTAL_LEAD_PX
        : connectorX + MAP_COMMENT_CALLOUT_CONNECTOR_HORIZONTAL_LEAD_PX;
    return `M ${markerX} ${markerY} L ${approachX} ${connectorY} H ${connectorX}`;
  }, [commentCalloutPosition]);
  const editingComment = useMemo(
    () => visibleMapComments.find((comment) => comment.id === editingCommentId) ?? null,
    [editingCommentId, visibleMapComments],
  );

  const composerRelativeTime = useMemo(
    () =>
      commentComposerAnchor?.anchorType === 'race_time'
        ? getMapCommentRelativeTime({
            anchorUtc: commentComposerAnchor.anchorUtc,
            track: safeTrackData,
          })
        : null,
    [commentComposerAnchor, safeTrackData],
  );

  const positionCommentCallout = useCallback(
    (comment: MapComment): void => {
      const map = mapInstance.current;
      if (!map) return;
      const projected = map.project([comment.anchorLon, comment.anchorLat]);
      const container = map.getContainer();
      const calloutWidth = Math.min(
        MAP_COMMENT_CALLOUT_WIDTH_PX,
        Math.max(0, container.clientWidth - MAP_COMMENT_CALLOUT_MARGIN_PX * 2),
      );
      const calloutHeight = MAP_COMMENT_CALLOUT_HEIGHT_PX;
      const maximumLeft = Math.max(
        MAP_COMMENT_CALLOUT_MARGIN_PX,
        container.clientWidth - calloutWidth - MAP_COMMENT_CALLOUT_MARGIN_PX,
      );
      const placeOnRight =
        projected.x + MAP_COMMENT_CALLOUT_GAP_PX + calloutWidth <=
        container.clientWidth - MAP_COMMENT_CALLOUT_MARGIN_PX;
      const left = placeOnRight
        ? Math.max(
            MAP_COMMENT_CALLOUT_MARGIN_PX,
            Math.min(projected.x + MAP_COMMENT_CALLOUT_GAP_PX, maximumLeft),
          )
        : Math.max(
            MAP_COMMENT_CALLOUT_MARGIN_PX,
            Math.min(projected.x - MAP_COMMENT_CALLOUT_GAP_PX - calloutWidth, maximumLeft),
          );
      const top = Math.max(
        MAP_COMMENT_CALLOUT_MARGIN_PX,
        Math.min(
          projected.y - MAP_COMMENT_CALLOUT_MARKER_OFFSET_PX,
          container.clientHeight - calloutHeight - MAP_COMMENT_CALLOUT_MARGIN_PX,
        ),
      );
      setCommentCalloutPosition({
        left,
        top,
        markerX: projected.x,
        markerY: projected.y,
        connectorX: placeOnRight ? left : left + calloutWidth,
        connectorY: top + MAP_COMMENT_CALLOUT_CONNECTOR_HEADER_OFFSET_PX,
      });
    },
    [mapInstance],
  );

  const openCommentCallout = useCallback(
    (comment: MapComment, pinned = false) => {
      clearCommentDismissTimer();
      positionCommentCallout(comment);
      setActiveCommentId(comment.id);
      setPinnedCommentId(pinned ? comment.id : null);
    },
    [clearCommentDismissTimer, positionCommentCallout],
  );

  const closeCommentCalloutSoon = useCallback(() => {
    if (pinnedCommentId !== null) return;
    clearCommentDismissTimer();
    commentDismissTimeoutRef.current = window.setTimeout(() => {
      setActiveCommentId(null);
      setCommentCalloutPosition(null);
      commentDismissTimeoutRef.current = null;
    }, 180);
  }, [clearCommentDismissTimer, pinnedCommentId]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !activeComment) return;
    const reposition = () => positionCommentCallout(activeComment);
    reposition();
    map.on('move', reposition);
    map.on('zoom', reposition);
    return () => {
      map.off('move', reposition);
      map.off('zoom', reposition);
    };
  }, [activeComment, mapInstance, positionCommentCallout]);

  const commentFeatureCollection = useMemo(
    () =>
      buildMapCommentPinFeatureCollection({
        comments:
          isEditingCommentPreview && editingCommentId !== null && commentComposerAnchor
            ? applyMapCommentAnchorPreview({
                comments: visibleMapComments,
                commentId: editingCommentId,
                anchor: commentComposerAnchor,
            })
            : visibleMapComments,
        editingCommentId: isEditingCommentPreview ? editingCommentId : null,
      }),
    [
      commentComposerAnchor,
      editingCommentId,
      isEditingCommentPreview,
      visibleMapComments,
    ],
  );

  const commentDraftPreviewFeatureCollection = useMemo(
    () =>
      buildMapCommentDraftPreview({
        anchor: commentComposerAnchor,
        isComposerOpen: isCommentComposerOpen,
        isEditing: editingCommentId !== null,
      }),
    [commentComposerAnchor, editingCommentId, isCommentComposerOpen],
  );

  // Run before either comment source is updated. During hot reload Safari can
  // keep a prior symbol layer attached to that source and fail while placing
  // it; comments deliberately use circle layers only now.
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    const removeObsoleteCommentGlyphLayers = () => {
      for (const layerId of [
        `map-comments-glyph-${idSuffix}`,
        `map-comment-draft-preview-glyph-${idSuffix}`,
      ]) {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      }
    };
    return subscribeWhenStyleReady(map, removeObsoleteCommentGlyphLayers);
  }, [idSuffix, mapInstance, mapReadyToken]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    const renderCommentPins = () => {
      if (!map.isStyleLoaded()) return;
      const haloPaint = {
        'circle-radius': MAP_COMMENT_HALO_RADIUS_PX,
        'circle-color': commentMarkerPalette.ring,
        'circle-opacity': MAP_COMMENT_HALO_OPACITY,
      };
      const pinPaint = {
        'circle-radius': MAP_COMMENT_PIN_RADIUS_PX,
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-opacity': 1,
        'circle-stroke-width': MAP_COMMENT_PIN_STROKE_WIDTH,
        'circle-stroke-color': commentMarkerPalette.ring,
        'circle-stroke-opacity': 0.98,
      };
      const editRingPaint = {
        'circle-radius': MAP_COMMENT_EDIT_RING_RADIUS_PX,
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-opacity': 1,
        'circle-stroke-width': MAP_COMMENT_EDIT_RING_STROKE_WIDTH,
        'circle-stroke-color': commentMarkerPalette.editRing,
        'circle-stroke-opacity': 0.96,
      };
      const source = map.getSource(commentsSourceId) as mapboxgl.GeoJSONSource | undefined;
      if (source) {
        source.setData(commentFeatureCollection);
      } else {
        map.addSource(commentsSourceId, { type: 'geojson', data: commentFeatureCollection });
      }
      if (map.getLayer(commentsHaloLayerId)) {
        map.setPaintProperty(commentsHaloLayerId, 'circle-radius', haloPaint['circle-radius']);
        map.setPaintProperty(commentsHaloLayerId, 'circle-color', haloPaint['circle-color']);
        map.setPaintProperty(commentsHaloLayerId, 'circle-opacity', haloPaint['circle-opacity']);
      } else {
        map.addLayer({
          id: commentsHaloLayerId,
          type: 'circle',
          source: commentsSourceId,
          paint: haloPaint,
        });
      }
      if (map.getLayer(commentsEditRingLayerId)) {
        map.setFilter(commentsEditRingLayerId, ['==', 'isEditing', true]);
        map.setPaintProperty(commentsEditRingLayerId, 'circle-radius', editRingPaint['circle-radius']);
        map.setPaintProperty(commentsEditRingLayerId, 'circle-color', editRingPaint['circle-color']);
        map.setPaintProperty(commentsEditRingLayerId, 'circle-opacity', editRingPaint['circle-opacity']);
        map.setPaintProperty(commentsEditRingLayerId, 'circle-stroke-width', editRingPaint['circle-stroke-width']);
        map.setPaintProperty(commentsEditRingLayerId, 'circle-stroke-color', editRingPaint['circle-stroke-color']);
        map.setPaintProperty(commentsEditRingLayerId, 'circle-stroke-opacity', editRingPaint['circle-stroke-opacity']);
      } else {
        map.addLayer({
          id: commentsEditRingLayerId,
          type: 'circle',
          source: commentsSourceId,
          filter: ['==', 'isEditing', true],
          paint: editRingPaint,
        });
      }
      if (map.getLayer(commentsPinLayerId)) {
        map.setPaintProperty(commentsPinLayerId, 'circle-radius', pinPaint['circle-radius']);
        map.setPaintProperty(commentsPinLayerId, 'circle-color', pinPaint['circle-color']);
        map.setPaintProperty(commentsPinLayerId, 'circle-opacity', pinPaint['circle-opacity']);
        map.setPaintProperty(commentsPinLayerId, 'circle-stroke-width', pinPaint['circle-stroke-width']);
        map.setPaintProperty(commentsPinLayerId, 'circle-stroke-color', pinPaint['circle-stroke-color']);
        map.setPaintProperty(commentsPinLayerId, 'circle-stroke-opacity', pinPaint['circle-stroke-opacity']);
      } else {
        map.addLayer({
          id: commentsPinLayerId,
          type: 'circle',
          source: commentsSourceId,
          paint: pinPaint,
        });
      }
      const hitPaint = {
        'circle-radius': MAP_COMMENT_HIT_RADIUS_PX,
        'circle-opacity': 0,
      };
      if (map.getLayer(commentsHitLayerId)) {
        map.setPaintProperty(commentsHitLayerId, 'circle-radius', hitPaint['circle-radius']);
        map.setPaintProperty(commentsHitLayerId, 'circle-opacity', hitPaint['circle-opacity']);
      } else {
        map.addLayer({
          id: commentsHitLayerId,
          type: 'circle',
          source: commentsSourceId,
          paint: hitPaint,
        });
      }
      const visibility = commentsVisible && commentPermission.canViewComments ? 'visible' : 'none';
      for (const layerId of [
        commentsHaloLayerId,
        commentsEditRingLayerId,
        commentsPinLayerId,
        commentsHitLayerId,
      ]) {
        if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    };
    return subscribeWhenStyleReady(map, renderCommentPins);
  }, [
    commentFeatureCollection,
    commentPermission.canViewComments,
    commentMarkerPalette,
    commentsEditRingLayerId,
    commentsHaloLayerId,
    commentsHitLayerId,
    commentsPinLayerId,
    commentsSourceId,
    commentsVisible,
    mapInstance,
    mapReadyToken,
  ]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    const renderCommentDraftPreview = () => {
      if (!map.isStyleLoaded()) return;
      const source = map.getSource(commentDraftPreviewSourceId) as mapboxgl.GeoJSONSource | undefined;
      if (source) {
        source.setData(commentDraftPreviewFeatureCollection);
      } else {
        map.addSource(commentDraftPreviewSourceId, {
          type: 'geojson',
          data: commentDraftPreviewFeatureCollection,
        });
      }

      const haloPaint = {
        'circle-radius': MAP_COMMENT_HALO_RADIUS_PX,
        'circle-color': commentMarkerPalette.ring,
        'circle-opacity': MAP_COMMENT_HALO_OPACITY,
      };
      const pinPaint = {
        'circle-radius': MAP_COMMENT_PIN_RADIUS_PX,
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-opacity': 1,
        'circle-stroke-width': MAP_COMMENT_PIN_STROKE_WIDTH,
        'circle-stroke-color': commentMarkerPalette.ring,
        'circle-stroke-opacity': 0.98,
      };
      const editRingPaint = {
        'circle-radius': MAP_COMMENT_EDIT_RING_RADIUS_PX,
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-opacity': 1,
        'circle-stroke-width': MAP_COMMENT_EDIT_RING_STROKE_WIDTH,
        'circle-stroke-color': commentMarkerPalette.editRing,
        'circle-stroke-opacity': 0.96,
      };
      if (map.getLayer(commentDraftPreviewHaloLayerId)) {
        map.setPaintProperty(commentDraftPreviewHaloLayerId, 'circle-radius', haloPaint['circle-radius']);
        map.setPaintProperty(commentDraftPreviewHaloLayerId, 'circle-color', haloPaint['circle-color']);
        map.setPaintProperty(commentDraftPreviewHaloLayerId, 'circle-opacity', haloPaint['circle-opacity']);
      } else {
        map.addLayer({
          id: commentDraftPreviewHaloLayerId,
          type: 'circle',
          source: commentDraftPreviewSourceId,
          paint: haloPaint,
        });
      }
      if (map.getLayer(commentDraftPreviewRingLayerId)) {
        map.setFilter(commentDraftPreviewRingLayerId, ['==', 'isEditing', true]);
        map.setPaintProperty(commentDraftPreviewRingLayerId, 'circle-radius', editRingPaint['circle-radius']);
        map.setPaintProperty(commentDraftPreviewRingLayerId, 'circle-color', editRingPaint['circle-color']);
        map.setPaintProperty(commentDraftPreviewRingLayerId, 'circle-opacity', editRingPaint['circle-opacity']);
        map.setPaintProperty(commentDraftPreviewRingLayerId, 'circle-stroke-width', editRingPaint['circle-stroke-width']);
        map.setPaintProperty(commentDraftPreviewRingLayerId, 'circle-stroke-color', editRingPaint['circle-stroke-color']);
        map.setPaintProperty(commentDraftPreviewRingLayerId, 'circle-stroke-opacity', editRingPaint['circle-stroke-opacity']);
      } else {
        map.addLayer({
          id: commentDraftPreviewRingLayerId,
          type: 'circle',
          source: commentDraftPreviewSourceId,
          filter: ['==', 'isEditing', true],
          paint: editRingPaint,
        });
      }
      if (map.getLayer(commentDraftPreviewPinLayerId)) {
        map.setPaintProperty(commentDraftPreviewPinLayerId, 'circle-radius', pinPaint['circle-radius']);
        map.setPaintProperty(commentDraftPreviewPinLayerId, 'circle-color', pinPaint['circle-color']);
        map.setPaintProperty(commentDraftPreviewPinLayerId, 'circle-opacity', pinPaint['circle-opacity']);
        map.setPaintProperty(commentDraftPreviewPinLayerId, 'circle-stroke-width', pinPaint['circle-stroke-width']);
        map.setPaintProperty(commentDraftPreviewPinLayerId, 'circle-stroke-color', pinPaint['circle-stroke-color']);
        map.setPaintProperty(commentDraftPreviewPinLayerId, 'circle-stroke-opacity', pinPaint['circle-stroke-opacity']);
      } else {
        map.addLayer({
          id: commentDraftPreviewPinLayerId,
          type: 'circle',
          source: commentDraftPreviewSourceId,
          paint: pinPaint,
        });
      }
      const hitPaint = {
        'circle-radius': MAP_COMMENT_HIT_RADIUS_PX,
        'circle-opacity': 0,
      };
      if (map.getLayer(commentDraftPreviewHitLayerId)) {
        map.setPaintProperty(commentDraftPreviewHitLayerId, 'circle-radius', hitPaint['circle-radius']);
        map.setPaintProperty(commentDraftPreviewHitLayerId, 'circle-opacity', hitPaint['circle-opacity']);
      } else {
        map.addLayer({
          id: commentDraftPreviewHitLayerId,
          type: 'circle',
          source: commentDraftPreviewSourceId,
          paint: hitPaint,
        });
      }
      const isVisible =
        !isEditingCommentPreview &&
        commentPermission.canManageComments &&
        commentDraftPreviewFeatureCollection.features.length > 0;
      for (const layerId of [
        commentDraftPreviewHaloLayerId,
        commentDraftPreviewRingLayerId,
        commentDraftPreviewPinLayerId,
        commentDraftPreviewHitLayerId,
      ]) {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none');
        }
      }
      if (isVisible) {
        safeMoveLayer(map, commentDraftPreviewHaloLayerId);
        safeMoveLayer(map, commentDraftPreviewRingLayerId);
        safeMoveLayer(map, commentDraftPreviewPinLayerId);
        safeMoveLayer(map, commentDraftPreviewHitLayerId);
      }
    };

    return subscribeWhenStyleReady(map, renderCommentDraftPreview);
  }, [
    commentDraftPreviewFeatureCollection,
    commentMarkerPalette,
    commentDraftPreviewHaloLayerId,
    commentDraftPreviewHitLayerId,
    commentDraftPreviewPinLayerId,
    commentDraftPreviewRingLayerId,
    commentDraftPreviewSourceId,
    commentPermission.canManageComments,
    isEditingCommentPreview,
    mapInstance,
    mapReadyToken,
  ]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    const removeObsoleteEditPreview = () => {
      for (const layerId of [
        obsoleteCommentEditPreviewHitLayerId,
        obsoleteCommentEditPreviewPinLayerId,
        obsoleteCommentEditPreviewRingLayerId,
        obsoleteCommentEditPreviewHaloLayerId,
      ]) {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      }
      if (map.getSource(obsoleteCommentEditPreviewSourceId)) {
        map.removeSource(obsoleteCommentEditPreviewSourceId);
      }
    };
    return subscribeWhenStyleReady(map, removeObsoleteEditPreview);
  }, [
    idSuffix,
    mapInstance,
    mapReadyToken,
    obsoleteCommentEditPreviewHaloLayerId,
    obsoleteCommentEditPreviewHitLayerId,
    obsoleteCommentEditPreviewPinLayerId,
    obsoleteCommentEditPreviewRingLayerId,
    obsoleteCommentEditPreviewSourceId,
  ]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !isCommentPositionDraggable || !commentComposerAnchor) {
      return;
    }

    let isDragging = false;
    let wasDragPanEnabled = false;
    let latestPointer: { lat: number; lon: number } | null = null;
    let latestAnchor = commentPositionAnchorRef.current ?? commentComposerAnchor;
    const dragHitLayerId =
      editingCommentId === null ? commentDraftPreviewHitLayerId : commentsHitLayerId;
    let animationFrame: number | null = null;

    const updatePreviewAnchor = (anchor: MapCommentDraftAnchor) => {
      if (editingCommentId === null) {
        const source = map.getSource(commentDraftPreviewSourceId) as
          | mapboxgl.GeoJSONSource
          | undefined;
        source?.setData(
          buildMapCommentDraftPreview({
            anchor,
            isComposerOpen: true,
            isEditing: false,
          }),
        );
        return;
      }
      const source = map.getSource(commentsSourceId) as mapboxgl.GeoJSONSource | undefined;
      source?.setData(
        buildMapCommentPinFeatureCollection({
          comments: applyMapCommentAnchorPreview({
            comments: visibleMapComments,
            commentId: editingCommentId,
            anchor,
          }),
          editingCommentId,
        }),
      );
    };

    const snapPointerToTrack = (pointer: { lat: number; lon: number }) => {
      const nextAnchor = resolveMapCommentAnchorAtPointer({
        map,
        lat: pointer.lat,
        lon: pointer.lon,
        // A point that lands on a recorded sample is a race-time comment,
        // even when it started as a free map point. Moving away falls back to
        // a spatial anchor in resolveMapCommentAnchorAtPointer.
        anchorType: 'race_time',
        track: safeTrackData,
      });
      latestAnchor = nextAnchor;
      commentPositionAnchorRef.current = nextAnchor;
      updatePreviewAnchor(nextAnchor);
    };

    const queuePreviewUpdate = (event: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => {
      latestPointer = { lat: event.lngLat.lat, lon: event.lngLat.lng };
      if (animationFrame !== null) return;
      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        if (latestPointer) snapPointerToTrack(latestPointer);
      });
    };

    const stopDragging = (commit: boolean) => {
      if (!isDragging) return;
      isDragging = false;
      map.off('mousemove', queuePreviewUpdate);
      map.off('touchmove', queuePreviewUpdate);
      map.off('mouseup', handleDragEnd);
      map.off('touchend', handleDragEnd);
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
      if (latestPointer) snapPointerToTrack(latestPointer);
      if (wasDragPanEnabled) map.dragPan.enable();
      map.getCanvas().style.cursor = '';
      if (commit) {
        setCommentComposerAnchor(latestAnchor);
        setCommentError(null);
      }
    };

    const handleDragEnd = () => stopDragging(true);

    const startDragging = (event: mapboxgl.MapLayerMouseEvent | mapboxgl.MapLayerTouchEvent) => {
      if (isDragging) return;
      if (editingCommentId !== null) {
        const commentId = Number(event.features?.[0]?.properties?.commentId);
        if (commentId !== editingCommentId) return;
      }
      event.preventDefault();
      event.originalEvent?.stopPropagation?.();
      isDragging = true;
      latestAnchor = commentPositionAnchorRef.current ?? commentComposerAnchor;
      wasDragPanEnabled = map.dragPan.isEnabled();
      if (wasDragPanEnabled) map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';
      queuePreviewUpdate(event);
      map.on('mousemove', queuePreviewUpdate);
      map.on('touchmove', queuePreviewUpdate);
      map.once('mouseup', handleDragEnd);
      map.once('touchend', handleDragEnd);
    };

    const showGrabCursor = (event: mapboxgl.MapLayerMouseEvent | mapboxgl.MapLayerTouchEvent) => {
      if (isDragging) return;
      if (editingCommentId === null) {
        map.getCanvas().style.cursor = 'grab';
        return;
      }
      const commentId = Number(event.features?.[0]?.properties?.commentId);
      if (commentId === editingCommentId) map.getCanvas().style.cursor = 'grab';
    };
    const clearGrabCursor = () => {
      if (!isDragging) map.getCanvas().style.cursor = '';
    };

    let dragHandlersAttached = false;
    const detachDragHandlers = () => {
      if (!dragHandlersAttached) return;
      map.off('mousedown', dragHitLayerId, startDragging);
      map.off('touchstart', dragHitLayerId, startDragging);
      map.off('mouseenter', dragHitLayerId, showGrabCursor);
      map.off('mouseleave', dragHitLayerId, clearGrabCursor);
      dragHandlersAttached = false;
    };
    const attachDragHandlers = () => {
      if (!map.getLayer(dragHitLayerId) || dragHandlersAttached) return;
      map.on('mousedown', dragHitLayerId, startDragging);
      map.on('touchstart', dragHitLayerId, startDragging);
      map.on('mouseenter', dragHitLayerId, showGrabCursor);
      map.on('mouseleave', dragHitLayerId, clearGrabCursor);
      dragHandlersAttached = true;
    };
    const unsubscribeStyleReady = subscribeWhenStyleReady(map, attachDragHandlers);
    return () => {
      stopDragging(false);
      unsubscribeStyleReady();
      detachDragHandlers();
      clearGrabCursor();
    };
  }, [
    commentComposerAnchor,
    commentDraftPreviewHitLayerId,
    commentDraftPreviewSourceId,
    commentsHitLayerId,
    commentsSourceId,
    editingCommentId,
    isCommentPositionDraggable,
    mapInstance,
    safeTrackData,
    visibleMapComments,
  ]);

  useEffect(() => {
    const map = mapInstance.current;
    if (
      !map ||
      mapInputMode !== 'idle' ||
      isCommentComposerOpen ||
      !commentsVisible ||
      !commentPermission.canViewComments
    ) {
      return;
    }
    const commentFromEvent = (event: mapboxgl.MapMouseEvent): MapComment | null => {
      if (!map.getLayer(commentsHitLayerId)) return null;
      const feature = map.queryRenderedFeatures(event.point, { layers: [commentsHitLayerId] })[0];
      const id = Number(feature?.properties?.commentId);
      return Number.isInteger(id)
        ? visibleMapComments.find((comment) => comment.id === id) ?? null
        : null;
    };
    const onMouseMove = (event: mapboxgl.MapMouseEvent) => {
      const comment = commentFromEvent(event);
      map.getCanvas().style.cursor = comment ? 'pointer' : '';
      if (comment) {
        if (activeCommentId !== comment.id || pinnedCommentId === null) {
          openCommentCallout(comment, false);
        }
      } else {
        closeCommentCalloutSoon();
      }
    };
    const onClick = (event: mapboxgl.MapMouseEvent) => {
      const comment = commentFromEvent(event);
      if (comment) {
        openCommentCallout(comment, true);
      } else if (pinnedCommentId !== null) {
        setPinnedCommentId(null);
        setActiveCommentId(null);
        setCommentCalloutPosition(null);
      }
    };
    map.on('mousemove', onMouseMove);
    map.on('click', onClick);
    return () => {
      map.getCanvas().style.cursor = '';
      map.off('mousemove', onMouseMove);
      map.off('click', onClick);
    };
  }, [
    activeCommentId,
    closeCommentCalloutSoon,
    commentPermission.canViewComments,
    commentsHitLayerId,
    commentsVisible,
    isCommentComposerOpen,
    mapInputMode,
    mapInstance,
    openCommentCallout,
    pinnedCommentId,
    visibleMapComments,
  ]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || mapInputMode !== 'comment-placement') return;
    const previousCursor = map.getCanvas().style.cursor;
    map.getCanvas().style.cursor = 'crosshair';
    const placeComment = (event: mapboxgl.MapMouseEvent) => {
      setCommentComposerAnchor(
        resolveMapCommentAnchorAtPointer({
          map,
          lat: event.lngLat.lat,
          lon: event.lngLat.lng,
          anchorType: 'race_time',
          track: safeTrackData,
        }),
      );
      mapInputModeTransitionRef.current?.('idle');
      setResumeCommentComposerAfterPlacement(false);
      setIsCommentComposerOpen(true);
      setCommentError(null);
    };
    map.once('click', placeComment);
    return () => {
      map.getCanvas().style.cursor = previousCursor;
      map.off('click', placeComment);
    };
  }, [mapInputMode, mapInstance, safeTrackData]);

  const beginMapCommentPlacement = useCallback(() => {
    setResumeCommentComposerAfterPlacement(
      isCommentComposerOpen && commentComposerAnchor !== null,
    );
    setIsCommentComposerOpen(false);
    setIsCommentListOpen(false);
    mapInputModeTransitionRef.current?.('comment-placement');
    setCommentError(null);
  }, [commentComposerAnchor, isCommentComposerOpen]);

  const cancelMapCommentPlacement = useCallback(() => {
    mapInputModeTransitionRef.current?.('idle');
    if (resumeCommentComposerAfterPlacement && commentComposerAnchor) {
      setIsCommentComposerOpen(true);
    } else {
      clearCommentDraft();
      setCommentComposerAnchor(null);
    }
    setResumeCommentComposerAfterPlacement(false);
  }, [clearCommentDraft, commentComposerAnchor, resumeCommentComposerAfterPlacement]);

  useEffect(() => {
    if (commentPlacementMode !== 'map_point') return;
    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      cancelMapCommentPlacement();
    };
    window.addEventListener('keydown', cancelOnEscape);
    return () => window.removeEventListener('keydown', cancelOnEscape);
  }, [cancelMapCommentPlacement, commentPlacementMode]);

  const openCommentEditor = useCallback(
    (comment: MapComment) => {
      clearCommentDraft();
      mapInputModeTransitionRef.current?.('idle');
      setIsCommentListOpen(false);
      // Editing is spatial: keep the same existing pin visible and draggable
      // even if the user had previously hidden the comments layer.
      setCommentsVisible(true);
      setEditingCommentId(comment.id);
      setEditingCommentVersion(comment.version);
      setCommentDraftBody(comment.body);
      setCommentComposerAnchor({
        anchorType: comment.anchorType,
        anchorUtc: comment.anchorUtc,
        anchorLat: comment.anchorLat,
        anchorLon: comment.anchorLon,
      });
      setIsCommentComposerOpen(true);
      setResumeCommentComposerAfterPlacement(false);
      setActiveCommentId(null);
      setPinnedCommentId(null);
      setCommentCalloutPosition(null);
      setCommentError(null);
    },
    [clearCommentDraft],
  );

  const closeCommentComposer = useCallback(() => {
    clearCommentDraft();
    setCommentComposerAnchor(null);
    setIsCommentComposerOpen(false);
    setResumeCommentComposerAfterPlacement(false);
    mapInputModeTransitionRef.current?.('idle');
  }, [clearCommentDraft]);

  const hasOpenCommentSurface = isCommentComposerOpen || commentPlacementMode === 'map_point';
  const isAddingComment = editingCommentId === null && hasOpenCommentSurface;
  const toggleCommentComposer = useCallback(() => {
    if (hasOpenCommentSurface) {
      closeCommentComposer();
      return;
    }
    beginMapCommentPlacement();
  }, [beginMapCommentPlacement, closeCommentComposer, hasOpenCommentSurface]);

  useEffect(() => {
    // A composer/list belongs to the selected file. Closing it on file
    // changes prevents an anchor or draft from being submitted to a new
    // track while the previous comments request is still settling.
    setIsCommentListOpen(false);
    setIsCommentComposerOpen(false);
    setCommentComposerAnchor(null);
    setActiveCommentId(null);
    setPinnedCommentId(null);
    setCommentCalloutPosition(null);
    setExpandedCommentAttachment(null);
    setResumeCommentComposerAfterPlacement(false);
    clearCommentDraft();
    mapInputModeTransitionRef.current?.('idle');
  }, [clearCommentDraft, selectedFile?.id]);

  const toggleCommentList = useCallback(() => {
    if (isCommentListOpen) {
      setIsCommentListOpen(false);
      return;
    }
    // The list is a read-focused surface. Close the composer and any map
    // input tool first so a click on a list row cannot accidentally place,
    // measure, or optimize a point behind the panel.
    closeCommentComposer();
    mapInputModeTransitionRef.current?.('idle');
    setIsCommentListOpen(true);
  }, [closeCommentComposer, isCommentListOpen]);

  const addCommentDraftFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const candidates = [...files].filter(
      (file) =>
        ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) && file.size > 0 && file.size <= 10 * 1024 * 1024,
    );
    setCommentDraftFiles((previous) => {
      const existingImageCount = editingComment
        ? editingComment.attachments.filter(
            (attachment) => !removedCommentAttachmentIds.includes(attachment.id),
          ).length
        : 0;
      const remainingSlots = Math.max(0, 5 - previous.length - existingImageCount);
      const next = candidates.slice(0, remainingSlots).map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      return [...previous, ...next];
    });
  }, [editingComment, removedCommentAttachmentIds]);

  const removeCommentDraftFile = useCallback((previewUrl: string) => {
    setCommentDraftFiles((previous) => {
      const target = previous.find((draftFile) => draftFile.previewUrl === previewUrl);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return previous.filter((draftFile) => draftFile.previewUrl !== previewUrl);
    });
  }, []);

  const reloadConflictedComment = useCallback(() => {
    if (!editingComment) {
      closeCommentComposer();
      return;
    }
    for (const previewUrl of commentDraftPreviewUrlsRef.current) {
      URL.revokeObjectURL(previewUrl);
    }
    commentDraftPreviewUrlsRef.current = [];
    setCommentDraftFiles([]);
    setCommentDraftBody(editingComment.body);
    setRemovedCommentAttachmentIds([]);
    setEditingCommentVersion(editingComment.version);
    setCommentConflict(false);
    setCommentError(null);
  }, [closeCommentComposer, editingComment]);

  const submitComment = useCallback(async () => {
    const fileId = selectedFile?.id;
    if (!fileId || !commentComposerAnchor) return;
    const body = commentDraftBody.trim();
    if (body.length === 0) {
      setCommentError(t('comments.bodyRequired', 'Escribe un comentario antes de guardarlo.'));
      return;
    }
    if (editingCommentId !== null && (!editingComment || editingCommentVersion === null)) {
      setCommentError(t('comments.missingAfterRefresh', 'El comentario ya no existe. Cierra y vuelve a cargar la lista.'));
      return;
    }
    if (commentConflict) {
      setCommentError(t('comments.conflictReloadRequired', 'Recarga el comentario antes de guardar tus cambios.'));
      return;
    }
    setCommentSubmitting(true);
    setCommentError(null);
    try {
      let saved: MapComment;
      if (editingComment) {
        saved = await updateMapComment({
          id: editingComment.id,
          version: editingCommentVersion as number,
          body,
          anchor: hasMapCommentAnchorChanged(editingComment, commentComposerAnchor)
            ? commentComposerAnchor
            : undefined,
          attachments: commentDraftFiles.map((draftFile) => draftFile.file),
          removeAttachmentIds: removedCommentAttachmentIds,
        });
        setMapComments((previous) =>
          previous.map((comment) => (comment.id === saved.id ? saved : comment)),
        );
      } else {
        saved = await createMapComment({
          fileId,
          anchorType: commentComposerAnchor.anchorType,
          anchorUtc: commentComposerAnchor.anchorUtc,
          anchorLat: commentComposerAnchor.anchorLat,
          anchorLon: commentComposerAnchor.anchorLon,
          body,
          attachments: commentDraftFiles.map((draftFile) => draftFile.file),
        });
        setMapComments((previous) => [...previous, saved]);
      }
      window.dispatchEvent(
        new CustomEvent(MAP_COMMENTS_CHANGED_EVENT, { detail: { fileId, sourceId: idSuffix } }),
      );
      closeCommentComposer();
      openCommentCallout(saved, true);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('comments.saveError', 'No se pudo guardar el comentario.');
      setCommentError(message);
      if (error instanceof CommentApiError && error.status === 409) {
        // Keep the original version. Refreshing must never turn the next click
        // into a silent overwrite of the other collaborator's work.
        setCommentConflict(true);
        void refreshMapComments();
      }
    } finally {
      setCommentSubmitting(false);
    }
  }, [
    closeCommentComposer,
    commentComposerAnchor,
    commentConflict,
    commentDraftBody,
    commentDraftFiles,
    editingComment,
    editingCommentId,
    editingCommentVersion,
    idSuffix,
    openCommentCallout,
    refreshMapComments,
    removedCommentAttachmentIds,
    selectedFile?.id,
    t,
  ]);

  const removeComment = useCallback(
    async (comment: MapComment) => {
      if (!window.confirm(t('comments.deleteConfirm', '¿Eliminar este comentario y sus imágenes?'))) return;
      try {
        await deleteMapComment(comment.id);
        setMapComments((previous) => previous.filter((entry) => entry.id !== comment.id));
        setActiveCommentId(null);
        setPinnedCommentId(null);
        setCommentCalloutPosition(null);
        window.dispatchEvent(
          new CustomEvent(MAP_COMMENTS_CHANGED_EVENT, {
            detail: { fileId: comment.fileId, sourceId: idSuffix },
          }),
        );
      } catch (error) {
        setCommentError(error instanceof Error ? error.message : t('comments.deleteError', 'No se pudo eliminar el comentario.'));
      }
    },
    [idSuffix, t],
  );

  const removeRaceStartLineLayer = useCallback(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    if (map.getLayer(raceStartLinePointLayerId)) {
      map.removeLayer(raceStartLinePointLayerId);
    }
    if (map.getLayer(raceStartLineLayerId)) {
      map.removeLayer(raceStartLineLayerId);
    }
    if (map.getSource(raceStartLineSourceId)) {
      map.removeSource(raceStartLineSourceId);
    }
  }, [mapInstance, raceStartLineLayerId, raceStartLinePointLayerId, raceStartLineSourceId]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }

    let cancelled = false;
    const renderRaceStartLines = () => {
      if (cancelled || mapInstance.current !== map) {
        return;
      }

      const overlayPalette = getCurrentMapOverlayPalette();
      const data = buildRaceStartLineFeatureCollection(
        startLineSegments,
        overlayPalette.startLine,
        overlayPalette.startLinePort,
        overlayPalette.startLineStbd,
      );
      if (data.features.length === 0) {
        removeRaceStartLineLayer();
        return;
      }

      if (map.getSource(raceStartLineSourceId)) {
        (map.getSource(raceStartLineSourceId) as mapboxgl.GeoJSONSource).setData(data);
      } else {
        map.addSource(raceStartLineSourceId, {
          type: 'geojson',
          data,
        });
      }

      if (map.getLayer(raceStartLineLayerId)) {
        map.setPaintProperty(raceStartLineLayerId, LINE_COLOR, ['get', 'color']);
        map.setPaintProperty(raceStartLineLayerId, LINE_WIDTH, 4);
        map.setPaintProperty(raceStartLineLayerId, 'line-opacity', 1);
      } else {
        map.addLayer({
          id: raceStartLineLayerId,
          type: 'line',
          source: raceStartLineSourceId,
          filter: ['==', ['geometry-type'], 'LineString'],
          paint: {
            [LINE_COLOR]: ['get', 'color'],
            [LINE_WIDTH]: 4,
            'line-opacity': 1,
          },
          layout: { [LINE_CAP]: 'round', [LINE_JOIN]: 'round' },
        });
      }

      if (map.getLayer(raceStartLinePointLayerId)) {
        map.setPaintProperty(raceStartLinePointLayerId, 'circle-color', ['get', 'color']);
        map.setPaintProperty(raceStartLinePointLayerId, 'circle-radius', 4);
        map.setPaintProperty(raceStartLinePointLayerId, 'circle-stroke-width', 0);
        map.setPaintProperty(raceStartLinePointLayerId, 'circle-opacity', 1);
      } else {
        map.addLayer({
          id: raceStartLinePointLayerId,
          type: 'circle',
          source: raceStartLineSourceId,
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-color': ['get', 'color'],
            'circle-radius': 4,
            'circle-stroke-width': 0,
            'circle-opacity': 1,
          },
        });
      }

      safeMoveLayer(map, raceStartLineLayerId);
      safeMoveLayer(map, raceStartLinePointLayerId);
    };

    if (map.isStyleLoaded()) {
      renderRaceStartLines();
    } else {
      map.once('styledata', renderRaceStartLines);
    }

    return () => {
      cancelled = true;
      map.off('styledata', renderRaceStartLines);
    };
  }, [
    mapInstance,
    mapReadyToken,
    raceStartLineLayerId,
    raceStartLinePointLayerId,
    raceStartLineSourceId,
    removeRaceStartLineLayer,
    startLineSegments,
  ]);

  /**
   * Garantiza que el par source/layer del track exista **exactamente una vez**.
   *  - Si la fuente ya existe ⇒ sólo se hace `setData`.
   *  - Si la capa ya existe ⇒ sólo se afinan paint/layout.
   *  - Controla z‑index con `moveLayer`, pero lo ignora si ya está arriba.
   */

  const getValidMetrics = useCallback(
    (dropdownKind: 'width' | 'speed') => {
      const metrics =
        dropdownKind === 'width'
          ? (['vmg', 'bsppc', 'vmgpc', 'bsp', 'sog', 'tws', 'twa_abs'] as const)
          : (['vmg', 'bsppc', 'vmgpc', 'bsp', 'sog', 'tws'] as const);

      return getFiniteMetricKeys(trackData, metrics);
    },
    [trackData],
  );

  const formatMetricOptionLabel = useCallback(
    (metric: string) => metric.replaceAll('_', '\u00A0').toUpperCase(),
    [],
  );

  const speedMetricOptions = useMemo<MapSelectOption<SpeedMetricKey>[]>(() => {
    const metrics = getValidMetrics('speed') as SpeedMetricKey[];
    return metrics.map((metric) => ({
      value: metric,
      label: formatMetricOptionLabel(metric),
    }));
  }, [formatMetricOptionLabel, getValidMetrics]);

  const widthMetricOptions = useMemo<MapSelectOption<WidthMetricKey>[]>(() => {
    const metrics = getValidMetrics('width') as ExtendedMetricKey[];
    return [
      {
        value: 'none',
        label: t('map.none', 'None'),
      },
      ...metrics.map((metric) => ({
        value: metric,
        label: formatMetricOptionLabel(metric),
      })),
    ];
  }, [formatMetricOptionLabel, getValidMetrics, t]);

  const isLadderAvailable = relatedTrackLayers.length > 0;

  const speedMetricValueForSelect: SpeedMetricKey =
    selectedMetric === 'heel'
      ? (speedMetricOptions[0]?.value ?? 'bsp')
      : (selectedMetric as SpeedMetricKey);

  type LadderCollections = {
    lines: GeoJSON.FeatureCollection<LineString, { label: string }>;
    labels: GeoJSON.FeatureCollection<GeoJSON.Point, { label: string }>;
  } | null;

  const ladderCollections = useMemo<LadderCollections>(() => {
    if (trackData.length === 0 || relatedTrackLayers.length === 0) {
      return null;
    }

    type TimedPoint = { time: number; coords: [number, number] };

    const buildTimeline = (points: TrackPoint[]): TimedPoint[] => {
      const timeline: TimedPoint[] = [];
      for (const point of points) {
        if (!Number.isFinite(point.lat) || !Number.isFinite(point.lon)) {
          continue;
        }
        const rawTime = point.utc ?? point.date;
        if (!rawTime) {
          continue;
        }
        const timestamp = Date.parse(rawTime);
        if (Number.isNaN(timestamp)) {
          continue;
        }
        timeline.push({
          time: timestamp,
          coords: [point.lon, point.lat],
        });
      }
      timeline.sort((a, b) => a.time - b.time);
      return timeline;
    };

    const interpolateCoordinate = (
      timeline: TimedPoint[],
      target: number,
    ): [number, number] | null => {
      if (timeline.length === 0) {
        return null;
      }
      const first = timeline[0];
      const last = timeline.at(-1);
      if (!last) {
        return null;
      }
      if (target < first.time || target > last.time) {
        return null;
      }
      let low = 0;
      let high = timeline.length - 1;
      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const midTime = timeline[mid].time;
        if (midTime === target) {
          return timeline[mid].coords;
        }
        if (midTime < target) {
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }
      const beforeIndex = Math.max(0, high);
      let afterIndex = beforeIndex + 1;
      while (
        afterIndex < timeline.length &&
        timeline[afterIndex].time === timeline[beforeIndex].time
      ) {
        afterIndex += 1;
      }
      if (afterIndex >= timeline.length) {
        return null;
      }
      const beforePoint = timeline[beforeIndex];
      const afterPoint = timeline[afterIndex];
      const span = afterPoint.time - beforePoint.time;
      if (span <= 0) {
        return null;
      }
      const ratio = (target - beforePoint.time) / span;
      return [
        beforePoint.coords[0] + (afterPoint.coords[0] - beforePoint.coords[0]) * ratio,
        beforePoint.coords[1] + (afterPoint.coords[1] - beforePoint.coords[1]) * ratio,
      ];
    };

    const alignToMinute = (timestamp: number, direction: 'ceil' | 'floor'): number => {
      const remainder = timestamp % LADDER_STEP_MS;
      if (remainder === 0) {
        return timestamp;
      }
      return direction === 'ceil'
        ? timestamp + (LADDER_STEP_MS - remainder)
        : timestamp - remainder;
    };

    const toLabel = (timestamp: number) => new Date(timestamp).toISOString().slice(11, 19);

    const baseTimeline = buildTimeline(trackData);
    if (baseTimeline.length === 0) {
      return null;
    }
    const baseStart = baseTimeline[0].time;
    const baseEnd = baseTimeline.at(-1)?.time ?? baseStart;

    const lineFeatures: GeoJSON.Feature<LineString, { label: string }>[] = [];
    const labelFeatures: GeoJSON.Feature<GeoJSON.Point, { label: string }>[] = [];

    for (const overlay of relatedTrackLayers) {
      const overlayTimeline = buildTimeline(overlay.trackData);
      if (overlayTimeline.length === 0) {
        continue;
      }
      const overlayStart = overlayTimeline[0].time;
      const overlayEnd = overlayTimeline.at(-1)?.time ?? overlayStart;
      const overlapStart = Math.max(baseStart, overlayStart);
      const overlapEnd = Math.min(baseEnd, overlayEnd);
      const minuteStart = alignToMinute(overlapStart, 'ceil');
      const minuteEnd = alignToMinute(overlapEnd, 'floor');
      if (minuteStart > minuteEnd) {
        continue;
      }
      for (let timestamp = minuteStart; timestamp <= minuteEnd; timestamp += LADDER_STEP_MS) {
        const baseCoord = interpolateCoordinate(baseTimeline, timestamp);
        const overlayCoord = interpolateCoordinate(overlayTimeline, timestamp);
        if (!baseCoord || !overlayCoord) {
          continue;
        }
        const midpoint: [number, number] = [
          (baseCoord[0] + overlayCoord[0]) / 2,
          (baseCoord[1] + overlayCoord[1]) / 2,
        ];
        const label = toLabel(timestamp);
        lineFeatures.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [baseCoord, overlayCoord],
          },
          properties: { label },
        });
        labelFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: midpoint },
          properties: { label },
        });
      }
    }

    if (lineFeatures.length === 0) {
      return null;
    }

    return {
      lines: { type: 'FeatureCollection', features: lineFeatures },
      labels: { type: 'FeatureCollection', features: labelFeatures },
    };
  }, [relatedTrackLayers, trackData]);

  const removeLadderLayers = useCallback(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    if (map.getLayer(ladderLinesLayerId)) {
      map.removeLayer(ladderLinesLayerId);
    }
    if (map.getLayer(ladderLabelsLayerId)) {
      map.removeLayer(ladderLabelsLayerId);
    }
    if (map.getSource(ladderLinesSourceId)) {
      map.removeSource(ladderLinesSourceId);
    }
    if (map.getSource(ladderLabelsSourceId)) {
      map.removeSource(ladderLabelsSourceId);
    }
  }, [
    ladderLabelsLayerId,
    ladderLabelsSourceId,
    ladderLinesLayerId,
    ladderLinesSourceId,
    mapInstance,
  ]);

  // Manejar el clic en los tacks

  const [resizeDirection, setResizeDirection] = useState<string>('');
  const [initialPanelRect, setInitialPanelRect] = useState<DOMRect | null>(null);
  const [initialMousePos, setInitialMousePos] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Función para iniciar el redimensionado desde un handle específico
  const handleResizeMouseDown = (event: React.MouseEvent<HTMLDivElement>, direction: string) => {
    event.stopPropagation();
    if (panelReference.current) {
      setInitialPanelRect(panelReference.current.getBoundingClientRect());
      setInitialMousePos({ x: event.clientX, y: event.clientY });
      setResizeDirection(direction);
      setResizing(true);
    }
  };

  function getColorLegendForMetric(
    metric: keyof (typeof metricBounds)['min'],
    mode: string,
  ): ColorLegend {
    const effectiveMetric = mode === 'tactical' ? 'heel' : metric;
    const absMin = metricBounds.min[effectiveMetric] ?? 0;
    const absMax = metricBounds.max[effectiveMetric] ?? 1;
    const startMin = metricBounds.iqrMin[effectiveMetric] ?? absMin;
    const startMax = metricBounds.iqrMax[effectiveMetric] ?? absMax;
    let min = startMin;
    let max = Math.max(startMax, min + 0.1);
    if (max > absMax) {
      max = absMax;
    }
    if (min < absMin) {
      min = absMin;
    }
    return { min, max, mode };
  }

  function updateColorLegend(metric: keyof (typeof metricBounds)['min'], mode: string) {
    setColorLegend(getColorLegendForMetric(metric, mode));
  }
  // Manejador completo para cambiar al modo Speed
  const handleSpeedMode = () => {
    try {
      const nextMetric = resolveDefaultSpeedMetric(
        speedMetricOptions.map((option) => option.value),
      );
      setSelectedMetric(nextMetric);
      updateColorLegend(nextMetric, 'speed');
      toggleLineMode('speed', undefined, nextMetric, selectedMetricWidth);
    } catch (error) {
      // En caso de error informamos en consola para debugging
      developmentLog.error('[MapPanel][handleSpeedMode] error:', error);
    }
  };

  // 🔁 R E N D E R  T A C K S  (versión definitiva)
  // 🔁 R E N D E R  T A C K S  (versión corregida)
  function renderTacks() {
    developmentLog.debug('[renderTacks] -> Iniciando render de tacks...');
    const overlayPalette = getCurrentMapOverlayPalette();

    // Asegúrate de que existen mapa, track y maniobras
    if (!mapInstance.current || cachedTrackData.length === 0 || tacks.length === 0 || !showTacks) {
      developmentLog.debug(
        '[renderTacks] -> Condiciones no cumplidas para pintar tacks. Retornando.',
      );
      return;
    }

    if (!lastFileId) {
      developmentLog.warn("[renderTacks] -> 'lastFileId' es null/undefined, no se pintarán tacks.");
      return;
    }

    /* 1️⃣  Usa geometría cacheada si existe ----------------------------------- */
    const cachedGeoJSON = cachedTacksGeoJSON[lastFileId];
    if (cachedGeoJSON) {
      developmentLog.debug(
        `[renderTacks] -> Usando geometría cacheada para fileId = ${lastFileId}`,
      );
      if (mapInstance.current.getSource(tackSourceId)) {
        (mapInstance.current.getSource(tackSourceId) as mapboxgl.GeoJSONSource).setData(
          cachedGeoJSON as unknown as GeoJSON.GeoJSON,
        );
      } else {
        mapInstance.current.addSource(tackSourceId, {
          type: 'geojson',
          data: cachedGeoJSON,
        });
        mapInstance.current.addLayer({
          id: tackLayerId,
          type: 'line',
          source: tackSourceId,
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 12,
            'line-opacity': 0.8,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
        });
      }
      syncOptionalLineCasingLayer({
        map: mapInstance.current,
        sourceId: tackSourceId,
        casingLayerId: tackCasingLayerId,
        beforeLayerId: tackLayerId,
        width: 14,
        palette: overlayPalette,
      });

      // 🟦⚓  Asegura recoloreado azul cuando el layer ya está listo
      const runColoring = () => updateTackColors();
      if (mapInstance.current.isStyleLoaded()) {
        runColoring(); // ① ya está todo listo → recolorea ahora
      } else {
        mapInstance.current.once('idle', runColoring); // ② si no, espera al idle
      }

      return;
    }

    /* 2️⃣  Construye la geometría completa ------------------------------------ */
    const tackFeatures: GeoJSON.Feature<GeoJSON.Geometry>[] = tacks
      .map((tack, tackIndex) => {
        const startAbs = tack.start_index;
        const endAbs = tack.end_index;

        const values = cachedTrackData.slice(startAbs, endAbs + 1);
        if (values.length === 0) {
          return null;
        }

        const coordinates = values.map((p) => [p.lon, p.lat]);
        const stats = calcularEstadisticasTack(values, cachedTrackData);
        const color = getTackColor(startAbs, endAbs);

        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates,
          },
          properties: {
            tackIndex,
            color,
            maxRudder: stats.maxRudder,
            timeToMaxRudder: stats.timeToMaxRudder,
            avgSogBeforeTack: stats.avgSogBeforeTack,
            avgTwaBeforeTack: stats.avgTwaBeforeTack,
            SogMin: stats.SogMin,
            timeToRecovery90: stats.timeToRecovery90,
            timeToRecovery95: stats.timeToRecovery95,
            type: stats.type,
          },
        };
      })
      .filter(Boolean) as GeoJSON.Feature<GeoJSON.Geometry>[];

    const allTacksGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: tackFeatures,
    };

    // Cachea
    setCachedTacksGeoJSON((previous) => ({
      ...previous,
      [lastFileId]: allTacksGeoJSON,
    }));

    /* 3️⃣  Añade al mapa ------------------------------------------------------- */
    if (mapInstance.current.getSource(tackSourceId)) {
      (mapInstance.current.getSource(tackSourceId) as mapboxgl.GeoJSONSource).setData(
        allTacksGeoJSON as unknown as GeoJSON.GeoJSON,
      );
    } else {
      mapInstance.current.addSource(tackSourceId, {
        type: 'geojson',
        data: allTacksGeoJSON,
      });
      mapInstance.current.addLayer({
        id: tackLayerId,
        type: 'line',
        source: tackSourceId,
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 12,
          'line-opacity': 0.8,
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
      });
      syncOptionalLineCasingLayer({
        map: mapInstance.current,
        sourceId: tackSourceId,
        casingLayerId: tackCasingLayerId,
        beforeLayerId: tackLayerId,
        width: 14,
        palette: overlayPalette,
      });
    }

    developmentLog.debug('[renderTacks] -> Fin de renderTacks.');

    // 🟦⚓  Forzamos recoloreado una vez que Mapbox termine de procesar el estilo
    if (mapInstance.current.isStyleLoaded()) {
      updateTackColors(); // ① ya está todo listo → recolorea ahora
    } else {
      mapInstance.current.once('idle', () => updateTackColors()); // ② si no, espera al idle
    }
  }

  const updateTackColors = () => {
    if (!mapInstance.current) {
      return;
    }

    const overlayPalette = getCurrentMapOverlayPalette();
    const activeTacks = new Set(tackStats.map((stat) => stat.tack - 1)); // Ajustar índice si es necesario
    const visibleColor = overlayPalette.tackVisible;
    const mutedColor = overlayPalette.tackMuted;

    // Crear la expresión 'match' con colores dinámicos
    const matchExpression = [
      'match',
      ['get', 'tackIndex'],
      // Generar pares índice-color
      ...tacks.flatMap((tack, index) => {
        const startIndex = globalToRelativeIndexMap[tack.start_index];
        const endIndex = globalToRelativeIndexMap[tack.end_index];
        if (startIndex !== undefined && endIndex !== undefined) {
          const defaultColor = getTackColor(startIndex, endIndex);
          // Si el tack está activo, usar --hoverAccent, si no, el color por defecto
          if (defaultColor === 'transparent') {
            return [];
          }
          const visible = maneuverVisibility?.[index] !== false;
          const color = visible
            ? activeTacks.has(index)
              ? overlayPalette.tackActive
              : visibleColor
            : mutedColor;
          return [index, color];
        }
        // Si no se pueden calcular los índices, no se asigna un color
        return [];
      }),
      // Color por defecto cuando no hay coincidencia
      'transparent', // --primary
    ] as unknown as DataDrivenPropertyValueSpecification<string>;

    // Actualizar los colores en la capa
    if (mapInstance.current.getLayer(tackLayerId)) {
      mapInstance.current.setPaintProperty(tackLayerId, LINE_COLOR, matchExpression);
      mapInstance.current.setPaintProperty(tackLayerId, 'line-opacity', 0.8);
      const shouldShowTacksBelowTrack = lineMode === 'speed' || lineMode === 'tactical';
      if (shouldShowTacksBelowTrack && mapInstance.current.getLayer(trackLayerId)) {
        safeMoveLayer(mapInstance.current, tackLayerId, trackLayerId);
      } else {
        safeMoveLayer(mapInstance.current, tackLayerId);
      }
      syncOptionalLineCasingLayer({
        map: mapInstance.current,
        sourceId: tackSourceId,
        casingLayerId: tackCasingLayerId,
        beforeLayerId: tackLayerId,
        width: 14,
        palette: overlayPalette,
      });
    }
  };

  useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
    } else {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
    };
  }, [resizing]);
  useEffect(() => {
    const isColorizedMode = lineMode === 'speed' || lineMode === 'tactical';
    if (!isColorizedMode || !colorLegend) {
      setShowLegend(false);
      setIsLegendEditing(false);
      return;
    }

    // Asegurarse de que inicialmente se muestre cerrado
    const timer = setTimeout(() => {
      setShowLegend(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [colorLegend, lineMode]);

  useEffect(() => {
    safeResizeMap(mapInstance.current);
  }, [showSidebar, showSidePage]);

  const cleanupSegmentEditingLayers = useCallback(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    for (const layerId of [editFullLayerId, editRangeLayerId]) {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    }
    for (const sourceId of [editFullSourceId, editRangeSourceId]) {
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    }
    if (previousTrackOpacityRef.current !== null && map.getLayer(trackLayerId)) {
      try {
        map.setPaintProperty(trackLayerId, 'line-opacity', previousTrackOpacityRef.current);
      } catch {
        /* silent */
      }
    }
    previousTrackOpacityRef.current = null;
  }, [editFullLayerId, editFullSourceId, editRangeLayerId, editRangeSourceId, trackLayerId]);

  useEffect(() => {
    const map = mapInstance.current;
    const hasEditing =
      editingSegmentId !== null &&
      editingSegmentId !== undefined &&
      editingRange &&
      cachedTrackData.length > 1;

    if (!map) {
      return;
    }

    if (!hasEditing) {
      cleanupSegmentEditingLayers();
      return;
    }

    const overlayPalette = getCurrentMapOverlayPalette();
    const fullCoords = cachedTrackData
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon))
      .map((point) => [point.lon, point.lat]);

    if (fullCoords.length >= 2) {
      const fullGeo: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: fullCoords },
            properties: {},
          },
        ],
      };
      if (map.getSource(editFullSourceId)) {
        (map.getSource(editFullSourceId) as mapboxgl.GeoJSONSource).setData(fullGeo);
      } else {
        map.addSource(editFullSourceId, { type: 'geojson', data: fullGeo });
      }
      if (map.getLayer(editFullLayerId)) {
        map.setPaintProperty(editFullLayerId, LINE_COLOR, overlayPalette.editFull);
        map.setPaintProperty(editFullLayerId, LINE_WIDTH, 4);
        map.setPaintProperty(editFullLayerId, 'line-opacity', 0.28);
      } else {
        map.addLayer({
          id: editFullLayerId,
          type: 'line',
          source: editFullSourceId,
          paint: {
            [LINE_COLOR]: overlayPalette.editFull,
            [LINE_WIDTH]: 4,
            'line-opacity': 0.28,
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });
      }
    }

    const [rawStart, rawEnd] = [
      Math.max(0, Math.min(editingRange?.start ?? 0, cachedTrackData.length - 1)),
      Math.max(0, Math.min(editingRange?.end ?? 0, cachedTrackData.length - 1)),
    ];
    const from = Math.min(rawStart, rawEnd);
    const to = Math.max(rawStart, rawEnd);
    const segmentCoords = cachedTrackData
      .slice(from, to + 1)
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon))
      .map((point) => [point.lon, point.lat]);

    if (segmentCoords.length >= 2) {
      const segmentGeo: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: segmentCoords },
            properties: {},
          },
        ],
      };

      if (map.getSource(editRangeSourceId)) {
        (map.getSource(editRangeSourceId) as mapboxgl.GeoJSONSource).setData(segmentGeo);
      } else {
        map.addSource(editRangeSourceId, { type: 'geojson', data: segmentGeo });
      }

      if (map.getLayer(editRangeLayerId)) {
        map.setPaintProperty(editRangeLayerId, LINE_COLOR, overlayPalette.editRange);
        map.setPaintProperty(editRangeLayerId, LINE_WIDTH, 10);
        map.setPaintProperty(editRangeLayerId, 'line-opacity', 0.9);
      } else {
        map.addLayer({
          id: editRangeLayerId,
          type: 'line',
          source: editRangeSourceId,
          paint: {
            [LINE_COLOR]: overlayPalette.editRange,
            [LINE_WIDTH]: 10,
            'line-opacity': 0.9,
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });
      }

      safeMoveLayer(map, editRangeLayerId);
    }

    if (map.getLayer(trackLayerId)) {
      if (previousTrackOpacityRef.current === null) {
        const currentOpacity = map.getPaintProperty(trackLayerId, 'line-opacity');
        previousTrackOpacityRef.current =
          typeof currentOpacity === 'number' && Number.isFinite(currentOpacity)
            ? currentOpacity
            : 1;
      }
      try {
        map.setPaintProperty(trackLayerId, 'line-opacity', 0.25);
      } catch {
        /* silent */
      }
    }
  }, [
    cachedTrackData,
    cleanupSegmentEditingLayers,
    editFullLayerId,
    editFullSourceId,
    editRangeLayerId,
    editRangeSourceId,
    editingRange,
    editingSegmentId,
    mapInstance,
    trackLayerId,
  ]);

  useEffect(() => cleanupSegmentEditingLayers, [cleanupSegmentEditingLayers]);

  useEffect(() => {
    if (mapInstance.current && mapInputMode === 'highlight') {
      const handleMapClick = (event: mapboxgl.MapMouseEvent) => handleHighlightClick(event);

      mapInstance.current.on('click', handleMapClick);

      return () => {
        mapInstance.current?.off('click', handleMapClick);
      };
    }
  }, [mapInputMode, startPoint, trackData, generateRandomColor]);
  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);
  const hideGridLayers = React.useCallback(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    renderedGridViewportRef.current = null;
    renderedGridVisibilityRef.current = null;
    for (const layerId of [GRID_LAYER_1X_ID(side), GRID_LAYER_10X_ID(side)]) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', 'none');
      }
    }
  }, [mapInstance, side]);
  const updateGridLayers = (
    loa: number,
    rotationAngle: number,
    refreshOptions?: GridLayerRefreshOptions,
  ): void => {
    try {
      /* ────────────────────────────────────────────────────────────────
	     More robust grid updater:
	       1️⃣  Ensures grid sources/layers exist once per panel
	       2️⃣  Only toggles layer visibility instead of removing sources
	       3️⃣  Updates GeoJSON data when a grid is visible
	     This prevents the “source … not found” errors that appeared
	     when rapid map events interleaved add/remove calls.
	     Now uses true 1 × LOA and 10 × LOA grids instead of 60 × LOA and 600 × LOA. */
      const source1xId = `grid-source-1x-${side}`;
      const layer1xId = `grid-layer-1x-${side}`;
      const source10xId = `grid-source-10x-${side}`;
      const layer10xId = `grid-layer-10x-${side}`;

      const map = mapInstance.current;
      if (!map) {
        return;
      }

      // La orientación de la rejilla sigue siempre la brujula/map bearing actual.
      const currentBearing = normalizeBearing(map.getBearing());
      type MapMaybeRotating = mapboxgl.Map & { isRotating?: () => boolean };
      const mapboxReportedRotation = (map as MapMaybeRotating).isRotating?.() ?? false;
      const rotationInProgress = isGridRotationActiveRef.current || mapboxReportedRotation;
      const gridLayers = [layer1xId, layer10xId];
      const overlayPalette = getCurrentMapOverlayPalette();

      // Evita mostrar la rejilla mientras el mapa rota
      if (rotationInProgress) {
        renderedGridViewportRef.current = null;
        renderedGridVisibilityRef.current = null;
        for (const layerId of gridLayers) {
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', 'none');
          }
        }
        return;
      }

      // Wait for the style to be ready
      if (!map.isStyleLoaded()) {
        map.once('styledata', () => updateGridLayers(loa, rotationAngle, refreshOptions));
        return;
      }

      /* 🛠 1. Ensure the source/layer pair exists just once */
      const ensurePair = (sourceId: string, lyrId: string, color: string, opacity: number) => {
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          });
        }
        // eslint-disable-next-line unicorn/no-negated-condition
        if (!map.getLayer(lyrId)) {
          map.addLayer({
            id: lyrId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': color,
              'line-width': 1,
              'line-opacity': opacity,
            },
          });
          safeMoveLayer(map, lyrId, trackLayerId);
        } else {
          map.setPaintProperty(lyrId, 'line-color', color);
          map.setPaintProperty(lyrId, 'line-opacity', opacity);
        }
      };

      ensurePair(source1xId, layer1xId, overlayPalette.gridFine, overlayPalette.gridFineOpacity);
      ensurePair(
        source10xId,
        layer10xId,
        overlayPalette.gridCoarse,
        overlayPalette.gridCoarseOpacity,
      );

      /* 📏 2. Calculate current pixel spacing for 1 × LOA and 10 × LOA grids */
      const bounds = map.getBounds();
      if (!bounds) {
        return;
      }

      const metresPerPixel =
        (156_543.033_92 * Math.cos((bounds.getCenter().lat * Math.PI) / 180)) /
        Math.pow(2, map.getZoom());

      const anchorPoint = gridOriginRef.current ?? bounds.getCenter();
      const anchor = { lng: anchorPoint.lng, lat: anchorPoint.lat };
      const pixelSpacing1x = loa / metresPerPixel;
      const pixelSpacing10x = (loa * 10) / metresPerPixel;

      /* 🔄 3. Helper to refresh data + visibility */
      const refreshGrid = (
        visible: boolean,
        sourceId: string,
        lyrId: string,
        spacing: number,
        refreshData = visible,
        gridOptions?: GridCreateOptions,
      ) => {
        /* Guard: the layer might not have been fully registered yet
			   (Mapbox adds layers asynchronously).  Avoid “layer ... does not exist”
			   errors by exiting early and letting the next throttled call handle it. */
        if (!map.getLayer(lyrId)) {
          return;
        }

        if (visible) {
          if (refreshData) {
            const data = createGrid(bounds, spacing, currentBearing, anchor, gridOptions);
            safeSetGeoJSONData(map, sourceId, data);
          }
          map.setLayoutProperty(lyrId, 'visibility', 'visible');
        } else {
          map.setLayoutProperty(lyrId, 'visibility', 'none');
        }
      };

      const { showFine: shouldShowFine, showCoarse: shouldShowCoarse } = getGridLayerVisibility({
        pixelSpacing1x,
        pixelSpacing10x,
      });
      renderedGridVisibilityRef.current = {
        showFine: shouldShowFine,
        showCoarse: shouldShowCoarse,
      };
      const hasVisibleGrid = shouldShowFine || shouldShowCoarse;
      if (hasVisibleGrid) {
        const center = bounds.getCenter();
        const rawLngSpan = Math.abs(bounds.getEast() - bounds.getWest());
        renderedGridViewportRef.current = {
          center: { lng: center.lng, lat: center.lat },
          spanLng: rawLngSpan > 180 ? 360 - rawLngSpan : rawLngSpan,
          spanLat: Math.abs(bounds.getNorth() - bounds.getSouth()),
        };
      } else {
        renderedGridViewportRef.current = null;
      }

      // Rejilla fina: sólo cuando el espaciado 1× es legible
      refreshGrid(
        shouldShowFine,
        source1xId,
        layer1xId,
        loa,
        refreshOptions?.refreshFine ?? shouldShowFine,
        { skipMajorLinesCoveredByCoarseGrid: shouldShowCoarse },
      );

      // Rejilla gruesa: visible siempre que la fina lo esté para marcar las líneas 10× por encima.
      refreshGrid(
        shouldShowCoarse,
        source10xId,
        layer10xId,
        loa * 10,
        refreshOptions?.refreshCoarse ?? shouldShowCoarse,
      );
      if (map.getLayer(layer1xId)) {
        safeMoveLayer(map, layer1xId, trackLayerId);
      }
      if (map.getLayer(layer10xId)) {
        safeMoveLayer(map, layer10xId, trackLayerId);
      }
    } catch (error) {
      developmentLog.warn('[MapPanel][grid] updateGridLayers error', error);
    }
  };
  const ensureStyleLoaded = useCallback(
    (callback: () => void) => {
      const map = mapInstance.current;
      if (!map) {
        return;
      }

      if (map.isStyleLoaded()) {
        callback();
        return;
      }

      // Escucha hasta que el estilo esté realmente cargado; algunos mapas emiten
      // un primer "styledata" antes de que isStyleLoaded() sea true.
      const handleStyleData = () => {
        if (!map.isStyleLoaded()) {
          return;
        }
        map.off('styledata', handleStyleData);
        callback();
      };

      map.on('styledata', handleStyleData);
    },
    [mapInstance],
  );

  const isCameraLoadActive = (cameraLoadToken?: number) =>
    cameraLoadToken === undefined ||
    (cameraLoadToken === cameraLoadTokenRef.current && pendingInitialCameraRef.current);
  /* 🔄 Rejilla: registrar TODAS las fases de movimiento/zoom y estilo */
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }

    const hasTrack = trackData.length > 0;
    if (!hasTrack) {
      // Si no hay track activo, nos aseguramos de que no quede rejilla pintada.
      clearMapArtifacts(map);
      return;
    }

    // Handler único (throttled 20 ms) que actualiza/dibuja la rejilla
    const throttledUpdate = throttle(() => updateGridLayers(loa, gridRotationAngle), 20);
    const throttledZoomUpdate = throttle(
      (refreshOptions: GridLayerRefreshOptions) =>
        updateGridLayers(loa, gridRotationAngle, refreshOptions),
      20,
    );
    const handleUpdate = () => throttledUpdate();
    const getGridVisibilityForCurrentZoom = () => {
      const bounds = map.getBounds();
      if (!bounds) {
        return null;
      }
      const metresPerPixel =
        (156_543.033_92 * Math.cos((bounds.getCenter().lat * Math.PI) / 180)) /
        Math.pow(2, map.getZoom());

      return getGridLayerVisibility({
        pixelSpacing1x: loa / metresPerPixel,
        pixelSpacing10x: (loa * 10) / metresPerPixel,
      });
    };
    const handleZoomVisibilityCheck = () => {
      const nextVisibility = getGridVisibilityForCurrentZoom();
      if (!nextVisibility) {
        return;
      }
      const shouldRefreshForZoom = shouldRefreshGridDuringZoom({
        previous: renderedGridVisibilityRef.current,
        next: nextVisibility,
      });
      if (shouldRefreshForZoom) {
        const refreshPlan = getGridZoomSourceRefreshPlan({
          previous: renderedGridVisibilityRef.current,
          next: nextVisibility,
        });
        throttledZoomUpdate(refreshPlan);
      }
    };
    const handleDragRecenterCheck = () => {
      const renderedGridViewport = renderedGridViewportRef.current;
      const bounds = map.getBounds();
      if (!renderedGridViewport || !bounds) {
        return;
      }
      type MapMaybeZooming = mapboxgl.Map & { isZooming?: () => boolean };
      const isZooming = (map as MapMaybeZooming).isZooming?.() ?? false;
      const currentCenter = bounds.getCenter();
      const shouldRecenter = shouldRecenterGridAfterDrag({
        renderedCenterX: renderedGridViewport.center.lng,
        renderedCenterY: renderedGridViewport.center.lat,
        currentCenterX: currentCenter.lng,
        currentCenterY: currentCenter.lat,
        spanX: renderedGridViewport.spanLng,
        spanY: renderedGridViewport.spanLat,
        isZooming,
      });
      if (shouldRecenter) {
        throttledUpdate();
      }
    };
    const handleIdleAfterRotate = () => throttledUpdate();
    const handleRotateStart = () => {
      isGridRotationActiveRef.current = true;
      hideGridLayers();
    };
    const handleRotateEnd = () => {
      isGridRotationActiveRef.current = false;
      // Espera al frame ocioso para evitar repintar mientras la inercia aún actúa
      if (map.isMoving?.()) {
        map.once('idle', handleIdleAfterRotate);
      } else {
        throttledUpdate();
      }
    };

    // ↻  Dibujo inmediato (cubre creación tardía o cambio de layout)
    throttledUpdate();

    // ⇢  Refrescar sólo en acciones que no provienen de zoom evita el salto al soltar la rueda/pinch.
    const gridRefreshEvents = ['dragend', 'resize'] as const;
    for (const event_ of gridRefreshEvents) {
      map.on(event_, handleUpdate);
    }
    const gridZoomVisibilityEvents = ['zoom'] as const;
    for (const event_ of gridZoomVisibilityEvents) {
      map.on(event_, handleZoomVisibilityCheck);
    }
    const gridDragRecenterEvents = ['drag'] as const;
    for (const event_ of gridDragRecenterEvents) {
      map.on(event_, handleDragRecenterCheck);
    }
    map.on('rotatestart', handleRotateStart);
    map.on('rotateend', handleRotateEnd);

    // 🧹 Limpieza al desmontar o cambiar de mapa
    return () => {
      throttledUpdate.cancel();
      throttledZoomUpdate.cancel();
      for (const event_ of gridRefreshEvents) {
        map.off(event_, handleUpdate);
      }
      for (const event_ of gridZoomVisibilityEvents) {
        map.off(event_, handleZoomVisibilityCheck);
      }
      for (const event_ of gridDragRecenterEvents) {
        map.off(event_, handleDragRecenterCheck);
      }
      map.off('rotatestart', handleRotateStart);
      map.off('rotateend', handleRotateEnd);
      map.off('idle', handleIdleAfterRotate);
    };
  }, [mapReadyToken, loa, gridRotationAngle, trackData.length, hideGridLayers]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }

    if (!trackData || trackData.length === 0) {
      for (const layerId of [GRID_LAYER_1X_ID(side), GRID_LAYER_10X_ID(side)]) {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', 'none');
        }
      }
      return;
    }

    ensureStyleLoaded(() => updateGridLayers(loa, gridRotationAngle));
  }, [mapReadyToken, gridRotationAngle, loa, side, trackData]);

  useEffect(() => {
    return () => {
      const map = mapInstance.current;
      if (!map) {
        return;
      }
      for (const layerId of GRID_ARTIFACT_POSITIONS.flatMap((gridSide) => [
        GRID_LAYER_1X_ID(gridSide),
        GRID_LAYER_10X_ID(gridSide),
      ])) {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
      }
      for (const sourceId of GRID_ARTIFACT_POSITIONS.flatMap((gridSide) => [
        GRID_SOURCE_1X_ID(gridSide),
        GRID_SOURCE_10X_ID(gridSide),
      ])) {
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
      }
    };
  }, [mapInstance, side]);

  /**
   * Dibuja los puntos y etiquetas de 10 minutos (y opcionalmente las flechas de TWD)
   * sobre el mapa.  Requiere que el mapa ya esté cargado.
   */
  const renderTenMinutePoints = (
    map: mapboxgl.Map,
    trackData: TrackPoint[],
    mode: LineMode,
    minDelta: number,
    labelOffsetMinutes: number,
  ) => {
    if (trackData.length === 0 || !['plain', 'speed', 'tactical'].includes(mode)) {
      return;
    }
    const overlayPalette = getCurrentMapOverlayPalette();

    /* ── 1. Calcular la lista de puntos ───────────────────────────────────── */
    const tenMinuteMarkers = getTenMinuteMarkers({
      trackData,
      mode,
      minDelta,
      labelOffsetMinutes,
    });

    /* ── 2. Features de puntos + etiquetas ────────────────────────────────── */
    const pointFeatures: GeoJSON.Feature<GeoJSON.Point, { label: string }>[] = tenMinuteMarkers.map(
      ({ point, label }) => {
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
          properties: { label },
        };
      },
    );

    const circleData: GeoJSON.FeatureCollection<GeoJSON.Point, { label: string }> = {
      type: 'FeatureCollection',
      features: pointFeatures,
    };

    /* ── 3. Source + layers de círculos y etiquetas ───────────────────────── */
    const sourceId = CIRCLE_POINTS_SOURCE_ID;
    const circleLyr = CIRCLE_LAYER_ID;
    const labelLyr = CIRCLE_LABEL_LAYER_ID;

    if (map.getSource(sourceId)) {
      (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(circleData);
    } else {
      map.addSource(sourceId, { type: 'geojson', data: circleData });
    }

    // eslint-disable-next-line unicorn/no-negated-condition
    if (!map.getLayer(circleLyr)) {
      map.addLayer({
        id: circleLyr,
        type: 'circle',
        source: sourceId,
        paint: { 'circle-radius': 1.5, 'circle-color': overlayPalette.tacticalPoint },
      });
    } else {
      map.setPaintProperty(circleLyr, 'circle-color', overlayPalette.tacticalPoint);
    }

    // eslint-disable-next-line unicorn/no-negated-condition
    if (!map.getLayer(labelLyr)) {
      map.addLayer({
        id: labelLyr,
        type: 'symbol',
        source: sourceId,
        layout: {
          'text-field': ['get', 'label'],
          'text-font': FONT_STACK_REGULAR,
          'text-size': 10,
          'text-anchor': 'left',
          'text-offset': [0.5, 0],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': overlayPalette.tacticalLabel,
          'text-halo-color': overlayPalette.tacticalLabelHalo,
          'text-halo-width': overlayPalette.tacticalLabelHaloWidth,
        },
      });
    } else {
      map.setPaintProperty(labelLyr, 'text-color', overlayPalette.tacticalLabel);
      map.setPaintProperty(labelLyr, 'text-halo-color', overlayPalette.tacticalLabelHalo);
      map.setPaintProperty(labelLyr, 'text-halo-width', overlayPalette.tacticalLabelHaloWidth);
      map.setLayoutProperty(labelLyr, 'text-allow-overlap', true);
      map.setLayoutProperty(labelLyr, 'text-ignore-placement', true);
    }

    /* ── 4. Flechas de TWD solo en modo táctico ───────────────────────────── */
    const twdSource = TWD_SOURCE_ID;
    const twdLayer = TWD_LAYER_ID;

    if (mode === 'tactical') {
      const twdLines: GeoJSON.Feature<GeoJSON.LineString>[] = tenMinuteMarkers.flatMap(({ point }) => {
        const twd = point.twd;
        if (!isFiniteTrackNumber(twd)) {
          return [];
        }

        const length_ = 0.0003,
          lengthSec = 0.0001;
        const endLat = point.lat + length_ * Math.cos((twd * Math.PI) / 180);
        const endLon = point.lon + length_ * Math.sin((twd * Math.PI) / 180);
        const ang120 = (twd + 70) % 360;
        const secLat = endLat + lengthSec * Math.cos((ang120 * Math.PI) / 180);
        const secLon = endLon + lengthSec * Math.sin((ang120 * Math.PI) / 180);

        return [
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [point.lon, point.lat],
                [endLon, endLat],
              ],
            },
            properties: {},
          },
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [endLon, endLat],
                [secLon, secLat],
              ],
            },
            properties: {},
          },
        ];
      });

      const twdData: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
        type: 'FeatureCollection',
        features: twdLines,
      };

      if (map.getSource(twdSource)) {
        (map.getSource(twdSource) as mapboxgl.GeoJSONSource).setData(
          twdData as unknown as GeoJSON.GeoJSON,
        );
      } else {
        map.addSource(twdSource, { type: 'geojson', data: twdData });
      }

      // eslint-disable-next-line unicorn/no-negated-condition
      if (!map.getLayer(twdLayer)) {
        map.addLayer({
          id: twdLayer,
          type: 'line',
          source: twdSource,
          paint: { 'line-color': overlayPalette.twdLine, 'line-width': 2 },
        });
      } else {
        map.setPaintProperty(twdLayer, LINE_COLOR, overlayPalette.twdLine);
        map.setPaintProperty(twdLayer, LINE_WIDTH, overlayPalette.scheme === 'dark' ? 2.4 : 2);
      }
    } else {
      if (map.getLayer(twdLayer)) {
        map.removeLayer(twdLayer);
      }
      if (map.getSource(twdSource)) {
        map.removeSource(twdSource);
      }
    }
  };
  // 🔸 ids “nucleo” que usa MapPanel (ajusta si añades más)
  const LAYERS_TO_DELETE = [
    trackCasingLayerId,
    trackLayerId,
    tackCasingLayerId,
    tackLayerId,
    CIRCLE_LAYER_ID,
    CIRCLE_LABEL_LAYER_ID,
    TWD_LAYER_ID,
    DISTANCE_LINE_LAYER_ID,
    LABEL_LAYER_ID,
    optimizedRouteLayerId,
    optimizedRoutePointsHaloLayerId,
    optimizedRoutePointsRingLayerId,
    optimizedRoutePointsLayerId,
    ladderLinesLayerId,
    ladderLabelsLayerId,
    playerLaylinesCasingLayerId,
    playerLaylinesLayerId,
    playerPathCasingLayerId,
    playerPathLayerId,

    ...GRID_ARTIFACT_POSITIONS.flatMap((gridSide) => [
      GRID_LAYER_1X_ID(gridSide),
      GRID_LAYER_10X_ID(gridSide),
    ]),
  ] as const;

  const SOURCES_TO_DELETE = [
    trackSourceId,
    tackSourceId,
    CIRCLE_POINTS_SOURCE_ID,
    TWD_SOURCE_ID,
    DISTANCE_LINE_SOURCE_ID,
    LABEL_SOURCE_ID,
    optimizedRouteSourceId,
    optimizedRoutePointsSourceId,
    ladderLinesSourceId,
    ladderLabelsSourceId,
    playerLaylinesSourceId,
    playerPathSourceId,

    ...GRID_ARTIFACT_POSITIONS.flatMap((gridSide) => [
      GRID_SOURCE_1X_ID(gridSide),
      GRID_SOURCE_10X_ID(gridSide),
    ]),
  ] as const;

  /** Elimina si existen (layers primero, después sources) */
  function clearMapArtifacts(map: mapboxgl.Map | null | undefined) {
    if (!map) {
      return;
    } // ⛑️  seguridad
    stopOptimizedRoutePulse();
    stopOptimizedRouteAnimation();
    forceRemovePlayerMarker();
    if (boatMarkerReference.current) {
      boatMarkerReference.current.remove();
      boatMarkerReference.current = null;
    }
    laylineLockRef.current = false;
    activeLaylineMarkRef.current = { mark: null, projection: null };

    /* ─── 1. Capas pre‑definidas ──────────────────────────────────────────── */
    for (const id of LAYERS_TO_DELETE) {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
    }

    /* ─── 2. Fuentes pre‑definidas ────────────────────────────────────────── */
    for (const id of SOURCES_TO_DELETE) {
      if (map.getSource(id)) {
        map.removeSource(id);
      }
    }

    /* ─── 3. Todo lo que contenga “highlight” ‑ dinámico ──────────────────── */
    if (!map.isStyleLoaded()) {
      return;
    } // evita acceder al style si aún no está

    // 3a) capas
    const style = map.getStyle();
    if (!style) {
      return;
    } // ⬅️  extra‑guarda

    // Capas que contengan "highlight"
    for (const l of (style.layers ?? []).filter((l) => l.id.includes('highlight'))) {
      map.getLayer(l.id) && map.removeLayer(l.id);
    }

    // 3‑b) Fuentes que contengan "highlight"
    for (const id of Object.keys(style.sources ?? {}).filter((id) => id.includes('highlight'))) {
      map.getSource(id) && map.removeSource(id);
    }
  }
  /** Vuelve a poner la UI en el estado por defecto (sin dependencias externas) */
  const resetUiState = (defaultBearing?: number | null, fromWind = false) => {
    lineModeApplyTokenRef.current += 1;
    // ① Capas y paneles
    setShowLegend(false);
    setLineMode('plain');
    setShowTacks(false);
    setMapInputMode('idle');
    setIsRaceStatsVisible(false);
    setIsPlayerActive(false);
    setIsPlaying(false);
    setPlayerIndex(0);
    activeLaylineMarkRef.current = { mark: null, projection: null };

    // ② Métricas y leyendas
    setSelectedMetric('bsp');
    setSelectedMetricWidth('tws');
    setColorLegend(null);

    // ③ Tacks, highlights y mediciones
    setTackStats([]);
    setTackMapping({});
    setHighlightSegments([]);
    setDraftHighlight(null);
    setPendingDraftHighlightId(null);
    laylineLockRef.current = false;
    lastTwdRef.current = null;
    initialFitAppliedRef.current = false;
    activeLaylineMarkRef.current = { mark: null, projection: null };

    // ④ Track orientation
    gridOriginRef.current = null;
    const hasBearing = Number.isFinite(defaultBearing);
    const bearingToApply = hasBearing ? normalizeBearing(defaultBearing as number) : 0;
    orientationIsWindRef.current = Boolean(fromWind && hasBearing);
    orientationIsStartLineRef.current = false;
    orientationBearingRef.current = bearingToApply;
    const isNorthish = Math.abs(angleDiff(bearingToApply, 0)) <= 1;
    setTrackModes(orientationIsWindRef.current ? 2 : isNorthish ? 1 : 0);
    setGridOrientationBearing(bearingToApply);
    setMapBearing(bearingToApply);
    if (mapInstance.current) {
      try {
        mapInstance.current.setBearing(bearingToApply);
      } catch {
        /* ignore */
      }
    }

    // ⑤ Panel flotante (si quieres devolverlo a su sitio original)
    setPanelSize({ width: 500, height: 400 });
    setPosition({
      top: window.innerHeight / 2 - 500 / 2,
      left: 100,
    });
  };

  function useLegendScale(isLegendEditing: boolean, setLegendScale: (v: number) => void) {
    const recalculateLegendScale = useCallback(() => {
      const container = legendContainerReference.current;
      if (isLegendEditing && container) {
        const currentHeight = container.getBoundingClientRect().height || 1;
        const targetHeight = window.innerHeight * 0.4; // 40% de ventana
        setLegendScale(Math.max(1, targetHeight / currentHeight));
      } else {
        setLegendScale(1);
      }
    }, [isLegendEditing, setLegendScale]);

    useEffect(() => {
      // Al montar y siempre que cambie isLegendEditing
      recalculateLegendScale();

      const container = legendContainerReference.current;
      const resizeObserver = container ? new ResizeObserver(recalculateLegendScale) : null;
      if (resizeObserver && container) {
        resizeObserver.observe(container);
      }
      window.addEventListener('resize', recalculateLegendScale);

      return () => {
        window.removeEventListener('resize', recalculateLegendScale);
        resizeObserver?.disconnect();
      };
    }, [isLegendEditing, recalculateLegendScale]);
  }

  useLegendScale(isLegendEditing, setLegendScale);

  const calculateAverageTwd = useCallback((): number | null => {
    if (!trackData || trackData.length === 0) {
      return null;
    }

    const validTwdValues = trackData
      .map((point) => point.twd)
      .filter((twd): twd is number => typeof twd === 'number' && Number.isFinite(twd))
      .map((twd) => normalizeBearing(twd));

    if (validTwdValues.length === 0) {
      return null;
    }

    const sumSin = validTwdValues.reduce((sum, value) => sum + Math.sin(toRadians(value)), 0);
    const sumCos = validTwdValues.reduce((sum, value) => sum + Math.cos(toRadians(value)), 0);
    const angle = Math.atan2(sumSin, sumCos);
    if (!Number.isFinite(angle)) {
      return null;
    }
    return normalizeBearing(toDegrees(angle));
  }, [trackData]);

  const getInitialTwdBearing = useCallback((): number | null => {
    if (!trackData || trackData.length === 0) {
      return null;
    }
    const firstValidPoint = trackData.find(
      (point) => typeof point.twd === 'number' && Number.isFinite(point.twd),
    );
    if (!firstValidPoint || typeof firstValidPoint.twd !== 'number') {
      return null;
    }
    const normalized = normalizeBearing(firstValidPoint.twd);
    return Number.isFinite(normalized) ? normalized : null;
  }, [trackData]);
  const isValidCoords = (point: TrackPoint | null | undefined): point is TrackPoint =>
    !!point &&
    typeof point.lat === 'number' &&
    typeof point.lon === 'number' &&
    Number.isFinite(point.lat) &&
    Number.isFinite(point.lon);

  const playerTimeline = useMemo(
    () =>
      safeTrackData
        .map((point, index) => {
          const rawTime = point.utc ?? point.date ?? null;
          const parsed = rawTime ? Date.parse(rawTime) : null;
          const timeMs = typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
          return { index, point, timeMs };
        })
        .filter(({ point }) => isValidCoords(point)),
    [safeTrackData],
  );

  const safePlayerIndex = Math.min(
    Math.max(playerIndex, 0),
    Math.max(playerTimeline.length - 1, 0),
  );
  const safePlayerIndexRef = useRef(safePlayerIndex);
  useEffect(() => {
    safePlayerIndexRef.current = safePlayerIndex;
  }, [safePlayerIndex]);
  useEffect(() => {
    // Fuerza un render completo al regenerar la línea temporal (p. ej. al cargar otro track)
    lastLaylineKeyRef.current = null;
  }, [playerTimeline]);
  const resetLaylineIndexCache = useCallback(() => {
    lastLaylineKeyRef.current = null;
  }, []);

  const currentPlayerPoint = playerTimeline[safePlayerIndex]?.point ?? null;
  useEffect(() => {
    currentPlayerPointRef.current = currentPlayerPoint;
  }, [currentPlayerPoint]);

  useEffect(() => {
    if (isPlaying) {
      resetLaylineIndexCache();
    }
  }, [isPlaying, resetLaylineIndexCache]);

  const playerMaxIndex = Math.max(playerTimeline.length - 1, 0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const { elapsedMs, remainingMs } = useMemo(() => {
    const firstTime = playerTimeline[0]?.timeMs ?? null;
    const lastTime = playerTimeline.at(-1)?.timeMs ?? null;
    const currentTime = playerTimeline[safePlayerIndex]?.timeMs ?? null;
    const hasFirst = typeof firstTime === 'number' && Number.isFinite(firstTime);
    const hasLast = typeof lastTime === 'number' && Number.isFinite(lastTime);
    const hasCurrent = typeof currentTime === 'number' && Number.isFinite(currentTime);
    const elapsed =
      hasFirst && hasCurrent && firstTime !== null && currentTime !== null
        ? Math.max(currentTime - firstTime, 0)
        : safePlayerIndex * PLAYBACK_INTERVAL_MS;
    const remaining =
      hasLast && hasCurrent && lastTime !== null && currentTime !== null
        ? Math.max(lastTime - currentTime, 0)
        : Math.max(playerMaxIndex - safePlayerIndex, 0) * PLAYBACK_INTERVAL_MS;
    return { elapsedMs: elapsed, remainingMs: remaining };
  }, [playerTimeline, safePlayerIndex, playerMaxIndex]);

  useEffect(() => {
    setPlayerIndex((previous) => Math.min(previous, Math.max(playerTimeline.length - 1, 0)));
  }, [playerTimeline.length]);

  const markNearestIndex = useMemo(() => {
    if (marks.length === 0 || safeTrackData.length === 0) {
      return new Map<number, number>();
    }
    const map = new Map<number, number>();
    for (const mark of marks) {
      if (!Number.isFinite(mark.lat) || !Number.isFinite(mark.lon)) {
        continue;
      }
      let bestIndex = -1;
      let bestDistance = Infinity;
      for (const [index, point] of safeTrackData.entries()) {
        if (!isValidCoords(point)) {
          continue;
        }
        const distance = calculateDistance(mark.lat, mark.lon, point.lat, point.lon);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      }
      if (bestIndex >= 0) {
        map.set(mark.id, bestIndex);
      }
    }
    return map;
  }, [marks, safeTrackData]);

  const getClosestMark = useCallback(
    (point: TrackPoint | null, pointIndex?: number | null) => {
      if (!point || marks.length === 0) {
        return null;
      }
      const validMarks = marks.filter(
        (m) =>
          Number.isFinite(m.lat) &&
          Number.isFinite(m.lon) &&
          typeof m.lat === 'number' &&
          typeof m.lon === 'number',
      );
      if (validMarks.length === 0) {
        return null;
      }

      if (pointIndex != null && markNearestIndex.size > 0) {
        let bestByIndex: (Mark & { distanceMeters: number }) | null = null;
        let bestIndexDelta = Infinity;
        let bestDistanceMeters = Infinity;

        for (const mark of validMarks) {
          const nearestIndex = markNearestIndex.get(mark.id);
          if (nearestIndex == null) {
            continue;
          }
          const indexDelta = Math.abs(nearestIndex - pointIndex);
          // Usa la distancia geográfica como desempate cuando el delta temporal es igual
          const distanceMeters = calculateDistance(point.lat, point.lon, mark.lat, mark.lon) * 1000;
          const isBetter =
            indexDelta < bestIndexDelta ||
            (indexDelta === bestIndexDelta && distanceMeters < bestDistanceMeters);
          if (isBetter) {
            bestIndexDelta = indexDelta;
            bestDistanceMeters = distanceMeters;
            bestByIndex = { ...mark, distanceMeters };
          }
        }

        if (bestByIndex) {
          return bestByIndex;
        }
      }

      let best = validMarks[0];
      let bestDistance = calculateDistance(point.lat, point.lon, best.lat, best.lon);
      for (const candidate of validMarks.slice(1)) {
        const distance = calculateDistance(point.lat, point.lon, candidate.lat, candidate.lon);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = candidate;
        }
      }
      return { ...best, distanceMeters: bestDistance * 1000 };
    },
    [markNearestIndex, marks],
  );

  const getPolarSpeed = useCallback(
    (twa: number, tws?: number | null) => {
      if (!polars || polars.angles.length === 0 || polars.tws.length === 0) {
        return null;
      }
      const twsValue = typeof tws === 'number' && Number.isFinite(tws) ? tws : null;
      const twsCandidates = polars.tws;
      let twsIndex = 0;
      if (twsValue !== null && twsCandidates.length > 0) {
        let bestDiff = Infinity;
        for (const [index, candidateTws] of twsCandidates.entries()) {
          const diff = Math.abs(candidateTws - twsValue);
          if (diff < bestDiff) {
            bestDiff = diff;
            twsIndex = index;
          }
        }
      }

      const targetTwa = Math.abs(twa);
      let angleIndex = 0;
      let angleDiff = Infinity;
      for (const [index, angle] of polars.angles.entries()) {
        const diff = Math.abs(angle - targetTwa);
        if (diff < angleDiff) {
          angleDiff = diff;
          angleIndex = index;
        }
      }

      return polars.speeds?.[angleIndex]?.[twsIndex] ?? null;
    },
    [polars],
  );
  const getOptimalHeadingFromPolars = useCallback(
    (point: TrackPoint, fallbackHeading?: number | null | undefined) => {
      // 1. Validation
      if (!polars || polars.angles.length === 0) {
        return null;
      }

      // 2. Determine TWD
      const twdBearing = (() => {
        if (typeof point.twd === 'number' && Number.isFinite(point.twd)) {
          const normalized = normalizeBearing(point.twd);
          lastTwdRef.current = normalized;
          return normalized;
        }
        if (Number.isFinite(lastTwdRef.current)) {
          return lastTwdRef.current as number;
        }
        if (Number.isFinite(mapBearingRef.current)) {
          return normalizeBearing(mapBearingRef.current);
        }
        return null;
      })();

      // 3. Determine Current Heading (for selection)
      const currentHeading =
        typeof fallbackHeading === 'number' && Number.isFinite(fallbackHeading)
          ? fallbackHeading
          : typeof point.hdg === 'number' && Number.isFinite(point.hdg)
            ? point.hdg
            : null;

      if (twdBearing == null || currentHeading == null) {
        return typeof currentHeading === 'number' && Number.isFinite(currentHeading)
          ? normalizeBearing(currentHeading)
          : null; // Cannot calculate optimal without TWD or Reference Heading
      }

      // 4. Calculate Optimal TWA from Polars (Upwind & Downwind)
      let bestUpwindTwa: number | null = null;
      let maxVmgUp = -Infinity;
      let bestDownwindTwa: number | null = null;
      let maxVmgDown = -Infinity;

      // Ensure we have a valid TWS to look up
      const twsToCheck =
        typeof point.tws === 'number' && Number.isFinite(point.tws) ? point.tws : 0;

      for (const angle of polars.angles) {
        const speed = getPolarSpeed(angle, twsToCheck);
        if (typeof speed !== 'number' || !Number.isFinite(speed)) {
          continue;
        }

        // Upwind VMG = speed * cos(TWA)
        const vmgUp = speed * Math.cos(toRadians(angle));
        if (vmgUp > maxVmgUp) {
          maxVmgUp = vmgUp;
          bestUpwindTwa = angle;
        }

        // Downwind VMG: maximize VMG downwind.
        // VMG Down = speed * cos(180 - TWA) = -speed * cos(TWA).
        // We look for max positive VMG Down.
        const vmgDown = speed * Math.cos(toRadians(180 - angle));
        if (vmgDown > maxVmgDown) {
          maxVmgDown = vmgDown;
          bestDownwindTwa = angle;
        }
      }

      if (bestUpwindTwa === null || bestDownwindTwa === null) {
        return normalizeBearing(currentHeading);
      }

      // 5. Generate 4 Candidates based on TWD
      const candidates = [
        normalizeBearing(twdBearing - bestUpwindTwa), // Starboard Upwind
        normalizeBearing(twdBearing + bestUpwindTwa), // Port Upwind
        normalizeBearing(twdBearing - bestDownwindTwa), // Starboard Downwind
        normalizeBearing(twdBearing + bestDownwindTwa), // Port Downwind
      ];

      // 6. Select Candidate closest to Current Heading
      let bestCandidate = candidates[0];
      let minDiff = 360;

      for (const cand of candidates) {
        const diff = Math.abs(angleDiff(cand, currentHeading));
        if (diff < minDiff) {
          minDiff = diff;
          bestCandidate = cand;
        }
      }

      return bestCandidate;
    },
    [getPolarSpeed, mapBearingRef, polars],
  );

  const forceRemovePlayerMarker = useCallback(() => {
    if (playerMarkerResizeHandlerRef.current && mapInstance.current) {
      mapInstance.current.off('zoom', playerMarkerResizeHandlerRef.current);
      playerMarkerResizeHandlerRef.current = null;
    }
    playerMarkerPositionRef.current = null;
    if (playerMarkerReference.current) {
      playerMarkerReference.current.remove();
      playerMarkerReference.current = null;
    }
    // Reset refs so a pending marker creation cannot reuse stale nodes
    playerMarkerElementRef.current = null;
    playerMarkerElementPromiseRef.current = null;
  }, [mapInstance]);

  const clearPlayerGraphics = useCallback(
    (options?: { preserveLaylines?: boolean }) => {
      const preserveLaylines = options?.preserveLaylines ?? false;
      const keepOptimizedStartIndex = Boolean(optimizedRoute);
      // Marca el modo player como inactivo (aunque preservemos laylines)
      isPlayerActiveRef.current = false;
      // Invalida cualquier render pendiente (heading/path) que pueda llegar tras eventos async
      playerRenderTokenRef.current += 1;

      resetLaylineIndexCache();
      if (!preserveLaylines) {
        laylineLockRef.current = false;
        activeLaylineMarkRef.current = { mark: null, projection: null };
      }
      forceRemovePlayerMarker();
      // Borra cualquier flecha de laylines cacheada
      if (boatMarkerReference.current) {
        boatMarkerReference.current.remove();
        boatMarkerReference.current = null;
      }
      const map = mapInstance.current;
      developmentLog.debug('[player-graphics] clear', {
        preserveLaylines,
        hasMarker: Boolean(playerMarkerReference.current),
        hasLaylinesLayer: map?.getLayer(playerLaylinesLayerId) != null,
        hasPathLayer: map?.getLayer(playerPathLayerId) != null,
      });
      if (!map) {
        return;
      }
      for (const [layerId, sourceId, casingLayerId] of [
        [playerLaylinesLayerId, playerLaylinesSourceId, playerLaylinesCasingLayerId],
        [playerPathLayerId, playerPathSourceId, playerPathCasingLayerId],
      ] as const) {
        // Si preservamos laylines, sólo evitamos borrar laylines; heading/path siempre se limpian
        if (preserveLaylines && layerId === playerLaylinesLayerId) {
          continue;
        }
        if (map.getLayer(layerId) || map.getSource(sourceId)) {
          developmentLog.debug('[player-graphics] removing layer/source', {
            layerId,
            hasLayer: Boolean(map.getLayer(layerId)),
            hasSource: Boolean(map.getSource(sourceId)),
          });
        }
        if (map.getLayer(casingLayerId)) {
          map.removeLayer(casingLayerId);
        }
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
      }
    },
    [
      mapInstance,
      playerLaylinesLayerId,
      playerLaylinesCasingLayerId,
      playerLaylinesSourceId,
      playerPathLayerId,
      playerPathCasingLayerId,
      playerPathSourceId,
      forceRemovePlayerMarker,

      optimizedRoute,
    ],
  );

  useEffect(() => {
    return () => clearPlayerGraphics();
  }, [clearPlayerGraphics]);

  const deactivatePlayerMode = useCallback(() => {
    isPlayerActiveRef.current = false;
    setIsPlaying(false);
    setIsPlayerActive(false);
    clearPlayerGraphics();
  }, [clearPlayerGraphics]);

  const getPlayerMarkerElement = useCallback(async () => {
    if (playerMarkerElementRef.current) {
      const clone = playerMarkerElementRef.current.cloneNode(true) as HTMLElement;
      clone.style.color = getCurrentMapOverlayPalette().boatMarker;
      return clone;
    }
    if (!playerMarkerElementPromiseRef.current) {
      playerMarkerElementPromiseRef.current = loadSvgAsHtml(arrowBoat)
        .then((svgText) => {
          const wrapper = document.createElement('div');
          wrapper.innerHTML = svgText;
          const svg = wrapper.querySelector('svg');
          if (!svg) {
            developmentLog.warn('[player-marker] arrow SVG not found');
            return null;
          }
          svg.style.pointerEvents = 'none';
          svg.style.position = 'absolute';
          svg.style.zIndex = '1000';
          svg.style.color = getCurrentMapOverlayPalette().boatMarker;
          adjustArrowSize(svg);
          playerMarkerElementRef.current = svg as unknown as HTMLElement;
          return playerMarkerElementRef.current;
        })
        .catch((error) => {
          developmentLog.warn('[player-marker] failed to load arrow svg', error);
          return null;
        });
    }
    return await playerMarkerElementPromiseRef.current;
  }, []);

  const updatePlayerPathLine = useCallback(
    (point: TrackPoint | null, heading: number | null | undefined) => {
      const map = mapInstance.current;
      const marker = playerMarkerReference.current;
      if (!map || !isPlayerActiveRef.current || !marker) {
        dbg('[player-path] skip: inactive or missing deps', {
          hasMap: Boolean(map),
          isActive: isPlayerActiveRef.current,
          hasMarker: Boolean(marker),
        });
        if (map?.getLayer(playerPathLayerId)) {
          map.removeLayer(playerPathLayerId);
        }
        if (map?.getSource(playerPathSourceId)) {
          map.removeSource(playerPathSourceId);
        }
        return;
      }
      const markerLngLat = marker?.getLngLat?.();
      const origin =
        point && Number.isFinite(point.lat) && Number.isFinite(point.lon)
          ? { lat: point.lat, lon: point.lon }
          : markerLngLat && Number.isFinite(markerLngLat.lat) && Number.isFinite(markerLngLat.lng)
            ? { lat: markerLngLat.lat, lon: markerLngLat.lng }
            : null;
      const headingFromRef =
        typeof playerMarkerHeadingRef.current === 'number' &&
        Number.isFinite(playerMarkerHeadingRef.current)
          ? playerMarkerHeadingRef.current
          : null;
      const headingToUse =
        typeof heading === 'number' && Number.isFinite(heading) ? heading : headingFromRef;
      const hasValidHeading = typeof headingToUse === 'number' && Number.isFinite(headingToUse);
      const hasOrigin = origin && Number.isFinite(origin.lat) && Number.isFinite(origin.lon);
      if (!hasValidHeading || !hasOrigin) {
        dbg('[player-path] skip: missing heading/origin', {
          headingFromRef,
          headingArg: heading,
          hasOrigin,
        });
        if (map.getLayer(playerPathLayerId)) {
          map.removeLayer(playerPathLayerId);
        }
        if (map.getSource(playerPathSourceId)) {
          map.removeSource(playerPathSourceId);
        }
        return;
      }

      const originDeltaMeters =
        origin && point
          ? calculateDistance(origin.lat, origin.lon, point.lat, point.lon) * 1000
          : null;
      const headingDrift =
        heading != null && headingFromRef != null ? angleDiff(heading, headingFromRef) : null;
      const originSource = markerLngLat ? 'marker' : 'point';
      dbg('[player-path] render', {
        headingToUse,
        headingFromRef,
        headingArg: heading,
        originSource,
        originDeltaMeters,
        headingDrift,
        renderToken: playerRenderTokenRef.current,
      });

      const baseLengthMeters =
        loaRef.current && Number.isFinite(loaRef.current) && loaRef.current > 0
          ? loaRef.current * 2 // exactamente dos esloras
          : OPTIMAL_HEADING_LINE_METERS;
      const lengthMeters = baseLengthMeters;
      const end = destinationPoint(origin.lat, origin.lon, headingToUse as number, lengthMeters);
      const data: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [origin.lon, origin.lat],
                [end.lon, end.lat],
              ],
            },
            properties: { heading: headingToUse },
          },
        ],
      };
      const renderToken = playerRenderTokenRef.current;
      const activeMap = mapInstance.current;
      const overlayPalette = getCurrentMapOverlayPalette();
      // Fast path: si ya existen capa y source, actualiza directo sin esperar a "style loaded"
      // (esto evita lag cuando el style check se bloquea por otras actualizaciones)
      if (
        activeMap &&
        activeMap.getSource(playerPathSourceId) &&
        activeMap.getLayer(playerPathLayerId)
      ) {
        safeSetGeoJSONData(activeMap, playerPathSourceId, data);
        activeMap.setPaintProperty(playerPathLayerId, LINE_COLOR, overlayPalette.playerPath);
        safeMoveLayer(activeMap, playerPathLayerId, trackLayerId);
        syncOptionalLineCasingLayer({
          map: activeMap,
          sourceId: playerPathSourceId,
          casingLayerId: playerPathCasingLayerId,
          beforeLayerId: playerPathLayerId,
          width: 6,
          palette: overlayPalette,
        });
        return;
      }

      ensureStyleLoaded(() => {
        if (renderToken !== playerRenderTokenRef.current) {
          return;
        }
        const map = mapInstance.current;
        if (!map) {
          return;
        }
        safeSetGeoJSONData(map, playerPathSourceId, data);
        if (!map.getLayer(playerPathLayerId)) {
          map.addLayer({
            id: playerPathLayerId,
            type: 'line',
            source: playerPathSourceId,
            paint: {
              'line-color': overlayPalette.playerPath,
              'line-width': 4,
              'line-opacity': 0.9,
            },
            layout: { [LINE_CAP]: 'round', [LINE_JOIN]: 'round' },
          });
        }
        map.setPaintProperty(playerPathLayerId, LINE_COLOR, overlayPalette.playerPath);
        safeMoveLayer(map, playerPathLayerId, trackLayerId);
        syncOptionalLineCasingLayer({
          map,
          sourceId: playerPathSourceId,
          casingLayerId: playerPathCasingLayerId,
          beforeLayerId: playerPathLayerId,
          width: 6,
          palette: overlayPalette,
        });
      });
    },
    [
      ensureStyleLoaded,
      isValidCoords,
      mapInstance,
      playerPathCasingLayerId,
      playerPathLayerId,
      playerPathSourceId,
      trackLayerId,
    ],
  );

  const updatePlayerMarker = useCallback(
    async (
      point: TrackPoint,
      heading: number | null | undefined,
      options?: { force?: boolean; optimalHeading?: number | null | undefined },
    ) => {
      const map = mapInstance.current;
      const force = options?.force ?? false;
      const optimalHeading = options?.optimalHeading;
      const headingForLine =
        typeof optimalHeading === 'number' && Number.isFinite(optimalHeading)
          ? optimalHeading
          : typeof heading === 'number' && Number.isFinite(heading)
            ? heading
            : null;
      if (!map || !isValidCoords(point) || (!isPlayerActiveRef.current && !force)) {
        dbg('[player-marker] skip update', {
          hasMap: Boolean(map),
          validPoint: isValidCoords(point),
          isActive: isPlayerActiveRef.current,
          force,
        });
        return;
      }
      try {
        if (playerMarkerReference.current) {
          playerMarkerReference.current.setLngLat([point.lon, point.lat]);
          playerMarkerPositionRef.current = { lat: point.lat, lon: point.lon };
        } else {
          const element = await getPlayerMarkerElement();
          if (!isPlayerActiveRef.current || !mapInstance.current) {
            return;
          }
          if (element) {
            const svgElement = element as unknown as SVGSVGElement;
            const resizeHandler = () => adjustArrowSize(svgElement);
            resizeHandler();
            if (playerMarkerResizeHandlerRef.current) {
              map.off('zoom', playerMarkerResizeHandlerRef.current);
            }
            map.on('zoom', resizeHandler);
            playerMarkerResizeHandlerRef.current = resizeHandler;

            const marker = new mapboxgl.Marker({
              element,
              rotationAlignment: 'map',
              anchor: 'center',
            })
              .setLngLat([point.lon, point.lat])
              .addTo(map);

            playerMarkerReference.current = marker;
            playerMarkerPositionRef.current = { lat: point.lat, lon: point.lon };
          }
        }
        // El marcador debe reflejar el heading real del punto; sólo cae al heading óptimo si no hay dato real.
        const headingForMarker =
          typeof heading === 'number' && Number.isFinite(heading)
            ? heading
            : typeof headingForLine === 'number' && Number.isFinite(headingForLine)
              ? headingForLine
              : null;
        if (playerMarkerReference.current && headingForMarker != null) {
          playerMarkerReference.current.setRotation(headingForMarker);
        }
        playerMarkerHeadingRef.current = headingForMarker;
        dbg('[player-marker] updated', {
          heading,
          headingForLine,
          optimalHeading,
          isActive: isPlayerActiveRef.current,
          renderToken: playerRenderTokenRef.current,
        });
        updatePlayerPathLine(point, headingForLine);
      } catch (error) {
        developmentLog.warn('[MapPanel] player marker setLngLat failed:', error);
      }
    },
    [getPlayerMarkerElement, mapInstance, optimizedRouteTrack, updatePlayerPathLine],
  );

  // Conserva el rumbo actual antes de activar el player para evitar saltos a norte
  const preserveCurrentBearing = useCallback(() => {
    const map = mapInstance.current;
    if (!map) {
      return null;
    }
    const current = map.getBearing();
    if (!Number.isFinite(current)) {
      return null;
    }
    const normalized = normalizeBearing(current);
    orientationBearingRef.current = normalized;
    setMapBearing(normalized);
    setGridOrientationBearing((previous) => (previous === normalized ? previous : normalized));
    return normalized;
  }, [mapInstance]);

  const updateLaylinesForPoint = useCallback(
    (
      point: TrackPoint,
      mark: (Mark & { distanceMeters: number }) | null,
      fallbackBearing: number | null,
      allowInactive = false,
    ) => {
      const map = mapInstance.current;
      if (
        !map ||
        !mark ||
        !isValidCoords(point) ||
        !Number.isFinite(mark.lat) ||
        !Number.isFinite(mark.lon)
      ) {
        return;
      }
      if (!isPlayerActiveRef.current && !allowInactive) {
        return;
      }
      const baseWindCandidate =
        typeof point.twd === 'number' && Number.isFinite(point.twd)
          ? normalizeBearing(point.twd)
          : (fallbackBearing ?? mapBearing);
      const baseWind = Number.isFinite(baseWindCandidate) ? baseWindCandidate : null;
      if (baseWind == null) {
        return;
      }
      const headingTowardBoat = calculateHeading(
        { lat: mark.lat, lon: mark.lon },
        { lat: point.lat, lon: point.lon },
      );
      const hasWindAndHeading = Number.isFinite(headingTowardBoat);

      // Elige el eje (baseWind o baseWind+180) cuya bisectriz queda más cerca del marcador
      const oppositeWind = normalizeBearing(baseWind + 180);
      const distanceToBase = distanceToBearingLineMeters(
        { lat: mark.lat, lon: mark.lon },
        { lat: point.lat, lon: point.lon },
        baseWind,
      );
      const distanceToOpposite = distanceToBearingLineMeters(
        { lat: mark.lat, lon: mark.lon },
        { lat: point.lat, lon: point.lon },
        oppositeWind,
      );
      const windAxis =
        distanceToOpposite < distanceToBase
          ? oppositeWind // usa el eje cuya bisectriz pasa más cerca del player
          : baseWind;

      const isUpwindLeg =
        hasWindAndHeading && windAxis != null
          ? Math.abs(angleDiff(headingTowardBoat, windAxis)) > 90 // marca a barlovento
          : true;
      const windForLaylines =
        hasWindAndHeading && windAxis != null
          ? isUpwindLeg
            ? windAxis // ciñendo: TWD como referencia
            : normalizeBearing(windAxis + 180) // popa: TWD+180 como referencia
          : windAxis;
      const tws = typeof point.tws === 'number' && Number.isFinite(point.tws) ? point.tws : null;
      // Encuentra el ángulo óptimo respecto al eje del viento usando polars; fallback al ángulo por defecto
      let optimalAxisAngle = DEFAULT_LAYLINE_ANGLE;
      if (polars) {
        let bestProjection = -Infinity;
        for (const angle of polars.angles) {
          const speed = getPolarSpeed(angle, tws);
          if (speed == null) {
            continue;
          }
          const projection = isUpwindLeg
            ? speed * Math.cos(toRadians(angle)) // VMG contra el viento
            : speed * Math.cos(toRadians(180 - angle)); // VMG a favor del viento
          if (projection > bestProjection) {
            bestProjection = projection;
            optimalAxisAngle = isUpwindLeg ? angle : 180 - angle;
          }
        }
      }
      // Calcula la longitud mínima para que el segmento entre los extremos de laylines pase por el barco
      const liveDistanceMeters = calculateDistance(mark.lat, mark.lon, point.lat, point.lon) * 1000;
      const distanceToBoatMeters =
        Number.isFinite(liveDistanceMeters) && liveDistanceMeters > 0
          ? liveDistanceMeters
          : Number.isFinite(mark.distanceMeters) && mark.distanceMeters > 0
            ? mark.distanceMeters
            : MIN_PLAYER_LINE_METERS;
      const axisBearing =
        windForLaylines == null ? headingTowardBoat : normalizeBearing(windForLaylines + 180);
      const deltaFromAxis = axisBearing == null ? null : angleDiff(headingTowardBoat, axisBearing);
      const halfAngle = Math.max(optimalAxisAngle, 1); // evita dividir por cero en cos/sin
      const halfAngleRad = toRadians(halfAngle);
      const cosHalfAngle = Math.cos(halfAngleRad);
      const sinHalfAngle = Math.sin(halfAngleRad);
      const deltaRad = deltaFromAxis == null ? null : toRadians(deltaFromAxis);
      const axialProjection =
        deltaRad == null ? Number.NaN : distanceToBoatMeters * Math.cos(deltaRad);
      const lateralOffset =
        deltaRad == null ? Number.NaN : Math.abs(distanceToBoatMeters * Math.sin(deltaRad));
      const projectedLength =
        Number.isFinite(axialProjection) && axialProjection > 0 && cosHalfAngle > 1e-6
          ? axialProjection / cosHalfAngle
          : null;
      // Si el barco está dentro del ángulo entre laylines, baseLength >= lateralLength y el segmento pasa por él
      const lateralLength =
        Number.isFinite(lateralOffset) && lateralOffset > 0 && sinHalfAngle > 1e-6
          ? lateralOffset / sinHalfAngle
          : projectedLength;
      const geometryLength =
        projectedLength == null
          ? null
          : Math.max(projectedLength, lateralLength ?? projectedLength);
      const distanceMeters =
        geometryLength == null
          ? Math.max(distanceToBoatMeters + MIN_PLAYER_LINE_METERS, MIN_PLAYER_LINE_METERS * 2)
          : Math.max(geometryLength, MIN_PLAYER_LINE_METERS);

      const bearings = [
        normalizeBearing(windForLaylines + optimalAxisAngle),
        normalizeBearing(windForLaylines - optimalAxisAngle),
      ].map((bearing) => normalizeBearing(bearing + 180)); // invertir dirección

      const features: GeoJSON.Feature<GeoJSON.LineString>[] = bearings.map((bearing, index) => {
        const end = destinationPoint(mark.lat, mark.lon, bearing, distanceMeters);
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [mark.lon, mark.lat],
              [end.lon, end.lat],
            ],
          },
          properties: {
            bearing,
            side: index === 0 ? 'starboard' : 'port',
          },
        };
      });

      const data: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
        type: 'FeatureCollection',
        features,
      };

      const renderToken = playerRenderTokenRef.current;
      const overlayPalette = getCurrentMapOverlayPalette();
      const laylineColorExpression = [
        'match',
        ['get', 'side'],
        'starboard',
        overlayPalette.laylineStarboard,
        'port',
        overlayPalette.laylinePort,
        overlayPalette.laylineFallback,
      ] as unknown as DataDrivenPropertyValueSpecification<string>;

      // Fast path: updates data immediately if layer exists, handling sync updates (prevents lag)
      if (map.getSource(playerLaylinesSourceId) && map.getLayer(playerLaylinesLayerId)) {
        safeSetGeoJSONData(map, playerLaylinesSourceId, data);
        map.setPaintProperty(playerLaylinesLayerId, LINE_COLOR, laylineColorExpression);
        safeMoveLayer(map, playerLaylinesLayerId, trackLayerId);
        syncOptionalLineCasingLayer({
          map,
          sourceId: playerLaylinesSourceId,
          casingLayerId: playerLaylinesCasingLayerId,
          beforeLayerId: playerLaylinesLayerId,
          width: 4,
          palette: overlayPalette,
          dashArray: [1.5, 1.5],
        });
        return;
      }

      ensureStyleLoaded(() => {
        if (renderToken !== playerRenderTokenRef.current) {
          developmentLog.debug('[player-laylines] skipped stale render');
          return;
        }
        if (!isPlayerActiveRef.current && !allowInactive) {
          return;
        }
        safeSetGeoJSONData(map, playerLaylinesSourceId, data);
        if (!map.getLayer(playerLaylinesLayerId)) {
          map.addLayer({
            id: playerLaylinesLayerId,
            type: 'line',
            source: playerLaylinesSourceId,
            paint: {
              'line-color': laylineColorExpression,
              'line-width': 2,
              'line-dasharray': [1.5, 1.5],
              'line-opacity': 0.9,
            },
            layout: { [LINE_CAP]: 'round', [LINE_JOIN]: 'round' },
          });
        }
        map.setPaintProperty(playerLaylinesLayerId, LINE_COLOR, laylineColorExpression);
        safeMoveLayer(map, playerLaylinesLayerId, trackLayerId);
        syncOptionalLineCasingLayer({
          map,
          sourceId: playerLaylinesSourceId,
          casingLayerId: playerLaylinesCasingLayerId,
          beforeLayerId: playerLaylinesLayerId,
          width: 4,
          palette: overlayPalette,
          dashArray: [1.5, 1.5],
        });
      });
    },
    [
      ensureStyleLoaded,
      mapBearing,
      playerLaylinesCasingLayerId,
      playerLaylinesLayerId,
      playerLaylinesSourceId,
      trackLayerId,
      polars,
    ],
  );

  const updatePlayerGraphics = useCallback(() => {
    const currentIndex = safePlayerIndexRef.current;
    const laylineMark = activeLaylineMarkRef.current.mark;
    const currentLaylineKey = `${currentIndex}:${laylineMark?.id ?? 'none'}`;
    const shouldRenderPlayer = isPlayerActive || laylineLockRef.current;
    if (!shouldRenderPlayer || playerTimeline.length === 0) {
      if (!shouldRenderPlayer) {
        clearPlayerGraphics({ preserveLaylines: laylineLockRef.current });
      }
      dbg('[player] skip update: inactive or empty timeline', {
        isPlayerActive: shouldRenderPlayer,
        timelineLength: playerTimeline.length,
      });
      return;
    }

    const timelineEntry = playerTimeline[currentIndex];
    if (!timelineEntry) {
      deactivatePlayerMode();
      dbg('[player] timeline entry missing, stopping playback', {
        safePlayerIndex: currentIndex,
        timelineLength: playerTimeline.length,
      });
      return;
    }
    const markerPosition = playerMarkerPositionRef.current;
    const hasMarkerCoords =
      markerPosition && Number.isFinite(markerPosition.lat) && Number.isFinite(markerPosition.lon);
    // Usa siempre las coordenadas del frame actual; sólo recurre al marker si el punto es inválido
    const point: TrackPoint | null = isValidCoords(timelineEntry.point)
      ? timelineEntry.point
      : hasMarkerCoords
        ? ({ ...(timelineEntry.point as object), ...markerPosition } as TrackPoint)
        : null;
    if (!point || !isValidCoords(point)) {
      deactivatePlayerMode();
      dbg('[player] timeline entry invalid after fallback, stopping playback', {
        safePlayerIndex: currentIndex,
        timelineLength: playerTimeline.length,
      });
      return;
    }
    const map = mapInstance.current;
    if (!map) {
      return;
    }

    const hasFrameChanged = currentLaylineKey !== lastLaylineKeyRef.current;
    const hasLaylineRender =
      !laylineMark ||
      (Boolean(map.getSource(playerLaylinesSourceId)) &&
        Boolean(map.getLayer(playerLaylinesLayerId)));
    const hasPlayerGraphics = Boolean(playerMarkerReference.current) && hasLaylineRender;
    if (!hasFrameChanged && hasPlayerGraphics) {
      if (lastPlayerSkipKeyRef.current !== currentLaylineKey) {
        lastPlayerSkipKeyRef.current = currentLaylineKey;
        dbg('[player] skip update: same frame and graphics already present', {
          currentIndex,
          currentLaylineKey,
        });
      }
      return;
    }
    lastPlayerSkipKeyRef.current = null;

    const previousCandidate = playerTimeline[Math.max(currentIndex - 1, 0)]?.point;
    const nextCandidate =
      playerTimeline[Math.min(currentIndex + 1, playerTimeline.length - 1)]?.point;
    const previousPoint = isValidCoords(previousCandidate)
      ? (previousCandidate as TrackPoint)
      : null;
    const nextPoint = isValidCoords(nextCandidate) ? (nextCandidate as TrackPoint) : null;
    const derivedHeading =
      typeof point.hdg === 'number' && Number.isFinite(point.hdg)
        ? normalizeBearing(point.hdg)
        : previousPoint && (previousPoint.lat !== point.lat || previousPoint.lon !== point.lon)
          ? calculateHeading(
              { lat: previousPoint.lat ?? point.lat, lon: previousPoint.lon ?? point.lon },
              { lat: point.lat, lon: point.lon },
            )
          : nextPoint
            ? calculateHeading(
                { lat: point.lat, lon: point.lon },
                { lat: nextPoint.lat ?? point.lat, lon: nextPoint.lon ?? point.lon },
              )
            : null;
    const fallbackBearing = Number.isFinite(derivedHeading) ? derivedHeading : mapBearing;
    const twdBearing =
      typeof point.twd === 'number' && Number.isFinite(point.twd)
        ? normalizeBearing(point.twd)
        : null;
    let closestMark = getClosestMark(point, currentIndex);
    let targetBearing = fallbackBearing;

    const baseWindForProjection = Number.isFinite(twdBearing)
      ? (twdBearing as number)
      : fallbackBearing;
    const hasLockedMark = laylineLockRef.current && activeLaylineMarkRef.current.mark;
    const lockedMark = hasLockedMark ? activeLaylineMarkRef.current.mark : null;

    if (lockedMark) {
      closestMark = lockedMark;
    }

    const optimalHeading = getOptimalHeadingFromPolars(point, derivedHeading);

    void updatePlayerMarker(point, derivedHeading, { optimalHeading });

    if (lockedMark) {
      const projection =
        baseWindForProjection != null && closestMark
          ? windProjection(closestMark, point, baseWindForProjection)
          : null;
      activeLaylineMarkRef.current = { mark: closestMark, projection };
      updateLaylinesForPoint(point, closestMark, fallbackBearing, true);
    } else if (
      closestMark &&
      Number.isFinite(closestMark.lat) &&
      Number.isFinite(closestMark.lon)
    ) {
      const projection =
        baseWindForProjection != null && Number.isFinite(baseWindForProjection)
          ? windProjection(closestMark, point, baseWindForProjection)
          : null;

      if (
        activeLaylineMarkRef.current.mark &&
        activeLaylineMarkRef.current.projection != null &&
        projection != null &&
        activeLaylineMarkRef.current.projection >= 0 &&
        projection >= 0
      ) {
        if (baseWindForProjection == null) {
          closestMark = activeLaylineMarkRef.current.mark;
        } else {
          const activeMark = activeLaylineMarkRef.current.mark;
          const currentDistance = distanceToBearingLineMeters(
            { lat: activeMark.lat, lon: activeMark.lon },
            { lat: point.lat, lon: point.lon },
            baseWindForProjection,
          );
          const candidateDistance = distanceToBearingLineMeters(
            { lat: closestMark.lat, lon: closestMark.lon },
            { lat: point.lat, lon: point.lon },
            baseWindForProjection,
          );
          const preferCandidate = isPlayerActive || candidateDistance < currentDistance;
          closestMark = preferCandidate ? closestMark : activeMark;
          activeLaylineMarkRef.current = {
            mark: closestMark,
            projection: preferCandidate ? projection : activeLaylineMarkRef.current.projection,
          };
        }
      } else {
        activeLaylineMarkRef.current = { mark: closestMark, projection };
      }

      if (closestMark) {
        targetBearing = calculateHeading(
          { lat: point.lat, lon: point.lon },
          { lat: closestMark.lat, lon: closestMark.lon },
        );
        updateLaylinesForPoint(point, closestMark, fallbackBearing, true);
      }
    }
    if (!closestMark) {
      const activeMap = mapInstance.current;
      if (activeMap?.getLayer(playerLaylinesLayerId)) {
        activeMap.removeLayer(playerLaylinesLayerId);
      }
      if (activeMap?.getSource(playerLaylinesSourceId)) {
        activeMap.removeSource(playerLaylinesSourceId);
      }
      activeLaylineMarkRef.current = { mark: null, projection: null };
      dbg('[player] no valid mark for laylines', { pointIndex: currentIndex });
    }

    const firstTime = playerTimeline[0]?.timeMs ?? null;
    const currentTime = playerTimeline[currentIndex]?.timeMs ?? null;
    const hasFirstTime = typeof firstTime === 'number' && Number.isFinite(firstTime);
    const hasCurrentTime = typeof currentTime === 'number' && Number.isFinite(currentTime);
    const elapsedSeconds =
      hasFirstTime && hasCurrentTime && firstTime !== null && currentTime !== null
        ? Math.max((currentTime - firstTime) / 1000, 0)
        : (currentIndex * PLAYBACK_INTERVAL_MS) / 1000;

    const nextLaylineKey = `${currentIndex}:${activeLaylineMarkRef.current.mark?.id ?? 'none'}`;
    lastLaylineKeyRef.current = nextLaylineKey;
  }, [
    clearPlayerGraphics,
    getClosestMark,
    isPlayerActive,
    isPlaying,
    mapBearing,
    mapInstance,
    playerLaylinesLayerId,
    playerLaylinesSourceId,
    playerTimeline,
    updateLaylinesForPoint,
    updatePlayerMarker,
    getOptimalHeadingFromPolars,
    safeTrackData,
    deactivatePlayerMode,
  ]);

  useEffect(() => {
    mapBearingRef.current = mapBearing;
    updateLaylinesForPointRef.current = updateLaylinesForPoint;
    updatePlayerMarkerRef.current = updatePlayerMarker;
    updatePlayerGraphicsRef.current = updatePlayerGraphics;
  }, [mapBearing, updateLaylinesForPoint, updatePlayerMarker, updatePlayerGraphics]);

  useEffect(() => {
    isPlayerActiveRef.current = isPlayerActive;
    const wasActive = wasPlayerActiveRef.current;
    wasPlayerActiveRef.current = isPlayerActive;

    if (!isPlayerActive) {
      if (wasActive) {
        setIsPlaying(false);
        clearPlayerGraphics({ preserveLaylines: false });
        laylineLockRef.current = false;
        activeLaylineMarkRef.current = { mark: null, projection: null };
        // Reaplica orientación guardada sólo al salir del player para evitar saltos inesperados
        if (mapInstance.current) {
          const bearing = orientationBearingRef.current ?? mapBearing;
          if (Number.isFinite(bearing)) {
            setBearingIfMeaningfullyChanged(
              mapInstance.current,
              bearing,
              PLAYER_BEARING_SYNC_TOLERANCE_DEG,
            );
            setMapBearing(normalizeBearing(bearing));
          }
        }
      }
      return;
    }
    lastLaylineKeyRef.current = null;
    updatePlayerGraphics();
  }, [clearPlayerGraphics, isPlayerActive, mapBearing, updatePlayerGraphics]);

  useEffect(() => {
    if (!isPlayerActive || playerTimeline.length === 0) {
      return;
    }
    updatePlayerGraphics();
  }, [isPlayerActive, playerTimeline, safePlayerIndex, updatePlayerGraphics]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    const refreshPlayer = () => {
      if (isPlayerActive) {
        updatePlayerGraphics();
      }
    };
    if (isPlayerActive && map.loaded()) {
      refreshPlayer();
    } else if (isPlayerActive) {
      map.once('load', refreshPlayer);
    }
    map.on('styledata', refreshPlayer);
    return () => {
      map.off('styledata', refreshPlayer);
      map.off('load', refreshPlayer);
    };
  }, [isPlayerActive, mapInstance, updatePlayerGraphics]);

  useEffect(() => {
    if (!isPlaying && mapInstance.current) {
      try {
        mapInstance.current.stop();
      } catch (error) {
        developmentLog.warn('[player] map.stop() failed on pause/stop', error);
      }
    }
  }, [isPlaying, mapInstance]);

  useEffect(() => {
    if (!isPlayerActive || !isPlaying || playerTimeline.length === 0 || isOptimizingRoute) {
      return;
    }
    const desiredInterval = PLAYBACK_INTERVAL_MS / Math.max(playbackRate, 0.25);
    const intervalMs = Math.max(Math.round(desiredInterval), 16); // evita intervalos <~1 frame
    const stepSize = Math.max(1, Math.round(intervalMs / Math.max(desiredInterval, 1)));
    const handle = window.setInterval(() => {
      setPlayerIndex((current) => {
        const next = current + stepSize * PLAYER_STEP_POINTS;
        if (next >= playerTimeline.length - 1) {
          setIsPlaying(false);
          return playerTimeline.length - 1;
        }
        return next;
      });
    }, intervalMs);
    return () => window.clearInterval(handle);
  }, [isOptimizingRoute, isPlayerActive, isPlaying, playerTimeline.length, playbackRate]);

  // ─────────────────────────────────────────────────────────────
  // ① Recalcula la leyenda siempre que cambien modo, métrica o datos
  useEffect(() => {
    if (lineMode === 'speed' || lineMode === 'tactical') {
      updateColorLegend(selectedMetric, lineMode);
    }
  }, [lineMode, metricBounds, selectedMetric, trackData]);

  // ─────────────────────────────────────────────────────────────
  // ② Aplica el coloreado sólo cuando la leyenda ya está lista
  useEffect(() => {
    if ((lineMode !== 'speed' && lineMode !== 'tactical') || !colorLegend) {
      return;
    }

    const lineModeApplyToken = lineModeApplyTokenRef.current;
    const metricForRecolor: ColorMetricKey = lineMode === 'tactical' ? 'heel' : selectedMetric;
    ensureStyleLoaded(() => {
      if (lineModeApplyTokenRef.current !== lineModeApplyToken) {
        return;
      }
      if (!mapInstance.current?.getSource(trackSourceId)) {
        return;
      }
      colorTrackByMetric(metricForRecolor, selectedMetricWidth, lineMode, safeTrackData);
    });
  }, [
    colorLegend,
    ensureStyleLoaded,
    lineMode,
    selectedMetric,
    selectedMetricWidth,
    safeTrackData,
    trackData,
    trackSourceId,
  ]);

  const applyLaylinesAtTarget = useCallback(
    (
      target: { id?: number; lat: number; lon: number },
      options?: { source?: 'buoy' | 'map' | 'buoy-snap' },
    ) => {
      const isBuoySource = options?.source === 'buoy' || options?.source === 'buoy-snap';
      if (mapInputMode !== 'idle' && mapInputMode !== 'player-target') {
        return;
      }
      if (isBuoySource && !isPlayerActiveRef.current) {
        developmentLog.debug('[laylines] skip buoy click because player is inactive');
        return;
      }
      const markerLngLat = playerMarkerReference.current?.getLngLat();
      let markerBasedPoint: TrackPoint | null = null;
      if (markerLngLat) {
        const base = currentPlayerPointRef.current;
        markerBasedPoint = base
          ? { ...base, lat: markerLngLat.lat, lon: markerLngLat.lng }
          : ({ lat: markerLngLat.lat, lon: markerLngLat.lng } as TrackPoint);
      }
      const activePointCandidate =
        markerBasedPoint && isValidCoords(markerBasedPoint)
          ? markerBasedPoint
          : currentPlayerPointRef.current;
      const activePoint = isValidCoords(activePointCandidate)
        ? (activePointCandidate as TrackPoint)
        : (() => {
            let nearest: TrackPoint | null = null;
            let best = Infinity;
            for (const candidate of safeTrackData) {
              if (!isValidCoords(candidate)) {
                continue;
              }
              const d = calculateDistance(target.lat, target.lon, candidate.lat, candidate.lon);
              if (d < best) {
                best = d;
                nearest = candidate as TrackPoint;
              }
            }
            return nearest;
          })();
      const distanceMeters = activePoint
        ? Math.max(
            calculateDistance(activePoint.lat, activePoint.lon, target.lat, target.lon) * 1000,
            MIN_PLAYER_LINE_METERS,
          )
        : MIN_PLAYER_LINE_METERS;
      const markId = Number.isFinite(target.id)
        ? (target.id as number)
        : manualLaylineIdRef.current--;
      const markWithDistance = { ...target, id: markId, distanceMeters };
      laylineLockRef.current = true;
      if (!isPlayerActive && !isBuoySource) {
        setMapInputMode('player-target');
        setIsPlayerActive(true);
      }
      const windFromPoint =
        typeof activePoint?.twd === 'number' && Number.isFinite(activePoint.twd)
          ? normalizeBearing(activePoint.twd)
          : null;
      const baseWind =
        windFromPoint ??
        (Number.isFinite(mapBearingRef.current) && mapBearingRef.current != null
          ? normalizeBearing(mapBearingRef.current)
          : null);
      const projection =
        activePoint && baseWind != null
          ? windProjection(markWithDistance, activePoint, baseWind)
          : null;
      activeLaylineMarkRef.current = { mark: markWithDistance, projection };
      developmentLog.debug('[laylines] mark click', {
        source: options?.source ?? 'map',
        buoyId: markWithDistance.id,
        activePointExists: Boolean(activePoint),
        baseWind,
        projection,
        isPlayerActive: isPlayerActiveRef.current,
        hasMarker: Boolean(playerMarkerReference.current),
        distanceMeters: Math.round(distanceMeters),
        markCoords: { lat: markWithDistance.lat, lon: markWithDistance.lon },
        pointCoords: activePoint ? { lat: activePoint.lat, lon: activePoint.lon } : null,
      });
      if (!activePoint) {
        return;
      }
      updateLaylinesForPointRef.current?.(activePoint, markWithDistance, baseWind, true);
      // Fuerza el player a mantenerse activo visualmente al cambiar laylines manuales
      isPlayerActiveRef.current = isPlayerActiveRef.current || !isBuoySource;
      const headingToMark = calculateHeading(
        { lat: activePoint.lat, lon: activePoint.lon },
        { lat: markWithDistance.lat, lon: markWithDistance.lon },
      );
      const optimalHeading = getOptimalHeadingFromPolars(activePoint, headingToMark);
      // Asegura marker + heading + path sincronizados con la nueva boya
      void updatePlayerMarkerRef.current?.(activePoint, headingToMark, {
        force: true,
        optimalHeading,
      });
      developmentLog.debug('[laylines] post-click render', {
        hasMarker: Boolean(playerMarkerReference.current),
        headingToMark,
        playerToken: playerRenderTokenRef.current,
      });
      if (!playerMarkerReference.current) {
        requestAnimationFrame(() => {
          if (isPlayerActiveRef.current) {
            developmentLog.debug('[laylines] raf re-render marker', {
              hasMarker: Boolean(playerMarkerReference.current),
              playerToken: playerRenderTokenRef.current,
            });
            void updatePlayerMarkerRef.current?.(activePoint, headingToMark, {
              force: true,
              optimalHeading,
            });
          }
        });
      }
      if (isPlayerActiveRef.current) {
        updatePlayerGraphicsRef.current?.();
        // Refresca marker y línea óptima en el siguiente frame para evitar parpadeos al cambiar laylines
        requestAnimationFrame(() => {
          if (isPlayerActiveRef.current) {
            updatePlayerGraphicsRef.current?.();
          }
        });
      } else {
        developmentLog.debug('[laylines] skip refresh because player inactive');
      }
    },
    [getOptimalHeadingFromPolars, isPlayerActive, mapInputMode, safeTrackData],
  );

  // Asegúrate de registrar el evento en el mapa al cargarlo
  const previousTrackReference = useRef<TrackPoint[] | null>(null);
  const completedTrackRenderKeyRef = useRef<string | null>(null);
  const hasRenderedTrackLine = () => {
    const map = mapInstance.current;
    return Boolean(map && map.getLayer(trackLayerId) && map.getSource(trackSourceId));
  };

  useEffect(() => {
    // Evita limpiar y recomputar si el mismo array de track sigue activo
    if (previousTrackReference.current === trackData && hasRenderedTrackLine()) {
      return;
    }
    const cameraLoadToken = cameraLoadTokenRef.current + 1;
    cameraLoadTokenRef.current = cameraLoadToken;
    pendingInitialCameraRef.current = true;
    setIsMapLoading(true);

    // Si no hay puntos válidos, ocultamos el loader y limpiamos lo que haya
    if (safeTrackData.length === 0) {
      if (mapInstance.current) {
        clearMapArtifacts(mapInstance.current);
      }
      pendingInitialCameraRef.current = false;
      completedTrackRenderKeyRef.current = null;
      setIsMapLoading(false);
      dbg('[player] safeTrackData vacío tras filtro de coordenadas; se omite carga');
      return;
    }

    // Si no hay track (aunque haya mapa), limpia y sal
    if (trackData.length === 0) {
      if (mapInstance.current) {
        clearMapArtifacts(mapInstance.current);
        resetUiState();
      }
      pendingInitialCameraRef.current = false;
      completedTrackRenderKeyRef.current = null;
      setIsMapLoading(false);
      return;
    }

    if (boatMarkerReference.current) {
      boatMarkerReference.current.remove();
      boatMarkerReference.current = null;
    }

    // 1) Si no hay mapa, no podemos continuar.
    if (!mapInstance.current) {
      return;
    }
    previousTrackReference.current = trackData;

    // 1‑a) Siempre limpia la rejilla y capas previas antes de cargar nuevos datos
    clearMapArtifacts(mapInstance.current);

    const safeTrack = safeTrackData;
    // 3) Lanza la rutina que ya tenías
    const initialBearing = getInitialTwdBearing();
    const averageBearing = calculateAverageTwd();
    const orientationCandidate =
      initialBearing !== null && Number.isFinite(initialBearing)
        ? initialBearing
        : Number.isFinite(averageBearing ?? Number.NaN)
          ? averageBearing
          : null;

    const orientationFromWind = Number.isFinite(orientationCandidate ?? Number.NaN);

    resetUiState(orientationCandidate, orientationFromWind);

    actualizarMapaConTrack(safeTrack, cameraLoadToken);
  }, [mapReadyToken, trackData]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    const applyOverlayLayers = () => {
      const overlayPalette = getCurrentMapOverlayPalette();
      const activeIds = new Set<number>();
      for (const layer of relatedTrackLayers) {
        const coordinates = layer.trackData
          .map((point) => [point.lon, point.lat] as [number, number])
          .filter(([lon, lat]) => Number.isFinite(lon) && Number.isFinite(lat));
        if (coordinates.length < 2) {
          continue;
        }
        const geojson: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'LineString', coordinates },
              properties: { label: layer.label },
            },
          ],
        };
        const sourceId = `${overlaySourcePrefix}${layer.fileId}`;
        const layerId = `${overlayLayerPrefix}${layer.fileId}`;
        const casingLayerId = `${layerId}-casing`;
        // Asegura la fuente antes de intentar añadir la capa (Mapbox lanza si falta)
        safeSetGeoJSONData(map, sourceId, geojson);
        const hasSource = !!map.getSource(sourceId);
        if (!hasSource) {
          try {
            map.addSource(sourceId, { type: 'geojson', data: geojson });
          } catch {
            developmentLog.warn('[MapPanel][overlay] source missing, skipping layer', {
              sourceId,
              layerId,
            });
            continue;
          }
        }

        if (map.getLayer(layerId)) {
          map.setPaintProperty(layerId, LINE_COLOR, layer.color);
          map.setPaintProperty(layerId, LINE_WIDTH, 3);
          map.setPaintProperty(layerId, 'line-opacity', 0.9);
        } else {
          try {
            map.addLayer({
              id: layerId,
              type: 'line',
              source: sourceId,
              paint: {
                [LINE_COLOR]: layer.color,
                [LINE_WIDTH]: 3,
                'line-opacity': 0.9,
                'line-dasharray': [1.5, 1.5],
              },
              layout: { [LINE_CAP]: 'round', [LINE_JOIN]: 'round' },
            });
          } catch (error) {
            developmentLog.warn('[MapPanel][overlay] unable to add layer', {
              sourceId,
              layerId,
              error,
            });
            continue;
          }
        }
        safeMoveLayer(map, layerId);
        syncOptionalLineCasingLayer({
          map,
          sourceId,
          casingLayerId,
          beforeLayerId: layerId,
          width: 5,
          palette: overlayPalette,
          dashArray: [1.5, 1.5],
          color: overlayPalette.overlayCasing,
          opacity: overlayPalette.overlayCasingOpacity,
        });
        activeIds.add(layer.fileId);
      }

      const style = map.getStyle();
      for (const styleLayer of style?.layers ?? []) {
        if (!styleLayer.id.startsWith(overlayLayerPrefix)) {
          continue;
        }
        const isCasingLayer = styleLayer.id.endsWith('-casing');
        const baseLayerId = isCasingLayer
          ? styleLayer.id.slice(0, -'-casing'.length)
          : styleLayer.id;
        const idPart = baseLayerId.replace(overlayLayerPrefix, '');
        const numericId = Number.parseInt(idPart, 10);
        if (Number.isNaN(numericId) || activeIds.has(numericId)) {
          continue;
        }
        if (isCasingLayer) {
          if (map.getLayer(styleLayer.id)) {
            map.removeLayer(styleLayer.id);
          }
          continue;
        }
        const casingLayerId = `${styleLayer.id}-casing`;
        if (map.getLayer(casingLayerId)) {
          map.removeLayer(casingLayerId);
        }
        if (map.getLayer(styleLayer.id)) {
          map.removeLayer(styleLayer.id);
        }
        const sourceId = `${overlaySourcePrefix}${numericId}`;
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
      }
    };

    ensureStyleLoaded(applyOverlayLayers);
  }, [relatedTrackLayers, overlayLayerPrefix, overlaySourcePrefix, mapInstance]);

  useEffect(() => {
    if (lineMode !== 'ladder') {
      removeLadderLayers();
      return;
    }
    if (!ladderCollections) {
      removeLadderLayers();
      return;
    }
    ensureStyleLoaded(() => {
      const map = mapInstance.current;
      if (!map) {
        return;
      }
      const overlayPalette = getCurrentMapOverlayPalette();
      const { lines, labels } = ladderCollections;
      if (map.getSource(ladderLinesSourceId)) {
        (map.getSource(ladderLinesSourceId) as mapboxgl.GeoJSONSource).setData(lines);
      } else {
        map.addSource(ladderLinesSourceId, { type: 'geojson', data: lines });
        map.addLayer({
          id: ladderLinesLayerId,
          type: 'line',
          source: ladderLinesSourceId,
          paint: {
            'line-color': overlayPalette.ladderLine,
            'line-width': 1,
            'line-opacity': 0.7,
          },
          layout: { [LINE_CAP]: 'round', [LINE_JOIN]: 'round' },
        });
      }
      map.setPaintProperty(ladderLinesLayerId, LINE_COLOR, overlayPalette.ladderLine);
      map.setPaintProperty(ladderLinesLayerId, 'line-opacity', 0.7);
      safeMoveLayer(map, ladderLinesLayerId);

      if (labels.features.length > 0) {
        if (map.getSource(ladderLabelsSourceId)) {
          (map.getSource(ladderLabelsSourceId) as mapboxgl.GeoJSONSource).setData(labels);
        } else {
          map.addSource(ladderLabelsSourceId, { type: 'geojson', data: labels });
          map.addLayer({
            id: ladderLabelsLayerId,
            type: 'symbol',
            source: ladderLabelsSourceId,
            layout: {
              'text-field': ['get', 'label'],
              'text-font': FONT_STACK_BOLD,
              'text-size': 11,
              'text-anchor': 'center',
              'text-offset': [0, 0.8],
            },
            paint: {
              'text-color': overlayPalette.ladderLabel,
              'text-halo-color': overlayPalette.ladderLabelHalo,
              'text-halo-width': 0.9,
            },
          });
        }
        map.setPaintProperty(ladderLabelsLayerId, 'text-color', overlayPalette.ladderLabel);
        map.setPaintProperty(
          ladderLabelsLayerId,
          'text-halo-color',
          overlayPalette.ladderLabelHalo,
        );
        map.setPaintProperty(ladderLabelsLayerId, 'text-halo-width', 0.9);
        safeMoveLayer(map, ladderLabelsLayerId);
      } else if (map.getLayer(ladderLabelsLayerId)) {
        map.removeLayer(ladderLabelsLayerId);
        if (map.getSource(ladderLabelsSourceId)) {
          map.removeSource(ladderLabelsSourceId);
        }
      }
    });
  }, [
    ladderCollections,
    ladderLabelsLayerId,
    ladderLabelsSourceId,
    ladderLinesLayerId,
    ladderLinesSourceId,
    lineMode,
    mapInstance,
    removeLadderLayers,
  ]);

  useEffect(() => {
    return () => {
      const map = mapInstance.current;
      if (!map) {
        return;
      }
      const style = map.getStyle();
      for (const layer of style?.layers ?? []) {
        if (layer.id.startsWith(overlayLayerPrefix) && map.getLayer(layer.id)) {
          map.removeLayer(layer.id);
        }
      }
      for (const sourceId of Object.keys(style?.sources ?? {})) {
        if (sourceId.startsWith(overlaySourcePrefix) && map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
      }
    };
  }, [mapInstance, overlayLayerPrefix, overlaySourcePrefix]);

  useEffect(() => {
    if (!mapInstance.current || !isMeasuringDistance) {
      return;
    }

    const handleMouseMove = (event: mapboxgl.MapMouseEvent) => onTrackHover(event);
    mapInstance.current.off('mousemove', handleMouseMove); // desengancha si ya estaba
    mapInstance.current.on('mousemove', handleMouseMove);

    return () => {
      mapInstance.current?.off('mousemove', handleMouseMove);
    };
  }, [isMeasuringDistance, startPoint]);

  useEffect(() => {
    if (mapInstance.current) {
      const handleMouseMove = (event: mapboxgl.MapMouseEvent) => {
        requestAnimationFrame(() => updateInfoText(event.lngLat.lng, event.lngLat.lat));
      };
      mapInstance.current.off('mousemove', handleMouseMove);
      mapInstance.current.on('mousemove', handleMouseMove);
      return () => {
        mapInstance.current?.off('mousemove', handleMouseMove);
      };
    }
  }, [startPoint, isMeasuringDistance, mapInstance]);

  // useEffect para registrar el evento de movimiento del ratón
  useEffect(() => {
    if (mapInstance.current) {
      // Asegúrate de que el evento 'mousemove' esté siempre registrado
      const handleMouseMove = (event: mapboxgl.MapMouseEvent) => onTrackHover(event);
      mapInstance.current.off('mousemove', handleMouseMove);
      mapInstance.current.on('mousemove', handleMouseMove);

      // Limpia el evento solo al desmontar el componente para evitar conflictos
      return () => {
        mapInstance.current?.off('mousemove', handleMouseMove);
      };
    }
  }, [trackData, isMeasuringDistance, startPoint]);
  useEffect(() => {
    if (!showTacks || !maneuverVisibility) {
      setManeuverLabels({});
      return;
    }
    if (tacks.length === 0 || cachedTrackData.length === 0) {
      setTackStats([]);
      setTackMapping((previous) => {
        return Object.fromEntries(
          Object.entries(previous).filter(([key]) => !key.startsWith(`${idSuffix}-`)),
        );
      });
      setManeuverLabels({});
      return;
    }

    const visible = tacks
      .map((tack, index) => ({ tack, index }))
      .filter(({ index }) => maneuverVisibility[index] !== false);

    const nextStats: TackStats[] = [];
    const nextMapping: Record<string, number> = {};
    const labelMap: Record<number, number> = {};
    let labelCounter = 1;

    for (const { tack, index } of visible) {
      const segment = cachedTrackData.slice(tack.start_index, tack.end_index + 1);
      if (segment.length === 0) {
        continue;
      }
      const stats = calcularEstadisticasTack(segment, cachedTrackData);
      nextStats.push({
        tack: index + 1,
        maxRudder: stats.maxRudder ?? 0,
        timeToMaxRudder: stats.timeToMaxRudder ?? undefined,
        avgSogBeforeTack: stats.avgSogBeforeTack ?? undefined,
        avgTwaBeforeTack: stats.avgTwaBeforeTack ?? undefined,
        SogMin: stats.SogMin ?? undefined,
        timeToRecovery90: stats.timeToRecovery90 ?? undefined,
        timeToRecovery95: stats.timeToRecovery95 ?? undefined,
        type: stats.type === 'gybe' ? 'gybe' : 'tack',
      });
      nextMapping[`${idSuffix}-${index + 1}`] = labelCounter;
      labelMap[index] = labelCounter;
      labelCounter += 1;
    }

    setManeuverLabels(labelMap);
    setTackStats(nextStats);
    setTackMapping((previous) => {
      const otherPanels = Object.fromEntries(
        Object.entries(previous).filter(([key]) => !key.startsWith(`${idSuffix}-`)),
      );
      return { ...otherPanels, ...nextMapping };
    });
  }, [maneuverVisibility, tacks, cachedTrackData, idSuffix, showTacks]);

  useEffect(() => {
    if (!mapInstance.current || !startPoint) {
      return;
    }

    // Crear el elemento de información de distancia.
    const distanceInfoElement = document.createElement('div');
    distanceInfoElement.id = 'distance-info';
    distanceInfoElement.style.color = 'var(--contrast)';
    distanceInfoElement.style.padding = '4px 8px';
    distanceInfoElement.style.background = 'var(--surface)';
    distanceInfoElement.style.border = '1px solid var(--borderSubtle)';
    distanceInfoElement.style.borderRadius = '4px';
    distanceInfoElement.style.fontSize = '10px';
    distanceInfoElement.style.whiteSpace = 'nowrap';
    distanceInfoElement.style.pointerEvents = 'none';
    distanceInfoElement.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
  }, [mapInstance, startPoint, isMeasuringDistance]);

  useEffect(() => {
    if (!mapInstance.current) {
      return;
    }
    const map = mapInstance.current;

    const handleResize = () => {
      if (!isMeasuringDistance) {
        return;
      }
      const start = isArrowFixed && startPointOld ? startPointOld : startPoint;
      const end = isArrowFixed ? endPointReference.current : null;
      if (!start || !end) {
        return;
      }

      const distributionKm = calculateDistance(start.lat, start.lng, end.lat, end.lng);
      const distributionM = distributionKm * 1000;
      const distributionNm = distributionKm / 1.852;
      const distributionLoa = loa > 0 ? distributionM / loa : 0;
      const heading = calculateHeading(
        { lat: start.lat, lon: start.lng },
        { lat: end.lat, lon: end.lng },
      );

      const info =
        `${heading.toFixed(2)}° / ` +
        `${
          distributionM < 1000 ? distributionM.toFixed(2) + ' m' : distributionKm.toFixed(2) + ' km'
        } / ` +
        `${distributionNm.toFixed(2)} nm / ${distributionLoa.toFixed(2)} LOAs`;

      updateTextLabel(start.lng, start.lat, end.lng, end.lat, info);
    };

    map.on('resize', handleResize);

    /* ⚠️  IMPORTANTE: la función de limpieza NO debe devolver nada */
    return () => {
      map.off('resize', handleResize);
    };
  }, [mapInstance, isMeasuringDistance, isArrowFixed, startPoint, startPointOld, loa]);

  // ⬇️ Sustituye TODO el useEffect (el que pinta/borra los tacks) por este
  useEffect(() => {
    if (!showTacks || tacks.length === 0 || cachedTrackData.length === 0 || !lastFileId) {
      removeTacks();
      for (const marker of labelMarkers.current) {
        marker.remove();
      }
      labelMarkers.current = [];
      return;
    }

    renderTacks();
    updateTackColors();
    updateTackLabels();
  }, [showTacks, tacks, cachedTrackData, lastFileId]);

  useEffect(() => {
    if (!mapInstance.current) {
      return;
    }

    removeTacks();
    for (const marker of labelMarkers.current) {
      marker.remove();
    }
    labelMarkers.current = [];
  }, [selectedFile?.id]);

  useEffect(() => {
    updateTackColors();
  }, [tackStats, maneuverVisibility]);

  useEffect(() => {
    updateTackLabels();
  }, [tackStats, tackMapping, tacks, cachedTrackData, maneuverVisibility]);

  useEffect(() => {
    updateTackColors();
  }, [tackStats, maneuverVisibility]);
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }

    // Función que efectivamente pinta las boyas
    const drawMarks = () => {
      // Si no hay marcas, nada que hacer

      renderMarks(marks, loa);
    };

    // Si el estilo aún no está listo, esperamos al evento 'styledata'
    if (map.isStyleLoaded()) {
      // Si ya está listo, pintamos inmediatamente

      drawMarks();
    } else {
      map.once('styledata', drawMarks);
    }
  }, [editingRange, editingSegmentId, loa, marks, safeTrackData, trackData]);
  useEffect(() => {
    if (!mapContainer.current) {
      return;
    }
    let cleanup: (() => void) | null = null;
    const updatePanelMetrics = () => {
      const element = mapContainer.current;
      if (!element) {
        return;
      }
      const { clientWidth, clientHeight } = element;

      // queremos la mitad de ancho y alto
      const width = clientWidth / 2;
      const height = clientHeight / 2;

      // centramos en el contenedor
      const left = (clientWidth - width) / 2;
      const top = (clientHeight - height) / 2;

      setPanelSize({ width, height });
      setPosition({ top, left });
    };

    // medida inicial
    ensureStyleLoaded(() => {
      updatePanelMetrics();

      const map = mapInstance.current;

      if (!map) {
        return;
      }

      const onResize = () => safeResizeMap(map);
      map.on('resize', onResize);

      cleanup = () => {
        map.off('resize', onResize); // <-- importante
      };
    });
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [trackData, ensureStyleLoaded, mapInstance]);
  useEffect(() => {
    /* Se ejecuta UNA sola vez POR contenedor.  
	   Si el MapPanel se desmonta y React crea un nuevo <div className="map-container">,
	   debemos comprobar si el mapInstance aún apunta al contenedor antiguo.         */
    let disposed = false;
    let rotateListener: (() => void) | null = null;
    let handleCameraChange: (() => void) | null = null;
    let rotationDebugStart:
      | ((event_: mapboxgl.MapboxEvent & { originalEvent?: MouseEvent | TouchEvent }) => void)
      | null = null;
    let rotationDebugEnd: (() => void) | null = null;
    let canvasContextMenuDebug: ((event: MouseEvent) => void) | null = null;
    let basemapToneAndRoadsRefresh: (() => void) | null = null;
    const rotationDebugTick: (() => void) | null = null;
    let rotationStartBearing: number | null = null;
    dbg('useEffect‑init | mapExists=', Boolean(mapInstance.current));

    /* 🆕 1) Si ya hay un mapa PERO su contenedor es distinto del actual,
		   destruimos el mapa anterior y dejamos que se cree uno nuevo. */
    // Si el MapPanel se desmonta y React monta otro div, aseguramos coherencia
    if (
      mapInstance.current &&
      mapContainer.current &&
      mapInstance.current.getContainer() !== mapContainer.current
    ) {
      mapInstance.current.remove(); // 🔥 mata la instancia huérfana
      mapInstance.current = null;
      try {
        if (typeof mapboxgl.clearPrewarmedResources === 'function') {
          mapboxgl.clearPrewarmedResources();
        }
      } catch {
        /* ignore */
      }
    }

    /* 2) Si ya hay un mapa válido, el loader lo controla el efecto del track. */
    if (mapInstance.current) {
      dbg('existing map; keep loader state owned by track fit');
      return; // mismo contenedor ⇒ no tocar
    }

    /* 3) A partir de aquí permanece tu código original de creación */
    interface MapboxTokenResponse {
      token: string;
    }
    const getCredentials = async () => {
      const storedToken = localStorage.getItem('token') ?? '';
      const headers = {
        Authorization: `Bearer ${storedToken}`,
      };
      const baseUrl = process.env.REACT_APP_API_BASE_URL ?? '';
      const tokenRequest = cachedMapboxToken
        ? Promise.resolve({ data: { token: cachedMapboxToken } })
        : axios.post<MapboxTokenResponse>(`${baseUrl}/api/auth/mapbox-token`, {}, { headers });
      const [tokenResponse, styleResponse] = await Promise.all([
        tokenRequest,
        axios.post<MapboxStyleResponse>(`${baseUrl}/api/auth/mapbox-style-url`, {}, { headers }),
      ]);
      if (!tokenResponse.data?.token) {
        throw new Error('Invalid Mapbox credentials');
      }
      const styleURLs = normalizeMapboxStyleURLs(styleResponse.data);
      cachedMapboxToken = tokenResponse.data.token;

      return {
        token: tokenResponse.data.token,
        styleURLs,
      };
    };

    void (async () => {
      try {
        /* ────────────────────────────────────────────────────────────────
				   Esperamos a que el contenedor tenga un tamaño (> 0 × 0) antes
				   de inicializar el mapa. En layouts como “quadrants” el panel
				   inferior‑derecho puede montarse con ancho/alto 0 px y Mapbox
				   no dispara el evento 'load', dejando el spinner infinito.
				──────────────────────────────────────────────────────────────── */
        if (mapContainer.current) {
          const hasSize = () => {
            const element = mapContainer.current;
            return !!(element && element.offsetWidth > 0 && element.offsetHeight > 0);
          };
          dbg('Container initial size', {
            w: mapContainer.current?.offsetWidth,
            h: mapContainer.current?.offsetHeight,
          });

          // If the container is still 0 × 0 px we wait **at most** 1.5 s for it
          // to get a real size. This prevents the loader from hanging forever
          // when a panel (e.g. side “b” in quadrants) is not actually rendered.
          if (!hasSize()) {
            await new Promise<void>((resolve) => {
              const ro = new ResizeObserver(() => {
                if (hasSize()) {
                  dbg('ResizeObserver: size ready', {
                    w: mapContainer.current?.offsetWidth ?? 0,
                    h: mapContainer.current?.offsetHeight ?? 0,
                  });
                  ro.disconnect();
                  resolve();
                }
              });
              ro.observe(mapContainer.current as HTMLElement);

              // ─── fallback ────────────────────────────────────────────────
              setTimeout(() => {
                dbg('ResizeObserver timeout → container never became visible');
                ro.disconnect();
                resolve();
              }, 1500); // 1.5 s is enough for normal layout transitions
            });

            // Still no dimensions? Abort map creation and hide the spinner.
            if (!hasSize()) {
              dbg('Skipping Mapbox init for hidden container');
              setIsMapLoading(false);
              return; // ⬅️  exit early
            }
          }
        }
        setIsMapLoading(true);
        dbg('setIsMapLoading → true');

        const { token, styleURLs } = await getCredentials();
        if (disposed || !mapContainer.current) {
          return;
        } // seguridad

        const mapColorMode = getCurrentMapColorMode();
        const styleColorScheme = resolveMapColorScheme(getCurrentColorScheme(), mapColorMode);
        setMapColorScheme(styleColorScheme);
        const styleURL = selectMapboxStyleURL(styleURLs, styleColorScheme, mapColorMode);
        mapboxgl.accessToken = token;
        const map = new mapboxgl.Map({
          container: mapContainer.current,
          style: styleURL,
          preserveDrawingBuffer: true,
          center: [0, 0],
          zoom: 1,
        });

        dbg('Map instance created');

        // Ensure rotation gestures stay enabled (right‑click drag + touch rotation)
        map.dragRotate.enable();
        map.touchZoomRotate.enable();
        if (typeof map.touchZoomRotate.enableRotation === 'function') {
          map.touchZoomRotate.enableRotation();
        }
        const logRotationStatus = (reason: string) => {
          const dragRotateEnabled = !!map.dragRotate?.isEnabled?.();
          const touchZoomRotateEnabled = !!map.touchZoomRotate?.isEnabled?.();
          developmentLog.debug('[MapPanel] rotate-debug status', {
            reason,
            dragRotateEnabled,
            touchZoomRotateEnabled,
            bearing: map.getBearing(),
            pitch: map.getPitch(),
          });
        };
        logRotationStatus('after-enable');
        rotationDebugStart = (
          event_: mapboxgl.MapboxEvent & { originalEvent?: MouseEvent | TouchEvent },
        ) => {
          rotationStartBearing = map.getBearing();
          developmentLog.debug('[MapPanel] rotatestart', {
            button: (event_.originalEvent as MouseEvent | undefined)?.button,
            defaultPrevented: (event_.originalEvent as MouseEvent | undefined)?.defaultPrevented,
            bearing: map.getBearing(),
            pitch: map.getPitch(),
          });
        };
        rotationDebugEnd = () => {
          const endBearing = map.getBearing();
          const rotationDelta =
            rotationStartBearing === null ? null : endBearing - rotationStartBearing;
          developmentLog.debug('[MapPanel] rotateend delta', {
            start: rotationStartBearing,
            end: endBearing,
            delta: rotationDelta,
          });
          rotationStartBearing = null;
          logRotationStatus('rotateend');
        };

        map.on('rotatestart', rotationDebugStart);
        map.on('rotateend', rotationDebugEnd);
        canvasContextMenuDebug = (event: MouseEvent) => {
          developmentLog.debug('[MapPanel] contextmenu on canvas', {
            defaultPrevented: event.defaultPrevented,
            target: (event.target as HTMLElement)?.className,
          });
          event.preventDefault();
        };
        map.getCanvas().addEventListener('contextmenu', canvasContextMenuDebug);

        // Si ya existe una cámara compartida del mismo archivo, arrancamos con
        // el mismo zoom/orientación que el principal.
        applySharedCameraIfMatchingTrack();

        map.addControl(
          new mapboxgl.NavigationControl({ showCompass: false, showZoom: true }),
          'top-right',
        );
        const transparentPixel = {
          width: 1,
          height: 1,
          data: new Uint8Array([0, 0, 0, 0]),
        };
        // ── Guard missing/undefined sprite images to avoid runtime crashes ──
        const styleInternals = map as unknown as {
          style?: {
            imageManager?: {
              getImage: (id: string, scope?: string) => unknown;
              addScope?: (scope: string) => void;
              images?: Map<string, unknown>;
              scope?: string;
            };
          };
        };
        const imageManager = styleInternals.style?.imageManager;
        if (imageManager && typeof imageManager.getImage === 'function') {
          const originalImageGet = imageManager.getImage.bind(imageManager);
          const defaultScope = 'default';
          imageManager.getImage = (id: string, scope?: string) => {
            const resolvedScope = scope ?? defaultScope;
            const hasScope =
              typeof imageManager.images?.has === 'function' &&
              imageManager.images.has(resolvedScope);
            if (!hasScope && typeof imageManager.addScope === 'function') {
              try {
                imageManager.addScope(resolvedScope);
              } catch (error) {
                developmentLog.warn('[MapPanel] imageManager.addScope failed:', error);
                return null;
              }
            }
            try {
              return originalImageGet(id, resolvedScope);
            } catch (error) {
              developmentLog.warn('[MapPanel] guarded imageManager.getImage failed:', error);
              return null;
            }
          };
        }
        // Patch image lookups: if a sprite is missing, inject a transparent pixel instead of throwing.
        const originalStyle = map.getStyle as unknown as { getImage?: (id: string) => unknown };
        const originalGetImage =
          (originalStyle &&
            typeof originalStyle.getImage === 'function' &&
            originalStyle.getImage.bind(originalStyle)) ||
          null;
        map.on('styleimagemissing', (event_) => {
          const id = event_.id;
          if (!id || map.hasImage(id)) {
            return;
          }
          try {
            map.addImage(id, transparentPixel, { sdf: true });
          } catch (error) {
            developmentLog.warn('[MapPanel] styleimagemissing addImage skipped:', error);
          }
        });
        if (originalGetImage) {
          map.getStyle = (() => {
            const style = originalStyle ? { ...originalStyle } : {};
            return {
              ...style,
              getImage: (id: string) => {
                const found = originalGetImage(id);
                if (found) {
                  return found;
                }
                if (!map.hasImage(id)) {
                  try {
                    map.addImage(id, transparentPixel, { sdf: true });
                  } catch {
                    /* ignore */
                  }
                }
                return originalGetImage(id) ?? transparentPixel;
              },
            } as typeof originalStyle;
          }) as unknown as mapboxgl.Map['getStyle'];
        }

        const refreshBasemapToneAndRoads = () => {
          if (!map.isStyleLoaded()) {
            return;
          }
          ensureAnalysisBasemapToneLayer(map, styleColorScheme, mapColorMode);
          applyDarkBasemapRoadContrast(map, styleColorScheme);
        };
        basemapToneAndRoadsRefresh = refreshBasemapToneAndRoads;
        map.on('styledata', refreshBasemapToneAndRoads);
        map.on('idle', refreshBasemapToneAndRoads);

        map.once('load', () => {
          dbg("Map 'load' event fired");
          refreshBasemapToneAndRoads();
          if (!pendingInitialCameraRef.current) {
            setIsMapLoading(false);
          }
          // El loader seguirá activo hasta que el track esté dibujado
        });

        map.on('error', (event_) => {
          developmentLog.error('[MapPanel] map error:', event_.error);
          setIsMapLoading(false); // quita el spinner aunque falle la carga
        });

        mapInstance.current = map; // guárdalo para reutilizar
        setMapReadyToken((previous) => previous + 1);
        setMapboxStyleURL(styleURL); // por si otros hooks lo usan

        handleCameraChange = () => captureCameraState();
        map.on('moveend', handleCameraChange);
        map.on('zoomend', handleCameraChange);
        map.on('rotateend', handleCameraChange);

        const updateBearing = (event_?: mapboxgl.MapboxEvent) => {
          const next = normalizeBearing(map.getBearing());
          setMapBearing(next);
          const isUserRotate = Boolean(
            (event_ as { originalEvent?: unknown } | undefined)?.originalEvent,
          );
          if (isUserRotate) {
            orientationIsWindRef.current = false;
            orientationIsStartLineRef.current = false;
            orientationBearingRef.current = next;
            const averageTwd = calculateAverageTwd();
            const tolerance = 2; // degrees
            let nextMode = 0;
            if (Math.abs(angleDiff(next, 0)) <= tolerance) {
              nextMode = 1;
            } else if (averageTwd !== null && Math.abs(angleDiff(next, averageTwd)) <= tolerance) {
              nextMode = 2;
              orientationIsWindRef.current = true;
            }
            setTrackModes(nextMode);
            setGridOrientationBearing((previous) => (previous === next ? previous : next));
          }
        };

        rotateListener = updateBearing;
        updateBearing();
        map.on('rotate', updateBearing);

        // El efecto principal de trackData se reejecuta con mapReadyToken y aporta
        // el cameraLoadToken activo; no arranques un render huérfano desde init.

        // Función que efectivamente pinta las boyas
        const drawMarks = () => {
          // Si no hay marcas, nada que hacer

          renderMarks(marks, loa);
        };
        // Si el estilo aún no está listo, esperamos al evento 'styledata'
        if (map.isStyleLoaded()) {
          // Si ya está listo, pintamos inmediatamente

          drawMarks();
        } else {
          map.once('styledata', drawMarks);
        }
      } catch (error) {
        developmentLog.error('[MapPanel] init error:', error);
        if (!disposed) {
          setIsMapLoading(false);
          dbg('setIsMapLoading → false');
        }
      }
    })();

    /* cleanup: se ejecuta cuando el componente se DESMONTA
		   o cuando React Fast‑Refresh hace HMR en desarrollo.
		   🔥 Es crucial *destruir* la instancia de Mapbox para
		   evitar el crash “applyProjectionUpdate is undefined”
		   que aparece al recargar módulos. */
    return () => {
      disposed = true;

      // Elimina la instancia si sigue viva
      if (mapInstance.current) {
        if (rotateListener) {
          mapInstance.current.off('rotate', rotateListener);
        }
        if (mapInstance.current) {
          if (rotationDebugStart) {
            mapInstance.current.off('rotatestart', rotationDebugStart);
          }
          if (rotationDebugTick) {
            mapInstance.current.off('rotate', rotationDebugTick);
          }
          if (rotationDebugEnd) {
            mapInstance.current.off('rotateend', rotationDebugEnd);
          }
          try {
            if (canvasContextMenuDebug) {
              mapInstance.current
                .getCanvas()
                .removeEventListener('contextmenu', canvasContextMenuDebug);
            }
          } catch {
            /* ignore */
          }
        }
        if (handleCameraChange) {
          mapInstance.current.off('moveend', handleCameraChange);
          mapInstance.current.off('zoomend', handleCameraChange);
          mapInstance.current.off('rotateend', handleCameraChange);
        }
        if (basemapToneAndRoadsRefresh) {
          mapInstance.current.off('styledata', basemapToneAndRoadsRefresh);
          mapInstance.current.off('idle', basemapToneAndRoadsRefresh);
        }
        try {
          mapInstance.current.remove();
        } catch (error) {
          // En producción a veces ya está eliminada
          developmentLog.warn('[MapPanel] Mapbox remove() error (ignorado):', error);
        }
        mapInstance.current = null;
      }
      try {
        if (typeof mapboxgl.clearPrewarmedResources === 'function') {
          mapboxgl.clearPrewarmedResources();
        }
      } catch {
        /* ignore */
      }
    };
  }, []);

  useEffect(() => {
    const ajustarLeft = () => {
      const container = legendContainerReference.current;
      if (!container) {
        return;
      }

      // Suaviza la transición de 'left'
      container.style.transition = 'left 0.3s ease-in-out';

      // Calcular ancho máximo de los hijos
      const allDescendants = container.querySelectorAll('*');
      let maxWidth = 0;
      for (const child of allDescendants) {
        const w = (child as HTMLElement).offsetWidth || 0;
        if (w > maxWidth) {
          maxWidth = w;
        }
      }

      // Si estamos editando, escalamos; si no, ancho real
      const finalWidth = isLegendEditing ? maxWidth * legendScale : maxWidth;

      container.style.left = `${finalWidth / 2 - 10}px`;
    };

    window.addEventListener('resize', ajustarLeft);
    // Ejecutar inmediatamente
    ajustarLeft();

    return () => {
      window.removeEventListener('resize', ajustarLeft);
    };
  }, [showLegend, lineMode, selectedMetricWidth, isLegendEditing, legendScale]);

  useEffect(() => {
    if (!mapInstance.current) {
      return;
    }

    const handleTackClick = (event_: mapboxgl.MapLayerMouseEvent) => {
      try {
        if (!event_.features?.length) {
          return;
        }

        // Sólo clicks simples con botón izquierdo
        if (event_.originalEvent.button !== 0 || event_.originalEvent.detail !== 1) {
          return;
        }
        event_.originalEvent.stopPropagation();

        type TackFeatureProperties = {
          tackIndex: number;
          maxRudder?: number;
          timeToMaxRudder?: number;
          avgSogBeforeTack?: number;
          avgTwaBeforeTack?: number;
          SogMin?: number;
          timeToRecovery90?: number;
          timeToRecovery95?: number;
          type?: string;
        };
        const clicked = event_.features[0] as unknown as GeoJSON.Feature<
          GeoJSON.Geometry,
          TackFeatureProperties
        >;
        const tackIndexAbs: number = clicked.properties?.tackIndex + 1;
        if (typeof tackIndexAbs !== 'number') {
          return;
        }

        const mappingKey = `${idSuffix}-${tackIndexAbs}`;

        setTackStats((previous) => {
          const exists = previous.some((t) => t.tack === tackIndexAbs);

          if (exists) {
            // ─── quitar ────────────────────────────────────────────────────
            onManeuverVisibilityChange?.(tackIndexAbs - 1, false);
            setTackMapping((m) => {
              const { [mappingKey]: _, ...rest } = m;
              return rest;
            });
            return previous.filter((t) => t.tack !== tackIndexAbs);
          }

          // ─── añadir ─────────────────────────────────────────────────────
          onManeuverVisibilityChange?.(tackIndexAbs - 1, true);
          const newLabel =
            tackMapping[mappingKey] ?? // ¿ya tenía número?
            (Object.values(tackMapping).length > 0
              ? Math.max(...Object.values(tackMapping)) + 1
              : 1);

          setTackMapping((m) => ({ ...m, [mappingKey]: newLabel }));

          !isRaceStatsVisible && setIsRaceStatsVisible(true);

          return [
            ...previous,
            {
              tack: tackIndexAbs,
              maxRudder: clicked.properties?.maxRudder ?? 0,
              timeToMaxRudder: clicked.properties?.timeToMaxRudder ?? 0,
              avgSogBeforeTack: clicked.properties?.avgSogBeforeTack ?? 0,
              avgTwaBeforeTack: clicked.properties?.avgTwaBeforeTack ?? 0,
              SogMin: clicked.properties?.SogMin ?? 0,
              timeToRecovery90: clicked.properties?.timeToRecovery90 ?? 0,
              timeToRecovery95: clicked.properties?.timeToRecovery95 ?? 0,
              type:
                clicked.properties?.type === 'tack' || clicked.properties?.type === 'gybe'
                  ? clicked.properties.type
                  : 'tack',
            },
          ];
        });
      } catch (error) {
        developmentLog.error('[handleTackClick] error:', error);
      }
    };
    mapInstance.current.off('click', tackLayerId, handleTackClick);
    mapInstance.current.on('click', tackLayerId, handleTackClick);
    return () => {
      mapInstance.current?.off('click', tackLayerId, handleTackClick);
    };
  }, [
    mapInstance,
    tackStats,
    tackMapping,
    setTackStats,
    setTackMapping,
    updateTackColors,
    isRaceStatsVisible,
    setIsRaceStatsVisible,
  ]);

  function renderMarks(marks: Array<{ id: number; lat: number; lon: number }>, loa: number) {
    const isEditingMarks = editingSegmentId !== null && editingSegmentId !== undefined;
    const editingModeValue = editingMode ?? 'idle';
    const marksToRender = selectRenderableMarks({
      marks,
      trackData,
      editingRange,
      loa,
      isEditingMarks,
    });
    developmentLog.debug('renderMarks: received marks:', marksToRender);
    const map = mapInstance.current;
    if (!map) {
      developmentLog.error('renderMarks: mapInstance is not available.');
      return;
    }
    const overlayPalette = getCurrentMapOverlayPalette();

    // IDs con sufijo para evitar colisiones entre paneles
    const centerSourceId = `buoys-center-source-${idSuffix}`;
    const centerLayerId = `buoys-center-layer-${idSuffix}`;
    const centerHitLayerId = `buoys-center-hit-layer-${idSuffix}`;
    const radiusSourceId = `buoys-radius-source-${idSuffix}`;
    const radiusLayerId = `buoys-radius-layer-${idSuffix}`;

    const clickTargets = [centerHitLayerId];
    if (markClickHandlerRef.current) {
      for (const layerId of clickTargets) {
        map.off('click', layerId, markClickHandlerRef.current);
      }
      markClickHandlerRef.current = null;
    }
    if (markDragHandlerRef.current) {
      for (const layerId of clickTargets) {
        map.off('mousedown', layerId, markDragHandlerRef.current);
      }
      markDragHandlerRef.current = null;
    }
    if (markDragTouchHandlerRef.current) {
      for (const layerId of clickTargets) {
        map.off('touchstart', layerId, markDragTouchHandlerRef.current);
      }
      markDragTouchHandlerRef.current = null;
    }

    // 1) Eliminamos cualquier source/layer previo
    for (const layerId of [centerLayerId, centerHitLayerId]) {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    }
    if (map.getSource(centerSourceId)) {
      map.removeSource(centerSourceId);
    }
    if (map.getLayer(radiusLayerId)) {
      map.removeLayer(radiusLayerId);
    }
    if (map.getSource(radiusSourceId)) {
      map.removeSource(radiusSourceId);
    }

    // 2) Construimos dos colecciones de Features:
    //    - Una para los PUNTOS "center"
    //    - Una para los POLÍGONOS "circunferencia" con radio=3*LOA
    const centerFeatures: GeoJSON.Feature<GeoJSON.Point>[] = [];
    const radiusFeatures: GeoJSON.Feature<GeoJSON.Polygon>[] = [];

    for (const mark of marksToRender) {
      // El punto "central" de la boya
      centerFeatures.push(createBuoyCenterPoint(mark));

      // La circunferencia alrededor de la boya
      const circlePolygon = createGeodesicCircle(
        [mark.lon, mark.lat],
        3 * loa, // radio en metros
        80, // más puntos para suavizar
      );
      radiusFeatures.push(circlePolygon);
    }

    const centerCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: centerFeatures,
    };

    const radiusCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: radiusFeatures,
    };

    // 3) Creamos las fuentes y capas con IDs únicos
    // ---- a) Punto central salmón ----
    map.addSource(centerSourceId, {
      type: 'geojson',
      data: centerCollection,
    });
    map.addLayer({
      id: centerHitLayerId,
      type: 'circle',
      source: centerSourceId,
      paint: {
        'circle-radius': 14, // área invisible más amplia para clic/drag
        'circle-color': 'rgba(0,0,0,0)',
      },
    });
    map.addLayer({
      id: centerLayerId,
      type: 'circle',
      source: centerSourceId,
      paint: {
        'circle-radius': 2, // en píxeles
        'circle-color': overlayPalette.buoy,
        'circle-stroke-width': 1.5,
        'circle-stroke-color': overlayPalette.buoy,
      },
    });

    // ---- b) Circunferencia geodésica de radio 3*LOA ----
    map.addSource(radiusSourceId, {
      type: 'geojson',
      data: radiusCollection,
    });
    map.addLayer({
      id: radiusLayerId,
      type: 'line',
      source: radiusSourceId,
      paint: {
        'line-color': overlayPalette.buoy,
        'line-width': 2,
        'line-opacity': 0.8,
      },
    });

    // Aseguramos que la circunferencia quede por debajo del punto central
    safeMoveLayer(map, radiusLayerId, centerLayerId);

    const handleMarkClick = (event: mapboxgl.MapLayerMouseEvent) => {
      const buoyIdRaw = event.features?.[0]?.properties?.buoyId;
      const buoyId = typeof buoyIdRaw === 'number' ? buoyIdRaw : Number(buoyIdRaw);
      if (!Number.isFinite(buoyId)) {
        return;
      }
      const clickedMark = marksRef.current.find((m) => m.id === buoyId);
      if (!clickedMark || !Number.isFinite(clickedMark.lat) || !Number.isFinite(clickedMark.lon)) {
        return;
      }
      if (isEditingMarks) {
        suppressCurrentEditingMapClick();
        event.preventDefault();
        event.originalEvent?.stopPropagation?.();
        if (editingModeRef.current === 'deleteBuoy') {
          const currentMarks = marksRef.current;
          onEditingMarksChange?.(currentMarks.filter((mark) => mark.id !== buoyId));
          return;
        }
        // moving handled via drag; clicks only delete
      }
      applyLaylinesAtTarget(clickedMark, { source: 'buoy' });
    };

    markClickHandlerRef.current = handleMarkClick;
    for (const layerId of clickTargets) {
      map.on('click', layerId, handleMarkClick);
    }

    const handleDragStart = (event: mapboxgl.MapLayerMouseEvent | mapboxgl.MapLayerTouchEvent) => {
      // Use ref to get current editingMode value (avoids stale closure)
      if (!isEditingMarks || editingModeRef.current === 'deleteBuoy' || !onEditingMarksChange) {
        return;
      }
      const buoyIdRaw = event.features?.[0]?.properties?.buoyId;
      const buoyId = typeof buoyIdRaw === 'number' ? buoyIdRaw : Number(buoyIdRaw);
      if (!Number.isFinite(buoyId)) {
        return;
      }
      suppressCurrentEditingMapClick();
      event.preventDefault();
      event.originalEvent?.stopPropagation?.();
      setMovingBuoyId(buoyId);
      map.getCanvas().style.cursor = 'grabbing';

      // Store the current mark position and track the latest position during drag
      const currentMark = marksRef.current.find((m) => m.id === buoyId);
      if (!currentMark) {
        return;
      }

      let latestPosition = { lat: currentMark.lat, lon: currentMark.lon };
      let rafId: number | null = null;

      // Function to update the visual position on the map without triggering React state updates
      const updateVisualPosition = () => {
        const source = map.getSource(centerSourceId) as mapboxgl.GeoJSONSource | undefined;
        const radiusSource = map.getSource(radiusSourceId) as mapboxgl.GeoJSONSource | undefined;

        if (source) {
          // Update center points with the dragged buoy's new position
          const updatedMarks = marksRef.current.map((mark) =>
            mark.id === buoyId
              ? { ...mark, lat: latestPosition.lat, lon: latestPosition.lon }
              : mark,
          );
          const centerFeatures = updatedMarks
            .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lon))
            .map((m) => createBuoyCenterPoint(m));
          source.setData({
            type: 'FeatureCollection',
            features: centerFeatures,
          });

          // Update radius circles
          if (radiusSource) {
            const radiusFeatures = updatedMarks
              .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lon))
              .map((m) => createGeodesicCircle([m.lon, m.lat], 3 * loa, 80));
            radiusSource.setData({
              type: 'FeatureCollection',
              features: radiusFeatures,
            });
          }
        }
        rafId = null;
      };

      const handleMove = (moveEvent: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => {
        // Update the latest position
        latestPosition = { lat: moveEvent.lngLat.lat, lon: moveEvent.lngLat.lng };

        // Schedule visual update via requestAnimationFrame for smooth rendering
        if (rafId === null) {
          rafId = requestAnimationFrame(updateVisualPosition);
        }
      };

      const handleEnd = () => {
        map.off('mousemove', handleMove);
        map.off('touchmove', handleMove);
        map.off('mouseup', handleEnd);
        map.off('touchend', handleEnd);

        // Cancel any pending animation frame
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }

        // Commit the final position to React state
        const currentMarks = marksRef.current;
        onEditingMarksChange(
          currentMarks.map((mark) =>
            mark.id === buoyId
              ? { ...mark, lat: latestPosition.lat, lon: latestPosition.lon }
              : mark,
          ),
        );

        setMovingBuoyId(null);
        map.getCanvas().style.cursor = '';
      };

      map.on('mousemove', handleMove);
      map.on('touchmove', handleMove);
      map.once('mouseup', handleEnd);
      map.once('touchend', handleEnd);
    };

    const enableDragging = editingModeValue !== 'deleteBuoy';
    markDragHandlerRef.current = enableDragging ? handleDragStart : null;
    markDragTouchHandlerRef.current = enableDragging ? handleDragStart : null;
    if (enableDragging) {
      for (const layerId of clickTargets) {
        map.on('mousedown', layerId, handleDragStart);
        map.on('touchstart', layerId, handleDragStart);
      }
    }
  }

  useEffect(() => {
    if (!editingSegmentId || !onEditingMarksChange || !mapInstance.current) {
      return;
    }
    const map = mapInstance.current;
    const handleMapClick = (event: mapboxgl.MapMouseEvent & { defaultPrevented?: boolean }) => {
      if (suppressNextEditingMapClickRef.current) {
        suppressNextEditingMapClickRef.current = false;
        return;
      }
      if (event.defaultPrevented) {
        return;
      }
      if (editingMode !== 'addBuoy') {
        return;
      }
      const target = { lat: event.lngLat.lat, lon: event.lngLat.lng };
      const id = tempMarkIdRef.current;
      tempMarkIdRef.current -= 1;
      onEditingMarksChange([...marksRef.current, { id, lat: target.lat, lon: target.lon }]);
    };
    map.on('click', handleMapClick);
    return () => {
      map.off('click', handleMapClick);
    };
  }, [editingSegmentId, editingMode, mapInstance, marks, onEditingMarksChange]);

  useEffect(() => {
    if (editingMode !== 'moveBuoy') {
      setMovingBuoyId(null);
    }
  }, [editingMode]);

  const toggleTacks = () => {
    setShowTacks((previous) => !previous);
  };

  /* FUNCIÓN handleTackClick COMPLETA (ya modificada) */

  const addArrowLine = (start: mapboxgl.LngLat, end: mapboxgl.LngLat) => {
    if (!isMeasuringDistance || (!startPoint && !startPointOld) || !mapInstance.current) {
      return;
    }
    const overlayPalette = getCurrentMapOverlayPalette();

    const toRadians = (degrees: number) => degrees * (Math.PI / 180);

    // Calcular el factor de escala basado en el nivel de zoom

    const totalDistance = calculateDistance(start.lat, start.lng, end.lat, end.lng) * 1000; // Total distance in meters
    let numberSegments_raw = totalDistance / loa; // Number of segments based on LOA
    let numberSegments = Math.ceil(totalDistance / loa); // Number of segments based on LOA

    if (numberSegments > 100) {
      numberSegments = 1;
      numberSegments_raw = 1;
    }
    const latIncrement = (end.lat - start.lat) / numberSegments_raw;
    const lngIncrement = (end.lng - start.lng) / numberSegments_raw;

    const features: GeoJSON.Feature<GeoJSON.Geometry>[] = [];

    for (let index = 0; index < numberSegments - 1; index++) {
      const segmentStart = {
        lng: start.lng + index * lngIncrement,
        lat: start.lat + index * latIncrement,
      };
      const segmentEnd = {
        lng: start.lng + (index + 1) * lngIncrement,
        lat: start.lat + (index + 1) * latIncrement,
      };

      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [segmentStart.lng, segmentStart.lat],
            [segmentEnd.lng, segmentEnd.lat],
          ],
        },
        properties: {
          color:
            index % 2 === 0 ? overlayPalette.distancePrimary : overlayPalette.distanceSecondary,
        },
      });
    }
    // Nota: el cálculo del zoom/heading no es necesario aquí

    // Convertir el ángulo en direcciones de flechas en grados a desplazamientos en pantalla
    const arrowLengthPixels = 10; // Longitud fija en píxeles
    // Proyecta los puntos inicial y final a coordenadas de pantalla
    const startPixel = mapInstance.current.project(start);
    const endPixel = mapInstance.current.project(end);

    // Calcula el ángulo en función de las coordenadas en pantalla
    const deltaX = endPixel.x - startPixel.x;
    const deltaY = endPixel.y - startPixel.y;
    const angleInDegrees = (Math.atan2(deltaY, deltaX) * 180) / Math.PI;

    // Calcula las posiciones de las flechas laterales en píxeles
    const leftArrowPixel = new mapboxgl.Point(
      endPixel.x + arrowLengthPixels * Math.cos(toRadians(angleInDegrees + 45 + 180)),
      endPixel.y + arrowLengthPixels * Math.sin(toRadians(angleInDegrees + 45 + 180)),
    );
    const rightArrowPixel = new mapboxgl.Point(
      endPixel.x + arrowLengthPixels * Math.cos(toRadians(angleInDegrees - 45 + 180)),
      endPixel.y + arrowLengthPixels * Math.sin(toRadians(angleInDegrees - 45 + 180)),
    );

    // Convierte las posiciones de las flechas de vuelta a coordenadas geográficas
    const leftArrowLngLat = mapInstance.current.unproject(leftArrowPixel);
    const rightArrowLngLat = mapInstance.current.unproject(rightArrowPixel);

    // Añadir las líneas de las flechas laterales al mapa como de costumbre
    features.push(
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [end.lng, end.lat],
            [leftArrowLngLat.lng, leftArrowLngLat.lat],
          ],
        },
        properties: { color: overlayPalette.distancePrimary },
      },
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [end.lng, end.lat],
            [rightArrowLngLat.lng, rightArrowLngLat.lat],
          ],
        },
        properties: { color: overlayPalette.distancePrimary },
      },
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [
              start.lng + (numberSegments - 1) * lngIncrement,
              start.lat + (numberSegments - 1) * latIncrement,
            ],
            [end.lng, end.lat],
          ],
        },
        properties: {
          color:
            (numberSegments - 1) % 2 === 0
              ? overlayPalette.distancePrimary
              : overlayPalette.distanceSecondary,
        },
      },
    );

    const lineData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features,
    };

    if (mapInstance.current.getSource(DISTANCE_LINE_SOURCE_ID)) {
      (mapInstance.current.getSource(DISTANCE_LINE_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(
        lineData,
      );
      if (mapInstance.current.getLayer(DISTANCE_LINE_LAYER_ID)) {
        mapInstance.current.setPaintProperty(DISTANCE_LINE_LAYER_ID, LINE_COLOR, ['get', 'color']);
      }
    } else {
      mapInstance.current.addSource(DISTANCE_LINE_SOURCE_ID, {
        type: 'geojson',
        data: lineData,
      });

      mapInstance.current.addLayer({
        id: DISTANCE_LINE_LAYER_ID,
        type: 'line',
        source: DISTANCE_LINE_SOURCE_ID,
        layout: {},
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 2,
        },
      });
    }
  };
  const updateTextLabel = (
    startLng: number,
    startLat: number,
    endLng: number,
    endLat: number,
    text: string,
  ) => {
    if (!mapInstance.current) {
      return;
    }
    const overlayPalette = getCurrentMapOverlayPalette();

    // Funciones auxiliares
    const toRadians = (degrees: number) => degrees * (Math.PI / 180);
    const toDegrees = (radians: number) => radians * (180 / Math.PI);

    // Convertir latitud y longitud a coordenadas Mercator X e Y
    const latLonToMercator = (lat: number, lon: number) => {
      const R = 6_378_137; // Radio de la Tierra en metros
      const x = R * toRadians(lon);
      const y = Math.log(Math.tan(Math.PI / 4 + toRadians(lat) / 2)) * R;
      return { x, y };
    };

    // Convertir coordenadas Mercator X e Y de vuelta a latitud y longitud
    const mercatorToLatLon = (x: number, y: number) => {
      const R = 6_378_137; // Radio de la Tierra en metros
      const lon = toDegrees(x / R);
      const lat = toDegrees(2 * Math.atan(Math.exp(y / R)) - Math.PI / 2);
      return { lat, lon };
    };
    const { x: x1, y: y1 } = latLonToMercator(startLat, startLng);
    const { x: x2, y: y2 } = latLonToMercator(endLat, endLng);
    // Convertir puntos de inicio y fin a coordenadas Mercator
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;

    // Convertir punto medio de vuelta a latitud y longitud
    const { lat: midLat, lon: midLng } = mercatorToLatLon(midX, midY);

    // Calcular ángulo entre los puntos en coordenadas Mercator
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const angleDegrees = -toDegrees(angle);

    const bearing = mapInstance.current.getBearing();

    // Definir la distancia de desplazamiento en píxeles
    const midPointPixels = mapInstance.current.project([midLng, midLat]);

    // Definir la distancia de desplazamiento en píxeles
    const offsetDistancePixels = 15; // Ajusta según tus necesidades

    // Calcular el desplazamiento en píxeles
    const adjustedAngle = angle + toRadians(bearing);
    const offsetX = offsetDistancePixels * Math.cos(-adjustedAngle + Math.PI / 2);
    const offsetY = offsetDistancePixels * Math.sin(-adjustedAngle + Math.PI / 2);
    const rotate = adjustedAngle >= -Math.PI / 2 && adjustedAngle <= Math.PI / 2 ? 0 : 180;

    // Aplicar el desplazamiento al punto medio en píxeles
    const offsetMidPointPixels: [number, number] = [
      midPointPixels.x + offsetX,
      midPointPixels.y + offsetY,
    ];

    // Convertir el punto desplazado de vuelta a coordenadas de mapa (latitud y longitud)
    const offsetMidPoint = mapInstance.current.unproject(offsetMidPointPixels);
    // Convertir el punto desplazado de vuelta a latitud y longitud

    const labelData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [offsetMidPoint.lng, offsetMidPoint.lat],
          },
          properties: {
            description: text,
            rotation: angleDegrees + rotate,
          },
        },
      ],
    };

    if (mapInstance.current.getSource(LABEL_SOURCE_ID)) {
      safeSetGeoJSONData(mapInstance.current, LABEL_SOURCE_ID, labelData);
      // 🔝 Bring label layer to front after data refresh
      if (mapInstance.current?.getLayer(LABEL_LAYER_ID)) {
        mapInstance.current.setPaintProperty(
          LABEL_LAYER_ID,
          'text-color',
          overlayPalette.distanceLabel,
        );
        mapInstance.current.setPaintProperty(
          LABEL_LAYER_ID,
          'text-halo-color',
          overlayPalette.distanceLabelHalo,
        );
        mapInstance.current.setPaintProperty(
          LABEL_LAYER_ID,
          'text-halo-width',
          overlayPalette.distanceLabelHaloWidth,
        );
        mapInstance.current.moveLayer(LABEL_LAYER_ID);
      }
    } else {
      mapInstance.current.addSource(LABEL_SOURCE_ID, {
        type: 'geojson',
        data: labelData,
      });

      mapInstance.current.addLayer({
        id: LABEL_LAYER_ID,
        type: 'symbol',
        source: LABEL_SOURCE_ID,
        layout: {
          'text-field': ['get', 'description'],
          'text-font': FONT_STACK_REGULAR,
          'text-size': 12,
          'text-anchor': 'center',
          'text-rotate': ['get', 'rotation'],
          'text-max-width': 1000,
          'text-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': overlayPalette.distanceLabel,
          'text-halo-color': overlayPalette.distanceLabelHalo,
          'text-halo-width': overlayPalette.distanceLabelHaloWidth,
        },
      });
      // 🔝 Ensure the distance label stays on top as well
      if (mapInstance.current?.getLayer(LABEL_LAYER_ID)) {
        mapInstance.current.moveLayer(LABEL_LAYER_ID);
      }
    }
  };

  const adjustArrowSize = (svg: SVGSVGElement) => {
    // Fallback al último tamaño conocido para mantener consistencia visual
    if (!mapInstance.current || !svg.viewBox || !svg.viewBox.baseVal) {
      if (arrowSizeRef.current) {
        svg.style.width = `${arrowSizeRef.current.width}px`;
        svg.style.height = `${arrowSizeRef.current.height}px`;
      }
      return;
    }
    const zoom = mapInstance.current.getZoom();
    // 1️⃣ Cuántos metros caben en un píxel ahora:
    const metresPerPixel =
      (156_543.033_92 * Math.cos((mapInstance.current.getCenter().lat * Math.PI) / 180)) /
      Math.pow(2, zoom);

    // 2️⃣ Largo en píxeles que debe ocupar 1 LOA:
    const pixelLength = (loaRef.current / metresPerPixel) * 2.2;

    // 3️⃣ Aspect ratio basado en viewBox:
    const viewBox = svg.viewBox.baseVal; // { width: 60, height: 200 }
    const aspect = viewBox.width / viewBox.height; // 0.3

    // 4️⃣ Fijamos tamaño CSS exacto:
    const widthPx = pixelLength * aspect;
    const heightPx = pixelLength;
    svg.style.width = `${widthPx}px`;
    svg.style.height = `${heightPx}px`;
    arrowSizeRef.current = { width: widthPx, height: heightPx };

    // 5️⃣ (Opcional) Centrar origen de transformaciones
    svg.style.transformOrigin = 'center';
  };

  /**
   * Crea un Feature de tipo Point para la boya en sí, para
   * luego dibujar un círculo "pequeño" (en píxeles) de color salmón.
   */
  function createGrid(
    bounds: mapboxgl.LngLatBounds,
    size: number,
    rotationDegrees: number,
    origin?: { lng: number; lat: number },
    options: GridCreateOptions = {},
  ): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
    const features: GeoJSON.Feature<GeoJSON.Geometry>[] = [];
    const {
      marginCells = 1,
      skipMajorLinesCoveredByCoarseGrid = false,
      majorLineInterval = 10,
    } = options;

    if (!Number.isFinite(size) || size <= 0) {
      return { type: 'FeatureCollection', features };
    }

    const anchor = origin ?? bounds.getCenter();
    const metresPerDegreeLat = 111_320;
    const latRadians = (anchor.lat * Math.PI) / 180;
    const cosLatitude = Math.cos(latRadians);
    const metresPerDegreeLon = Math.max(Math.abs(111_320 * cosLatitude), 1e-6);

    const toLocal = (lng: number, lat: number) => ({
      x: (lng - anchor.lng) * metresPerDegreeLon,
      y: (lat - anchor.lat) * metresPerDegreeLat,
    });

    const fromLocal = (x: number, y: number) => ({
      lng: anchor.lng + x / metresPerDegreeLon,
      lat: anchor.lat + y / metresPerDegreeLat,
    });

    const rotationRadians = (-rotationDegrees * Math.PI) / 180; // bearings rotate clockwise; negate for math CCW
    const sinRotation = Math.sin(rotationRadians);
    const cosRotation = Math.cos(rotationRadians);

    const rotateToGrid = (point: { x: number; y: number }) => ({
      x: point.x * cosRotation + point.y * sinRotation,
      y: -point.x * sinRotation + point.y * cosRotation,
    });

    const rotateFromGrid = (point: { x: number; y: number }) => ({
      x: point.x * cosRotation - point.y * sinRotation,
      y: point.x * sinRotation + point.y * cosRotation,
    });

    const rotatedCorners = [
      bounds.getSouthWest(),
      bounds.getNorthWest(),
      bounds.getNorthEast(),
      bounds.getSouthEast(),
    ].map(({ lng, lat }) => rotateToGrid(toLocal(lng, lat)));

    const minX = Math.min(...rotatedCorners.map((corner) => corner.x));
    const maxX = Math.max(...rotatedCorners.map((corner) => corner.x));
    const minY = Math.min(...rotatedCorners.map((corner) => corner.y));
    const maxY = Math.max(...rotatedCorners.map((corner) => corner.y));

    // padding extra para que la rejilla nunca se corte en los bordes visibles
    const spanX = maxX - minX;
    const spanY = maxY - minY;
    const dynamicMarginCells = getGridMarginCells({
      spanX,
      spanY,
      size,
      marginCells,
    });

    const minXWithMargin = minX - dynamicMarginCells * size;
    const maxXWithMargin = maxX + dynamicMarginCells * size;
    const minYWithMargin = minY - dynamicMarginCells * size;
    const maxYWithMargin = maxY + dynamicMarginCells * size;

    const startXIndex = Math.floor(minXWithMargin / size);
    const endXIndex = Math.ceil(maxXWithMargin / size);
    const startYIndex = Math.floor(minYWithMargin / size);
    const endYIndex = Math.ceil(maxYWithMargin / size);

    for (let index = startXIndex; index <= endXIndex; index++) {
      if (
        !shouldRenderGridLine({
          lineIndex: index,
          majorGridVisible: skipMajorLinesCoveredByCoarseGrid,
          majorLineInterval,
        })
      ) {
        continue;
      }
      const x = index * size;
      const startLocal = rotateFromGrid({ x, y: minYWithMargin });
      const endLocal = rotateFromGrid({ x, y: maxYWithMargin });

      const startGeo = fromLocal(startLocal.x, startLocal.y);
      const endGeo = fromLocal(endLocal.x, endLocal.y);

      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [startGeo.lng, startGeo.lat],
            [endGeo.lng, endGeo.lat],
          ],
        },
        properties: {},
      });
    }

    for (let index = startYIndex; index <= endYIndex; index++) {
      if (
        !shouldRenderGridLine({
          lineIndex: index,
          majorGridVisible: skipMajorLinesCoveredByCoarseGrid,
          majorLineInterval,
        })
      ) {
        continue;
      }
      const y = index * size;
      const startLocal = rotateFromGrid({ x: minXWithMargin, y });
      const endLocal = rotateFromGrid({ x: maxXWithMargin, y });

      const startGeo = fromLocal(startLocal.x, startLocal.y);
      const endGeo = fromLocal(endLocal.x, endLocal.y);

      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [startGeo.lng, startGeo.lat],
            [endGeo.lng, endGeo.lat],
          ],
        },
        properties: {},
      });
    }

    return { type: 'FeatureCollection', features } as GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  }
  /* 🔧 MapPanel.tsx – añade justo después de las demás helpers ----------------- */

  /* --------------------------------------------------------------------------- */

  const clearDistanceMeasurement = useCallback(() => {
    for (const marker of arrowMarkers.current) {
      marker.remove();
    }
    arrowMarkers.current = [];
    for (const marker of labelMarkers.current) {
      marker.remove();
    }
    labelMarkers.current = [];

    if (mapInstance.current?.getSource(DISTANCE_LINE_SOURCE_ID)) {
      mapInstance.current.removeLayer(DISTANCE_LINE_LAYER_ID);
      mapInstance.current.removeSource(DISTANCE_LINE_SOURCE_ID);
    }

    if (mapInstance.current?.getSource(LABEL_SOURCE_ID)) {
      mapInstance.current.removeLayer(LABEL_LAYER_ID);
      mapInstance.current.removeSource(LABEL_SOURCE_ID);
    }

    setStartPointOld(null);
    setStartPoint(null);
  }, []);

  const createLineGradient = useCallback(
    (stops: Array<{ position: number; color: string }>): mapboxgl.Expression => {
      const clampedStops = stops
        .map(({ position, color }) => ({ position: Math.min(Math.max(position, 0), 1), color }))
        .sort((a, b) => a.position - b.position);
      const expression: unknown[] = ['interpolate', ['linear'], ['line-progress']];
      for (const stop of clampedStops) {
        expression.push(stop.position, stop.color);
      }
      return expression as unknown as mapboxgl.Expression;
    },
    [],
  );

  const applyOptimizedRouteGradient = useCallback(
    (progress: number) => {
      const map = mapInstance.current;
      if (!map) {
        return;
      }
      const clamped = Math.min(Math.max(progress, 0), 1);

      // Progressive drawing effect: line appears from start to current position
      const headPosition = Math.max(clamped, 0.001);
      const headGlow = 0.05; // Small glow window at the drawing head

      const glowStart = Math.max(headPosition - headGlow, 0);
      const glowEnd = Math.min(headPosition + headGlow * 0.3, 1);

      // Build gradient: invisible until head, then visible with accent at tip
      const stops = [
        { position: 0, color: OPTIMIZED_ROUTE_MAIN }, // Already drawn part is solid
        { position: glowStart, color: OPTIMIZED_ROUTE_MAIN },
        { position: headPosition, color: OPTIMIZED_ROUTE_ACCENT }, // Bright head
        { position: glowEnd, color: OPTIMIZED_ROUTE_HIDDEN }, // Fade out after head
        { position: 1, color: OPTIMIZED_ROUTE_HIDDEN }, // Rest is invisible
      ];

      // Sort and remove duplicates to ensure strictly ascending positions
      const uniqueStops: Array<{ position: number; color: string }> = [];
      const sortedStops = stops.sort((a, b) => a.position - b.position);

      for (const current of sortedStops) {
        const prev = uniqueStops.at(-1);

        // Only add if position is strictly greater than previous
        if (!prev || current.position > prev.position + 0.001) {
          uniqueStops.push(current);
        }
      }

      const gradient = createLineGradient(uniqueStops);
      for (const layerId of [optimizedRouteGlowLayerId, optimizedRouteLayerId]) {
        if (map.getLayer(layerId)) {
          map.setPaintProperty(layerId, 'line-gradient', gradient);
        }
      }
    },
    [createLineGradient, mapInstance, optimizedRouteGlowLayerId, optimizedRouteLayerId],
  );

  const setOptimizedRouteFinalGradient = useCallback(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    const finalGradient = createLineGradient([
      { position: 0, color: OPTIMIZED_ROUTE_MAIN },
      { position: 1, color: OPTIMIZED_ROUTE_MAIN },
    ]);
    for (const layerId of [optimizedRouteGlowLayerId, optimizedRouteLayerId]) {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'line-gradient', finalGradient);
      }
    }
  }, [createLineGradient, mapInstance, optimizedRouteGlowLayerId, optimizedRouteLayerId]);

  const startOptimizedRouteAnimation = useCallback(
    (fullCoordinates: [number, number][], baseSegmentCount?: number) => {
      const map = mapInstance.current;
      if (!map || fullCoordinates.length < 2) {
        return;
      }

      const lineSource = map.getSource(optimizedRouteSourceId) as
        | mapboxgl.GeoJSONSource
        | undefined;
      if (!lineSource) {
        return;
      }

      const fullLineData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: fullCoordinates },
            properties: {},
          },
        ],
      };
      lineSource.setData(fullLineData as unknown as GeoJSON.GeoJSON);

      stopOptimizedRouteAnimation();
      applyOptimizedRouteGradient(0);

      const segmentsFromCoords = fullCoordinates.length - 1;
      const totalSegments =
        baseSegmentCount != null && baseSegmentCount > 0
          ? Math.min(Math.max(baseSegmentCount, 1), segmentsFromCoords)
          : segmentsFromCoords;
      const duration = Math.min(5000, Math.max(1800, totalSegments * 200)); // Longer duration for smoother effect

      const step = (timestamp: number) => {
        if (optimizedRouteAnimationStartRef.current === null) {
          optimizedRouteAnimationStartRef.current = timestamp;
        }
        const elapsed = timestamp - optimizedRouteAnimationStartRef.current;
        const linear = Math.min(elapsed / duration, 1);
        // easeInOutQuart for smoother acceleration and deceleration
        const eased =
          linear < 0.5
            ? 8 * linear * linear * linear * linear
            : 1 - Math.pow(-2 * linear + 2, 4) / 2;
        applyOptimizedRouteGradient(eased);

        if (linear < 1) {
          optimizedRouteAnimationFrameRef.current = requestAnimationFrame(step);
          return;
        }

        setOptimizedRouteFinalGradient();
        stopOptimizedRouteAnimation();
      };

      optimizedRouteAnimationFrameRef.current = requestAnimationFrame(step);
    },
    [
      applyOptimizedRouteGradient,
      mapInstance,
      optimizedRouteSourceId,
      setOptimizedRouteFinalGradient,
      stopOptimizedRouteAnimation,
    ],
  );

  const startOptimizedRoutePulse = useCallback(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    stopOptimizedRoutePulse();
    const cycle = 1400;

    const step = (timestamp: number) => {
      if (optimizedRoutePulseStartRef.current === null) {
        optimizedRoutePulseStartRef.current = timestamp;
      }
      if (!map.getStyle || !map.getStyle()) {
        stopOptimizedRoutePulse();
        return;
      }
      const elapsed = timestamp - optimizedRoutePulseStartRef.current;
      const t = (elapsed % cycle) / cycle;
      const eased = 1 - Math.pow(1 - t, 2);
      const haloRadius = 11 + eased * 11;
      const haloOpacity = Math.max(0, 0.48 - eased * 0.32);
      const ringRadius = 7.5 + eased * 4.5;
      const ringOpacity = Math.max(0.36, 0.88 - eased * 0.5);

      if (map.getLayer(optimizedRoutePointsHaloLayerId)) {
        map.setPaintProperty(optimizedRoutePointsHaloLayerId, 'circle-radius', haloRadius);
        map.setPaintProperty(optimizedRoutePointsHaloLayerId, 'circle-opacity', haloOpacity);
      }

      if (map.getLayer(optimizedRoutePointsRingLayerId)) {
        map.setPaintProperty(optimizedRoutePointsRingLayerId, 'circle-radius', ringRadius);
        map.setPaintProperty(optimizedRoutePointsRingLayerId, 'circle-opacity', ringOpacity);
      }

      optimizedRoutePulseFrameRef.current = requestAnimationFrame(step);
    };

    optimizedRoutePulseFrameRef.current = requestAnimationFrame(step);
  }, [
    mapInstance,
    optimizedRoutePointsHaloLayerId,
    optimizedRoutePointsRingLayerId,
    stopOptimizedRoutePulse,
  ]);

  useEffect(() => {
    return () => {
      stopOptimizedRouteAnimation();
      stopOptimizedRoutePulse();
    };
  }, [stopOptimizedRouteAnimation, stopOptimizedRoutePulse]);

  const clearOptimizedRouteLayers = useCallback(() => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }
    stopOptimizedRouteAnimation();
    stopOptimizedRoutePulse();
    for (const layerId of [
      optimizedRouteGlowLayerId,
      optimizedRouteLayerId,
      optimizedRoutePointsHaloLayerId,
      optimizedRoutePointsRingLayerId,
      optimizedRoutePointsLayerId,
    ]) {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    }
    for (const sourceId of [optimizedRouteSourceId, optimizedRoutePointsSourceId]) {
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    }
  }, [
    mapInstance,
    optimizedRouteGlowLayerId,
    optimizedRouteLayerId,
    optimizedRoutePointsHaloLayerId,
    optimizedRoutePointsRingLayerId,
    optimizedRoutePointsLayerId,
    optimizedRoutePointsSourceId,
    optimizedRouteSourceId,

    stopOptimizedRouteAnimation,
    stopOptimizedRoutePulse,
  ]);

  const resetOptimizationState = useCallback(() => {
    clearOptimizedRouteLayers();
    setOptimizeStart(null);
    setOptimizeGoal(null);
    setOptimizedRoute(null);
    setOptimizeError(null);
    setOptimizeLoading(false);
    setMapInputMode((current) => (current === 'optimize' ? 'idle' : current));
  }, [clearOptimizedRouteLayers]);

  const resetMapInputArtifacts = useCallback(() => {
    if (isMeasuringDistance) {
      clearDistanceMeasurement();
      setIsArrowFixed(false);
    }
    if (mapInputMode === 'optimize') {
      resetOptimizationState();
    }
    if (isHighlighting) {
      clearMapDraftHighlight();
      setPendingDraftHighlightId(null);
      setStartPoint(null);
      currentHighlightColorReference.current = null;
    }
    // Keep the player and the exclusive map input mode in sync even if an
    // asynchronous render left one of them stale for a frame.
    if (isPlayerActive) {
      deactivatePlayerMode();
    }
    setMapInputMode('idle');
  }, [
    clearDistanceMeasurement,
    clearMapDraftHighlight,
    deactivatePlayerMode,
    isHighlighting,
    isMeasuringDistance,
    mapInputMode,
    isPlayerActive,
    resetOptimizationState,
  ]);

  const selectMapInputMode = useCallback(
    (nextMode: MapInputMode) => {
      const targetMode =
        nextMode === 'idle'
          ? 'idle'
          : toggleMapInputMode(mapInputMode, nextMode);
      resetMapInputArtifacts();
      if (targetMode !== 'idle') {
        setMapInputMode(targetMode);
      }
    },
    [mapInputMode, resetMapInputArtifacts],
  );

  mapInputModeTransitionRef.current = selectMapInputMode;

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }
      if (isCommentComposerOpen) {
        closeCommentComposer();
        return;
      }
      if (mapInputMode !== 'idle') {
        event.preventDefault();
        resetMapInputArtifacts();
        return;
      }
      if (isCommentListOpen) {
        event.preventDefault();
        setIsCommentListOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [
    closeCommentComposer,
    isCommentComposerOpen,
    isCommentListOpen,
    mapInputMode,
    resetMapInputArtifacts,
  ]);

  const updateOptimizationSelection = useCallback(
    (start: { lat: number; lon: number } | null, goal: { lat: number; lon: number } | null) => {
      const map = mapInstance.current;
      if (!map) {
        return;
      }
      if (!start && !goal) {
        stopOptimizedRoutePulse();
        for (const layerId of [
          optimizedRoutePointsHaloLayerId,
          optimizedRoutePointsRingLayerId,
          optimizedRoutePointsLayerId,
        ]) {
          if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
          }
        }
        for (const sourceId of [optimizedRoutePointsSourceId]) {
          if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
          }
        }
        return;
      }

      const features: GeoJSON.Feature<GeoJSON.Geometry>[] = [];
      if (start) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [start.lon, start.lat] },
          properties: { label: t('map.optimize.startLabel', 'Start') },
        });
      }
      if (goal) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [goal.lon, goal.lat] },
          properties: { label: t('map.optimize.goalLabel', 'Goal') },
        });
      }
      const pointsData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
        type: 'FeatureCollection',
        features,
      };

      if (map.getSource(optimizedRoutePointsSourceId)) {
        (map.getSource(optimizedRoutePointsSourceId) as mapboxgl.GeoJSONSource).setData(pointsData);
      } else {
        map.addSource(optimizedRoutePointsSourceId, { type: 'geojson', data: pointsData });
      }

      if (!map.getLayer(optimizedRoutePointsHaloLayerId)) {
        map.addLayer({
          id: optimizedRoutePointsHaloLayerId,
          type: 'circle',
          source: optimizedRoutePointsSourceId,
          paint: {
            'circle-radius': 12,
            'circle-color': [
              'match',
              ['get', 'label'],
              t('map.optimize.startLabel', 'Start'),
              OPTIMIZED_POINT_HALO_START,
              OPTIMIZED_POINT_HALO_END,
            ],
            'circle-blur': 1.15,
            'circle-opacity': 0.36,
          },
        });
      }

      if (!map.getLayer(optimizedRoutePointsRingLayerId)) {
        map.addLayer({
          id: optimizedRoutePointsRingLayerId,
          type: 'circle',
          source: optimizedRoutePointsSourceId,
          paint: {
            'circle-radius': 8,
            'circle-color': [
              'match',
              ['get', 'label'],
              t('map.optimize.startLabel', 'Start'),
              OPTIMIZED_POINT_RING_START,
              OPTIMIZED_POINT_RING_END,
            ],
            'circle-stroke-width': 2.2,
            'circle-stroke-color': [
              'match',
              ['get', 'label'],
              t('map.optimize.startLabel', 'Start'),
              OPTIMIZED_POINT_RING_START,
              OPTIMIZED_POINT_RING_END,
            ],
            'circle-opacity': 0.82,
          },
        });
      }

      if (!map.getLayer(optimizedRoutePointsLayerId)) {
        map.addLayer({
          id: optimizedRoutePointsLayerId,
          type: 'circle',
          source: optimizedRoutePointsSourceId,
          paint: {
            'circle-radius': 7,
            'circle-color': [
              'match',
              ['get', 'label'],
              t('map.optimize.startLabel', 'Start'),
              OPTIMIZED_POINT_START,
              OPTIMIZED_POINT_END,
            ],
            'circle-stroke-width': 0,
            'circle-blur': 0.1,
          },
        });
      }
      startOptimizedRoutePulse();
    },
    [
      mapInstance,
      optimizedRoutePointsHaloLayerId,
      optimizedRoutePointsRingLayerId,
      optimizedRoutePointsLayerId,
      optimizedRoutePointsSourceId,
      startOptimizedRoutePulse,
      stopOptimizedRoutePulse,
      t,
    ],
  );

  const drawOptimizedRoute = useCallback(
    (path: OptimizedRoutePoint[], startIndex: number | null, etaSeconds?: number | null) => {
      const map = mapInstance.current;
      if (!map || !Array.isArray(path) || path.length === 0) {
        return;
      }

      const baseSegmentCount = Math.max(path.length - 1, 1);
      const coordinates = smoothOptimizedRoute(path);
      const initialLineData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates,
            },
            properties: {},
          },
        ],
      };

      const lastCoordinate = coordinates.at(-1);
      const pointsData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: coordinates[0] },
            properties: { label: t('map.optimize.startLabel', 'Start') },
          },
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: lastCoordinate ?? coordinates[0],
            },
            properties: { label: t('map.optimize.goalLabel', 'Goal') },
          },
        ],
      };

      if (map.getSource(optimizedRouteSourceId)) {
        (map.getSource(optimizedRouteSourceId) as mapboxgl.GeoJSONSource).setData(initialLineData);
      } else {
        map.addSource(optimizedRouteSourceId, {
          type: 'geojson',
          data: initialLineData,
          lineMetrics: true,
        });
      }
      const hasGlowLayer = map.getLayer(optimizedRouteGlowLayerId);
      if (hasGlowLayer) {
        map.setPaintProperty(optimizedRouteGlowLayerId, 'line-color', OPTIMIZED_ROUTE_GLOW);
        map.setPaintProperty(optimizedRouteGlowLayerId, 'line-width', 11);
        map.setPaintProperty(optimizedRouteGlowLayerId, 'line-opacity', 0.38);
        map.setPaintProperty(optimizedRouteGlowLayerId, 'line-blur', 1.2);
        map.setLayoutProperty(optimizedRouteGlowLayerId, 'line-cap', 'round');
        map.setLayoutProperty(optimizedRouteGlowLayerId, 'line-join', 'round');
      } else {
        map.addLayer({
          id: optimizedRouteGlowLayerId,
          type: 'line',
          source: optimizedRouteSourceId,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': OPTIMIZED_ROUTE_GLOW,
            'line-width': 11,
            'line-opacity': 0.38,
            'line-blur': 1.2,
          },
        });
      }
      const hasRouteLayer = map.getLayer(optimizedRouteLayerId);
      if (hasRouteLayer) {
        map.setPaintProperty(optimizedRouteLayerId, 'line-color', OPTIMIZED_ROUTE_MAIN);
        map.setPaintProperty(optimizedRouteLayerId, 'line-width', 5.5);
        map.setPaintProperty(optimizedRouteLayerId, 'line-opacity', 0.92);
        map.setPaintProperty(optimizedRouteLayerId, 'line-blur', 0.3);
        map.setLayoutProperty(optimizedRouteLayerId, 'line-cap', 'round');
        map.setLayoutProperty(optimizedRouteLayerId, 'line-join', 'round');
      } else {
        map.addLayer({
          id: optimizedRouteLayerId,
          type: 'line',
          source: optimizedRouteSourceId,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': OPTIMIZED_ROUTE_MAIN,
            'line-width': 5.5,
            'line-opacity': 0.92,
            'line-blur': 0.3,
          },
        });
      }

      if (coordinates.length >= 2) {
        startOptimizedRouteAnimation(coordinates, baseSegmentCount);
      } else {
        setOptimizedRouteFinalGradient();
      }

      if (map.getSource(optimizedRoutePointsSourceId)) {
        (map.getSource(optimizedRoutePointsSourceId) as mapboxgl.GeoJSONSource).setData(pointsData);
      } else {
        map.addSource(optimizedRoutePointsSourceId, { type: 'geojson', data: pointsData });
      }

      const hasPointsLayer = map.getLayer(optimizedRoutePointsHaloLayerId);
      if (hasPointsLayer) {
        map.setPaintProperty(
          optimizedRoutePointsHaloLayerId,
          'circle-stroke-color',
          OPTIMIZED_POINT_HALO_START,
        );
      } else {
        map.addLayer({
          id: optimizedRoutePointsHaloLayerId,
          type: 'circle',
          source: optimizedRoutePointsSourceId,
          paint: {
            'circle-radius': 11,
            'circle-color': OPTIMIZED_ROUTE_HIDDEN,
            'circle-stroke-width': 6,
            'circle-stroke-color': [
              'match',
              ['get', 'label'],
              t('map.optimize.startLabel', 'Start'),
              OPTIMIZED_POINT_HALO_START,
              OPTIMIZED_POINT_HALO_END,
            ],
            'circle-stroke-opacity': 0.4,
            'circle-opacity': 0.4,
          },
        });
      }

      if (!map.getLayer(optimizedRoutePointsRingLayerId)) {
        map.addLayer({
          id: optimizedRoutePointsRingLayerId,
          type: 'circle',
          source: optimizedRoutePointsSourceId,
          paint: {
            'circle-radius': 7.5,
            'circle-color': OPTIMIZED_ROUTE_HIDDEN,
            'circle-stroke-width': 2.2,
            'circle-stroke-color': [
              'match',
              ['get', 'label'],
              t('map.optimize.startLabel', 'Start'),
              OPTIMIZED_POINT_RING_START,
              OPTIMIZED_POINT_RING_END,
            ],
            'circle-opacity': 0.82,
          },
        });
      }

      if (!map.getLayer(optimizedRoutePointsLayerId)) {
        map.addLayer({
          id: optimizedRoutePointsLayerId,
          type: 'circle',
          source: optimizedRoutePointsSourceId,
          paint: {
            'circle-radius': 7,
            'circle-color': [
              'match',
              ['get', 'label'],
              t('map.optimize.startLabel', 'Start'),
              OPTIMIZED_POINT_START,
              OPTIMIZED_POINT_END,
            ],
            'circle-stroke-width': 0,
            'circle-blur': 0.1,
          },
        });
      }
      startOptimizedRoutePulse();

      setOptimizedRoute({
        path,
        startIndex: startIndex ?? null,
        etaSeconds: etaSeconds ?? null,
      });
    },
    [
      mapInstance,
      optimizedRouteGlowLayerId,
      optimizedRouteLayerId,
      optimizedRoutePointsHaloLayerId,
      optimizedRoutePointsRingLayerId,
      optimizedRoutePointsLayerId,
      optimizedRoutePointsSourceId,
      optimizedRouteSourceId,
      startOptimizedRouteAnimation,
      startOptimizedRoutePulse,
      setOptimizedRouteFinalGradient,
      t,
    ],
  );

  const pollOptimizedRouteJob = useCallback(
    async (jobId: string, token: string, baseUrl: string): Promise<OptimizedRouteResult> => {
      const deadline = Date.now() + 16 * 60 * 1000;
      let delayMs = 750;

      while (Date.now() < deadline) {
        await wait(delayMs);
        const { data } = await axios.get<OptimizeRouteApiResponse>(
          `${baseUrl}/api/track/optimizeRoute/jobs/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (!data?.success) {
          throw new Error(
            data?.message || t('map.optimize.error', 'Could not calculate the route'),
          );
        }

        const job = isRouteOptimizationJob(data.data) ? data.data : null;
        if (job?.status === 'failed') {
          throw new Error(
            job.error || job.message || t('map.optimize.error', 'Could not calculate the route'),
          );
        }
        if (job?.status === 'completed') {
          const result = getOptimizedRouteResult(job);
          if (result?.path && result.path.length > 0) {
            return result;
          }
          throw new Error(t('map.optimize.emptyRoute', 'The optimizer returned an empty route'));
        }

        delayMs = Math.min(2000, Math.round(delayMs * 1.25));
      }

      throw new Error(t('map.optimize.timeout', 'Route optimization timed out'));
    },
    [t],
  );

  const requestOptimizedRoute = useCallback(
    async (startIndex: number, goal: { lat: number; lon: number }) => {
      if (!selectedFile?.id) {
        setOptimizeError(t('map.optimize.missingFile', 'Select a file to optimize the route'));
        return;
      }

      setOptimizeLoading(true);
      setOptimizeError(null);
      clearOptimizedRouteLayers();
      if (optimizeStart) {
        updateOptimizationSelection(
          { lat: optimizeStart.lat, lon: optimizeStart.lon },
          { lat: goal.lat, lon: goal.lon },
        );
      }
      try {
        const trimmedMaxTacks = optimizeMaxTacksInput.trim();
        const trimmedMinTacks = optimizeMinTacksInput.trim();
        const maxTacksValue =
          trimmedMaxTacks === ''
            ? null
            : (() => {
                const parsed = Number.parseInt(trimmedMaxTacks, 10);
                return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
              })();
        const minTacksValue =
          trimmedMinTacks === ''
            ? null
            : (() => {
                const parsed = Number.parseInt(trimmedMinTacks, 10);
                return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
              })();
        const firstBoardValue = optimizeFirstBoard === 'auto' ? null : optimizeFirstBoard;
        const lastBoardValue = optimizeLastBoard === 'auto' ? null : optimizeLastBoard;
        const maxTacksPayload = maxTacksValue ?? undefined;
        const minTacksPayload = minTacksValue ?? undefined;
        const firstBoardPayload = firstBoardValue ?? undefined;
        const lastBoardPayload =
          optimizeLastBoard === 1 ? undefined : (lastBoardValue ?? undefined);
        const token = localStorage.getItem('token') ?? '';
        const baseUrl = process.env.REACT_APP_API_BASE_URL ?? '';
        const { data } = await axios.post<OptimizeRouteApiResponse>(
          `${baseUrl}/api/track/optimizeRoute`,
          {
            fileId: selectedFile.id,
            startIndex,
            goal: { lat: goal.lat, lon: goal.lon },
            maxTacks: maxTacksPayload,
            minTacks: minTacksPayload,
            firstBoard: firstBoardPayload,
            lastBoard: lastBoardPayload,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Prefer: 'respond-async',
              'X-Route-Optimization-Async': '1',
            },
          },
        );

        const routeResult =
          data?.processing && data.jobId
            ? await pollOptimizedRouteJob(data.jobId, token, baseUrl)
            : getOptimizedRouteResult(data?.data);

        if (!data?.success || !routeResult?.path || routeResult.path.length === 0) {
          const apiMessage =
            data?.message ?? t('map.optimize.error', 'Could not calculate the route');
          setOptimizeError(apiMessage);
          return;
        }

        drawOptimizedRoute(
          routeResult.path,
          routeResult.resultStartIndex ??
            routeResult.start_index ??
            routeResult.startIndex ??
            startIndex,
          routeResult.etaSeconds ?? routeResult.eta_seconds ?? null,
        );
        setMapInputMode((current) => (current === 'optimize' ? 'idle' : current));
      } catch (error) {
        const message =
          axios.isAxiosError(error) && typeof error.response?.data?.message === 'string'
            ? error.response.data.message
            : (error as Error).message;
        setOptimizeError(message || t('map.optimize.error', 'Could not calculate the route'));
      } finally {
        setOptimizeLoading(false);
      }
    },
    [
      clearOptimizedRouteLayers,
      drawOptimizedRoute,
      loa,
      optimizeStart,
      optimizeFirstBoard,
      optimizeLastBoard,
      optimizeMaxTacksInput,
      optimizeMinTacksInput,
      pollOptimizedRouteJob,
      polars?.raw,
      selectedFile?.id,
      t,
      trackData,
      updateOptimizationSelection,
    ],
  );

  // Referencias para el drag & drop de puntos de optimización
  const isDraggingStartRef = useRef(false);
  const isDraggingGoalRef = useRef(false);

  const handleOptimizationDragStart = useCallback(
    (event: mapboxgl.MapMouseEvent) => {
      const map = mapInstance.current;
      if (!map || !isOptimizingRoute) return;

      const features = map.queryRenderedFeatures(event.point, {
        layers: [optimizedRoutePointsLayerId, optimizedRoutePointsHaloLayerId],
      });

      if (features.length > 0) {
        const label = features[0].properties?.label;
        const startLabel = t('map.optimize.startLabel', 'Start');
        const goalLabel = t('map.optimize.goalLabel', 'Goal');
        if (label === startLabel) {
          isDraggingStartRef.current = true;
          map.dragPan.disable();
          event.preventDefault();
        } else if (label === goalLabel) {
          isDraggingGoalRef.current = true;
          map.dragPan.disable();
          event.preventDefault();
        }
      }
    },
    [
      isOptimizingRoute,
      mapInstance,
      optimizedRoutePointsLayerId,
      optimizedRoutePointsHaloLayerId,
      t,
    ],
  );

  const handleOptimizationDragMove = useCallback(
    (event: mapboxgl.MapMouseEvent) => {
      if (!isOptimizingRoute) return;
      if (!isDraggingStartRef.current && !isDraggingGoalRef.current) return;

      const { lng, lat } = event.lngLat;

      if (isDraggingStartRef.current) {
        // Snap al track
        let closestIndex = -1;
        let bestDistanceMeters = Number.POSITIVE_INFINITY;

        // Optimización: buscar en un subset si el track es muy grande sería ideal,
        // pero por ahora iteramos completo (performant para ~miles de puntos)
        for (const [index, point] of trackData.entries()) {
          if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) {
            continue;
          }
          // Distancia euclídea simple aproximada para velocidad, o haversine si se prefiere
          // Usamos la util existente calculateDistance
          const d = calculateDistance(lat, lng, point.lat, point.lon);
          if (d < bestDistanceMeters) {
            bestDistanceMeters = d;
            closestIndex = index;
          }
        }

        if (closestIndex !== -1) {
          const p = trackData[closestIndex];
          setOptimizeStart({ index: closestIndex, lat: p.lat, lon: p.lon });
        }
      } else if (isDraggingGoalRef.current) {
        // Movimiento libre
        setOptimizeGoal({ lat, lon: lng });
      }
    },
    [isOptimizingRoute, trackData],
  );

  const handleOptimizationDragEnd = useCallback(() => {
    if (isDraggingStartRef.current || isDraggingGoalRef.current) {
      isDraggingStartRef.current = false;
      isDraggingGoalRef.current = false;
      mapInstance.current?.dragPan.enable();
    }
  }, []);

  // Hook global para eventos de mouse move/up porque el arrastre puede salir del punto original
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    if (isOptimizingRoute) {
      map.on('mousedown', handleOptimizationDragStart);
      map.on('mousemove', handleOptimizationDragMove);
      map.on('mouseup', handleOptimizationDragEnd);
    }

    return () => {
      map.off('mousedown', handleOptimizationDragStart);
      map.off('mousemove', handleOptimizationDragMove);
      map.off('mouseup', handleOptimizationDragEnd);
    };
  }, [
    isOptimizingRoute,
    handleOptimizationDragStart,
    handleOptimizationDragMove,
    handleOptimizationDragEnd,
  ]);

  /**
   * Gestiona clics en el mapa: medición de distancias y laylines manuales en modo player.
   */
  const handleMapClick = (event: mapboxgl.MapMouseEvent) => {
    const map = mapInstance.current;
    if (!map) {
      return;
    }

    if (isOptimizingRoute) {
      if (trackData.length === 0) {
        setOptimizeError(t('map.optimizeMissingTrack', 'Carga un track antes de optimizar.'));
        return;
      }
      const { lng, lat } = event.lngLat;

      if (!optimizeStart) {
        let closestIndex = -1;
        let bestDistanceMeters = Number.POSITIVE_INFINITY;
        for (const [index, point] of trackData.entries()) {
          if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) {
            continue;
          }
          const distanceMeters = calculateDistance(lat, lng, point.lat, point.lon) * 1000;
          if (distanceMeters < bestDistanceMeters) {
            bestDistanceMeters = distanceMeters;
            closestIndex = index;
          }
        }
        if (closestIndex === -1) {
          setOptimizeError(t('map.optimizeMissingTrack', 'Carga un track antes de optimizar.'));
          return;
        }
        const toleranceMeters = Math.max(20, calculateMetersPerPixel(map.getZoom(), lat) * 12);
        if (!Number.isFinite(bestDistanceMeters) || bestDistanceMeters > toleranceMeters) {
          setOptimizeError(
            t('map.optimizeStartOnTrack', {
              defaultValue: 'Haz click sobre el track para fijar el inicio (máx. {{max}} m)',
              max: Math.round(toleranceMeters),
            }),
          );
          return;
        }
        const point = trackData[closestIndex];
        setOptimizeStart({ index: closestIndex, lat: point.lat, lon: point.lon });
        setOptimizeGoal(null);
        setOptimizeError(null);
        clearOptimizedRouteLayers();
        setOptimizedRoute(null);
        return;
      }

      const goal = { lat, lon: lng };
      setOptimizeGoal(goal);
      setOptimizeError(null);
      return;
    }

    if (mapInputMode === 'player-target' && isPlayerActive) {
      const centerLayerId = `buoys-center-layer-${idSuffix}`;
      const clickedBuoy =
        map.isStyleLoaded() &&
        map.getLayer(centerLayerId) &&
        map.queryRenderedFeatures(event.point, { layers: [centerLayerId] }).length > 0;
      if (clickedBuoy) {
        return;
      }
      const { lng, lat } = event.lngLat;
      let snappedMark: Mark | null = null;
      let snappedDistance = Infinity;
      const validMarks = marks.filter(
        (m) =>
          typeof m.lat === 'number' &&
          typeof m.lon === 'number' &&
          Number.isFinite(m.lat) &&
          Number.isFinite(m.lon),
      );
      for (const mark of validMarks) {
        const distanceMeters = calculateDistance(lat, lng, mark.lat, mark.lon) * 1000;
        if (distanceMeters < snappedDistance) {
          snappedDistance = distanceMeters;
          snappedMark = mark;
        }
      }

      if (snappedMark && snappedDistance <= BUOY_SNAP_THRESHOLD_METERS) {
        applyLaylinesAtTarget(snappedMark, { source: 'buoy-snap' });
      } else {
        applyLaylinesAtTarget({ lat, lon: lng }, { source: 'map' });
      }
      return;
    }

    if (mapInputMode !== 'measure') {
      return;
    }
    const { lng, lat } = event.lngLat;

    if (startPoint) {
      if (!isArrowFixed) {
        setIsArrowFixed(true);
        endPointReference.current = { lng, lat }; // ⬅️ guardado “definitivo”
        setStartPointOld(startPoint);
        setStartPoint(null);
        // Dibuja inmediatamente la flecha y la etiqueta para garantizar que
        // siempre aparezcan, incluso cuando no hay movimiento del ratón entre
        // los dos clics del modo Measure.
        const end = { lng, lat };

        addArrowLine(
          new mapboxgl.LngLat(startPoint.lng, startPoint.lat),
          new mapboxgl.LngLat(end.lng, end.lat),
        );

        const distributionKm = calculateDistance(startPoint.lat, startPoint.lng, end.lat, end.lng);
        const distributionM = distributionKm * 1000;
        const distributionNm = distributionKm / 1.852;
        const distributionLoa = loa > 0 ? distributionM / loa : 0;
        const heading = calculateHeading(
          { lat: startPoint.lat, lon: startPoint.lng },
          { lat: end.lat, lon: end.lng },
        );

        const infoText =
          `${heading.toFixed(2)}° / ` +
          `${
            distributionM < 1000
              ? distributionM.toFixed(2) + ' m'
              : distributionKm.toFixed(2) + ' km'
          } / ` +
          `${distributionNm.toFixed(2)} nm / ${distributionLoa.toFixed(2)} LOAs`;

        updateTextLabel(startPoint.lng, startPoint.lat, end.lng, end.lat, infoText);
      }
    } else {
      setStartPoint({ lng, lat });
      startPointReference.current = { lng, lat }; // (útil por si lo necesitas)
      setIsArrowFixed(false);
    }
  };

  const toggleMeasureDistance = () => {
    if (mapInputMode === 'measure') {
      selectMapInputMode('idle');
      return;
    }
    selectMapInputMode('measure');
    // Al entrar en medición se empieza siempre una medición nueva.
    setStartPointOld(null);
    setStartPoint(null);
  };

  const toggleOptimizeRoute = () => {
    if (mapInputMode === 'optimize') {
      selectMapInputMode('idle');
      return;
    }
    selectMapInputMode('optimize');
    clearOptimizedRouteLayers();
    setOptimizedRoute(null);
    setOptimizeStart(null);
    setOptimizeGoal(null);
    setOptimizeError(null);
  };

  useEffect(() => {
    resetOptimizationState();
  }, [resetOptimizationState, trackData]);

  useEffect(() => {
    if (optimizedRoute) {
      return;
    }
    updateOptimizationSelection(optimizeStart, optimizeGoal);
  }, [optimizeGoal, optimizeStart, optimizedRoute, updateOptimizationSelection]);

  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.off('click', handleMapClick); // desengancha si ya estaba
      mapInstance.current.on('click', handleMapClick);
    }
    return () => {
      if (mapInstance.current) {
        mapInstance.current.off('click', handleMapClick); // desengancha si ya estaba
        mapInstance.current.off('click', handleMapClick);
      }
    };
  }, [
    applyLaylinesAtTarget,
    clearOptimizedRouteLayers,
    handleMapClick,
    mapInputMode,
    isPlayerActive,
    marks,
    optimizeStart,
    requestOptimizedRoute,
    startPoint,
    t,
    trackData,
  ]);

  useEffect(() => {
    if (!isPlayerActive || !isMeasuringDistance) {
      return;
    }
    resetMapInputArtifacts();
  }, [isMeasuringDistance, isPlayerActive, resetMapInputArtifacts]);

  useEffect(() => {
    const isEditing = editingSegmentId !== null && editingSegmentId !== undefined;
    isEditingSegmentRef.current = isEditing;
    if (isEditing) {
      clearArrowMarkers();
      if (boatMarkerReference.current) {
        boatMarkerReference.current.remove();
        boatMarkerReference.current = null;
      }
    }
  }, [boatMarkerReference, clearArrowMarkers, editingSegmentId]);

  const onTrackHover = (event: mapboxgl.MapMouseEvent) => {
    if (adminToolsConfig.showHoverCoordinates) {
      lastHoverCoordsRef.current = { lat: event.lngLat.lat, lon: event.lngLat.lng };
      if (hoverFrameRef.current === null) {
        hoverFrameRef.current = requestAnimationFrame(() => {
          hoverFrameRef.current = null;
          if (lastHoverCoordsRef.current) {
            setHoverCoordinates(lastHoverCoordsRef.current);
          }
        });
      }
    }
    if (isEditingSegmentRef.current) {
      clearArrowMarkers();
      return;
    }
    if (isPlayerActiveRef.current) {
      if (boatMarkerReference.current) {
        boatMarkerReference.current.remove();
        boatMarkerReference.current = null;
      }
      return;
    }
    if (safeTrackData.length === 0) {
      return;
    }
    if (isMeasuringDistance && startPoint && !isArrowFixed) {
      const { lng, lat } = event.lngLat;

      // ✅ almacenamos en refs (no re-renderiza)
      endPointReference.current = { lng, lat };

      // cálculos de distancia y heading
      const distributionKm = calculateDistance(startPoint.lat, startPoint.lng, lat, lng);
      const distributionM = distributionKm * 1000;
      const distributionNm = distributionKm / 1.852;
      const distributionLoa = loa > 0 ? distributionM / loa : 0;
      const heading = calculateHeading(
        { lat: startPoint.lat, lon: startPoint.lng },
        { lat, lon: lng },
      );

      const infoText =
        `${heading.toFixed(2)}° / ` +
        `${
          distributionM < 1000 ? distributionM.toFixed(2) + ' m' : distributionKm.toFixed(2) + ' km'
        } / ` +
        `${distributionNm.toFixed(2)} nm / ${distributionLoa.toFixed(2)} LOAs`;

      updateTextLabel(startPoint.lng, startPoint.lat, lng, lat, infoText);
      addArrowLine(
        new mapboxgl.LngLat(startPoint.lng, startPoint.lat),
        new mapboxgl.LngLat(lng, lat),
      );
    }

    if (
      isHighlighting &&
      startPoint &&
      startPoint.index !== undefined &&
      safeTrackData.length > 0
    ) {
      const { lng, lat } = event.lngLat;
      let closestIndex = 0;
      let bestDistance = calculateDistance(lat, lng, safeTrackData[0].lat, safeTrackData[0].lon);
      for (let index = 1; index < safeTrackData.length; index++) {
        const point = safeTrackData[index];
        const distance = calculateDistance(lat, lng, point.lat, point.lon);
        if (distance < bestDistance) {
          bestDistance = distance;
          closestIndex = index;
        }
      }

      const [startIndex, endIndex] =
        startPoint.index < closestIndex
          ? [startPoint.index, closestIndex]
          : [closestIndex, startPoint.index];

      const color = currentHighlightColorReference.current ?? '#FFD700';
      setDraftHighlight((current) => {
        if (
          current &&
          current.source === 'map' &&
          current.startIndex === startIndex &&
          current.endIndex === endIndex &&
          current.color === color
        ) {
          return current;
        }
        return { id: 'draft-map', startIndex, endIndex, color, source: 'map' };
      });
    }

    if (
      mapInstance.current &&
      safeTrackData.length > 0 &&
      !isMeasuringDistance &&
      !isEditingSegmentRef.current
    ) {
      const cursorLng = event.lngLat.lng;
      const cursorLat = event.lngLat.lat;

      let closestPoint = safeTrackData[0];
      let bestDistance = calculateDistance(
        closestPoint.lat,
        closestPoint.lon,
        cursorLat,
        cursorLng,
      );
      for (let index = 1; index < safeTrackData.length; index++) {
        const point = safeTrackData[index];
        const distance = calculateDistance(point.lat, point.lon, cursorLat, cursorLng);
        if (distance < bestDistance) {
          bestDistance = distance;
          closestPoint = point;
        }
      }

      // ⇢ Distancia máxima (≈ 10 px) adaptada al nivel de zoom
      const metresPerPixel = calculateMetersPerPixel(mapInstance.current.getZoom());
      const thresholdMeters = metresPerPixel * 10; // 10 px de tolerancia

      // Distancia real entre cursor y punto más cercano
      const closestDistanceMeters =
        calculateDistance(closestPoint.lat, closestPoint.lon, cursorLat, cursorLng) * 1000; // km → m

      if (closestDistanceMeters < thresholdMeters && !isPlayerActive) {
        const previousIndex = Math.max(safeTrackData.indexOf(closestPoint) - 1, 0);
        const previousPoint = safeTrackData[previousIndex];
        const heading = calculateHeading(previousPoint, closestPoint);

        void initializeArrowMarker(closestPoint.lon, closestPoint.lat, heading);
      } else {
        // Si el cursor se aleja, ocultamos la flecha
        clearArrowMarkers();
      }
    }
  };
  // (removed unused helpers: getLineStringFromData, chunkLineString)

  function actualizarMapaConTrack(track: TrackPoint[], cameraLoadToken: number) {
    const validTrack = track?.filter((p) => isValidCoords(p)) ?? [];
    if (validTrack.length === 0 || !mapContainer.current) {
      return;
    }
    const map = mapInstance.current;
    if (!map) {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    void (async () => {
      if (!mapContainer.current?.offsetWidth) {
        await new Promise<void>((resolve) => {
          const ro = new ResizeObserver(() => {
            if (mapContainer.current && mapContainer.current.offsetWidth) {
              ro.disconnect();
              resolve();
            }
          });
          if (mapContainer.current) {
            ro.observe(mapContainer.current);
          }
          signal.addEventListener('abort', () => ro.disconnect());
        });
        if (signal.aborted) {
          return;
        }
      }
      if (!isCameraLoadActive(cameraLoadToken)) {
        return;
      }

      const coordinates = validTrack.map((p) => [p.lon, p.lat]);
      const fullLine: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates },
            properties: {},
          },
        ],
      };

      const lats = validTrack.map((p) => p.lat);
      const lons = validTrack.map((p) => p.lon);
      const padLat = 750 / 111_320;
      const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const padLon = 750 / (111_320 * Math.cos((midLat * Math.PI) / 180));
      const bounds = new mapboxgl.LngLatBounds(
        [Math.min(...lons) - padLon, Math.min(...lats) - padLat],
        [Math.max(...lons) + padLon, Math.max(...lats) + padLat],
      );

      const boundsCenter = bounds.getCenter();
      gridOriginRef.current = { lng: boundsCenter.lng, lat: boundsCenter.lat };

      const map = mapInstance.current;
      if (!map) {
        return;
      }

      // Fija el bearing objetivo antes de dibujar nada para evitar el “salto” de rotación
      const targetBearing = normalizeBearing(orientationBearingRef.current ?? 0);
      map.jumpTo({ bearing: targetBearing });
      setMapBearing(targetBearing);

      //  MapPanel.tsx  (dentro de actualizarMapaConTrack)
      const fitToken = fitTokenRef.current + 1;
      fitTokenRef.current = fitToken;
      map.once('idle', () => {
        if (!isCameraLoadActive(cameraLoadToken)) {
          return;
        }
        // Marca el fit inicial como pendiente hasta que el movimiento termine
        initialFitAppliedRef.current = false;
        dbg('[loader] idle before fitBounds', {
          bounds: bounds.toArray(),
          targetBearing,
          startWait: Date.now(),
          fitToken,
        });
        /* ① creas/actualizas source y layer del track */
        const overlayPalette = getCurrentMapOverlayPalette();
        if (map.getSource(trackSourceId)) {
          (map.getSource(trackSourceId) as mapboxgl.GeoJSONSource).setData(fullLine);
        } else {
          map.addSource(trackSourceId, {
            type: 'geojson',
            tolerance: 0.01,
            data: fullLine,
          });
          map.addLayer({
            id: trackLayerId,
            type: 'line',
            source: trackSourceId,
            paint: {
              [LINE_COLOR]: overlayPalette.plainTrack,
              [LINE_WIDTH]: overlayPalette.plainTrackWidth,
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          });
        }
        map.setPaintProperty(trackLayerId, LINE_COLOR, overlayPalette.plainTrack);
        map.setPaintProperty(trackLayerId, LINE_WIDTH, overlayPalette.plainTrackWidth);
        syncOptionalLineCasingLayer({
          map,
          sourceId: trackSourceId,
          casingLayerId: trackCasingLayerId,
          beforeLayerId: trackLayerId,
          width: overlayPalette.trackCasingWidth,
          palette: overlayPalette,
        });
        if (map.getLayer(raceStartLineLayerId)) {
          safeMoveLayer(map, raceStartLineLayerId);
        }
        if (map.getLayer(raceStartLinePointLayerId)) {
          safeMoveLayer(map, raceStartLinePointLayerId);
        }

        map.fitBounds(bounds, { padding: 256, maxZoom: 15, duration: 0 });
        const markFitComplete = () => {
          if (fitToken !== fitTokenRef.current) {
            dbg('[loader] fit complete skipped (stale token)', {
              fitToken,
              current: fitTokenRef.current,
            });
            return;
          }
          initialFitAppliedRef.current = true;
          dbg('[loader] initial fit completed', {
            moveendBearing: map.getBearing(),
            zoom: map.getZoom(),
            center: map.getCenter(),
            fitToken,
          });
        };
        map.once('moveend', markFitComplete);
        map.once('idle', markFitComplete);
        map.jumpTo({ bearing: orientationBearingRef.current });
        setMapBearing(orientationBearingRef.current);
        renderTenMinutePoints(
          map,
          validTrack,
          'plain',
          timeMarkerIntervalMinutes,
          timeMarkerLabelOffsetMinutes,
        );
        setLineMode('plain');

        let loadingResolved = false;
        let waitForFitStarted = false;
        let finalBearingApplied = false;
        let finalCameraAndTrackRenderSettled = false;
        let waitingForFinalCameraAndTrackRender = false;
        let bearingToApply = normalizeBearing(orientationBearingRef.current ?? 0);
        let hasWindBearing = Boolean(orientationIsWindRef.current);
        let isNorthish = Math.abs(angleDiff(bearingToApply, 0)) <= 1;
        const startWait = Date.now();
        const isMapBearingReadyForLoaderDismissal = () => {
          if (!hasWindBearing) {
            return true;
          }
          if (!mapInstance.current) {
            return false;
          }
          const currentMapBearing = normalizeBearing(mapInstance.current.getBearing());
          return (
            Math.abs(angleDiff(currentMapBearing, bearingToApply)) <= TWD_UP_BEARING_TOLERANCE_DEG
          );
        };
        const applyFinalBearingBeforeLoaderDismissal = () => {
          if (finalBearingApplied && isMapBearingReadyForLoaderDismissal()) {
            return;
          }
          const hasValidBearing =
            typeof orientationBearingRef.current === 'number' &&
            Number.isFinite(orientationBearingRef.current);
          bearingToApply = hasValidBearing ? normalizeBearing(orientationBearingRef.current) : 0;
          hasWindBearing = hasValidBearing && orientationIsWindRef.current;
          isNorthish = Math.abs(angleDiff(bearingToApply, 0)) <= 1;
          orientationBearingRef.current = bearingToApply;
          orientationIsWindRef.current = hasWindBearing;
          setTrackModes(hasWindBearing ? 2 : isNorthish ? 1 : 0);
          if (mapInstance.current) {
            dbg('[bearing] resetUiState applying bearing before loader dismissal', {
              source: 'resetUiState',
              bearingToApply,
              hasWindBearing,
            });
            const currentMapBearing = normalizeBearing(mapInstance.current.getBearing());
            if (
              Math.abs(angleDiff(currentMapBearing, bearingToApply)) > TWD_UP_BEARING_TOLERANCE_DEG
            ) {
              mapInstance.current.jumpTo({ bearing: bearingToApply });
            }
            setMapBearing(bearingToApply);
          }
          finalBearingApplied = isMapBearingReadyForLoaderDismissal();
        };
        const isTrackLineReadyForLoaderDismissal = () =>
          Boolean(
            map.getLayer(trackLayerId) &&
              map.getSource(trackSourceId) &&
              (map.isSourceLoaded?.(trackSourceId) ?? true),
          );
        const waitForFinalCameraAndTrackRender = () => {
          applyFinalBearingBeforeLoaderDismissal();
          if (!isMapBearingReadyForLoaderDismissal()) {
            return false;
          }
          if (!isTrackLineReadyForLoaderDismissal()) {
            return false;
          }
          if (finalCameraAndTrackRenderSettled) {
            return true;
          }
          const canSettleFinalCameraAndTrackRender = () =>
            !(map.isMoving?.() ?? false) && isTrackLineReadyForLoaderDismissal();
          const settleFinalCameraAndTrackRender = () => {
            if (finalCameraAndTrackRenderSettled) {
              return false;
            }
            map.off('idle', markFinalRenderSettled);
            waitingForFinalCameraAndTrackRender = false;
            finalCameraAndTrackRenderSettled = true;
            return true;
          };
          const markFinalRenderSettled = () => {
            if (settleFinalCameraAndTrackRender()) {
              waitForFit();
            }
          };
          if (waitingForFinalCameraAndTrackRender) {
            if (canSettleFinalCameraAndTrackRender()) {
              return settleFinalCameraAndTrackRender();
            }
            return false;
          }
          waitingForFinalCameraAndTrackRender = true;
          map.once('idle', markFinalRenderSettled);
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              if (
                finalCameraAndTrackRenderSettled ||
                signal.aborted ||
                fitToken !== fitTokenRef.current ||
                !isCameraLoadActive(cameraLoadToken)
              ) {
                return;
              }
              if (canSettleFinalCameraAndTrackRender()) {
                markFinalRenderSettled();
              }
            });
          });
          return false;
        };
        const finishLoading = () => {
          if (
            loadingResolved ||
            signal.aborted ||
            fitToken !== fitTokenRef.current ||
            !isCameraLoadActive(cameraLoadToken)
          ) {
            return;
          }
          if (!finalBearingApplied) {
            applyFinalBearingBeforeLoaderDismissal();
          }
          if (!isMapBearingReadyForLoaderDismissal()) {
            window.setTimeout(waitForFit, 50);
            return;
          }
          loadingResolved = true;
          pendingInitialCameraRef.current = false;
          setTrackModes(hasWindBearing ? 2 : isNorthish ? 1 : 0); // CUSTOM when not wind/north
          setGridOrientationBearing(bearingToApply);
          setMapBearing(bearingToApply);
          captureCameraState();
          completedTrackRenderKeyRef.current = getTrackRenderKey(validTrack, lastFileIdRef.current);
          setIsMapLoading(false);
        };
        const waitForFit = () => {
          if (fitToken !== fitTokenRef.current || signal.aborted || !isCameraLoadActive(cameraLoadToken)) {
            dbg('[loader] waitForFit stale token, abort loop', {
              fitToken,
              current: fitTokenRef.current,
            });
            return;
          }
          const elapsed = Date.now() - startWait;
          if (elapsed > 1800) {
            dbg('[loader] waitForFit timeout; initialFitAppliedRef', initialFitAppliedRef.current);
          }
          const stillMoving = map.isMoving?.() ?? false;
          const loaded = map.loaded?.() ?? true;
          const tilesLoaded = map.areTilesLoaded?.() ?? true;
          const fitRetryWindowElapsed = elapsed >= TRACK_FIT_SETTLE_DELAY_MS;
          if (!initialFitAppliedRef.current && elapsed > 1200) {
            dbg('[loader] force mark fit complete after timeout', { elapsed, fitToken });
            initialFitAppliedRef.current = true;
          }
          dbg('[loader] waiting for initial fit...', {
            elapsedMs: elapsed,
            initialFitApplied: initialFitAppliedRef.current,
            isMoving: stillMoving,
            loaded,
            tilesLoaded,
            fitRetryWindowElapsed,
            fitToken,
          });
          if (fitRetryWindowElapsed && initialFitAppliedRef.current && !stillMoving && loaded && tilesLoaded) {
            applyFinalBearingBeforeLoaderDismissal();
            window.requestAnimationFrame(() => {
              if (
                fitToken !== fitTokenRef.current ||
                signal.aborted ||
                !isCameraLoadActive(cameraLoadToken)
              ) {
                return;
              }
              if (!waitForFinalCameraAndTrackRender()) {
                window.setTimeout(waitForFit, 50);
                return;
              }
              finishLoading();
            });
            return;
          }
          if (elapsed > TRACK_FIT_LOADING_TIMEOUT_MS && !stillMoving) {
            dbg('[loader] final settle timeout; hiding loader after non-moving map', {
              elapsed,
              loaded,
              tilesLoaded,
              fitToken,
            });
            if (!waitForFinalCameraAndTrackRender()) {
              window.setTimeout(waitForFit, 50);
              return;
            }
            finishLoading();
            return;
          }
          window.setTimeout(waitForFit, 50);
        };
        const startWaitForFit = () => {
          if (waitForFitStarted) {
            return;
          }
          waitForFitStarted = true;
          waitForFit();
        };
        const fallbackTimeout = window.setTimeout(startWaitForFit, TRACK_FIT_LOADING_TIMEOUT_MS);

        ensureStyleLoaded(() => {
          updateGridLayers(loa, gridRotationAngle);
          window.clearTimeout(fallbackTimeout);
          startWaitForFit();
        });
      });
    })();

    return () => controller.abort();
  }

  const refreshGridAfterCompassOrientationChange = (normalizedTarget: number) => {
    const map = mapInstance.current;
    if (!map || trackData.length === 0) {
      return;
    }

    const refreshGrid = () => {
      if (map.isMoving?.()) {
        map.once('idle', refreshGrid);
        return;
      }
      isGridRotationActiveRef.current = false;
      ensureStyleLoaded(() => updateGridLayers(loa, normalizedTarget));
    };

    window.requestAnimationFrame(refreshGrid);
  };

  const handleTrackModeChange = (mode: number) => {
    let targetBearing: number | null = null;

    switch (mode) {
      case 0: {
        targetBearing = mapInstance.current?.getBearing?.() ?? mapBearing;
        break;
      }
      case 1: {
        targetBearing = 0;
        break;
      }
      case 2: {
        const average = trackData.length > 0 ? calculateAverageTwd() : null;
        targetBearing = average ?? null;
        break;
      }
      case START_LINE_ORIENTATION_MODE: {
        targetBearing = startLineReferenceBearing;
        break;
      }
      default: {
        break;
      }
    }

    const normalizedTarget =
      targetBearing !== null && Number.isFinite(targetBearing)
        ? normalizeBearing(targetBearing)
        : null;

    if (normalizedTarget === null) {
      return;
    }
    setTrackModes(mode);
    console.log('Ajustando rumbo del mapa a:', normalizedTarget);
    setGridOrientationBearing(normalizedTarget);
    orientationIsWindRef.current = mode === 2;
    orientationIsStartLineRef.current = mode === START_LINE_ORIENTATION_MODE;
    orientationBearingRef.current = normalizedTarget;

    const map = mapInstance.current;
    if (map) {
      dbg('[bearing] handleTrackModeChange', { mode, normalizedTarget });
      map.setBearing(normalizedTarget);
      setMapBearing(normalizedTarget);
      refreshGridAfterCompassOrientationChange(normalizedTarget);
    }
  };

  // 3️⃣ applyLineMode – usa isStyleLoaded y espera a "styledata" antes de pintar
  function applyLineMode(
    mode: LineMode,
    trackDataParameter?: typeof trackData,
    metricOverride?: ColorMetricKey,
    widthMetricOverride?: WidthMetricKey,
    existingLineModeApplyToken?: number,
  ) {
    const lineModeApplyToken = existingLineModeApplyToken ?? lineModeApplyTokenRef.current + 1;
    if (existingLineModeApplyToken === undefined) {
      lineModeApplyTokenRef.current = lineModeApplyToken;
    } else if (lineModeApplyTokenRef.current !== lineModeApplyToken) {
      return;
    }
    // Mostrar/ocultar leyenda
    setShowLegend(mode !== 'plain');

    const trackDataToUse = trackDataParameter || trackData;
    const metricForMode: ColorMetricKey =
      mode === 'tactical' ? 'heel' : (metricOverride ?? selectedMetric);
    const widthMetricForMode = widthMetricOverride ?? selectedMetricWidth;
    const nextColorLegend = getColorLegendForMetric(metricForMode, mode);
    // Actualiza leyenda y estado de modo
    setColorLegend(nextColorLegend);
    setLineMode(mode);

    const map = mapInstance.current;
    if (!map) {
      developmentLog.error('mapInstance no está definido.');
      return;
    }
    // Esperar a que el estilo esté completamente cargado
    if (!map.isStyleLoaded()) {
      map.once('styledata', () => {
        if (lineModeApplyTokenRef.current !== lineModeApplyToken) {
          return;
        }
        applyLineMode(
          mode,
          trackDataParameter,
          metricOverride,
          widthMetricOverride,
          lineModeApplyToken,
        );
      });
      return;
    }

    // 1️⃣ Ajustar ancho y color base de la capa principal
    const isColorizedMode = mode === 'speed' || mode === 'tactical';
    const overlayPalette = getCurrentMapOverlayPalette();

    if (!map.getLayer(trackLayerId) || !map.getSource(trackSourceId)) {
      if (isColorizedMode) {
        colorTrackByMetric(
          metricForMode,
          widthMetricForMode,
          mode,
          trackDataToUse,
          nextColorLegend,
        );
        renderTenMinutePoints(
          map,
          trackDataToUse,
          mode,
          timeMarkerIntervalMinutes,
          timeMarkerLabelOffsetMinutes,
        );
        return;
      }
      map.once('idle', () => {
        if (lineModeApplyTokenRef.current !== lineModeApplyToken) {
          return;
        }
        applyLineMode(
          mode,
          trackDataParameter,
          metricOverride,
          widthMetricOverride,
          lineModeApplyToken,
        );
      });
      return;
    }

    const baseWidth = isColorizedMode ? 5 : overlayPalette.plainTrackWidth;
    map.setPaintProperty(trackLayerId, LINE_WIDTH, baseWidth);
    map.setPaintProperty(
      trackLayerId,
      LINE_COLOR,
      isColorizedMode ? ['get', 'color'] : overlayPalette.plainTrack,
    );
    map.setPaintProperty(trackLayerId, 'line-opacity', 1);
    if (isColorizedMode) {
      removeOptionalLineCasingLayer(map, trackCasingLayerId);
    } else {
      syncOptionalLineCasingLayer({
        map,
        sourceId: trackSourceId,
        casingLayerId: trackCasingLayerId,
        beforeLayerId: trackLayerId,
        width: overlayPalette.trackCasingWidth,
        palette: overlayPalette,
      });
    }

    // 2️⃣ Dibujar / quitar puntos de 10 min según el modo
    if (['plain', 'speed', 'tactical'].includes(mode)) {
      renderTenMinutePoints(
        map,
        trackDataToUse,
        mode,
        timeMarkerIntervalMinutes,
        timeMarkerLabelOffsetMinutes,
      );
    } else {
      for (const id of [CIRCLE_LAYER_ID, CIRCLE_LABEL_LAYER_ID]) {
        if (map.getLayer(id)) {
          map.removeLayer(id);
        }
      }
      if (map.getSource(CIRCLE_POINTS_SOURCE_ID)) {
        map.removeSource(CIRCLE_POINTS_SOURCE_ID);
      }
      if (map.getLayer(TWD_LAYER_ID)) {
        map.removeLayer(TWD_LAYER_ID);
      }
      if (map.getSource(TWD_SOURCE_ID)) {
        map.removeSource(TWD_SOURCE_ID);
      }
    }

    // 3️⃣ Color por métrica cuando no es plain
    if (isColorizedMode) {
      colorTrackByMetric(metricForMode, widthMetricForMode, mode, trackDataToUse, nextColorLegend);
    }
  }

  const toggleRaceStats = () => {
    setIsRaceStatsVisible((previous) => !previous);
  };
  const togglePlayerMode = () => {
    if (!isPlayerActive && playerTimeline.length === 0) {
      dbg('[player] toggle requested but timeline vacío');
      return;
    }
    const preservedBearing = preserveCurrentBearing();
    forceRemovePlayerMarker();
    if (boatMarkerReference.current) {
      boatMarkerReference.current.remove();
      boatMarkerReference.current = null;
    }
    // Limpia cualquier rastro previo del player para evitar que un primer frame quede “pegado”
    clearPlayerGraphics();
    if (isPlayerActive) {
      selectMapInputMode('idle');
      dbg('[player] toggled OFF');
      return;
    }
    if (preservedBearing != null && mapInstance.current) {
      setBearingIfMeaningfullyChanged(
        mapInstance.current,
        preservedBearing,
        PLAYER_BEARING_SYNC_TOLERANCE_DEG,
      );
    }
    selectMapInputMode('player-target');
    isPlayerActiveRef.current = true;
    setPlayerIndex((current) => Math.min(Math.max(current, 0), playerMaxIndex));
    setIsPlayerActive(true);
    dbg('[player] toggled ON', {
      timelineLength: playerTimeline.length,
      safeTrackLength: safeTrackData.length,
    });
  };
  const handlePlayPause = () => {
    if (playerTimeline.length === 0) {
      dbg('[player] play/pause ignored: timeline vacío');
      return;
    }
    if (!isPlayerActive) {
      const preservedBearing = preserveCurrentBearing();
      selectMapInputMode('player-target');
      setIsPlayerActive(true);
      if (preservedBearing != null && mapInstance.current) {
        setBearingIfMeaningfullyChanged(
          mapInstance.current,
          preservedBearing,
          PLAYER_BEARING_SYNC_TOLERANCE_DEG,
        );
      }
    }
    setIsPlaying((previous) => !previous);
  };
  const handleTimelineScrub = (value: number) => {
    if (Number.isNaN(value) || playerTimeline.length === 0) {
      return;
    }
    const clamped = Math.min(Math.max(Math.round(value), 0), playerMaxIndex);
    const changed = clamped !== safePlayerIndexRef.current;
    if (changed) {
      setPlayerIndex(clamped);
      safePlayerIndexRef.current = clamped;
    }
    // Refresca marker/laylines al arrastrar el thumb incluso en pausa
    if (isPlayerActiveRef.current || laylineLockRef.current) {
      updatePlayerGraphicsRef.current?.();
    }
  };
  const handleTimelineInput = (
    event: React.ChangeEvent<HTMLInputElement> | React.FormEvent<HTMLInputElement>,
  ) => {
    const value = Number((event.currentTarget as HTMLInputElement).value);
    handleTimelineScrub(value);
  };
  const toggleLineMode = (
    mode: LineMode,
    trackDataParameter?: typeof trackData,
    metricOverride?: ColorMetricKey,
    widthMetricOverride?: WidthMetricKey,
  ) => {
    const trackDataToUse = trackDataParameter || trackData;
    if (!trackDataToUse || trackDataToUse.length === 0) {
      developmentLog.warn('No hay datos para aplicar el modo:', mode);
      return;
    }
    if (!mapInstance.current) {
      developmentLog.error('mapInstance no está definido');
      return;
    }
    applyLineMode(mode, trackDataToUse, metricOverride, widthMetricOverride);
  };
  useEffect(() => {
    if (lineMode === 'ladder' && !isLadderAvailable) {
      toggleLineMode('plain');
    }
  }, [isLadderAvailable, lineMode]);
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!panelReference.current) {
      return;
    }

    const rect = panelReference.current.getBoundingClientRect();

    setDragging(true);
    setDragOffset({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };
  const handleResizeMouseMove = (event: MouseEvent) => {
    if (!resizing || !panelReference.current || !initialPanelRect || !initialMousePos) {
      return;
    }
    let newWidth = initialPanelRect.width;
    let newHeight = initialPanelRect.height;
    let newLeft = position.left;
    let newTop = position.top;
    const dx = event.clientX - initialMousePos.x;
    const dy = event.clientY - initialMousePos.y;

    if (resizeDirection.includes('e')) {
      newWidth = initialPanelRect.width + dx;
    }
    if (resizeDirection.includes('w')) {
      newWidth = initialPanelRect.width - dx;
      newLeft = position.left + dx;
    }
    if (resizeDirection.includes('s')) {
      newHeight = initialPanelRect.height + dy;
    }
    if (resizeDirection.includes('n')) {
      newHeight = initialPanelRect.height - dy;
      newTop = position.top + dy;
    }

    // Limitar tamaño mínimo
    if (newWidth < 200) {
      newWidth = 200;
    }
    if (newHeight < 150) {
      newHeight = 150;
    }

    setPanelSize({ width: newWidth, height: newHeight });
    setPosition({ top: newTop, left: newLeft });
  };

  // Función para finalizar el redimensionado
  const handleResizeMouseUp = () => {
    setResizing(false);
    setResizeDirection('');
    setInitialPanelRect(null);
    setInitialMousePos(null);
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (!dragging || !panelReference.current || !mapContainer.current) {
      return;
    }

    // 1️⃣ Tomamos el rectángulo real del contenedor de este panel
    const containerRect = mapContainer.current.getBoundingClientRect();

    // 2️⃣ Calculamos la nueva posición (igual que antes)
    const newLeft = event.clientX - containerRect.left - dragOffset.x;
    const newTop = event.clientY - containerRect.top - dragOffset.y;

    // 3️⃣ Clampeamos para que no salga del contenedor
    const clampedLeft = Math.max(
      0,
      Math.min(newLeft, containerRect.width - panelReference.current.offsetWidth),
    );
    const clampedTop = Math.max(
      0,
      Math.min(newTop, containerRect.height - panelReference.current.offsetHeight),
    );

    setPosition({ top: clampedTop, left: clampedLeft });
  };

  const startLineReferenceBearing = useMemo(
    () => calculateStartLineReferenceBearing(startLineSegments, trackData),
    [startLineSegments, trackData],
  );

  useEffect(() => {
    if (startLineReferenceBearing !== null || trackModes !== START_LINE_ORIENTATION_MODE) {
      return;
    }
    orientationIsStartLineRef.current = false;
    setTrackModes(Math.abs(angleDiff(mapBearing, 0)) <= 1 ? 1 : 0);
  }, [mapBearing, startLineReferenceBearing, trackModes]);

  const orientationOptions = useMemo<MapSelectOption<number>[]>(() => {
    const toUpper = (value: string) => value.toUpperCase();
    const customBearing = Number.isFinite(mapBearing) ? normalizeBearing(mapBearing) : 0;
    const customLabel = `${t('map.customOrientation', 'CUSTOM')} (${customBearing.toFixed(0)}°)`;
    const options: MapSelectOption<number>[] = [
      { value: 1, label: toUpper(t('map.northUp', 'NORTE ARRIBA')) },
      { value: 0, label: toUpper(customLabel) },
    ];
    if (startLineReferenceBearing !== null) {
      const formattedStartLineBearing = Number.isFinite(startLineReferenceBearing)
        ? startLineReferenceBearing.toFixed(0)
        : '0';
      const startLineLabel = t('map.startLineOrientation', {
        defaultValue: 'Start line ({{bearing}}°)',
        bearing: formattedStartLineBearing,
      });
      options.push({
        value: START_LINE_ORIENTATION_MODE,
        label: toUpper(startLineLabel),
      });
    }
    if (hasTwdData) {
      const average = calculateAverageTwd();
      if (average !== null) {
        const formattedAverage = Number.isFinite(average) ? average.toFixed(0) : '0';
        const avgLabel = t('map.averageTwdUp', {
          defaultValue: 'TWD MEDIO ARRIBA ({{average}}°)',
          average: formattedAverage,
        });
        options.push({
          value: 2,
          label: toUpper(avgLabel),
        });
      }
    }
    return options;
  }, [calculateAverageTwd, hasTwdData, mapBearing, startLineReferenceBearing, t]);

  const targetCompassRotation = useMemo(() => normalizeBearing(360 - mapBearing), [mapBearing]);
  const [smoothCompassRotation, setSmoothCompassRotation] = useState(targetCompassRotation);

  useEffect(() => {
    setSmoothCompassRotation((previous) => {
      if (!Number.isFinite(previous)) {
        return targetCompassRotation;
      }

      let adjusted = targetCompassRotation;

      while (adjusted - previous > 180) {
        adjusted -= 360;
      }
      while (adjusted - previous < -180) {
        adjusted += 360;
      }

      return adjusted;
    });
  }, [targetCompassRotation]);

  const handleMouseUp = () => {
    setDragging(false);
  };
  // Evento de clic en el mapa para establecer el punto inicial de la medición

  // getColorBasedOnMetric moved to module scope
  /*───────────────────────────────────────────────────────────
   util  combineSegments  – ahora genérica y tipada
───────────────────────────────────────────────────────────*/
  // combineSegments moved to module scope

  /*───────────────────────────────────────────────────────────
  colorTrackByMetric  –  tipado OK y sin chunks
───────────────────────────────────────────────────────────*/
  const colorTrackByMetric = (
    metric: ColorMetricKey,
    metricWidth: WidthMetricKey,
    mode: string,
    trackDataParameter: TrackPoint[] = safeTrackData,
    legendOverride?: ColorLegend,
  ) => {
    if (mode === 'plain') {
      return;
    }
    if (mode === 'tactical') {
      metric = 'heel';
    }
    const lineTrackData = trackDataParameter.filter(
      (point) => Number.isFinite(point.lat) && Number.isFinite(point.lon),
    );
    if (!mapInstance.current || lineTrackData.length < 2) {
      return;
    }
    const legendForPaint = legendOverride ?? colorLegend;
    const minMetricValue = legendForPaint?.min ?? metricBounds.min[metric] ?? 0;
    const maxMetricValue = legendForPaint?.max ?? metricBounds.max[metric] ?? 1;
    const overlayPalette = getCurrentMapOverlayPalette();
    const lineFeatures: GeoJSON.Feature<GeoJSON.Geometry>[] = [];
    for (let index = 0; index < lineTrackData.length - 1; index++) {
      const startPoint = lineTrackData[index];
      const endPoint = lineTrackData[index + 1];
      const rawValue = startPoint[metric];
      const value = isFiniteMetricValue(rawValue) ? rawValue : null;
      const widthValue = metricWidth === 'none' ? null : startPoint[metricWidth];
      let color: string;
      if (metric === 'heel' && value !== null) {
        color = getTacticalMetricColor({
          value,
          min: minMetricValue,
          max: maxMetricValue,
          colorMin: overlayPalette.tacticalRampStart,
          colorMid: overlayPalette.tacticalRampMid,
          colorMax: overlayPalette.tacticalRampEnd,
        });
      } else if (value === null) {
        color = MISSING_METRIC_COLOR;
      } else {
        color = getColorBasedOnMetricForPalette(
          value,
          minMetricValue,
          maxMetricValue,
          overlayPalette,
        );
      }
      const lineWidth = getMetricLineWidth({
        value: widthValue,
        min: metricWidth === 'none' ? 0 : (metricBounds.min[metricWidth] ?? 0),
        max: metricWidth === 'none' ? 0 : (metricBounds.max[metricWidth] ?? 0),
      });
      lineFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [startPoint.lon, startPoint.lat],
            [endPoint.lon, endPoint.lat],
          ],
        },
        properties: {
          color: color,
          lineWidth: lineWidth,
        },
      });
    }
    const lineData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: combineSegments(lineFeatures),
    };
    const lineWidthProperty: DataDrivenPropertyValueSpecification<number> =
      metricWidth === 'none'
        ? 5
        : (['get', 'lineWidth'] as unknown as DataDrivenPropertyValueSpecification<number>);

    if (mapInstance.current.getSource(trackSourceId)) {
      (mapInstance.current.getSource(trackSourceId) as mapboxgl.GeoJSONSource).setData(lineData);
      if (!mapInstance.current.getLayer(trackLayerId)) {
        mapInstance.current.addLayer({
          id: trackLayerId,
          type: 'line',
          source: trackSourceId,
          maxzoom: 17,
          layout: {
            [LINE_CAP]: 'round',
            [LINE_JOIN]: 'round',
          },
          paint: {
            [LINE_COLOR]: ['get', 'color'],
            [LINE_WIDTH]: lineWidthProperty,
            'line-opacity': 1,
          },
        });
      }
      mapInstance.current.setPaintProperty(trackLayerId, LINE_COLOR, ['get', 'color']);
      mapInstance.current.setPaintProperty(trackLayerId, LINE_WIDTH, lineWidthProperty);
      mapInstance.current.setPaintProperty(trackLayerId, 'line-opacity', 1);
    } else {
      mapInstance.current.addSource(trackSourceId, {
        type: 'geojson',
        tolerance: 0.01,
        data: lineData,
      });
      mapInstance.current.addLayer({
        id: trackLayerId,
        type: 'line',
        source: trackSourceId,
        maxzoom: 17,
        layout: {
          [LINE_CAP]: 'round', // ← añade estas dos líneas
          [LINE_JOIN]: 'round', //    (o "bevel" si prefieres un corte plano)
        },
          paint: {
            [LINE_COLOR]: ['get', 'color'],
            [LINE_WIDTH]:
              metricWidth === 'none'
                ? 5
                : (['get', 'lineWidth'] as unknown as DataDrivenPropertyValueSpecification<number>),
            'line-opacity': 1,
          },
        });
      }
    removeOptionalLineCasingLayer(mapInstance.current, trackCasingLayerId);
  };

  const toggleHighlightMode = () => {
    if (mapInputMode === 'highlight') {
      selectMapInputMode('idle');
      return;
    }
    selectMapInputMode('highlight');
    setPendingDraftHighlightId(null);
    clearMapDraftHighlight();
    setStartPoint(null);
    currentHighlightColorReference.current = null;
  };
  const updateInfoText = (lng: number, lat: number) => {
    if (!startPoint || !distanceInfoElementReference.current) {
      return;
    }

    const currentDistanceKm = calculateDistance(startPoint.lat, startPoint.lng, lat, lng);
    const currentDistanceMeters = currentDistanceKm * 1000;
    const currentDistanceNauticalMiles = currentDistanceKm / 1.852;
    const currentDistanceLOA = loa > 0 ? currentDistanceMeters / loa : 0;

    const heading = calculateHeading(
      { lat: startPoint.lat, lon: startPoint.lng },
      { lat, lon: lng },
    );

    const newInfoText = `${heading.toFixed(2)}° / ${
      currentDistanceMeters < 1000
        ? currentDistanceMeters.toFixed(2) + ' m'
        : currentDistanceKm.toFixed(2) + ' km'
    } / ${currentDistanceNauticalMiles.toFixed(2)} nm / ${currentDistanceLOA.toFixed(2)} LOAs`;

    // Actualiza solo el texto sin modificar la visibilidad o el DOM
    distanceInfoElementReference.current.textContent = newInfoText;
  };

  const handleHighlightClick = (event: mapboxgl.MapMouseEvent) => {
    if (!isHighlighting || trackData.length === 0) {
      return;
    }

    const { lng, lat } = event.lngLat;

    // Encuentra el índice más cercano dentro del `filteredTrackData`
    let closestIndex: number | null = null;
    let bestDistance = Infinity;
    for (const [index, point] of trackData.entries()) {
      if (!Number.isFinite(point.lat) || !Number.isFinite(point.lon)) {
        continue;
      }
      const distance = calculateDistance(lat, lng, point.lat, point.lon);
      if (distance < bestDistance) {
        bestDistance = distance;
        closestIndex = index;
      }
    }
    if (closestIndex === null) {
      return;
    }

    if (startPoint) {
      // Calcula los índices globales de `trackData`
      if (startPoint.index == null) {
        return;
      }
      const startIndex = startPoint.index;
      const endIndex = closestIndex;

      const color =
        currentHighlightColorReference.current === null
          ? generateRandomColor()
          : currentHighlightColorReference.current;

      const newSegment: HighlightSegment = {
        id: createHighlightId(),
        startIndex,
        endIndex,
        color,
      };

      setDraftHighlight({
        id: 'draft-map',
        startIndex,
        endIndex,
        color,
        source: 'map',
      });
      setHighlightSegments((previous) => [...previous, newSegment]);
      setPendingDraftHighlightId(newSegment.id);

      setStartPoint(null);

      currentHighlightColorReference.current = null;
    } else {
      const color = generateRandomColor(); // Genera un nuevo color
      setStartPoint({ lng, lat, index: closestIndex });
      currentHighlightColorReference.current = color; // Almacena el color
      setDraftHighlight((current) => {
        if (
          current &&
          current.source === 'map' &&
          current.startIndex === closestIndex &&
          current.endIndex === closestIndex &&
          current.color === color
        ) {
          return current;
        }
        return {
          id: 'draft-map',
          startIndex: closestIndex,
          endIndex: closestIndex,
          color,
          source: 'map',
        };
      });
    }
  };

  function updateTackLabels() {
    if (!mapInstance.current) {
      return;
    }

    if (!showTacks) {
      for (const m of labelMarkers.current) {
        m.remove();
      }
      labelMarkers.current = [];
      return;
    }

    // 1) eliminar antiguos
    for (const m of labelMarkers.current) {
      m.remove();
    }
    labelMarkers.current = [];

    // 2) sólo los de este panel
    for (const [key, label] of Object.entries(tackMapping).filter(([k]) =>
      k.startsWith(`${idSuffix}-`),
    )) {
      const absIndex = Number(key.split('-')[1]); // parte numérica
      const tackObject = tacks[absIndex - 1];
      if (!tackObject) {
        continue;
      }
      if (maneuverVisibility?.[absIndex - 1] === false) {
        continue;
      }

      const mid = Math.floor((tackObject.start_index + tackObject.end_index) / 2);
      const p = cachedTrackData[mid];
      if (!p) {
        continue;
      }

      const element = document.createElement('div');
      element.style.cssText =
        'background:#1a3a4f;color:#dce9f2;padding:2px 4px;border-radius:2px;font-size:12px;text-align:center';
      element.textContent = String(label);

      const mk = new mapboxgl.Marker({ element: element, anchor: 'center' })
        .setLngLat([p.lon, p.lat])
        .addTo(mapInstance.current);
      labelMarkers.current.push(mk);
    }

    // Etiquetas fallback si no hay mapping explícito
    if (labelMarkers.current.length === 0) {
      let fallbackIndex = 1;
      const visibleIndices = tacks
        .map((tack, index) => ({ tack, index }))
        .filter(({ index }) => maneuverVisibility?.[index] !== false);

      for (const { tack, index } of visibleIndices) {
        const mid = Math.floor((tack.start_index + tack.end_index) / 2);
        const p = cachedTrackData[mid];
        if (!p) {
          continue;
        }

        const element = document.createElement('div');
        element.style.cssText =
          'background:#1a3a4f;color:#dce9f2;padding:2px 4px;border-radius:2px;font-size:12px;text-align:center';
        element.textContent = String(fallbackIndex);
        fallbackIndex += 1;

        const mk = new mapboxgl.Marker({ element: element, anchor: 'center' })
          .setLngLat([p.lon, p.lat])
          .addTo(mapInstance.current);
        labelMarkers.current.push(mk);
      }
    }
  }

  const removeTacks = () => {
    if (mapInstance.current?.getLayer(tackCasingLayerId)) {
      mapInstance.current.removeLayer(tackCasingLayerId);
    }
    if (mapInstance.current?.getLayer(tackLayerId)) {
      mapInstance.current.removeLayer(tackLayerId);
    }
    if (mapInstance.current?.getSource(tackSourceId)) {
      mapInstance.current.removeSource(tackSourceId);
    }
  };

  const initializeArrowMarker = async (lng: number, lat: number, angle: number) => {
    if (isPlayerActiveRef.current) {
      return;
    }
    if (!mapInstance.current) {
      return;
    }

    const svgText = await loadSvgAsHtml(arrowBoat);
    const arrowElement = document.createElement('div');
    arrowElement.innerHTML = svgText;

    const svg = arrowElement.querySelector('svg');
    if (!svg) {
      developmentLog.error('SVG element not found');
      return;
    }
    svg.style.pointerEvents = 'none';
    svg.style.position = 'absolute';
    svg.style.zIndex = '1000';
    svg.style.color = getCurrentMapOverlayPalette().boatMarker;
    adjustArrowSize(svg);

    if (boatMarkerReference.current) {
      boatMarkerReference.current.getElement().style.color =
        getCurrentMapOverlayPalette().boatMarker;
      boatMarkerReference.current.setLngLat([lng, lat]).setRotation(angle);
      adjustArrowSize(svg);
    } else {
      const newMarker = new mapboxgl.Marker({
        element: svg as unknown as HTMLElement, // Conversión a unknown primero, luego a HTMLElement
        rotation: angle,
        rotationAlignment: 'map',
        anchor: 'center',
      })
        .setLngLat([lng, lat])
        .addTo(mapInstance.current);

      boatMarkerReference.current = newMarker;
      mapInstance.current.on('zoom', () => adjustArrowSize(svg));
    }
  };

  const optimizeWorkingLabel = t('map.optimizeWorking', 'Calculando ruta óptima');

  const baselineMs = useMemo(() => {
    if (!optimizeGoal || !optimizeStart || trackData.length === 0) {
      return null;
    }
    const startIdx = optimizeStart.index;
    const startPoint = trackData[startIdx];
    if (!startPoint?.utc) {
      return null;
    }
    let closestIndex = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const [index, point] of trackData.entries()) {
      if (index < startIdx) {
        continue;
      }
      if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) {
        continue;
      }
      const dist = calculateDistance(optimizeGoal.lat, optimizeGoal.lon, point.lat, point.lon);
      if (dist < bestDistance) {
        bestDistance = dist;
        closestIndex = index;
      }
    }
    if (closestIndex === -1) {
      return null;
    }
    const startUtc = startPoint.utc;
    const targetUtc = trackData[closestIndex]?.utc;
    const startTime = startUtc ? new Date(startUtc).getTime() : Number.NaN;
    const targetTime = targetUtc ? new Date(targetUtc).getTime() : Number.NaN;
    if (!Number.isFinite(startTime) || !Number.isFinite(targetTime) || targetTime <= startTime) {
      return null;
    }
    return targetTime - startTime;
  }, [optimizeGoal, trackData]);

  const optimizedMs = optimizedRoute?.etaSeconds == null ? null : optimizedRoute.etaSeconds * 1000;
  const improvementPercent =
    optimizedMs !== null &&
    Number.isFinite(optimizedMs) &&
    baselineMs !== null &&
    Number.isFinite(baselineMs) &&
    baselineMs > 0
      ? ((baselineMs - optimizedMs) / baselineMs) * 100
      : null;

  const formatDuration = (ms: number | null) => {
    if (ms === null || !Number.isFinite(ms)) {
      return '--:--';
    }
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    if (h > 0) {
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleOptimizeClick = useCallback(() => {
    if (optimizeLoading) {
      return;
    }
    if (!optimizeStart) {
      setOptimizeError(t('map.optimize.selectStart', 'Click on the track to choose the start'));
      return;
    }
    if (!optimizeGoal) {
      setOptimizeError(t('map.optimize.selectGoal', 'Select the end point on the map'));
      return;
    }
    const startIndexAbs = trackData[optimizeStart.index]?.globalIndex ?? optimizeStart.index;
    void requestOptimizedRoute(startIndexAbs, optimizeGoal);
  }, [optimizeGoal, optimizeLoading, optimizeStart, requestOptimizedRoute, trackData, t]);

  const currentTrackRenderKey = getTrackRenderKey(safeTrackData, lastFileId);
  const hasPendingTrackRenderForLoader =
    safeTrackData.length > 0 &&
    (completedTrackRenderKeyRef.current !== currentTrackRenderKey ||
      pendingInitialCameraRef.current ||
      (isMapLoading && !hasRenderedTrackLine()));
  const shouldShowMapLoader =
    (isMapLoading || hasPendingTrackRenderForLoader) && !suppressLoadingOverlay;
  const isColorizedLineMode = lineMode === 'speed' || lineMode === 'tactical';
  const shouldRenderColorLegend = Boolean(colorLegend) && isColorizedLineMode && !isEditingSegment;
  const activeColorMetric = colorLegend?.mode === 'tactical' ? 'heel' : selectedMetric;
  const mapInputStatusMessage =
    mapInputMode === 'measure'
      ? t('map.measureActiveStatus', 'Medición activa: selecciona dos puntos del mapa.')
      : mapInputMode === 'optimize'
        ? t('map.optimizeActiveStatus', 'Optimización activa: selecciona inicio y destino en el track.')
        : mapInputMode === 'highlight'
          ? t('map.highlightActiveStatus', 'Resaltado activo: selecciona dos puntos del track.')
          : mapInputMode === 'comment-placement'
            ? t('comments.placeOnMap', 'Toca cualquier punto del mapa para situar el comentario.')
            : mapInputMode === 'player-target'
              ? t('map.playerTargetStatus', 'Reproductor activo: selecciona un punto o una boya para las laylines.')
              : '';
  const legendOverlayPalette = getCurrentMapOverlayPalette();
  const legendGradientBackground =
    colorLegend?.mode === 'tactical'
      ? `linear-gradient(to top, ${rgbTupleToCss(
          legendOverlayPalette.tacticalRampStart,
        )}, ${rgbTupleToCss(legendOverlayPalette.tacticalRampMid)}, ${rgbTupleToCss(
          legendOverlayPalette.tacticalRampEnd,
        )})`
      : colorLegend?.mode === 'speed'
        ? `linear-gradient(to top, ${rgbTupleToCss(
            legendOverlayPalette.speedRampStart,
          )}, ${rgbTupleToCss(legendOverlayPalette.speedRampEnd)})`
        : `linear-gradient(to top, #D3D3D3, #0C1A26)`;

  return (
    <div
      className="map-container"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        flex: 1,
      }}
    >
      <div
        ref={mapContainer}
        className="map-container__map"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />
      {editingSegmentId !== null && editingRange && cachedTrackData.length > 1 && (
        <div className="segment-editor-overlay">
          <SegmentEditorTimebar
            cachedTrackData={cachedTrackData}
            editingRange={editingRange}
            onEditingRangeChange={onEditingRangeChange}
            t={translateEditor}
            editingMode={editingMode}
            onEditingModeChange={setEditingMode}
            onSaveEditing={onSaveEditing}
            onCancelEditing={onCancelEditing}
            editingSegmentName={editingSegmentName}
            editingSegmentType={editingSegmentType}
            showManualZoneTools={SHOW_MANUAL_SEGMENT_TOOLS}
          />
        </div>
      )}
      {optimizeLoading && (
        <div className="optimize-card-overlay" aria-label={optimizeWorkingLabel}>
          <div className="floating-particle particle-1" />
          <div className="floating-particle particle-2" />
          <div className="floating-particle particle-3" />
          <div className="floating-particle particle-4" />
          <div className="floating-particle particle-5" />
          <OptimizeRoutePixelCard />
        </div>
      )}
      {shouldShowMapLoader && <div className="map-loading-overlay" />}
      {shouldShowMapLoader && (
        <div className="loader">
          <div className="loader-content">
            <SailTrackLogo className="logo" aria-hidden="true" focusable="false" />
            <span className="text">Sailtrack</span>
          </div>
          <div className="loader-content-transparent">
            <SailTrackLogo className="logo" aria-hidden="true" focusable="false" />
            <span className="text">Sailtrack</span>
          </div>
        </div>
      )}

      {mapInputStatusMessage && (
        <span className="sr-only" role="status" aria-live="polite">
          {mapInputStatusMessage}
        </span>
      )}

      {adminToolsConfig.showHoverCoordinates && hoverCoordinates && (
        <div className="map-hover-coordinates">
          <div className="map-hover-coordinates__item">
            <span className="map-hover-coordinates__label">Lat</span>
            <span className="map-hover-coordinates__value">{hoverCoordinates.lat.toFixed(5)}</span>
          </div>
          <div className="map-hover-coordinates__divider" />
          <div className="map-hover-coordinates__item">
            <span className="map-hover-coordinates__label">Lon</span>
            <span className="map-hover-coordinates__value">{hoverCoordinates.lon.toFixed(5)}</span>
          </div>
        </div>
      )}

      {commentPlacementMode === 'map_point' && (
        <div className="map-comment-placement-hint" role="status">
          <span>{t('comments.placeOnMap', 'Toca cualquier punto del mapa para situar el comentario.')}</span>
          <button type="button" onClick={cancelMapCommentPlacement}>
            {t('common.cancel', 'Cancelar')}
          </button>
        </div>
      )}

      {commentsLoading && (
        <span className="map-comments-loading" role="status">
          {t('common.loading', 'Cargando…')}
        </span>
      )}

      {activeComment && commentCalloutPosition && commentsVisible && (
        <>
          {commentCalloutConnectorPath && (
            <svg
              className={`map-comment-callout-connector${pinnedCommentId === activeComment.id ? ' is-pinned' : ''}`}
              data-map-color-scheme={mapColorScheme}
              aria-hidden="true"
            >
              <path d={commentCalloutConnectorPath} />
            </svg>
          )}
          <article
            className={`map-comment-callout${pinnedCommentId === activeComment.id ? ' is-pinned' : ''}`}
            data-map-color-scheme={mapColorScheme}
            style={{ left: commentCalloutPosition.left, top: commentCalloutPosition.top }}
            onMouseEnter={clearCommentDismissTimer}
            onMouseLeave={closeCommentCalloutSoon}
            aria-label={t('comments.calloutLabel', 'Comentario del mapa')}
          >
          <header className="map-comment-callout__header">
            <div className="map-comment-callout__author">
              <CommentAuthorAvatar author={activeComment.author} />
              <strong>{activeComment.author.fullName || activeComment.author.email}</strong>
            </div>
            <button
              type="button"
              className="map-comment-callout__close"
              onClick={() => {
                clearCommentDismissTimer();
                setActiveCommentId(null);
                setPinnedCommentId(null);
                setCommentCalloutPosition(null);
              }}
              aria-label={t('comments.close', 'Cerrar comentario')}
            >
              ×
            </button>
          </header>
          <MapCommentTimeMeta
            trackTime={
              activeComment.anchorType === 'race_time'
                ? commentRelativeTimes.get(activeComment.id) ?? null
                : null
            }
            createdAt={activeComment.createdAt}
            locale={navigator.language}
            trackLabel={t('comments.segmentTime', 'Tiempo del segmento')}
            createdLabel={t('comments.createdAt', 'Creado')}
            noCreatedLabel={t('comments.unknownCreation', 'Fecha no disponible')}
          />
          <p className="map-comment-callout__body">{activeComment.body}</p>
          {activeComment.attachments.length > 0 && (
            <div className="map-comment-images">
              {activeComment.attachments.map((attachment) => (
                <PrivateCommentImage
                  key={attachment.id}
                  attachment={attachment}
                  alt={t('comments.openImage', 'Abrir imagen adjunta')}
                  onOpen={setExpandedCommentAttachment}
                />
              ))}
            </div>
          )}
          {commentPermission.canManageComments && (
            <footer className="map-comment-callout__actions">
              <button type="button" onClick={() => openCommentEditor(activeComment)}>
                {t('common.edit', 'Editar')}
              </button>
              <button type="button" className="danger" onClick={() => void removeComment(activeComment)}>
                {t('common.delete', 'Eliminar')}
              </button>
            </footer>
          )}
          </article>
        </>
      )}

      {isCommentListOpen && commentPermission.canViewComments && (
        <aside
          id={`map-comments-list-${idSuffix}`}
          className={`map-comments-list${isCommentListOpen ? ' is-open' : ''}`}
          aria-label={t('comments.listLabel', 'Lista de comentarios')}
        >
          <div className="map-comments-list__header">
            <strong>{t('comments.title', 'Comentarios')}</strong>
            <button
              type="button"
              onClick={() => setIsCommentListOpen(false)}
              aria-label={t('comments.closeList', 'Cerrar lista de comentarios')}
            >
              ×
            </button>
          </div>
          {visibleMapComments.length === 0 ? (
            <p className="map-comments-list__empty">{t('comments.empty', 'Todavía no hay comentarios.')}</p>
          ) : (
            <div className="map-comments-list__items">
              {visibleMapComments.map((comment) => (
                <button
                  type="button"
                  key={comment.id}
                  className={`map-comments-list__item${comment.id === activeCommentId ? ' active' : ''}`}
                  aria-current={comment.id === activeCommentId ? 'true' : undefined}
                  onClick={() => {
                    if (!commentsVisible) {
                      setCommentsVisible(true);
                    }
                    openCommentCallout(comment, true);
                  }}
                >
                  <MapCommentTimeMeta
                    trackTime={
                      comment.anchorType === 'race_time'
                        ? commentRelativeTimes.get(comment.id) ?? null
                        : null
                    }
                    createdAt={comment.createdAt}
                    locale={navigator.language}
                    trackLabel={t('comments.segmentTime', 'Tiempo del segmento')}
                    createdLabel={t('comments.createdAt', 'Creado')}
                    noCreatedLabel={t('comments.unknownCreation', 'Fecha no disponible')}
                    compact
                  />
                  <strong>{comment.body}</strong>
                </button>
              ))}
            </div>
          )}
        </aside>
      )}

      {isCommentComposerOpen && commentComposerAnchor && (
        <section
          className="map-comment-composer"
          role="dialog"
          aria-modal="false"
          aria-label={
            editingComment
              ? t('comments.editTitle', 'Editar comentario')
              : t('comments.addTitle', 'Añadir comentario')
          }
        >
          <header className="map-comment-composer__header">
            <div>
              <span>{editingComment ? t('comments.editTitle', 'Editar comentario') : t('comments.addTitle', 'Añadir comentario')}</span>
              <small>
                {commentComposerAnchor.anchorType === 'race_time'
                  ? composerRelativeTime ?? '—'
                  : t('comments.previewOnMap', 'Vista previa en el mapa')}
              </small>
            </div>
            <button type="button" onClick={closeCommentComposer} aria-label={t('common.cancel', 'Cancelar')}>
              ×
            </button>
          </header>
          <textarea
            value={commentDraftBody}
            onChange={(event) => setCommentDraftBody(event.target.value)}
            maxLength={4000}
            rows={4}
            placeholder={t('comments.placeholder', 'Escribe una observación para el equipo…')}
            aria-label={t('comments.placeholder', 'Escribe una observación para el equipo…')}
          />
          {editingComment && editingComment.attachments.length > 0 && (
            <div className="map-comment-existing-images">
              {editingComment.attachments.map((attachment) => {
                const isRemoved = removedCommentAttachmentIds.includes(attachment.id);
                return (
                  <div key={attachment.id} className={`map-comment-existing-image${isRemoved ? ' is-removed' : ''}`}>
                    <PrivateCommentImage
                      attachment={attachment}
                      alt={t('comments.openImage', 'Abrir imagen adjunta')}
                      onOpen={setExpandedCommentAttachment}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setRemovedCommentAttachmentIds((previous) =>
                          previous.includes(attachment.id)
                            ? previous.filter((id) => id !== attachment.id)
                            : [...previous, attachment.id],
                        )
                      }
                    >
                      {isRemoved ? t('comments.keepImage', 'Conservar') : t('comments.removeImage', 'Quitar')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="map-comment-composer__uploads">
            {commentDraftFiles.map((draftFile) => (
              <div key={draftFile.previewUrl} className="map-comment-draft-image">
                <img src={draftFile.previewUrl} alt="" />
                <button
                  type="button"
                  onClick={() => removeCommentDraftFile(draftFile.previewUrl)}
                  aria-label={t('comments.removeImage', 'Quitar imagen')}
                >
                  ×
                </button>
              </div>
            ))}
            <label className="map-comment-upload-button">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={(event) => {
                  addCommentDraftFiles(event.currentTarget.files);
                  event.currentTarget.value = '';
                }}
              />
              {t('comments.attachImage', 'Adjuntar imágenes')}
            </label>
          </div>
          {commentError && <p className="map-comment-composer__error" role="alert">{commentError}</p>}
          {commentConflict && (
            <div className="map-comment-composer__conflict" role="alert">
              <span>{t('comments.conflict', 'Otra persona ha cambiado este comentario.')}</span>
              <button type="button" onClick={reloadConflictedComment} disabled={commentSubmitting}>
                {t('comments.reloadComment', 'Recargar comentario')}
              </button>
            </div>
          )}
          <footer className="map-comment-composer__actions">
            <button type="button" onClick={closeCommentComposer} disabled={commentSubmitting}>
              {t('common.cancel', 'Cancelar')}
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => void submitComment()}
              disabled={commentSubmitting || commentConflict}
            >
              {commentSubmitting ? t('common.loading', 'Guardando…') : t('comments.save', 'Guardar comentario')}
            </button>
          </footer>
        </section>
      )}

      {expandedCommentAttachment && (
        <div className="map-comment-lightbox" role="dialog" aria-modal="true" aria-label={expandedCommentAttachment.originalName}>
          <button
            type="button"
            className="map-comment-lightbox__backdrop"
            onClick={() => setExpandedCommentAttachment(null)}
            aria-label={t('comments.closeImage', 'Cerrar imagen')}
          />
          <div className="map-comment-lightbox__content">
            <PrivateCommentImage
              attachment={expandedCommentAttachment}
              alt={t('comments.openImage', 'Abrir imagen adjunta')}
              onOpen={() => null}
            />
            <button
              type="button"
              className="map-comment-lightbox__close"
              onClick={() => setExpandedCommentAttachment(null)}
              aria-label={t('comments.closeImage', 'Cerrar imagen')}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {!isEditingSegment && (isOptimizingRoute || optimizedRoute) && (
        <div className="optimize-route-panel">
          <div className="optimize-route-header">
            <span>{t('map.optimize.title', 'Optimal Route')}</span>
            <span className="beta-pill">Beta</span>
            <button
              className="optimize-route-close"
              onClick={resetOptimizationState}
              aria-label={t('map.optimize.clear', 'Close optimal route')}
            >
              ×
            </button>
          </div>
          <div className="optimize-route-body">
            <div className="optimize-route-options">
              <div className="optimize-route-option">
                <span className="optimize-route-option__label">
                  {t('map.optimize.maxTacks', 'Max maneuvers')}
                </span>
                <div className="optimize-route-option__control">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    className="optimize-route-input"
                    value={optimizeMaxTacksInput}
                    aria-label={t('map.optimize.maxTacks', 'Max maneuvers')}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      if (/^\d*$/.test(nextValue)) {
                        setOptimizeMaxTacksInput(nextValue);
                      }
                    }}
                    placeholder={t('map.optimize.maxTacksPlaceholder', 'No limit')}
                  />
                  <span className="optimize-route-option__hint">
                    {t('map.optimize.maxTacksHint', 'Leave blank for no limit')}
                  </span>
                </div>
              </div>
              <div className="optimize-route-option">
                <span className="optimize-route-option__label">
                  {t('map.optimize.minTacks', 'Min maneuvers')}
                </span>
                <div className="optimize-route-option__control">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    className="optimize-route-input"
                    value={optimizeMinTacksInput}
                    aria-label={t('map.optimize.minTacks', 'Min maneuvers')}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      if (/^\d*$/.test(nextValue)) {
                        setOptimizeMinTacksInput(nextValue);
                      }
                    }}
                    placeholder={t('map.optimize.minTacksPlaceholder', 'No minimum')}
                  />
                  <span className="optimize-route-option__hint">
                    {t('map.optimize.minTacksHint', 'Leave blank for no minimum')}
                  </span>
                </div>
              </div>
              <div className="optimize-route-option">
                <span className="optimize-route-option__label">
                  {t('map.optimize.firstBoard', 'Start board')}
                </span>
                <MapSelect
                  value={optimizeFirstBoard}
                  options={firstBoardOptions}
                  onChange={(value) => setOptimizeFirstBoard(value)}
                  wrapperClassName="optimize-route-select"
                  ariaLabel={t('map.optimize.firstBoard', 'Start board')}
                />
              </div>
              <div className="optimize-route-option">
                <span className="optimize-route-option__label">
                  {t('map.optimize.lastBoard', 'Finish board')}
                </span>
                <MapSelect
                  value={optimizeLastBoard}
                  options={lastBoardOptions}
                  onChange={(value) => setOptimizeLastBoard(value)}
                  wrapperClassName="optimize-route-select"
                  ariaLabel={t('map.optimize.lastBoard', 'Finish board')}
                />
              </div>
            </div>
            {optimizedRoute ? (
              <>
                <div className="optimize-route-metrics">
                  <div className="optimize-metric-card">
                    <span className="metric-label">
                      {t('map.optimize.timeBaseline', 'Unoptimized')}
                    </span>
                    <span className="metric-value">{formatDuration(baselineMs)}</span>
                  </div>
                  <div className="optimize-metric-card">
                    <span className="metric-label">
                      {t('map.optimize.timeOptimized', 'Optimized')}
                    </span>
                    <span className="metric-value highlight-value">
                      {formatDuration(optimizedMs)}
                    </span>
                  </div>
                  <div className="optimize-metric-card highlight-card">
                    <span className="metric-label">
                      {t('map.optimize.improvement', 'Improvement')}
                    </span>
                    <span className="metric-value">
                      {improvementPercent === null ? '--' : `${improvementPercent.toFixed(1)}%`}
                    </span>
                  </div>
                </div>
                {optimizeError && <div className="optimize-route-error">{optimizeError}</div>}
                <button
                  type="button"
                  className="optimize-button inline"
                  onClick={handleOptimizeClick}
                  disabled={optimizeLoading}
                >
                  {t('map.optimize.optimizeRoute', 'Optimize route')}
                </button>
              </>
            ) : (
              <>
                <div className="optimize-route-step">
                  {isOptimizingRoute
                    ? optimizeStart
                      ? t('map.optimize.selectGoal', 'Select the end point on the map')
                      : t('map.optimize.selectStart', 'Click on the track to choose the start')
                    : t('map.optimize.summary', 'Route calculated')}
                </div>
                <button
                  type="button"
                  className="optimize-button inline"
                  onClick={handleOptimizeClick}
                  disabled={optimizeLoading}
                >
                  {t('map.optimize.optimizeRoute', 'Optimize route')}
                </button>
                {optimizeError && <div className="optimize-route-error">{optimizeError}</div>}
              </>
            )}
          </div>
        </div>
      )}

      {SHOW_MAP_HIGHLIGHT_SEGMENT && !isEditingSegment && (
        <div className="highlights-container">
          {highlightSegments.map((segment, index) => {
            const highlightNumber = index + 1;
            const saveHighlightLabel = t('map.saveHighlight', {
              index: highlightNumber,
              defaultValue: `Save highlight ${highlightNumber}`,
            });
            const deleteHighlightLabel = t('map.deleteHighlight', {
              index: highlightNumber,
              defaultValue: `Delete highlight ${highlightNumber}`,
            });
            return (
              <div key={segment.id} className="highlight-item">
                <div
                  className="highlight-color-container"
                  style={
                    {
                      '--highlight-border-color': segment.color,
                    } as React.CSSProperties
                  }
                >
                  {canManageSelectedFile && (
                    <button
                      className="highlight-save"
                      onClick={() => {
                        setSelectedHighlightSegment(segment);
                        setShowSaveTrainingModal(true);
                      }}
                      aria-label={saveHighlightLabel}
                      title={saveHighlightLabel}
                    >
                      <SaveIcon className="highlight-save-icon" />
                    </button>
                  )}

                  {canManageSelectedFile && (
                    <button
                      className="highlight-delete"
                      onClick={() => removeHighlight(index)}
                      aria-label={deleteHighlightLabel}
                      title={deleteHighlightLabel}
                    >
                      <TrashIcon className="trash-icon" />
                    </button>
                  )}

                  <div
                    className="highlight-color"
                    style={
                      {
                        backgroundColor: segment.color,
                      } as React.CSSProperties
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {shouldRenderColorLegend && colorLegend ? (
        <div
          ref={legendContainerReference}
          className={`legends-container ${showLegend ? 'open' : ''} ${
            isLegendEditing ? 'editing' : ''
          }`}
          role={isLegendEditing ? 'group' : 'button'}
          aria-label={
            isLegendEditing
              ? t('map.legend.editor', 'Legend bounds editor')
              : t('map.legend.openEditor', 'Edit legend bounds')
          }
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            if ((event.target as HTMLElement).closest('.legend-editor')) {
              return;
            }
            setIsLegendEditing((previous) => !previous);
          }}
          onKeyDown={(event) => {
            if (!isLegendEditing && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              setIsLegendEditing(true);
            } else if (isLegendEditing && event.key === 'Escape') {
              event.preventDefault();
              setIsLegendEditing(false);
            }
          }}
        >
          <>
            <div
              className={`legend-box ${isLegendEditing ? 'is-editing' : ''}`}
              style={{ transform: `scale( ${legendScale})` }}
            >
              <strong className="legend-title">
                {colorLegend.mode === 'speed'
                  ? selectedMetric.replaceAll('_', '\u00A0').toUpperCase()
                  : 'HEEL'}
              </strong>
              {isLegendEditing ? (
                <div className="legend-editor">
                  <div className="legend-editor-row legend-editor-row--max">
                    <span className="legend-editor-label">{t('map.legend.max', 'Max')}</span>
                    <QuantitySelector
                      value={colorLegend.max}
                      min={colorLegend.min + 0.1}
                      max={metricBounds.max[activeColorMetric] ?? 100}
                      isMax
                      ariaLabel={t('map.legend.maxInput', 'Legend maximum')}
                      decrementLabel={t('map.legend.decreaseMax', 'Decrease legend maximum')}
                      incrementLabel={t('map.legend.increaseMax', 'Increase legend maximum')}
                      onChange={(newValue: number) => {
                        setColorLegend({ ...colorLegend, max: newValue });
                      }}
                      onCommit={() => {
                        setIsLegendEditing(false);
                      }}
                    />
                  </div>
                  <div className="legend-gradient-container legend-gradient-container--editing">
                    <div
                      className="legend-gradient"
                      style={{
                        background: legendGradientBackground,
                      }}
                    />
                  </div>
                  <div className="legend-editor-row legend-editor-row--min">
                    <span className="legend-editor-label">{t('map.legend.min', 'Min')}</span>
                    <QuantitySelector
                      value={colorLegend.min}
                      min={metricBounds.min[activeColorMetric] ?? -100}
                      max={colorLegend.max - 0.1}
                      ariaLabel={t('map.legend.minInput', 'Legend minimum')}
                      decrementLabel={t('map.legend.decreaseMin', 'Decrease legend minimum')}
                      incrementLabel={t('map.legend.increaseMin', 'Increase legend minimum')}
                      onChange={(newValue: number) => {
                        setColorLegend({ ...colorLegend, min: newValue });
                      }}
                      onCommit={() => {
                        setIsLegendEditing(false);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="legend-gradient-container">
                    <span className="legend-label-max">{colorLegend.max.toFixed(1)}</span>
                    <div
                      className="legend-gradient"
                      style={{
                        background: legendGradientBackground,
                      }}
                    />
                  </div>
                  <span className="legend-label-min">{colorLegend.min.toFixed(1)}</span>
                </>
              )}
            </div>

            {!isLegendEditing && selectedMetricWidth !== 'none' && (
              <div className="legend-box">
                <strong>{selectedMetricWidth.replaceAll('_', '\u00A0').toUpperCase()}</strong>
                <div className="legend-gradient-container">
                  <span className="legend-label-max">
                    {(selectedMetricWidth === 'twa_abs'
                      ? 180
                      : metricBounds.max[selectedMetricWidth] || 0
                    ).toFixed(1)}
                  </span>
                  <TriangleIcon className="triangle-icon" />
                  <span className="legend-label-min">
                    {(selectedMetricWidth === 'twa_abs'
                      ? 0
                      : metricBounds.min[selectedMetricWidth] || 0
                    ).toFixed(1)}
                  </span>
                </div>
              </div>
            )}
          </>
        </div>
      ) : null}
      {!isEditingSegment && (
        <div className={`player-controls-container${isPlayerActive ? ' active' : ''}`}>
          {isPlayerActive ? (
            <div className="player-controls-bar">
              <button
                className="player-play"
                onClick={handlePlayPause}
                aria-label={isPlaying ? t('map.pause', 'Pause') : t('map.play', 'Play')}
              >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </button>
              <div className="player-slider-block">
                <span className="player-time-left">{formatDuration(elapsedMs)}</span>
                <input
                  className="player-slider"
                  type="range"
                  min={0}
                  max={playerMaxIndex}
                  step={1}
                  value={safePlayerIndex}
                  onInput={handleTimelineInput}
                  aria-label={t('map.playerTimeline', 'Player timeline')}
                />
                <span className="player-time-right">{formatDuration(remainingMs)}</span>
              </div>
              <div className="player-speed-control">
                <MapSelect
                  value={playbackRate}
                  options={[
                    { value: 0.5, label: '0.5x' },
                    { value: 1, label: '1x' },
                    { value: 1.5, label: '1.5x' },
                    { value: 2, label: '2x' },
                    { value: 3, label: '3x' },
                    { value: 5, label: '5x' },
                    { value: 10, label: '10x' },
                    { value: 25, label: '25x' },
                    { value: 50, label: '50x' },
                    { value: 100, label: '100x' },
                  ]}
                  onChange={setPlaybackRate}
                  wrapperClassName="speed-select-wrapper"
                  triggerClassName="speed-select-trigger"
                  ariaLabel={t('map.playerSpeed', 'Playback speed')}
                  align="top"
                />
              </div>
            </div>
          ) : null}
        </div>
      )}
      {!isEditingSegment && (
        <>
          <div
            className="controls-bottom"
            role="toolbar"
            aria-label={t('map.tools', 'Herramientas del mapa')}
          >
            <button
              type="button"
              className={`player-button${isPlayerActive ? ' active' : ''}`}
              onClick={togglePlayerMode}
              disabled={playerTimeline.length === 0 && !isPlayerActive}
              aria-pressed={isPlayerActive}
              aria-label={
                isPlayerActive
                  ? t('map.playerStop', 'Desactivar reproductor')
                  : t('map.player', 'Activar reproductor')
              }
              title={
                isPlayerActive
                  ? t('map.playerStop', 'Desactivar reproductor')
                  : t('map.player', 'Activar reproductor')
              }
            >
              <MapControlIcon name="player" />
            </button>
            {commentPermission.canViewComments && (
              <button
                type="button"
                className={`map-comments-toggle${commentsVisible ? ' active' : ''}`}
                onClick={() => {
                  setCommentsVisible((visible) => !visible);
                  if (commentsVisible) {
                    setActiveCommentId(null);
                    setPinnedCommentId(null);
                    setCommentCalloutPosition(null);
                  }
                }}
                aria-pressed={commentsVisible}
                aria-label={
                  commentsVisible
                    ? t('comments.hidePins', 'Ocultar puntos de comentario')
                    : t('comments.showPins', 'Mostrar puntos de comentario')
                }
                title={
                  commentsVisible
                    ? t('comments.hidePins', 'Ocultar puntos de comentario')
                    : t('comments.showPins', 'Mostrar puntos de comentario')
                }
              >
                <MapControlIcon name="comments" />
              </button>
            )}
            {commentPermission.canViewComments && (
              <button
                type="button"
                className={`map-comments-list-trigger${isCommentListOpen ? ' active' : ''}`}
                onClick={toggleCommentList}
                aria-expanded={isCommentListOpen}
                aria-controls={`map-comments-list-${idSuffix}`}
                aria-label={
                  isCommentListOpen
                    ? t('comments.closeList', 'Cerrar lista de comentarios')
                    : t('comments.openList', 'Abrir lista de comentarios')
                }
                title={
                  isCommentListOpen
                    ? t('comments.closeList', 'Cerrar lista de comentarios')
                    : t('comments.openList', 'Abrir lista de comentarios')
                }
              >
                <MapControlIcon name="commentList" />
              </button>
            )}
            {commentPermission.canManageComments && (
              <button
                type="button"
                className={`map-comments-add${isAddingComment ? ' active' : ''}`}
                onClick={toggleCommentComposer}
                aria-pressed={isAddingComment}
                aria-label={
                  isEditingCommentPreview
                    ? t('comments.closeEdit', 'Cerrar edición de comentario')
                    : isAddingComment
                    ? t('comments.cancelAdd', 'Cancelar añadir comentario')
                    : t('comments.addTitle', 'Añadir comentario')
                }
                title={
                  isEditingCommentPreview
                    ? t('comments.closeEdit', 'Cerrar edición de comentario')
                    : isAddingComment
                    ? t('comments.cancelAdd', 'Cancelar añadir comentario')
                    : t('comments.addTitle', 'Añadir comentario')
                }
              >
                <MapControlIcon name="addComment" />
              </button>
            )}
            <button
              type="button"
              className={`measure-button${isMeasuringDistance ? ' active' : ''}`}
              onClick={toggleMeasureDistance}
              aria-pressed={isMeasuringDistance}
              aria-label={
                isMeasuringDistance
                  ? t('map.stopMeasuring', 'Stop Measuring')
                  : t('map.measureDistance', 'Measure Distance')
              }
              title={
                isMeasuringDistance
                  ? t('map.stopMeasuring', 'Stop Measuring')
                  : t('map.measureDistance', 'Measure Distance')
              }
            >
              <MapControlIcon name="measure" />
            </button>
            <button
              type="button"
              className={`optimize-button${isOptimizingRoute ? ' active' : ''}${
                optimizedRoute && !isOptimizingRoute ? ' has-result' : ''
              }`}
              onClick={toggleOptimizeRoute}
              aria-pressed={isOptimizingRoute}
              data-state={isOptimizingRoute ? 'selecting' : optimizedRoute ? 'result' : 'idle'}
              title={
                isOptimizingRoute
                  ? t(
                      'map.optimize.tooltipActive',
                      'Optimize route (click track for start, then destination)',
                    )
                  : optimizedRoute
                    ? t('map.optimize.resultVisible', 'Ruta optimizada visible; calcular otra')
                    : t('map.optimize.tooltip', 'Optimizar ruta')
              }
              aria-label={
                isOptimizingRoute
                  ? t(
                      'map.optimize.tooltipActive',
                      'Optimize route (click track for start, then destination)',
                    )
                  : optimizedRoute
                    ? t('map.optimize.resultVisible', 'Ruta optimizada visible; calcular otra')
                    : t('map.optimize.tooltip', 'Optimizar ruta')
              }
            >
              <MapControlIcon name="optimize" />
            </button>
            {SHOW_MAP_HIGHLIGHT_SEGMENT && (
              <button
                type="button"
                className={`highlight-button${isHighlighting ? ' active' : ''}`}
                onClick={toggleHighlightMode}
                aria-pressed={isHighlighting}
                aria-label={
                  isHighlighting
                    ? t('map.stopHighlighting', 'Stop Highlighting')
                    : t('map.highlightSegment', 'Highlight Segment')
                }
                title={
                  isHighlighting
                    ? t('map.stopHighlighting', 'Stop Highlighting')
                    : t('map.highlightSegment', 'Highlight Segment')
                }
              >
                <MapControlIcon name="highlight" />
              </button>
            )}
            <button
              type="button"
              className={`maneuvers-button${showTacks ? ' active' : ''}`}
              onClick={toggleTacks}
              aria-pressed={showTacks}
              aria-label={
                showTacks
                  ? t('map.hideManeuvers', 'Hide Maneuvers')
                  : t('map.showManeuvers', 'Show Maneuvers')
              }
              title={
                showTacks
                  ? t('map.hideManeuvers', 'Hide Maneuvers')
                  : t('map.showManeuvers', 'Show Maneuvers')
              }
            >
              <MapControlIcon name="tacks" />
            </button>
            {isLadderAvailable ? (
              <button
                type="button"
                className={`line-mode-button ladder-button${lineMode === 'ladder' ? ' active' : ''}`}
                onClick={() => {
                  toggleLineMode(lineMode === 'ladder' ? 'plain' : 'ladder');
                }}
                aria-pressed={lineMode === 'ladder'}
                aria-label={t('map.ladder', 'Ladder')}
                title={t('map.ladder', 'Ladder')}
              >
                <MapControlIcon name="ladder" />
              </button>
            ) : null}
          </div>
          <div className="orientation-control">
            <MapSelect
              value={trackModes}
              onChange={handleTrackModeChange}
              options={orientationOptions}
              wrapperClassName="track-modes-wrapper orientation-toggle"
              triggerClassName="track-modes orientation-toggle-trigger"
              align="top"
              ariaLabel={t('map.trackOrientation', 'Track orientation')}
              triggerContent={
                <span className="orientation-trigger-content" aria-hidden="true">
                  <MapControlIcon
                    name="orientation"
                    className="orientation-icon"
                    style={{ transform: `rotate(${smoothCompassRotation}deg)` }}
                  />
                </span>
              }
            />
          </div>
        </>
      )}
      {!isEditingSegment && (
        <div
          className="controls-up"
          role="toolbar"
          aria-label={t('map.lineModesAndStats', 'Modos de línea y estadísticas')}
        >
          <button
            type="button"
            className={`line-mode-button${lineMode === 'plain' ? ' active' : ''}`}
            onClick={() => toggleLineMode('plain')}
            aria-pressed={lineMode === 'plain'}
            aria-label={t('map.plain', 'Plain')}
            title={t('map.plain', 'Plain')}
          >
            <MapControlIcon name="plain" />
          </button>

          <button
            type="button"
            className={`line-mode-button${lineMode === 'speed' ? ' active' : ''}`}
            onClick={() => {
              handleSpeedMode();
            }}
            aria-pressed={lineMode === 'speed'}
            aria-label={t('map.speed', 'Speed')}
            title={t('map.speed', 'Speed')}
          >
            <MapControlIcon name="speed" />
          </button>

          <button
            type="button"
            className={`line-mode-button${lineMode === 'tactical' ? ' active' : ''}`}
            onClick={() => {
              setSelectedMetric('heel');
              updateColorLegend('heel', 'tactical');
              toggleLineMode('tactical', undefined, 'heel', selectedMetricWidth);
            }}
            aria-pressed={lineMode === 'tactical'}
            aria-label={t('map.tactical', 'Tactical')}
            title={t('map.tactical', 'Tactical')}
          >
            <MapControlIcon name="tactical" />
          </button>
          <button
            type="button"
            className={`race-stats-button${isRaceStatsVisible ? ' active' : ''}`}
            onClick={toggleRaceStats}
            aria-pressed={isRaceStatsVisible}
            aria-label={t('map.raceStats', 'Race Stats')}
            title={t('map.raceStats', 'Race Stats')}
          >
            <MapControlIcon name="stats" />
          </button>
          {lineMode === 'speed' && speedMetricOptions.length > 0 ? (
            <MapSelect
              value={speedMetricValueForSelect}
              onChange={(next) => {
                const newMetric = next as SpeedMetricKey;
                setSelectedMetric(newMetric);
                updateColorLegend(newMetric, lineMode);
              }}
              options={speedMetricOptions}
              wrapperClassName="speed-select-wrapper"
              ariaLabel={t('map.colorMetricDropdown', 'Select color metric')}
            />
          ) : null}
          {(lineMode === 'speed' || lineMode === 'tactical') && widthMetricOptions.length > 0 ? (
            <MapSelect
              value={selectedMetricWidth}
              onChange={(next) => {
                const newMetricMode = next as WidthMetricKey;
                setSelectedMetricWidth(newMetricMode);
              }}
              options={widthMetricOptions}
              wrapperClassName="metric-select-wrapper"
              ariaLabel={t('map.widthMetricDropdown', 'Select width metric')}
            />
          ) : null}
        </div>
      )}
      {!isEditingSegment && isRaceStatsVisible && (
        <div
          className="race-stats-panel"
          ref={panelReference}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${panelSize.width}px`,
            height: `${panelSize.height}px`,
            position: 'absolute',
            cursor: dragging ? 'grabbing' : 'grab',
            userSelect: 'none',
          }}
          role="dialog"
          aria-label={t('map.raceStatsPanelAria', 'Race stats panel')}
        >
          <div
            className="race-stats-header"
            onMouseDown={handleMouseDown}
            role="button"
            tabIndex={0}
            onKeyDown={(event_) => {
              if (event_.key === 'Escape') {
                toggleRaceStats();
              }
            }}
            aria-label={t('map.dragRaceStatsPanelAria', 'Drag race stats panel')}
          >
            <h2>{t('map.raceStats')}</h2>
            <button
              className="close-stats-button"
              onClick={toggleRaceStats}
              aria-label={t('map.raceStatsPanel.close', 'Close race stats panel')}
              title={t('map.raceStatsPanel.close', 'Close race stats panel')}
            >
              ×
            </button>
          </div>
          <div className="race-stats-content">
            {races.length === 0 && tackStats.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#D3D3D3',
                  fontSize: '1.2rem',
                }}
              >
                {t(
                  'map.raceStatsPanel.emptyMessage',
                  'No information to display. Please select a race, maneuver or file.',
                )}
              </div>
            ) : (
              <>
                {races.length > 0 && (
                  <RenderizarRaces
                    races={races}
                    trackDataArray={cachedTrackData}
                    modes={selectedFile?.modes || []}
                    tacks={tacks}
                  />
                )}
                {tackStats.length > 0 && (
                  <>
                    <h3 className="tacks-header">
                      {t('map.raceStatsPanel.tacksHeader', 'Tacks/Gybes:')}
                    </h3>
                    <table className="race-stats-table">
                      <thead>
                        <tr>
                          {/* Cabecera "TACK / GYBE:" centrada */}
                          <th style={{ textAlign: 'center' }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                              }}
                            >
                              <strong>
                                {t('map.raceStatsPanel.tackGybeHeader', 'TACK / GYBE:')}
                              </strong>
                            </div>
                          </th>

                          {/* Columnas TACK/GYBE con la "x" también centradas */}
                          {tackStats.map((tack, index) => {
                            const tackTypeLabel =
                              tack.type === 'gybe'
                                ? t('map.raceStatsPanel.gybe', 'Gybe')
                                : t('map.raceStatsPanel.tack', 'Tack');
                            const tackName = tackMapping[`${idSuffix}-${tack.tack}`] ?? tack.tack;
                            const removeColumnLabel = t('map.raceStatsPanel.removeColumn', {
                              column: `${tackTypeLabel} ${tackName}`,
                              defaultValue: `Remove column ${tackTypeLabel} ${tackName}`,
                            });
                            return (
                              <th key={`tack-header-${tack.tack}`} style={{ textAlign: 'center' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                  }}
                                >
                                  <strong>
                                    {tackTypeLabel} {tackName}
                                  </strong>

                                  <button
                                    className="delete-column-button"
                                    onClick={() => {
                                      setTackStats((previousStats) =>
                                        previousStats.filter((_, index_) => index_ !== index),
                                      );
                                      setTackMapping((previousMapping) => {
                                        const newMapping = { ...previousMapping };
                                        delete newMapping[tack.tack];
                                        return newMapping;
                                      });
                                    }}
                                    aria-label={removeColumnLabel}
                                    title={removeColumnLabel}
                                  >
                                    ×
                                  </button>
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{t('map.raceStatsPanel.rows.maxRudder', 'Max rudder [º]')}</td>
                          {tackStats.map((tack) => (
                            <td key={`max-rudder-${tack.tack}`}>
                              {tack.maxRudder == null
                                ? notAvailableLabel
                                : tack.maxRudder.toFixed(1)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td>
                            {t('map.raceStatsPanel.rows.timeToMaxRudder', 'Time to max rudder [s]')}
                          </td>
                          {tackStats.map((tack) => (
                            <td key={`time-max-rudder-${tack.tack}`}>
                              {tack.timeToMaxRudder == null
                                ? notAvailableLabel
                                : tack.timeToMaxRudder.toFixed(1)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td>{t('map.raceStatsPanel.rows.preTackSog', 'Pre-tack SOG [kts]')}</td>
                          {tackStats.map((tack) => (
                            <td key={`sog-pre-${tack.tack}`}>
                              {tack.avgSogBeforeTack == null
                                ? notAvailableLabel
                                : tack.avgSogBeforeTack.toFixed(1)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td>{t('map.raceStatsPanel.rows.preTackTwa', 'Pre-tack TWA [º]')}</td>
                          {tackStats.map((tack) => (
                            <td key={`twa-pre-${tack.tack}`}>
                              {tack.avgTwaBeforeTack == null
                                ? notAvailableLabel
                                : tack.avgTwaBeforeTack.toFixed(1)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td>{t('map.raceStatsPanel.rows.minSog', 'Min SOG [kts]')}</td>
                          {tackStats.map((tack) => (
                            <td key={`sog-min-${tack.tack}`}>
                              {tack.SogMin == null ? notAvailableLabel : tack.SogMin.toFixed(1)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td>{t('map.raceStatsPanel.rows.timeToSog90', 'Time to SOG 90% [s]')}</td>
                          {tackStats.map((tack) => (
                            <td key={`recover-90-${tack.tack}`}>
                              {tack.timeToRecovery90 == null
                                ? notAvailableLabel
                                : tack.timeToRecovery90.toFixed(1)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td>{t('map.raceStatsPanel.rows.timeToSog95', 'Time to SOG 95% [s]')}</td>
                          {tackStats.map((tack) => (
                            <td key={`recover-95-${tack.tack}`}>
                              {tack.timeToRecovery95 == null
                                ? notAvailableLabel
                                : tack.timeToRecovery95.toFixed(1)}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}
              </>
            )}
          </div>
          <div
            className="resize-handle resize-handle-n"
            onMouseDown={(event) => handleResizeMouseDown(event, 'n')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
            }}
            style={{
              position: 'absolute',
              top: '0',
              left: '50%',
              transform: 'translateX(-50%)',
              cursor: 'ns-resize',
              width: '100%',
              height: '8px',
              zIndex: 100,
            }}
          />
          <div
            className="resize-handle resize-handle-s"
            onMouseDown={(event) => handleResizeMouseDown(event, 's')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
            }}
            style={{
              position: 'absolute',
              bottom: '0',
              left: '50%',
              transform: 'translateX(-50%)',
              cursor: 'ns-resize',
              width: '100%',
              height: '8px',
              zIndex: 100,
            }}
          />
          <div
            className="resize-handle resize-handle-e"
            onMouseDown={(event) => handleResizeMouseDown(event, 'e')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
            }}
            style={{
              position: 'absolute',
              top: '50%',
              right: '0',
              transform: 'translateY(-50%)',
              cursor: 'ew-resize',
              width: '8px',
              height: '100%',
              zIndex: 100,
            }}
          />
          <div
            className="resize-handle resize-handle-w"
            onMouseDown={(event) => handleResizeMouseDown(event, 'w')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
            }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '0',
              transform: 'translateY(-50%)',
              cursor: 'ew-resize',
              width: '8px',
              height: '100%',
              zIndex: 100,
            }}
          />
          <div
            className="resize-handle resize-handle-nw"
            onMouseDown={(event) => handleResizeMouseDown(event, 'nw')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
            }}
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              cursor: 'nwse-resize',
              width: '16px',
              height: '16px',
              zIndex: 101,
            }}
          />
          <div
            className="resize-handle resize-handle-ne"
            onMouseDown={(event) => handleResizeMouseDown(event, 'ne')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
            }}
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              cursor: 'nesw-resize',
              width: '16px',
              height: '16px',
              zIndex: 101,
            }}
          />
          <div
            className="resize-handle resize-handle-sw"
            onMouseDown={(event) => handleResizeMouseDown(event, 'sw')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
            }}
            style={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              cursor: 'nesw-resize',
              width: '16px',
              height: '16px',
              zIndex: 101,
            }}
          />
          <div
            className="resize-handle resize-handle-se"
            onMouseDown={(event) => handleResizeMouseDown(event, 'se')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
            }}
            style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              cursor: 'nwse-resize',
              width: '16px',
              height: '16px',
              zIndex: 101,
            }}
          />
        </div>
      )}
    </div>
  );
};
// eslint-disable-next-line import/no-default-export
export default MapPanel;
